import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import QRCode from "qrcode";
import DraggableModal from "./DraggableModal";
/* Legacy emailHtml builder removed — Email Warehouse now uses
   EmailDialog + gmailApi. Kept import path if other modules need it. */
import BulkDeleteDialog from "./BulkDeleteDialog";
import BulkStatusDialog from "./BulkStatusDialog";
import SearchSelect from "./SearchSelect";
import FileDropZone from "./FileDropZone";
import EmailDialog from "./EmailDialog";
import ButtonMenu from "./ButtonMenu";
import TrackingLinkModal from "./TrackingLinkModal";
import DocPreviewModal from "./DocPreviewModal";
import VoiceAssistant from "./VoiceAssistant";
import { buildShipmentVoiceContract, draftShipmentFromVoiceState } from "./shipmentVoiceContract";
import { defaultOpenSeasonIds, buildSeasonFilterOptions, seasonFilterPlaceholder, resolveSeasonId } from "./seasonFilter";
import { extractCommodity, isCertAvailableForShipment, availableForCert, nrmCertNum, isCertExpired, computeCertUsage } from "./exportCertUsage";
import useDragReorder, { DragHandle } from "./useDragReorder";
import ExportButton from "./ExportButton";
import { num2, intOr, s as xs } from "./xlsxExport";
import { LOGO_B64 } from "./SalesReports";
import { deriveDeliveryStatus, derivePalletStatus, resolvePendingStage, buildSlPassingIndex, invoicesForPallet, twinRiverPalletStatus, twinRiverLotStatus } from "./saleStatusCalc";
import { loadCompanyProfile, composeEmailBody, pickEmailTemplate } from "./CompanyProfile";
import { appendDisclosureByAccount, filterContactsByCategory, appendQualityReportDisclosure } from "./emailHelpers";
import { QualityReportPreview, buildQualityReportBlob, qualityReportFilename, shipmentQualityProductGroups } from "./qualityReport";
import { buildBlackberrySpecAtOriginBlob, BLACKBERRY_SPEC_FILENAME } from "./blackberrySpecAtOrigin";
import { buildBlueberrySpecAtOriginBlob, BLUEBERRY_SPEC_FILENAME } from "./blueberrySpecAtOrigin";
import { saveDoc, loadDoc } from "./gfiDB";
import { loadFromSupabase, saveToSupabase, saveManyToSupabase, deleteFromSupabase, getCached } from "../supabaseDB";
import { RejectionBadge, rejectedCasesForLot, RejectionDetailsReadOnly } from "./rejectionReport";
import { ProtectionResponsesReadOnly } from "./protectionReport";
import useDocumentTitle from "../useDocumentTitle";
import { uploadFileToStorage, removeFromStorage } from "../storageImages";
import {
  isConfigured as driveConfigured,
  isSignedIn as driveSignedIn,
  signIn as driveSignIn,
  ensurePath as driveEnsurePath,
  ensureFolder as driveEnsureFolder,
  folderViewUrl as driveFolderViewUrl,
  rootPathSegments,
  seasonFolderName,
  embarqueFolderName,
  uploadFile as driveUploadFile,
  buildLocalPath,
  openLocalFolder,
} from "../googleDrive";
import { htmlToPdfBlob, safeFilename } from "../pdfRenderer";

function freshInvoicesShp() {
  var cached = getCached("invoices");
  return (cached && cached.length > 0) ? cached : loadLS("gfi-invoices", []);
}

/* Same bucket the shipper portal writes to (see ShipperPortal.js).
   Admin-side document uploads in this module now go to Storage too,
   so the `shipments.documents` JSONB column stays small (just
   metadata + a `storagePath` + `url`) — putting 1.5MB of base64 in
   the column was what blew past localStorage's 5MB cap. */
const SHIPMENT_DOCS_BUCKET = "shipment-docs";

/* Sanitize a free-text id (supplier code, lot, filename) for use
   as a Storage path segment. Storage keys are ascii-safe alphanumeric
   plus . _ - ; everything else (spaces, accents, slashes from the
   original filename) gets folded to "_". */
function safeStoragePathSegment(s) {
  return String(s == null ? "" : s)
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .replace(/^_+|_+$/g, "")
    || "_";
}

function extFromShipDoc(file) {
  var t = (file && file.type ? String(file.type) : "").toLowerCase();
  if (t.includes("pdf"))  return "pdf";
  if (t.includes("png"))  return "png";
  if (t.includes("jpeg") || t.includes("jpg")) return "jpg";
  if (t.includes("webp")) return "webp";
  if (t.includes("gif"))  return "gif";
  if (t.includes("msword") || t.includes("wordprocessingml")) return "docx";
  if (t.includes("excel") || t.includes("spreadsheetml")) return "xlsx";
  if (t.includes("plain")) return "txt";
  if (t.includes("csv"))   return "csv";
  var name = file && file.name ? String(file.name) : "";
  var m = name.match(/\.([a-zA-Z0-9]{1,8})$/);
  return m ? m[1].toLowerCase() : "bin";
}

const T = {
  en: {
    title: "Shipment Registry", add: "New shipment", edit: "Edit shipment",
    search: "Search by lot, shipper, invoice, or proforma...",
    showing: "Showing", of: "of", total: "shipments", noResults: "No shipments match",
    lot: "Lot", departure: "Departure", arrival: "Arrival", week: "Wk",
    shipper: "Shipper", proforma: "Proforma", shipperInvoice: "Invoice",
    warehouse: "Warehouse", products: "Products", itemDesc: "Item / Pack / Label", cases: "Cases", pallets: "Pallets", status: "Status",
    lotNumber: "Lot", dateDeparture: "Departure (MX)", dateArrival: "Arrival (US) ETA",
    dateDepartureSub: "Date of departure from origin",
    dateArrivalSub: "Estimated arrival date at destination",
    weekNumber: "Week", selectShipper: "Select shipper", sourceShipper: "Shipper", sourceClient: "Client", selectClient: "Select client",
    proformaNumber: "Proforma", shipperInvoiceNum: "Shipper invoice",
    numEconomico: "Económico", codigoAlpha: "Alpha code",
    entryNumber: "Entry #",
    brokerMX: "Broker MX", brokerUS: "Broker US",
    selectBroker: "Select", optionalField: "optional",
    selectWarehouse: "Select warehouse",
    product: "Product", selectProduct: "Select product",
    presentation: "Product description", selectPresentation: "Select description",
    category: "CAT", lineCases: "Cases", linePallets: "Pallets",
    linePrice: "Price", lineTotal: "Total",
    exportCert: "Export Certificate", selectCert: "Select certificate",
    noCertNeeded: "Not required", noCertAvailable: "No cert available",
    certPending: "Pending", certManual: "Manual entry",
    addLine: "Add line",
    /* 2026-10-23, Arturo: "ocasionalmente terey avocados exporta fruta
       de gomez berries por tener complicaciones, necesito poder
       distinguir los pallets de cada uno... una linea columna que se
       maneje solo a nivel interno, es decir no viaje a ningun
       documento... esto en los embarques y que viaje solo al bill" —
       internal-only note (never printed on any shipper/client
       document) for the real origin when the fruit on this pallet
       didn't actually come from the shipment's own official Shipper.
       2026-10-23 follow-up: "esa info no la necesito en la linea de
       producto solo en el pallet manifest" — pallet-level only, never
       on product lines. Dropdown over the Suppliers catalog (full name
       shown while open, collapses to just initials once picked — see
       actualShipperOptions). Rides automatically to the Bill the same
       way Tag ID/lost cases already do (BillRegistry.js's
       deepCopyPalletManifest spreads every pallet field) — never
       copied anywhere else (Invoice, ShipperPortal, Quality Report,
       Protection Notice, Packing List). */
    actualShipperInitials: "Actual Shipper (internal)",
    actualShipperInitialsShort: "Shipper (int.)",
    actualShipperInitialsHint: "Internal only — never printed on any document. Fill in only when fruit on this pallet actually came from a different shipper than the one on this shipment.",
    /* Pallet Manifest section */
    palletManifest: "Pallet Manifest",
    addPallet: "Add pallet",
    palletNum: "Pallet #",
    tagId: "Tag ID",
    condition: "Condition",
    packType: "Pack",
    unitsCase: "Units/Case",
    casesPallet: "Cases/Pallet",
    label: "Label",
    variety: "Variety",
    traceability: "Traceability (Julian)",
    growerCode: "Grower code",
    splitByProducer: "+ Split",
    removeProducerSplit: "Merge back to one producer",
    addAnotherProducer: "+ Add another producer",
    removeProducerRow: "Remove producer",
    producerCases: "Cases",
    producerTraceability: "Traceability",
    producerGrowerCode: "Grower Code",
    multipleProducersNote: "Multiple producers — see rows below",
    weight: "Weight",
    palletSubtotal: "Subtotal",
    palletGrandTotal: "TOTAL",
    texasQcReport: "Texas QC Report",
    texasQcModeInternal: "2 Columns (GFI Team)",
    texasQcModeClarifresh: "3 Columns (ClariFresh)",
    texasQuality: "Quality",
    texasCondition: "Condition",
    texasSevereCondition: "Severe condition",
    texasQcExcellent: "Excellent",
    texasQcVeryGood: "Very Good",
    texasQcGoodQuality: "Good Quality",
    texasQcFairQuality: "Fair Quality",
    texasQcGood: "Good",
    texasQcRegular: "Regular",
    texasQcBadQuality: "Bad Quality",
    texasQcPoor: "Poor",
    texasQcPoorQuality: "Poor Quality",
    qcDefects: "Defects",
    qcDefectTotal: "Total",
    qcDefectsNone: "No defects",
    qcDefectsAllAdded: "All added",
    qcCategoryPlaceholder: "Condition...",
    qcDefectAddPlaceholder: "+ Add defect",
    qcDefectCustomPlaceholder: "Defect name...",
    qcTolDecay: "Decay",
    qcTolMold: "Mold",
    qcTolInsect: "Insect",
    qcTolLeaky: "Leaky",
    qcTolShrivel: "Shrivel",
    qcTolSoft: "Soft",
    qcTolBrokenSkin: "Broken skin",
    qcTolAttachedRemains: "Attached remains",
    qcTolFloralRemains: "Floral remains",
    qcTolPeduncle: "Peduncle",
    qcTolOffColor: "Off color",
    qcTolBerryDamage: "Berry damage",
    qcTolRedBerries: "Red berries",
    qcTolGreenBerries: "Green berries",
    qcTolScars: "Scars",
    qcTolDryCell: "Dry cell",
    /* Blackberry-only (TR-SPEC-BLK-MX + real QC 17470/LOT 58069) —
       keys never used by any other product's TOLERANCE_DEFECTS_BY_PRODUCT
       list, so no collision risk reusing short names later. */
    qcTolRedCell: "Red cell",
    qcTolRegression: "Regression",
    /* GFI Team/internal mode only, 2026-10-15 — Arturo: "regression
       ponlo como mechanical damage, que sea identico al reporte" — the
       underlying key stays "regression" (same concept, see
       TOLERANCE_DEFECTS_INTERNAL's comment), but internal mode's real
       report literally calls it "Mechanical Damage" — ClariFresh
       (Blackberry condition) keeps "Regression" untouched. */
    qcTolMechanicalDamage: "Mechanical Damage",
    qcTolWhiteCells: "White cells",
    qcTolRedGreenDryTips: "Red/green or dry tips",
    qcTolAttachedCore: "Attached core",
    /* GFI Team (internal mode) quality report, 2026-10-15 — "Red Cells"/
       "Dry Cells"/"Leaking"/"Freeze Damage"/"Soft Fruit", straight from
       the real report. Regression/White Cells/Shrivel/Mold/Decay are
       reused from above (identical labels, no reason to duplicate). */
    qcTolRedCells: "Red cells",
    qcTolDryCells: "Dry cells",
    qcTolLeaking: "Leaking",
    qcTolFreezeDamage: "Freeze damage",
    qcTolSoftFruit: "Soft fruit",
    qcTolCustom: "Other (specify)",
    qcCatSevereCondition: "Severe Condition",
    qcCatCondition: "Condition",
    qcCatQuality: "Quality",
    /* 2026-10-21, Arturo: "debo agregar que se encontro gusano... no
       esta como un defecto de condicion ni calidad, mas bien agrega
       algo como Findings y ahi se selecciona de algunas opciones como
       bugs, worms, insects, others y en others se agrega
       manualmente... cualquier finding encontrado es de severidad
       media alta" — a 4th capture category, independent of ClariFresh
       vs internal mode (same fixed option list either way, see
       QC_FINDINGS_OPTIONS), always colored as medium-high severity
       regardless of which specific finding. */
    qcCatFindings: "Findings",
    qcTolBugs: "Bugs",
    qcTolWorms: "Worms",
    qcTolInsects: "Insects",
    qcDefectsSave: "Save",
    qcDefectsEdit: "Edit",
    qcDefectsRemoveCategory: "Remove category",
    lotRegressionLabel: "Total Regression shown in this load was:",
    qcTwinRiverStatus: "Twin River — delivery status",
    qcTwinRiverGood: "Good Delivery",
    qcTwinRiverProtection: "Protection",
    qcTwinRiverHint: "Set once per pallet, from Twin River's own quality report — not derived from the grades/defects above.",
    palletNoLabels: "No labels in catalog yet",
    palletProdMatch: "Catalog match",
    palletProdNew: "Not in catalog",
    /* Case-loss notation (2026-09-21, Arturo: repack at the Texas
       warehouse sometimes loses cases — record it per pallet on the
       shipment, carried forward to the Bill for settlement, without
       touching the pallet's real casesPallet count). Work type is one
       of the 2 real operations Arturo named: "Make Up" (cases just
       re-stacked/rearranged to hide bad-looking ones, fruit not
       actually removed) vs "Re-Pack" (mold/rot actually pulled out —
       this is the one that usually loses real cases). */
    casesLost: "Cases lost",
    lossReason: "Texas work",
    lossReasonMakeup: "Make Up",
    lossReasonRepack: "Re-Pack",
    lossReasonOther: "Other",
    lossReasonCustomPlaceholder: "Describe the cause",
    lossNotedBadge: "lost",
    /* Print docs */
    printReceiving: "Receiving Sheet",
    printInboundPacking: "Inbound Packing List",
    printCartaPorte: "Bill of Lading",
    printCartaInstr: "Shipping Instructions",
    /* Google Drive */
    driveCreateFolder: "Create Drive Folder",
    driveCreating: "Creating...",
    driveCreated: "Folder created on Google Drive",
    driveOpenFolder: "Open Folder",
    driveSignInNeeded: "Sign in to Google Drive first",
    driveNotConfigured: "REACT_APP_GOOGLE_CLIENT_ID not set in .env.local",
    driveError: "Drive error",
    driveUploadDocs: "Upload to Drive",
    driveUploading: "Uploading...",
    preview: "Preview",
    printPdf: "Print PDF",
    more: "More actions",
    emailInboundACS: "Email Warehouse",
    sendDumpOrder: "Email Dump Order",
    sendDumpOrderShipper: "Email Dump Order — Shipper",
    dumpOrderTitle: "Dump Order — {warehouse}",
    dumpOrderShipperTitle: "Dump Order — {shipper}",
    dumpOrderDefaultSubject: "Dump Order {lot}",
    dumpOrderDefaultBody: "Good morning,\n\nPlease find attached the Dump Order for LOT {lot}, authorizing disposal of the cases detailed below.\n\nThanks,\nGround Fresh International",
    dumpOrderShipperDefaultBody: "Good morning,\n\nPlease find attached the Dump Order issued for LOT {lot}, authorizing the warehouse to dispose of the cases detailed below.\n\nThanks,\nGround Fresh International",
    emailCustomsBroker: "Email Customs Broker",
    emailBrokerTitle: "Email to Customs Broker",
    emailBrokerDefaultSubject: "Shipment {lot} — {shipper} — ETA {eta}",
    emailBrokerDefaultBody: "Good morning,\n\nPlease find attached the documentation for shipment lot {lot} from {shipper}, ETA {eta}, proforma {proforma}. Please proceed with customs clearance.\n\nBest regards,\nGround Fresh International Inc.",
    emailBrokerDocBoL: "Bill of Lading",
    emailBrokerDocReceiving: "Receiving Sheet",
    emailBrokerDocShipment: "Shipment documents",
    emailNoBrokerMX: "No MX customs broker assigned to this shipment.",
    emailQualityReport: "Send Quality Report",
    emailQualityNoGrades: "Grade at least one pallet's Quality/Condition (Texas QC Report) before sending.",
    emailQualityNoShipperContacts: "This shipment's supplier has no contacts with an email on file",
    emailQualitySendBlocked: "Add the quality team's comments before sending",
    emailQualityAttachRequired: "Attach the inspection report before sending",
    sendTrackingLink: "Send Tracking Link",
    emailWarehouseTitle: "Email warehouse",
    emailWarehouseDocBoL: "Bill of Lading",
    emailWarehouseDocReceiving: "Receiving Manifest",
    emailWarehouseDocInboundPacking: "Inbound Packing List",
    emailWarehouseDocDumpOrder: "Dump Order",
    emailWarehouseDefaultSubject: "Receiving Sheet {lot}",
    emailWarehouseDefaultBody: "Good morning,\n\nPlease find attached the loading documents for the upcoming inbound shipment.\n\nLOT: {lot}\nProforma: {proforma}\nShipper: {shipper}\nETA: {eta}\n\nThanks,\nGround Fresh International",
    emailNoWarehouse: "This shipment has no warehouse assigned",
    emailHtmlCopied: "HTML copied to clipboard — paste into Outlook",
    emailHtmlError: "Couldn't copy HTML — browser blocked clipboard access",
    sealNumber: "Seal",
    thermograph: "Temp Recorder #",
    docPopupBlocked: "⚠ Pop-up blocked. Allow pop-ups for this site.",
    draft: "Draft", received: "Received", billed: "Billed",
    createBill: "Create Bill", duplicate: "New Proforma (same invoice)",
    documents: "Documents", uploadDoc: "Upload document",
    transport: "Carrier Info", transportSub: "Carrier and truck details",
    carrier: "Carrier name", driver: "Driver", platesTruck: "Truck plates", econTruck: "Truck number",
    platesTrailer: "Trailer plates", econTrailer: "Trailer number",
    caat: "CAAT", phone: "Cell phone", truckInfo: "Truck info", trailerInfo: "Trailer info",
    openLocalFolder: "Open folder",
    openLocalFolderHint: "Shortcut downloaded. IMPORTANT: rename the file — change the extension from .download to .url — then double-click it to open the folder. Path also copied to clipboard.",
    save: "Save", cancel: "Cancel", delete: "Delete", editBtn: "Edit", close: "Close",
    clearForm: "Clear Form", confirmClear: "Clear all form fields? Unsaved data will be lost.",
    confirmLoseManifestRows: "{n} Pallet Manifest row(s) with captured data will be removed. Continue?",
    duplicatePalletNum: "Duplicate Pallet #",
    confirmDel: "Delete this shipment?", yes: "Yes, delete", no: "Cancel",
    confirmExit: "Unsaved changes. Exit?", yesExit: "Yes, exit", noExit: "Keep editing",
    yesContinueSaving: "Continue saving", noContinueSaving: "Keep editing",
    saved: "Shipment saved", deleted: "Shipment deleted",
    dupLot: "Lot number already exists", fillRequired: "Fill required fields and add at least one product line",
    notes: "Notes",
    all: "All", allShippers: "All shippers", allWarehouses: "All warehouses", allStatuses: "All statuses", allSaleStatuses: "All sale statuses", allCerts: "All certificates",
    goodDelivery: "Good Delivery", protection: "Protection", pas: "PAS", dumped: "Dumped", donated: "Donated", samples: "Samples", transfer: "Transfer",
  },
  es: {
    title: "Registro de Embarques", add: "Nuevo embarque", edit: "Editar embarque",
    search: "Buscar por lote, proveedor, factura o proforma...",
    showing: "Mostrando", of: "de", total: "embarques", noResults: "Sin resultados",
    lot: "Lote", departure: "Salida", arrival: "Llegada", week: "Sem",
    shipper: "Exportador", proforma: "Proforma", shipperInvoice: "Factura",
    warehouse: "Bodega", products: "Productos", itemDesc: "Producto / Tipo / Etiqueta", cases: "Cajas", pallets: "Pallets", status: "Estado",
    lotNumber: "Lote", dateDeparture: "Salida (MX)", dateArrival: "Llegada (US) ETA",
    dateDepartureSub: "Fecha de salida de origen",
    dateArrivalSub: "Fecha estimada de llegada a destino",
    weekNumber: "Semana", selectShipper: "Seleccionar exportador", sourceShipper: "Exportador", sourceClient: "Cliente", selectClient: "Seleccionar cliente",
    proformaNumber: "Proforma", shipperInvoiceNum: "Factura proveedor",
    numEconomico: "Económico", codigoAlpha: "Código alfa",
    entryNumber: "Entry #",
    brokerMX: "Agente MX", brokerUS: "Agente US",
    selectBroker: "Seleccionar", optionalField: "opcional",
    selectWarehouse: "Seleccionar bodega",
    product: "Producto", selectProduct: "Seleccionar producto",
    presentation: "Descripción del producto", selectPresentation: "Seleccionar descripción",
    category: "CAT", lineCases: "Cajas", linePallets: "Pallets",
    linePrice: "Precio", lineTotal: "Total",
    exportCert: "Cert. exportación", selectCert: "Seleccionar certificado",
    noCertNeeded: "No requerido", noCertAvailable: "Sin certificado",
    certPending: "Pendiente", certManual: "Captura manual",
    addLine: "Agregar línea",
    actualShipperInitials: "Exportador real (interno)",
    actualShipperInitialsShort: "Exportador (int.)",
    actualShipperInitialsHint: "Solo interno — nunca se imprime en ningún documento. Llenar solo cuando la fruta de este pallet en realidad viene de un exportador distinto al de este embarque.",
    /* Sección Pallet Manifest */
    palletManifest: "Manifiesto de Pallets",
    addPallet: "Agregar pallet",
    palletNum: "Pallet #",
    tagId: "Tag ID",
    condition: "Condición",
    packType: "Empaque",
    unitsCase: "Unidades/Caja",
    casesPallet: "Cajas/Pallet",
    label: "Etiqueta",
    variety: "Variedad",
    traceability: "Trazabilidad (Juliano)",
    growerCode: "Código productor",
    splitByProducer: "+ Dividir",
    removeProducerSplit: "Unir de vuelta a un solo productor",
    addAnotherProducer: "+ Agregar otro productor",
    removeProducerRow: "Quitar productor",
    producerCases: "Cajas",
    producerTraceability: "Trazabilidad",
    producerGrowerCode: "Código Productor",
    multipleProducersNote: "Más de un productor — ver renglones abajo",
    weight: "Peso",
    palletSubtotal: "Subtotal",
    palletGrandTotal: "TOTAL",
    texasQcReport: "Reporte QC Texas",
    texasQcModeInternal: "2 Columnas (Equipo GFI)",
    texasQcModeClarifresh: "3 Columnas (ClariFresh)",
    texasQuality: "Calidad",
    texasCondition: "Condición",
    texasSevereCondition: "Condición severa",
    texasQcExcellent: "Excelente",
    texasQcVeryGood: "Muy Bueno",
    texasQcGoodQuality: "Buena Calidad",
    texasQcFairQuality: "Calidad Aceptable",
    texasQcGood: "Bueno",
    texasQcRegular: "Regular",
    texasQcBadQuality: "Mala Calidad",
    texasQcPoor: "Malo",
    texasQcPoorQuality: "Calidad Pobre",
    qcDefects: "Defectos",
    qcDefectTotal: "Total",
    qcDefectsNone: "Sin defectos",
    qcDefectsAllAdded: "Todos agregados",
    qcCategoryPlaceholder: "Condición...",
    qcDefectAddPlaceholder: "+ Agregar defecto",
    qcDefectCustomPlaceholder: "Nombre del defecto...",
    qcTolDecay: "Pudrición",
    qcTolMold: "Moho",
    qcTolInsect: "Insecto",
    qcTolLeaky: "Fuga de jugo",
    qcTolShrivel: "Deshidratación",
    qcTolSoft: "Blanda",
    qcTolBrokenSkin: "Piel rota",
    qcTolAttachedRemains: "Restos adheridos",
    qcTolFloralRemains: "Restos florales",
    qcTolPeduncle: "Pedúnculo",
    qcTolOffColor: "Color fuera de estándar",
    qcTolBerryDamage: "Daño en la baya",
    qcTolRedBerries: "Bayas rojas",
    qcTolGreenBerries: "Bayas verdes",
    qcTolScars: "Cicatrices",
    qcTolDryCell: "Célula seca",
    qcTolRedCell: "Célula roja",
    qcTolRegression: "Regresión",
    qcTolMechanicalDamage: "Daño Mecánico",
    qcTolWhiteCells: "Células blancas",
    qcTolRedGreenDryTips: "Puntas rojas/verdes o secas",
    qcTolAttachedCore: "Núcleo adherido",
    qcTolRedCells: "Células rojas",
    qcTolDryCells: "Células secas",
    qcTolLeaking: "Fuga de jugo",
    qcTolFreezeDamage: "Daño por congelamiento",
    qcTolSoftFruit: "Fruta blanda",
    qcTolCustom: "Otro (especificar)",
    qcCatSevereCondition: "Condición Severa",
    qcCatCondition: "Condición",
    qcCatQuality: "Calidad",
    qcCatFindings: "Hallazgos",
    qcTolBugs: "Bichos",
    qcTolWorms: "Gusanos",
    qcTolInsects: "Insectos",
    qcDefectsSave: "Guardar",
    qcDefectsEdit: "Editar",
    qcDefectsRemoveCategory: "Quitar condición",
    lotRegressionLabel: "Regresión total reportada en esta carga:",
    qcTwinRiverStatus: "Twin River — status de entrega",
    qcTwinRiverGood: "Buena Entrega",
    qcTwinRiverProtection: "Protección",
    qcTwinRiverHint: "Se marca una sola vez por pallet, según el propio reporte de calidad de Twin River — no se calcula de los grados/defectos de arriba.",
    palletNoLabels: "Aún no hay etiquetas en el catálogo",
    palletProdMatch: "Coincide con catálogo",
    palletProdNew: "No está en catálogo",
    /* Notación de cajas perdidas (2026-09-21, Arturo: al re-empacar en
       la bodega de Texas ocasionalmente se pierden cajas — registrarlo
       por pallet en el embarque, y que se arrastre al Bill para la
       liquidación, sin tocar el conteo real de casesPallet). Tipo de
       trabajo: los 2 reales que describió Arturo — "Make Up" (solo se
       reacomodan las cajas para ocultar lo que se vea mal, no se saca
       fruta) vs "Re-Pack" (aquí sí se saca lo malo, fruta con hongo o
       pudrición — el que normalmente sí pierde cajas reales). */
    casesLost: "Cajas perdidas",
    lossReason: "Trabajo en Texas",
    lossReasonMakeup: "Make Up",
    lossReasonRepack: "Re-Pack",
    lossReasonOther: "Otra",
    lossReasonCustomPlaceholder: "Describe la causa",
    lossNotedBadge: "perdidas",
    /* Documentos imprimibles */
    printReceiving: "Receiving Sheet",
    printInboundPacking: "Lista de Empaque de Entrada",
    printCartaPorte: "Bill of Lading",
    printCartaInstr: "Instrucciones de Embarque",
    preview: "Vista previa",
    printPdf: "Imprimir / PDF",
    more: "Más acciones",
    emailInboundACS: "Email Bodega",
    sendDumpOrder: "Email Orden de Desecho",
    sendDumpOrderShipper: "Email Orden de Desecho — Shipper",
    dumpOrderTitle: "Orden de Desecho — {warehouse}",
    dumpOrderShipperTitle: "Orden de Desecho — {shipper}",
    dumpOrderDefaultSubject: "Orden de Desecho {lot}",
    dumpOrderDefaultBody: "Buen día,\n\nAdjunto la Orden de Desecho del LOTE {lot}, autorizando el desecho de las cajas detalladas a continuación.\n\nSaludos,\nGround Fresh International",
    dumpOrderShipperDefaultBody: "Buen día,\n\nAdjunto la Orden de Desecho emitida para el LOTE {lot}, autorizando a la bodega a desechar las cajas detalladas a continuación.\n\nSaludos,\nGround Fresh International",
    emailCustomsBroker: "Email Agente Aduanal",
    emailBrokerTitle: "Email al Agente Aduanal",
    emailBrokerDefaultSubject: "Embarque {lot} — {shipper} — ETA {eta}",
    emailBrokerDefaultBody: "Buen día,\n\nAdjunto la documentación del embarque lote {lot} de {shipper}, ETA {eta}, proforma {proforma}. Favor de proceder con el trámite aduanal.\n\nSaludos,\nGround Fresh International Inc.",
    emailBrokerDocBoL: "Bill of Lading",
    emailBrokerDocReceiving: "Receiving Sheet",
    emailBrokerDocShipment: "Documentos del embarque",
    emailNoBrokerMX: "El embarque no tiene agente aduanal MX asignado.",
    emailQualityReport: "Enviar Reporte de Calidad",
    emailQualityNoGrades: "Califica al menos un pallet en Calidad/Condición (Reporte QC Texas) antes de enviar.",
    emailQualityNoShipperContacts: "El proveedor de este embarque no tiene contactos con correo registrado",
    emailQualitySendBlocked: "Agrega los comentarios del equipo de calidad antes de enviar",
    emailQualityAttachRequired: "Adjunta el reporte de inspección antes de enviar",
    sendTrackingLink: "Enviar Link de Rastreo",
    emailWarehouseTitle: "Enviar a bodega",
    emailWarehouseDocBoL: "Bill of Lading",
    emailWarehouseDocReceiving: "Receiving Manifest",
    emailWarehouseDocInboundPacking: "Lista de Empaque de Entrada",
    emailWarehouseDocDumpOrder: "Orden de Desecho",
    emailWarehouseDefaultSubject: "Receiving Sheet {lot}",
    emailWarehouseDefaultBody: "Buen día,\n\nAdjunto los documentos de carga para el embarque entrante.\n\nLOT: {lot}\nProforma: {proforma}\nShipper: {shipper}\nETA: {eta}\n\nSaludos,\nGround Fresh International",
    emailNoWarehouse: "Este embarque no tiene bodega asignada",
    /* Google Drive */
    driveCreateFolder: "Crear Carpeta en Drive",
    driveCreating: "Creando...",
    driveCreated: "Carpeta creada en Google Drive",
    driveOpenFolder: "Abrir Carpeta",
    driveSignInNeeded: "Primero inicia sesión en Google Drive",
    driveNotConfigured: "Falta REACT_APP_GOOGLE_CLIENT_ID en .env.local",
    driveError: "Error de Drive",
    driveUploadDocs: "Subir a Drive",
    driveUploading: "Subiendo...",
    emailHtmlCopied: "HTML copiado al portapapeles — pega en Outlook",
    emailHtmlError: "No se pudo copiar HTML — tu navegador bloqueó el portapapeles",
    sealNumber: "No. de Candado",
    thermograph: "No. de Termógrafo",
    docPopupBlocked: "⚠ El navegador bloqueó la ventana. Habilita pop-ups.",
    draft: "Borrador", received: "Recibido", billed: "Facturado",
    createBill: "Crear Bill", duplicate: "Nueva Proforma (misma factura)",
    documents: "Documentos", uploadDoc: "Subir documento",
    transport: "Datos del Transportista", transportSub: "Transportista y datos del camión",
    carrier: "Línea transportista", driver: "Nombre del chofer", platesTruck: "Placas del tractor", econTruck: "No. económico tractor",
    platesTrailer: "Placas de la caja", econTrailer: "No. económico caja",
    caat: "CAAT", phone: "Celular", truckInfo: "Datos del tracto", trailerInfo: "Datos de la caja",
    openLocalFolder: "Abrir Carpeta",
    openLocalFolderHint: "Acceso directo descargado. IMPORTANTE: renombra el archivo — cambia la extensión de .download a .url — y luego haz doble clic para abrir la carpeta. Ruta también copiada al portapapeles.",
    save: "Guardar", cancel: "Cancelar", delete: "Eliminar", editBtn: "Editar", close: "Cerrar",
    clearForm: "Limpiar Formulario", confirmClear: "¿Limpiar todos los campos del formulario? Los datos no guardados se perderán.",
    confirmLoseManifestRows: "Se eliminarán {n} fila(s) del Manifiesto de Pallets que ya tienen datos capturados. ¿Continuar?",
    duplicatePalletNum: "Pallet # duplicado",
    confirmDel: "¿Eliminar este embarque?", yes: "Sí, eliminar", no: "Cancelar",
    confirmExit: "Cambios sin guardar. ¿Salir?", yesExit: "Sí, salir", noExit: "Seguir editando",
    yesContinueSaving: "Continuar guardando", noContinueSaving: "Seguir editando",
    saved: "Embarque guardado", deleted: "Embarque eliminado",
    dupLot: "Ya existe un embarque con este lote", fillRequired: "Completa campos requeridos y agrega al menos una línea",
    notes: "Notas",
    all: "Todos", allShippers: "Todos los exportadores", allWarehouses: "Todas", allStatuses: "Todos", allSaleStatuses: "Todos los estados", allCerts: "Todos los certificados",
    goodDelivery: "Good Delivery", protection: "Protección", pas: "PAS", dumped: "Dumped", donated: "Donated", samples: "Samples", transfer: "Transferencia",
  },
};

/* ======================== CONSTANTS ======================== */
const STORAGE_KEY = "gfi-shipments";
const STATUSES = ["draft", "received", "billed"];
/* Mirror of the canonical SALE_STATUSES list in BillRegistry — kept
   inline per the registry "no shared internals" convention. Used to
   populate the Sale Status filter dropdown. */
const SALE_STATUSES = ["goodDelivery", "protection", "pas", "dumped", "donated", "samples", "transfer"];
const STATUS_STYLES = {
  draft: { bg: "rgba(107,114,128,0.10)", text: "#6B7280", border: "rgba(107,114,128,0.25)" },
  received: { bg: "rgba(16,185,129,0.10)", text: "#059669", border: "rgba(16,185,129,0.25)" },
  billed: { bg: "rgba(42,122,111,0.10)", text: "#2A7A6F", border: "rgba(42,122,111,0.25)" },
};
/* 2026-10-20, Arturo: "ningún campo de captura debe tener esquinas
   redondeadas" — this is the one shared plain-input style used
   throughout this file (dates, text fields, textareas); borderRadius
   0 here squares every one of them in one place. */
/* 2026-10-20, Arturo: "cases lost es mas pequeño que los de a lado,
   iguala el alto... igual que traceability... tambien cases... pallet
   # y warehouse tag tambien" then "reduce el alto de todos los
   renglones... de 32 a 30px" — plain `<input>`s here are explicitly
   border-box with `minHeight: 25`, landing on exactly 30px.
   SearchSelect's trigger is content-box by default (unchanged for
   every other module) but now honors a caller's `style.minHeight` as
   an exact border-box `height` override instead (see SearchSelect.js)
   — every SearchSelect in this file passes `minHeight: 25` too, for
   the same 30px row height everywhere. */
/* 2026-10-20, Arturo: "bajemos todo de 30 a 25" — `minHeight` alone
   stopped working here: padding(14) + the text's own line-height
   already totals ~30px on its own, so a `minHeight` of 25 (a floor)
   never had anything to raise — same trap SearchSelect's trigger fell
   into earlier. An explicit `height` (not minHeight) actually forces
   it down to 25, same border-box reasoning as before. */
/* 2026-10-20, Arturo: "todos los fonts dentro de las tarjetas (no los
   headers... ni tampoco los headers de titulos como pack, product,
   truck number), reduce en tamaño de sus fonts en 2 puntos" — the
   small uppercase field-name labels above each input and the card's
   own section-title bar are untouched; this is the VALUE text typed
   into a field, 2pt down from 12 to 10. */
const inp = { padding: "7px 8px", fontSize: 10, border: "1px solid var(--color-border)", borderRadius: 0, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", transition: "all 0.2s", fontFamily: "inherit", width: "100%", boxSizing: "border-box", height: 25 };
/* 2026-10-20, Arturo: "los dropdown dentro de filter haz que se vean
   en 3d con el color plata de mac" — sunken brushed-aluminum field,
   lighter silver than the panel plate behind it (so the field reads
   as recessed into the plate, Mac-OS-style), passed as the `style`
   prop to SearchSelect (which now special-cases
   background/border/borderRadius/boxShadow from it) and merged onto
   the plain date `<input>`s in the same Filters popover. */
const filterFieldPlate = {
  background: "linear-gradient(180deg, #f2f3f5 0%, #d7dadd 100%)",
  border: "1px solid #9aa0a8",
  borderRadius: 0,
  boxShadow: "inset 0 1px 2px rgba(0,0,0,0.15), inset 0 -1px 0 rgba(255,255,255,0.6)",
  minHeight: 25,
};
/* Read-only cell used in the Pallet Manifest for fields auto-filled
   from Product Lines. Muted gray + dashed border so the user sees
   immediately it's locked. Edit upstream in Product Lines. */
/* 2026-10-20, Arturo: "sigo viendo campos con esquinas redondeadas,
   los que ya tienen datos que obtienen de otro lado o por default" —
   this is that: the read-only cell style for fields auto-filled from
   elsewhere (Product Lines, etc.), still had borderRadius 5. */
const readOnlyCell = { width: "100%", padding: "6px 8px", border: "1px dashed var(--color-border)", borderRadius: 0, background: "rgba(0,0,0,0.04)", color: "var(--color-text-secondary)", fontSize: 10, fontFamily: "inherit", boxSizing: "border-box", height: 25, display: "flex", alignItems: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

/* ---------- Phone country list + helpers ----------
   Static list used by the Carrier Info phone field. MX/US/CA pinned
   to the top per spec; rest sorted alphabetically by language at
   render time. Duplicated from ShipperPortal per the "intentionally
   not shared" rule in CLAUDE.md. */
const PHONE_COUNTRIES = [
  { code: "MX", flag: "🇲🇽", dial: "+52", nameEn: "Mexico", nameEs: "México" },
  { code: "US", flag: "🇺🇸", dial: "+1",  nameEn: "United States", nameEs: "Estados Unidos" },
  { code: "CA", flag: "🇨🇦", dial: "+1",  nameEn: "Canada", nameEs: "Canadá" },
  { code: "AR", flag: "🇦🇷", dial: "+54", nameEn: "Argentina", nameEs: "Argentina" },
  { code: "AU", flag: "🇦🇺", dial: "+61", nameEn: "Australia", nameEs: "Australia" },
  { code: "AT", flag: "🇦🇹", dial: "+43", nameEn: "Austria", nameEs: "Austria" },
  { code: "BE", flag: "🇧🇪", dial: "+32", nameEn: "Belgium", nameEs: "Bélgica" },
  { code: "BZ", flag: "🇧🇿", dial: "+501", nameEn: "Belize", nameEs: "Belice" },
  { code: "BO", flag: "🇧🇴", dial: "+591", nameEn: "Bolivia", nameEs: "Bolivia" },
  { code: "BR", flag: "🇧🇷", dial: "+55", nameEn: "Brazil", nameEs: "Brasil" },
  { code: "CL", flag: "🇨🇱", dial: "+56", nameEn: "Chile", nameEs: "Chile" },
  { code: "CN", flag: "🇨🇳", dial: "+86", nameEn: "China", nameEs: "China" },
  { code: "CO", flag: "🇨🇴", dial: "+57", nameEn: "Colombia", nameEs: "Colombia" },
  { code: "CR", flag: "🇨🇷", dial: "+506", nameEn: "Costa Rica", nameEs: "Costa Rica" },
  { code: "CU", flag: "🇨🇺", dial: "+53", nameEn: "Cuba", nameEs: "Cuba" },
  { code: "CZ", flag: "🇨🇿", dial: "+420", nameEn: "Czech Republic", nameEs: "República Checa" },
  { code: "DK", flag: "🇩🇰", dial: "+45", nameEn: "Denmark", nameEs: "Dinamarca" },
  { code: "DO", flag: "🇩🇴", dial: "+1", nameEn: "Dominican Republic", nameEs: "República Dominicana" },
  { code: "EC", flag: "🇪🇨", dial: "+593", nameEn: "Ecuador", nameEs: "Ecuador" },
  { code: "EG", flag: "🇪🇬", dial: "+20", nameEn: "Egypt", nameEs: "Egipto" },
  { code: "SV", flag: "🇸🇻", dial: "+503", nameEn: "El Salvador", nameEs: "El Salvador" },
  { code: "FI", flag: "🇫🇮", dial: "+358", nameEn: "Finland", nameEs: "Finlandia" },
  { code: "FR", flag: "🇫🇷", dial: "+33", nameEn: "France", nameEs: "Francia" },
  { code: "DE", flag: "🇩🇪", dial: "+49", nameEn: "Germany", nameEs: "Alemania" },
  { code: "GR", flag: "🇬🇷", dial: "+30", nameEn: "Greece", nameEs: "Grecia" },
  { code: "GT", flag: "🇬🇹", dial: "+502", nameEn: "Guatemala", nameEs: "Guatemala" },
  { code: "HT", flag: "🇭🇹", dial: "+509", nameEn: "Haiti", nameEs: "Haití" },
  { code: "HN", flag: "🇭🇳", dial: "+504", nameEn: "Honduras", nameEs: "Honduras" },
  { code: "HU", flag: "🇭🇺", dial: "+36", nameEn: "Hungary", nameEs: "Hungría" },
  { code: "IN", flag: "🇮🇳", dial: "+91", nameEn: "India", nameEs: "India" },
  { code: "ID", flag: "🇮🇩", dial: "+62", nameEn: "Indonesia", nameEs: "Indonesia" },
  { code: "IE", flag: "🇮🇪", dial: "+353", nameEn: "Ireland", nameEs: "Irlanda" },
  { code: "IT", flag: "🇮🇹", dial: "+39", nameEn: "Italy", nameEs: "Italia" },
  { code: "JM", flag: "🇯🇲", dial: "+1", nameEn: "Jamaica", nameEs: "Jamaica" },
  { code: "JP", flag: "🇯🇵", dial: "+81", nameEn: "Japan", nameEs: "Japón" },
  { code: "MA", flag: "🇲🇦", dial: "+212", nameEn: "Morocco", nameEs: "Marruecos" },
  { code: "NL", flag: "🇳🇱", dial: "+31", nameEn: "Netherlands", nameEs: "Países Bajos" },
  { code: "NZ", flag: "🇳🇿", dial: "+64", nameEn: "New Zealand", nameEs: "Nueva Zelanda" },
  { code: "NI", flag: "🇳🇮", dial: "+505", nameEn: "Nicaragua", nameEs: "Nicaragua" },
  { code: "NG", flag: "🇳🇬", dial: "+234", nameEn: "Nigeria", nameEs: "Nigeria" },
  { code: "NO", flag: "🇳🇴", dial: "+47", nameEn: "Norway", nameEs: "Noruega" },
  { code: "PK", flag: "🇵🇰", dial: "+92", nameEn: "Pakistan", nameEs: "Pakistán" },
  { code: "PA", flag: "🇵🇦", dial: "+507", nameEn: "Panama", nameEs: "Panamá" },
  { code: "PY", flag: "🇵🇾", dial: "+595", nameEn: "Paraguay", nameEs: "Paraguay" },
  { code: "PE", flag: "🇵🇪", dial: "+51", nameEn: "Peru", nameEs: "Perú" },
  { code: "PH", flag: "🇵🇭", dial: "+63", nameEn: "Philippines", nameEs: "Filipinas" },
  { code: "PL", flag: "🇵🇱", dial: "+48", nameEn: "Poland", nameEs: "Polonia" },
  { code: "PT", flag: "🇵🇹", dial: "+351", nameEn: "Portugal", nameEs: "Portugal" },
  { code: "PR", flag: "🇵🇷", dial: "+1", nameEn: "Puerto Rico", nameEs: "Puerto Rico" },
  { code: "RO", flag: "🇷🇴", dial: "+40", nameEn: "Romania", nameEs: "Rumania" },
  { code: "RU", flag: "🇷🇺", dial: "+7", nameEn: "Russia", nameEs: "Rusia" },
  { code: "SA", flag: "🇸🇦", dial: "+966", nameEn: "Saudi Arabia", nameEs: "Arabia Saudita" },
  { code: "SG", flag: "🇸🇬", dial: "+65", nameEn: "Singapore", nameEs: "Singapur" },
  { code: "ZA", flag: "🇿🇦", dial: "+27", nameEn: "South Africa", nameEs: "Sudáfrica" },
  { code: "KR", flag: "🇰🇷", dial: "+82", nameEn: "South Korea", nameEs: "Corea del Sur" },
  { code: "ES", flag: "🇪🇸", dial: "+34", nameEn: "Spain", nameEs: "España" },
  { code: "SE", flag: "🇸🇪", dial: "+46", nameEn: "Sweden", nameEs: "Suecia" },
  { code: "CH", flag: "🇨🇭", dial: "+41", nameEn: "Switzerland", nameEs: "Suiza" },
  { code: "TH", flag: "🇹🇭", dial: "+66", nameEn: "Thailand", nameEs: "Tailandia" },
  { code: "TT", flag: "🇹🇹", dial: "+1", nameEn: "Trinidad and Tobago", nameEs: "Trinidad y Tobago" },
  { code: "TR", flag: "🇹🇷", dial: "+90", nameEn: "Turkey", nameEs: "Turquía" },
  { code: "UA", flag: "🇺🇦", dial: "+380", nameEn: "Ukraine", nameEs: "Ucrania" },
  { code: "AE", flag: "🇦🇪", dial: "+971", nameEn: "United Arab Emirates", nameEs: "Emiratos Árabes Unidos" },
  { code: "GB", flag: "🇬🇧", dial: "+44", nameEn: "United Kingdom", nameEs: "Reino Unido" },
  { code: "UY", flag: "🇺🇾", dial: "+598", nameEn: "Uruguay", nameEs: "Uruguay" },
  { code: "VE", flag: "🇻🇪", dial: "+58", nameEn: "Venezuela", nameEs: "Venezuela" },
  { code: "VN", flag: "🇻🇳", dial: "+84", nameEn: "Vietnam", nameEs: "Vietnam" },
];
function buildPhoneCountryOptions(lang) {
  var top = ["MX", "US", "CA"];
  var rest = PHONE_COUNTRIES.filter(function (c) { return top.indexOf(c.code) < 0; });
  rest.sort(function (a, b) {
    var an = lang === "es" ? a.nameEs : a.nameEn;
    var bn = lang === "es" ? b.nameEs : b.nameEn;
    return an.localeCompare(bn);
  });
  var ordered = top.map(function (k) { return PHONE_COUNTRIES.find(function (c) { return c.code === k; }); }).concat(rest);
  return ordered.map(function (c) {
    return {
      value: c.code,
      label: c.flag + " " + c.dial,
      search: c.nameEn + " " + c.nameEs + " " + c.code + " " + c.dial,
    };
  });
}
/* Mirrors the corrected logic in ./PhoneField.js: dash structure is
   preserved so "area only" stays distinguishable from "local only".
   The previous `.split(/[\s-]+/).filter(Boolean)` collapsed empty
   segments, so "+52-1" (area only) round-tripped as
   { area: "", local: "1" } — the first digit you typed into Area
   reflected back as Local and focus appeared to jump fields. */
function parsePhone(s) {
  var str = String(s == null ? "" : s).trim();
  if (!str) return { country: "MX", area: "", local: "" };
  var m = str.match(/^(\+\d{1,4})(.*)$/);
  if (!m) return { country: "MX", area: "", local: str };
  var dial = m[1];
  var country = (PHONE_COUNTRIES.find(function (c) { return c.dial === dial; }) || { code: "MX" }).code;
  var rest = m[2];
  if (rest.length > 0 && (rest.charAt(0) === "-" || rest.charAt(0) === " ")) {
    rest = rest.slice(1);
  }
  var dashIdx = rest.indexOf("-");
  if (dashIdx >= 0) {
    return {
      country: country,
      area: rest.slice(0, dashIdx).replace(/\D/g, ""),
      local: rest.slice(dashIdx + 1),
    };
  }
  if (rest.length <= 3 && /^\d*$/.test(rest)) {
    return { country: country, area: rest, local: "" };
  }
  return { country: country, area: "", local: rest };
}
function formatPhoneLocal(s) {
  var digits = String(s == null ? "" : s).replace(/\D/g, "").slice(0, 7);
  if (digits.length <= 3) return digits;
  return digits.slice(0, 3) + "-" + digits.slice(3);
}
/* ALWAYS emits dial-area-local with both segments explicit (even
   when empty): "+52-1-" vs "+52--1" — so parsePhone can disambiguate
   "area only" from "local only". */
function serializePhone(country, area, local) {
  var c = PHONE_COUNTRIES.find(function (x) { return x.code === country; }) || PHONE_COUNTRIES[0];
  var a = String(area || "").replace(/\D/g, "").slice(0, 3);
  var l = formatPhoneLocal(local);
  if (!a && !l) return "";
  return c.dial + "-" + a + "-" + l;
}
const EMPTY_LINE = { productBase: "", productId: "", productName: "", productDescription: "", category: "", cases: "", pallets: "", price: "", total: 0, exportCertId: "", exportCertNumber: "", exportCertManual: "" };
const EMPTY_TRANSPORT = { carrier: "", driver: "", platesTruck: "", econTruck: "", platesTrailer: "", econTrailer: "", caat: "", codigoAlpha: "", phone: "", sealNumber: "", thermograph: "" };

/* ===== PALLET MANIFEST CONSTANTS =====
   Mirrors the canonical lists from ProductRegistry so the manifest UI
   can size/pack/condition without depending on that file. SOZ converts
   each size to ounces per package; weight = oz/16 × units × cases. */
const PALLET_SIZES_BY_PRODUCT = {
  Blackberry: ["6oz", "12oz", "18oz"],
  Raspberry: ["6oz", "12oz", "18oz"],
  Blueberry: ["6oz", "18oz", "1 dry pint"],
  Strawberry: ["1lb", "2lb"],
  Avocado: ["28s", "32s", "36s", "40s", "48s", "60s", "70s", "84s"],
  Lime: ["110s", "150s", "175s", "200s", "230s", "250s"],
};
const PALLET_PACK_TYPES = ["clamshell", "bulk", "bag", "case_pack"];
const PALLET_CONDITIONS = ["Fresh", "Fresh Cut", "Frozen"];
const PALLET_SOZ = { "6oz": 6, "12oz": 12, "18oz": 18, "1 dry pint": 16, "1lb": 16, "2lb": 32 };
const PALLET_PRODUCTS = ["Blackberry", "Blueberry", "Raspberry", "Strawberry", "Avocado", "Lime"];
/* producers: [] by default — no sub-rows, the pallet-level
   casesPallet/traceability/growerCode are authoritative (the common
   single-producer case). Populated only via splitPalletProducers()
   when a pallet genuinely mixes fruit from >1 producer — see the
   2026-09-14 comment above updatePallet. */
const EMPTY_PALLET = { palletNum: "", tagId: "", productBase: "", category: "", condition: "", packType: "", size: "", unitsCase: "", casesPallet: "", labelInitials: "", variety: "", traceability: "", growerCode: "", texasQuality: "", texasCondition: "", texasSevereCondition: "", lostCases: "", lossReason: "", lossReasonCustom: "", producers: [], qcDefects: { severeCondition: [], condition: [], quality: [] }, twinRiverStatus: "", actualShipperInitials: "" };
const MAX_PALLETS = 30;

/* Case-loss notation (2026-09-21, Arturo: "ocasionalmente a la
   llegada a texas tenemos que re-empacar la fruta y suele pasar que
   perdemos algunas cajas... necesito registrar en ese embarque a
   nivel pallet que se perdieron cajas por la causa que haya sido" —
   then, clarifying the real work types: "podemos establecer a cada
   pallet el trabajo que se haya efectuado en texas, existen 2, make
   up, que es reacomodar las cajas solamente tratando de ocultar lo
   que se vea mal y el otro tipo el re-pack, aqui si se saca lo malo
   fruta con hongo o pudricion"). Deliberately a SEPARATE field
   (lostCases), never subtracted from casesPallet — Arturo: "en lugar
   de modificar las cajas de un pallet, necesito registrar..." —
   casesPallet keeps meaning "what physically arrived on this pallet"
   for every existing reader (weight calc, invoice-allocation ceiling,
   the printed reports), while lostCases is purely an informational
   note, logged regardless of which work type applied (a "Make Up"
   pallet can still lose a case or two even without pulling bad fruit).
   Same __custom__ convention as fee types elsewhere in this app:
   "other" opens a free-text field (lossReasonCustom). Unlike Texas QC
   (deliberately never propagated past this file), this DOES need to
   reach the Bill for settlement — it rides along automatically since
   BillRegistry.js's deepCopyPalletManifest clones every field present
   on each pallet row, no extra wiring needed there beyond a read-only
   display column. */
const PALLET_LOSS_REASONS = ["makeup", "repack", "other"];

/* Texas QC Report (2026-09-14, Arturo) — internal-only grade GF
   assigns per pallet on arrival at the Texas warehouse, replicated
   from the real inspection PDF format Arturo showed (Quality/
   Condition columns). Lives ONLY on shipment.palletManifest —
   deliberately never propagated to the Invoice (GF) or SweetLeaf side
   ("no tiene que distribuirse ni a PU de GF ni de SL"), unlike the
   client-facing saleStatus pallet grade.

   2026-10-01 — Arturo: "existen 2 tipos de qc en texas, el que hace
   mi equipo de calidad que es a 2 columnas... y cuando son para twin
   river los controles los hace CLARIFRESH" — both are the SAME kind
   of capture (Arturo reads a report someone hands him and picks a
   tier per pallet); `shipment.texasQcMode` ("internal", default, vs
   "clarifresh") controls ONLY whether the 3rd column (Severe
   Condition) renders — missing/undefined on every pre-existing
   shipment defaults to "internal" (2 columns), zero behavior change.

   The TIER options themselves are NOT mode-gated — Arturo, same day,
   correcting that assumption: "los conceptos de calidad pueden
   convivir juntos sin importar de quien es el reporte" (his own
   team's tiers and ClariFresh's own vocabulary — Very Good/Good
   Quality/Bad Quality, from the real ClariFresh report he showed —
   all sit in ONE shared list, available in every column regardless of
   texasQcMode). "good"/"goodQuality" and "poor"/"badQuality" are
   deliberately separate keys with separate colors — same underlying
   idea, different report's own wording, Arturo picks whichever
   matches what the report in his hand actually says.
   2026-10-02 — Arturo, on a 4th ClariFresh legend tier he spotted:
   "poor y poor quality son distintos" — "poorQuality" is its OWN tier,
   not a rename/replacement of "poor" (confirmed explicitly after an
   initial wrong attempt to just relabel "poor"). Orange, per the
   color swatch in the legend screenshot he shared.
   2026-10-13 — Arturo: "tengo que agregar otro factor de calidad a
   twin river que es Fair Quality color [yellow]" — a 5th Twin River/
   ClariFresh legend tier, same "shared list regardless of report
   source" rule as the rest ("Very Good"/purple, "Fair Quality"/yellow,
   "Bad Quality"/red, per his reference screenshot of the real
   legend). Yellow distinct from "good"'s own pale yellow (#FFF59D) so
   the two don't read as the same tier at a glance. */
const TEXAS_QC_TIERS = ["excellent", "veryGood", "goodQuality", "fairQuality", "good", "regular", "badQuality", "poor", "poorQuality"];
/* 2026-10-20, Arturo: "los colores de las columnas de calidad...
   mantengan su mismo color pero con tono de transparencia, el mismo
   efecto que viste en billed o en los defectos... el color no cambia
   solo el efecto" — same tinted-plate recipe already used for the
   edit-mode Billed badge (light rgba tint for bg, the original solid
   hex for text/border, plus a bevel boxShadow). Each tier keeps its
   exact original hue (`rawHex` below, unchanged from before this
   pass) — only how it's painted changes. The 2 tiers that used to be
   solid-fill-with-white-text (badQuality/poor, too dark/saturated to
   read white-on-tint) get their text swapped to their own hex instead
   — same principle as Billed's #1d4ed8 doing double duty as both
   tint source and text color. */
const TEXAS_QC_COLORS = (function () {
  var rawHex = {
    excellent: "#C8E6C9", veryGood: "#E1BEE7", goodQuality: "#AED581", fairQuality: "#FFEB3B",
    good: "#FFF59D", regular: "#FFCDD2", badQuality: "#C62828", poor: "#EF5350", poorQuality: "#FFB74D",
  };
  var textHex = {
    excellent: "#1B5E20", veryGood: "#6A1B9A", goodQuality: "#33691E", fairQuality: "#5D4A00",
    good: "#7A5C00", regular: "#B71C1C", badQuality: "#C62828", poor: "#C62828", poorQuality: "#E65100",
  };
  function toRgb(hex) { var h = hex.replace("#", ""); return [parseInt(h.substr(0, 2), 16), parseInt(h.substr(2, 2), 16), parseInt(h.substr(4, 2), 16)].join(","); }
  var out = {};
  Object.keys(rawHex).forEach(function (k) {
    out[k] = { bg: "rgba(" + toRgb(rawHex[k]) + ",0.18)", text: textHex[k], border: "rgba(" + toRgb(textHex[k]) + ",0.4)" };
  });
  return out;
})();
const TEXAS_QC_BEVEL = "inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 2px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.12)";

/* Tolerance-sheet defects (2026-10-15, Arturo: "necesito agregar la
   hoja de tolerancias para capturar los reportes de calidad de fruta
   de twin river... los defectos que agrego en el informe de
   proteccion... en el embarque en cada pallet... los porcentajes de
   los defectos encontrados y que de ahi se traslada el bill"). One
   list per tier category — the SAME 3 buckets as texasQuality/
   texasCondition/texasSevereCondition above, since Twin River's own
   "Defect group" table (ClariFresh report, e.g. QC Inspection 17472)
   scores a pallet on exactly these 3 groups, each with its own %.
   Keys/wording taken from TR-SPEC-BB-MX "Blueberry Spec - At Origin"
   §Quality Specification, PLUS 2 terms seen on a real ClariFresh
   report that aren't in the spec's own wording (brokenSkin, scars —
   ClariFresh's internal field names). Only meaningful in ClariFresh
   mode (texasQcMode==="clarifresh"), same gate as texasSevereCondition
   itself — deliberately NOT shared/imported from protectionReport.js's
   PROTECTION_DEFECTS (a different list, for the client-facing
   Protection dispute email on a SOLD invoice, not this arrival QC
   capture on the shipment) — same per-file duplication convention
   already used for TEXAS_QC_COLORS across this file/qualityReport.js/
   ShipperPortal.js. "__custom__" is the escape hatch for any
   ClariFresh wording not on this list (same pattern as
   PALLET_LOSS_REASONS' "other"). */
/* 2026-10-15 (2nd round), Arturo: "los defectos se deben mostrar de
   acuerdo al producto, cada uno son distintos" — confirmed against a
   real Blackberry ClariFresh report (QC 17470, LOT 58069/PU 17470,
   Terey Avocados) plus Twin River's own Blackberry spec
   (TR-SPEC-BLK-MX). Blackberry's Condition/Quality defects
   (Regression, Red Cell, White Cells, Attached Core, Red/Green/Dry
   Tips) don't exist at all for blueberry — one taxonomy can't serve
   both products — keyed by `productBase` (the same field already
   used everywhere else in this file to identify the fruit), with
   Blueberry as the fallback for anything not explicitly listed.
   2026-10-15 (correction, same day): Leaky was first placed under
   Blackberry's Severe Condition going purely off how that one QC
   17470 report's charts happened to be laid out — Arturo corrected
   it directly: "los defectos de la zarzamora, leaky es en condition
   no en severe condition", same bucket as Blueberry. */
const TOLERANCE_DEFECTS_BY_PRODUCT = {
  Blueberry: {
    severeCondition: ["decay", "mold", "insect", "__custom__"],
    condition: ["leaky", "shrivel", "soft", "brokenSkin", "__custom__"],
    /* Bloom/Wax deliberately excluded — 2026-10-15, Arturo: "bloom es
       un atributo", measured as a % in ClariFresh's own Attributes
       table (not part of this app's attributes scope either, see
       qcTolCustom comment), not a tagged defect like the rest of this
       list. */
    /* 2026-10-22, Arturo: "agrega para arandano en condicion: broken
       skin [ya estaba en condition arriba], y en quality floral
       remains" — Floral Remains = dried flower/calyx tissue still
       attached, a distinct Blueberry quality defect from the more
       general "Attached remains" (stems/leaves/petals) already here.
       2026-10-27, Arturo: "agrega al catalo de defectos de TR para
       blueberries Peduncle... es de quality no de condition" — the
       peduncle is the small woody stem/stalk that attaches the berry
       to the cluster; TR-SPEC-BB-MX grades a berry defective when that
       stem is still attached instead of having cleanly detached at
       harvest (distinct from Floral Remains, which is leftover dried
       flower/calyx tissue, not the stem itself) — Arturo places it
       under Quality, not Condition. */
    quality: ["attachedRemains", "floralRemains", "peduncle", "offColor", "berryDamage", "redBerries", "greenBerries", "scars", "dryCell", "__custom__"],
  },
  Blackberry: {
    /* 2026-11-03, Arturo: "leaking o leaky se puede usar tanto en
       condition como en severe condition dependiendo de la gravedad,
       ahora solo se ve en severe condition, hay que agregarlo a
       condition" — not a move this time (the 2026-10-27 entry below
       already settled where it defaults), just offering it as a
       pickable option under BOTH categories so Arturo can file a
       given pallet's leaking under whichever severity actually fits
       — the storage model already keeps severeCondition/condition as
       independent arrays (addPalletDefect only dedupes within the
       same category), so the same key living in both is safe. */
    severeCondition: ["decay", "mold", "insect", "leaky", "__custom__"],
    /* "regression" deliberately excluded — 2026-10-15, Arturo: "la
       regression no me la reportan a nivel pallet, si no a nivel el
       total de la carga" — captured once per shipment instead
       (f.lotRegressionPct, see the summary row under the Pallet
       Manifest table), never as a per-pallet defect entry. */
    condition: ["soft", "shrivel", "redCell", "dryCell", "leaky", "__custom__"],
    quality: ["whiteCells", "redGreenDryTips", "attachedCore", "scars", "__custom__"],
  },
};
function toleranceDefectsForProduct(productBase) {
  return TOLERANCE_DEFECTS_BY_PRODUCT[productBase] || TOLERANCE_DEFECTS_BY_PRODUCT.Blueberry;
}
/* GFI's own internal quality team's 2-column report (texasQcMode ===
   "internal", as opposed to ClariFresh's 3-column report) — 2026-10-15,
   Arturo, from a real report ("quality defects texas.pdf", LOT 58060
   pallet 68299). First pass had Arturo filing all 10 defects under one
   flat "Quality" bucket (Condition staying a plain tier grade) — he
   then compared the running totals against the real report and
   corrected the actual split (confirmed against ClariFresh's own
   Condition/Quality defect lists, pasted as 2 screenshots): Condition
   = Leaking/Shrivel/Freeze Damage/Soft Fruit/Mold/Decay, Quality =
   Regression/White Cells/Dry Cells/Red Cells — a REAL 2-category
   capture now, same shape as ClariFresh's 3-category one, just without
   Severe Condition. "Mechanical Damage" is the same concept this app
   already tracks as "regression" (Blackberry ClariFresh condition) —
   reused as the same key/label, per Arturo: "mechanical damage es = a
   regresion". Not product-specific — same list whether Blackberry/
   Blueberry/Raspberry. */
const TOLERANCE_DEFECTS_INTERNAL = {
  condition: ["leaking", "shrivel", "freezeDamage", "softFruit", "mold", "decay", "__custom__"],
  quality: ["regression", "whiteCells", "dryCells", "redCells", "__custom__"],
};
const TOLERANCE_CATEGORY_COLORS = {
  severeCondition: { bg: "rgba(220,38,38,0.08)", border: "rgba(220,38,38,0.3)", text: "#B91C1C" },
  condition: { bg: "rgba(217,119,6,0.08)", border: "rgba(217,119,6,0.3)", text: "#B45309" },
  quality: { bg: "rgba(31,78,121,0.08)", border: "rgba(31,78,121,0.3)", text: "#1F4E79" },
  /* 2026-10-21, Arturo: "cualquier finding encontrado es de severidad
     media alta" — same medium-high tone as protectionReport.js's
     PROTECTION_DEFECT_SEVERITY.mediumHigh, reused here for visual
     consistency across the app rather than inventing a new orange. */
  findings: { bg: "rgba(234,88,12,0.12)", border: "rgba(234,88,12,0.4)", text: "#C2410C" },
};
/* Findings — worms/bugs/insects found in the fruit, not a tolerance-
   sheet Condition/Quality defect (2026-10-21, Arturo: "no esta como
   un defecto de condicion ni calidad"). Same fixed options in both
   ClariFresh and internal mode — never product-specific. */
const QC_FINDINGS_OPTIONS = ["bugs", "worms", "insects", "__custom__"];
/* GFI Team (internal mode) severity coloring, 2026-10-15 — Arturo: "los
   defectos de tr marcados como severos... mold, decay, insects... los
   demas muestralos en color naranja", then "freeze damage es severo
   porque se pierde al igual que regression". Unlike ClariFresh (where
   severity already = which of the 3 categories a defect lives in),
   internal mode's 2 categories (Condition/Quality, see
   TOLERANCE_DEFECTS_INTERNAL) don't line up with "is this a total
   loss" — Regression is filed under Quality but still means the fruit
   is lost, same as Decay/Mold under Condition — so severity here is
   per-DEFECT, independent of which of the 2 real categories it's
   captured in: the 5 that mean a total loss (decay/mold/insect/
   freezeDamage/regression) render red, everything else renders orange.
   ClariFresh's own per-category coloring is untouched. */
const INTERNAL_SEVERE_DEFECT_KEYS = { decay: true, mold: true, insect: true, freezeDamage: true, regression: true };
function internalQualityDefectColor(key) {
  return INTERNAL_SEVERE_DEFECT_KEYS[key] ? TOLERANCE_CATEGORY_COLORS.severeCondition : TOLERANCE_CATEGORY_COLORS.condition;
}
/* Label resolver for internal-mode defect entries — "regression" shows
   as "Mechanical Damage" (matches the real report exactly), everything
   else uses the shared qcTol* lookup. Scoped to internal-mode render
   branches only, never ClariFresh (which keeps "Regression" for its
   own Blackberry condition entries). */
function internalDefectLabel(e, t) {
  if (e.key === "__custom__") return e.custom || t.qcTolCustom;
  if (e.key === "regression") return t.qcTolMechanicalDamage;
  return t["qcTol" + e.key.charAt(0).toUpperCase() + e.key.slice(1)];
}
/* Standalone (not component-scoped) label lookup for the read-only
   summary below — mirrors T.en/T.es's qcTol-prefixed and qcCat-prefixed
   keys, used from the view-mode DetailPanel table where building a
   full `t` lookup isn't worth it for one compact string. */

/* Per-pallet weight. lb = (oz per pkg × units × cases) / 16. Returns
   blanks when any factor is missing — column displays "—" then. */
function calcPalletWeight(p) {
  if (!p) return { lb: 0, kg: 0 };
  var oz = (PALLET_SOZ[p.size] || 0) * (parseFloat(p.unitsCase) || 0) * (parseFloat(p.casesPallet) || 0);
  if (!oz) return { lb: 0, kg: 0 };
  var lb = oz / 16;
  return { lb: lb, kg: lb * 0.453592 };
}

/* Collapses rows that share the same palletNum down to the first
   occurrence, for TOTALS ONLY — never for the row list itself. The
   duplicate-detector further down (pnCounts/pnDupe, edit mode) already
   flags a repeated palletNum visually so the user can fix it, but that
   flag was purely cosmetic: the grand-total/subtotal reducers summed
   every row unconditionally, so a duplicated pallet row silently
   doubled that pallet's cases/weight into the shipment's totals (e.g.
   lot 58046 showing ~2x its real case count). Rows with a blank
   palletNum are never deduped against each other — a blank doesn't
   identify a specific physical pallet, so two blank rows are two
   distinct not-yet-numbered pallets, not a duplicate of the same one. */
function dedupePalletsByNumber(pallets) {
  var seen = new Set();
  return (pallets || []).filter(function (p) {
    var key = String((p && p.palletNum) || "").trim().toUpperCase();
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/* Try to locate the catalog product matching a pallet's
   product+size+units+category+label. Returns null when no match — the
   manifest still uses the pallet's own values and never auto-creates
   a product (deliberate per spec). */
function findCatalogMatch(pallet) {
  /* 2026-09-30 — labelInitials dropped from this guard (same cause
     Arturo hit with the "missing fields" validation above): requiring
     it truthy just to ATTEMPT a match meant a genuine "No Label"
     product could never show as catalog-matched, even though the
     equality check two lines below already correctly compares
     `(p.labelInitials || "") === pallet.labelInitials` — "" === ""
     is a real match for a no-label product, it just never got a
     chance to run. */
  if (!pallet || !pallet.productBase || !pallet.size || !pallet.category) return null;
  var prods = loadLS("gfi-products", []);
  return prods.find(function (p) {
    return (p.product || "") === pallet.productBase
      && (p.size || "") === pallet.size
      && String(p.unitsPerCase || "") === String(pallet.unitsCase || "")
      && ((p.category === "Organic" ? "OG" : p.category === "Conventional" ? "CV" : p.category) || "") === pallet.category
      && (p.labelInitials || "") === pallet.labelInitials;
  }) || null;
}
/* ======================== HELPERS ======================== */
function loadLS(key, fb) { try { const d = localStorage.getItem(key); return d ? JSON.parse(d) : fb; } catch { return fb; } }
/* Tiny local read of SweetLeaf's settings — passed to
   emailHelpers.js's appendDisclosureByAccount() so it has the SL
   disclosure text available, even though shipment emails always send
   from the Ground-Fresh account (never SweetLeaf, by design — see the
   groundFreshAcct comment near Email Warehouse/Broker below) and so
   never actually take that branch. Kept for correctness/consistency
   rather than assuming that never changes. */
function readSlSettingsForDisclosure() { return loadLS("gfi-sl-settings", {}); }
function getSuppliers() { return loadLS("gfi-suppliers", []); }
function getClients() { return loadLS("gfi-clients", []); }
function getProducts() { return loadLS("gfi-products", []).filter(p => p.isActive !== false); }
function getWarehouses() { return loadLS("gfi-warehouses", []); }
function getActiveWarehouses() { return loadLS("gfi-warehouses", []).filter(w => w.isActive !== false); }
/* Always-fresh shipper-name lookup. Falls back to the cached
   shipperName on the shipment record only if the supplier was deleted
   or the supplierId never got set (legacy records). Prefers shortName
   per the list-view rule. */
function resolveShipperName(supplierId, cached) {
  if (!supplierId) return cached || "";
  const sup = getSuppliers().find(s => String(s.id) === String(supplierId));
  if (!sup) return cached || "";
  return sup.shortName || sup.fullName || sup.name || sup.companyName || cached || "";
}
function getBrokers() { return loadLS("gfi-brokers", []).filter(b => b.isActive !== false); }
function getExportCerts() { return loadLS("gfi-export-certs", []); }
function getSeasons() { return loadLS("gfi-seasons", []); }

/* Full presentation/pack/label for a shipment line — same fallback
   contract as BillRegistry.resolveProductDesc. Falls back to a
   catalog lookup by productId when productDescription is empty so
   the export never collapses Description down to just the commodity. */
function resolveShipLineDesc(line) {
  if (!line) return "";
  if (line.productDescription) return line.productDescription;
  if (!line.productId) return "";
  var prod = loadLS("gfi-products", []).find(function (p) { return String(p.id) === String(line.productId); });
  if (!prod) return "";
  var cat = (prod.category === "Organic" || prod.category === "OG") ? "OG" : "CV";
  return (prod.description || (prod.size || "") + " " + (prod.unitsCase || "") + "u " + cat + " " + (prod.labelInitials || "")).trim();
}

function getBaseProducts() {
  const prods = getProducts();
  const bases = [...new Set(prods.map(p => p.product || p.productName || "").filter(Boolean))];
  return bases.sort();
}
function getPresentations(baseName) {
  if (!baseName) return [];
  const prods = getProducts();
  return prods.filter(p => (p.product || p.productName || "") === baseName);
}

function getISOWeek(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const w1 = new Date(d.getFullYear(), 0, 4);
  return String(1 + Math.round(((d - w1) / 86400000 - 3 + ((w1.getDay() + 6) % 7)) / 7)).padStart(2, "0");
}
function fmtDate(d) { if (!d) return "\u2014"; const dt = new Date(d + "T00:00:00"); return dt.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" }); }
function fmtMoney(n) { const v = parseFloat(n) || 0; return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function nextLot(shipments) {
  const lots = shipments.map(s => parseInt(s.lot, 10)).filter(n => !isNaN(n));
  return lots.length > 0 ? String(Math.max(...lots) + 1) : "58022";
}
function nextId(items) {
  const nums = items.map(i => parseInt((i.id || "").replace("SHP-", ""), 10)).filter(n => !isNaN(n));
  return "SHP-" + String((Math.max(0, ...nums) + 1)).padStart(4, "0");
}
function nextProforma(shipments, supplierId, initials, seasonId) {
  const matching = shipments.filter(s => s.supplierId === supplierId && (!seasonId || s.seasonId === seasonId));
  const nums = matching.map(s => { const p = (s.proforma || "").split("-"); return parseInt(p[p.length - 1], 10); }).filter(n => !isNaN(n));
  const next = nums.length > 0 ? Math.max(...nums) + 1 : 1;
  return (initials || "XX") + "-" + String(next).padStart(3, "0");
}
/* 2026-09-14 — Arturo: he updated Terey Avocados' proforma prefix in
   Shipper Settings (TA- → GF-) but a shipment created for that shipper
   directly in this module (Ground-Fresh's own ShipmentRegistry, not
   the Shipper Portal) still showed the old "TA-" prefix — the Settings
   screen's change never had anywhere to go. Root cause: this file's
   own `nextProforma` above (initials-based, scanning `shipments`) and
   ShipperPortal.js's `reserveProformaConsec`/`commitProformaConsec`
   (Settings-configured prefix + counter, gfi-shipper-settings) are two
   completely independent numbering schemes — Ground-Fresh never read
   the Settings one at all. Mirrors ShipperPortal.js's functions
   exactly (same file-local duplication convention as everywhere else
   in this codebase) so both sides share one counter and never collide
   or diverge. */
function reserveProformaFromSettings(supplierId, initials, shipments, seasonId) {
  if (!supplierId) return "??-001";
  var all = loadLS("gfi-shipper-settings", {}) || {};
  var rec = all[supplierId] || {};
  var prefix = rec.proformaPrefix;
  var nextNum = parseInt(rec.nextProformaNumber, 10);
  if (!isNaN(nextNum) && nextNum > 0) {
    var effectivePrefix = (prefix != null && prefix !== "") ? prefix : (initials ? String(initials).toUpperCase() + "-" : "");
    return effectivePrefix + String(nextNum).padStart(3, "0");
  }
  return nextProforma(shipments, supplierId, initials, seasonId);
}
/* Advances the shared Settings counter past whatever consec this save
   actually used — idempotent (only moves forward), so re-saving the
   same shipment never double-advances. Called right before onSave in
   ShipmentForm's handleSave, same trigger point ShipperPortal.js uses
   (handleSave/handleSubmit) so a canceled draft never burns a slot. */
function commitProformaToSettings(supplierId, proforma) {
  if (!supplierId || !proforma) return;
  var m = /(\d+)$/.exec(String(proforma));
  if (!m) return;
  var used = parseInt(m[1], 10);
  if (isNaN(used)) return;
  var all = loadLS("gfi-shipper-settings", {}) || {};
  var rec = all[supplierId] || {};
  var current = parseInt(rec.nextProformaNumber, 10);
  if (isNaN(current) || used + 1 > current) {
    all[supplierId] = { ...rec, nextProformaNumber: used + 1 };
    try { localStorage.setItem("gfi-shipper-settings", JSON.stringify(all)); } catch (e) {}
    try { saveToSupabase("shipper_settings", { id: supplierId, ...all[supplierId] }); } catch (e) {}
  }
}
/* Configured shipper-invoice prefix for this supplier, same field
   ShipperPortal.js's invoicePrefixFor() reads (gfi-shipper-settings
   [supplierId].invoicePrefix) — previously only Gomez Berries had a
   prefix at all here, hardcoded inline in handleShipperChange. */
function invoicePrefixFromSettings(supplierId) {
  if (!supplierId) return "";
  var all = loadLS("gfi-shipper-settings", {}) || {};
  var rec = all[supplierId] || {};
  return rec.invoicePrefix || "";
}
/* Configured default US customs broker id for this supplier
   (gfi-shipper-settings[supplierId].defaultUSBroker) — same field the
   Shipping Instructions doc already falls back to when printing, now
   also applied when the shipper is first selected on the form. */
function defaultUsBrokerFromSettings(supplierId) {
  if (!supplierId) return "";
  var all = loadLS("gfi-shipper-settings", {}) || {};
  var rec = all[supplierId] || {};
  return rec.defaultUSBroker || "";
}
function detectSeason(dateStr) {
  if (!dateStr) return null;
  const seasons = getSeasons();
  const d = new Date(dateStr + "T00:00:00");
  return seasons.find(s => new Date(s.startDate + "T00:00:00") <= d && d <= new Date(s.endEstimate + "T00:00:00"));
}
function getCurrentSeason() {
  const seasons = getSeasons();
  const now = new Date();
  const current = seasons.find(s => new Date(s.startDate + "T00:00:00") <= now && now <= new Date(s.endEstimate + "T00:00:00"));
  return current || (seasons.length > 0 ? seasons[seasons.length - 1] : null);
}
function getSeasonLabel(dateStr) {
  const s = detectSeason(dateStr);
  if (s) return s.seasonName || s.startDate.substring(0,4) + "-" + s.endEstimate.substring(0,4);
  if (!dateStr) return "";
  const y = parseInt(dateStr.substring(0,4), 10); const m = parseInt(dateStr.substring(5,7), 10);
  return m >= 9 ? y + "-" + (y+1) : (y-1) + "-" + y;
}
function isSupplierMexican(supplierId) {
  const sup = getSuppliers().find(s => String(s.id) === String(supplierId));
  if (!sup) return false;
  const c = (sup.country || "").toLowerCase();
  return c.includes("mex") || c.includes("m\u00e9x") || c === "mx";
}
/* Returns certs eligible to be picked for a shipment line.
   Filters:
     1. Cert's shipper matches the shipment's supplier (by id or name).
     2. Cert's commodity (extracted from cert.productName) matches the
        line's commodity (Blackberry/Blueberry/Raspberry/…). Pack size,
        label, and category are ignored — one cert covers one commodity.
     3. Available > 0 OR the current shipment's shipper invoice already
        appears in the cert's usage list (so editing a shipment that
        already holds the cert keeps the option visible even if the
        cert is otherwise full).
   `currentShipmentId` is the in-flight shipment's id ("" for a new
   one); `currentShipperInvoice` is its shipperInvoice field. */
function getAvailableExportCerts(supplierId, productLineName, currentShipperInvoice, currentShipmentId) {
  const certs = getExportCerts();
  const shipments = loadLS("gfi-shipments", []);
  const bills = loadLS("gfi-bills", []);
  const sup = getSuppliers().find(s => String(s.id) === String(supplierId));
  const shipperName = sup ? (sup.shortName || sup.fullName || "") : "";
  const wantedCommodity = extractCommodity(productLineName);
  if (!wantedCommodity) return [];
  return certs.filter(c => {
    const matchShipper = c.shipperName === shipperName || String(c.supplierId) === String(supplierId);
    if (!matchShipper) return false;
    const certCommodity = extractCommodity(c.productName);
    if (!certCommodity || certCommodity !== wantedCommodity) return false;
    if (!isCertAvailableForShipment(c, shipments, currentShipperInvoice, currentShipmentId, bills)) return false;
    /* 2026-11-04 (Arturo, Gomez Berries: "840-1560012870-260006 es de
       la temporada pasada... si esta vencido por fecha no deberia
       salir tampoco") — availability (total - used > 0) alone isn't
       enough: a cert past its own periodTo must stop being OFFERED,
       even with unused slots left. Kept selectable only when THIS
       shipment already references it, so an existing legacy
       assignment made before it expired doesn't silently disappear
       while editing. */
    if (isCertExpired(c)) {
      var usage = computeCertUsage(c, shipments, bills);
      var alreadyLinked = usage.some(function (u) {
        if (currentShipperInvoice && u.shipperInvoice === currentShipperInvoice) return true;
        if (!currentShipperInvoice && currentShipmentId && String(u.shipmentId) === String(currentShipmentId)) return true;
        return false;
      });
      if (!alreadyLinked) return false;
    }
    return true;
  });
}
function calcTotals(lines) {
  let cases = 0, pallets = 0, value = 0;
  (lines || []).forEach(l => { cases += parseInt(l.cases, 10) || 0; pallets += parseInt(l.pallets, 10) || 0; value += parseFloat(l.total) || 0; });
  return { cases, pallets, value };
}
function productSummary(lines) {
  return (lines || []).filter(l => l.productName || l.productBase).map(l => l.productName || l.productBase).join(", ") || "\u2014";
}
/* Build the local Windows path to a shipment's embarque folder.
   Mirrors the same path Phase 1 "Create Drive Folder" creates on
   Drive (web), so the Drive desktop mount at G:\My Drive\... will
   resolve to the same folder. Uses `dateArrival` to match the
   embarqueFolderName helper. */
function buildShipmentLocalFolder(shipment) {
  const company = loadCompanyProfile();
  const supplier = getSuppliers().find(s => String(s.id) === String(shipment.supplierId));
  const shipperShort = (supplier && (supplier.shortName || supplier.fullName)) || shipment.shipperName || "Unknown";
  const season = getSeasons().find(s => String(s.id) === String(shipment.seasonId));
  return buildLocalPath(company.localDrivePath, [
    seasonFolderName(season),
    embarqueFolderName(shipment, shipperShort),
  ]);
}

/* ======================== PRINT DOCUMENTS ========================
   Three printable docs generated from a shipment + its palletManifest:
   1. Receiving Sheet      — GFI-branded (logo + navy header)
   2. Carta Porte          — Mexican shipping doc, no GFI branding
   3. Shipping Instructions — Transport instructions, no GFI branding

   All three are pure HTML strings with inline <style> so the print
   window keeps formatting. Multi-print concatenates bodies with a
   page-break between docs. */

/* Zero-pad the trailing numeric segment of a proforma to 3 digits.
   "GB-1" → "GB-001", "GB-14" → "GB-014", "GB-115" → "GB-115" (unchanged
   since it already has 3+ digits). Non-numeric proformas pass through. */
function padProforma(proforma) {
  if (!proforma) return "";
  return String(proforma).replace(/(\d+)$/, function (m) { return m.padStart(3, "0"); });
}

function escHtml(s) {
  if (s === undefined || s === null) return "";
  return String(s).replace(/[&<>"']/g, function (c) { return ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]; });
}
function fmtDateLong(d) { if (!d) return ""; return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
function fmtKg(lb) { return (lb * 0.453592).toFixed(1); }

/* Look up commodity HTS / SAT codes for the Carta Porte. Reads
   gfi-commodities (seeded by ProductRegistry). Match by English name
   for now — that's how lines store the commodity. */
function findCommodity(commodityName, commodities) {
  if (!commodityName) return null;
  var n = String(commodityName).trim().toLowerCase();
  return (commodities || []).find(function (c) {
    return (c.nameEn || "").toLowerCase() === n || (c.nameEs || "").toLowerCase() === n;
  }) || null;
}

/* ──────── QR CODE HELPERS ────────
   Concise text summary so the QR encodes a short payload — small
   payload + lowest error-correction level keeps module density low
   enough to scan reliably at the 60×60 print size. Fields match the
   spec: Shipment #, Shipper, Date, Products, Total Cases, Total
   Pallets. Falls back to product-line case/pallet totals when the
   pallet manifest is empty (portal-only records) so the QR always
   carries quantity info. Production swap point flagged below — when
   ground-fresh.com hosts the doc, replace the body with the URL. */
function buildQRPayload(shipment) {
  /* PRODUCTION:
     return "https://ground-fresh.com/bol/" + (shipment.id || ""); */
  var lines = [];
  if (shipment.proforma) lines.push("Shipment#: " + shipment.proforma);
  if (shipment.shipperName) lines.push("Shipper: " + shipment.shipperName);
  if (shipment.dateDeparture) lines.push("Date: " + shipment.dateDeparture);
  var prods = [...new Set((shipment.lines || []).map(function (l) { return l.productBase || l.productName; }).filter(Boolean))];
  if (prods.length > 0) lines.push("Products: " + prods.join(", "));
  /* Pallet-manifest totals (preferred — counts real loaded pallets). */
  var realPallets = (shipment.palletManifest || []).filter(function (p) { return p && p.productBase && (parseInt(p.casesPallet, 10) || 0) > 0; });
  var totalCases = realPallets.reduce(function (s, p) { return s + (parseInt(p.casesPallet, 10) || 0); }, 0);
  var totalPallets = realPallets.length;
  /* Fallback to product-line totals when the manifest is empty
     (typical of portal-only records before Arturo's manifest fill). */
  if (totalCases === 0) {
    totalCases = (shipment.lines || []).reduce(function (s, l) { return s + (parseInt(l.cases, 10) || 0); }, 0);
  }
  if (totalPallets === 0) {
    totalPallets = (shipment.lines || []).reduce(function (s, l) { return s + (parseInt(l.pallets, 10) || 0); }, 0);
  }
  if (totalCases > 0)   lines.push("Cases: " + totalCases);
  if (totalPallets > 0) lines.push("Pallets: " + totalPallets);
  return lines.join("\n");
}
async function generateQRDataUrl(shipment) {
  try {
    var payload = buildQRPayload(shipment);
    /* 240px source displayed at 60px = 4× supersample, gives sharp
       module edges at print DPI. errorCorrectionLevel "L" keeps the
       symbol low-density (fewer modules per version) so it scans
       reliably even at the small 60px display size. */
    return await QRCode.toDataURL(payload, { width: 240, margin: 1, errorCorrectionLevel: "L" });
  } catch (e) {
    return "";
  }
}

/* ──────── PRINT-TIME VALIDATION ────────
   Pure helper that re-runs the L1+L2 blocking checks against a
   shipment record (vs. the form's `validate()` which depends on
   the form's `f` + `errors` state). Returns a string[] of bilingual
   error messages — empty array = OK to print. Used by the DetailPanel
   print buttons + the list-view bulk print toolbar to refuse with a
   specific cause message when the underlying shipment isn't clean.

   Save is intentionally NOT gated by this (per spec — users may save
   incomplete embarques to continue later). Print + Submit are. */
function validateShipmentForPrint(shipment, allShipments, lang) {
  var ES = lang === "es";
  var errs = [];
  var blank = function (v) { return v == null || String(v).trim() === ""; };
  /* 2026-10-01 — FOB (domestic Texas purchase): no border crossing, so
     no MX customs broker and no transport/truck data exist at all.
     Missing shipmentType (every pre-existing record) defaults to the
     normal import flow — zero behavior change for existing data. */
  var isFobShip = shipment.shipmentType === "fob";
  if (blank(shipment.lot))           errs.push(ES ? "Lote" : "Lot");
  if (blank(shipment.dateDeparture)) errs.push(ES ? "Fecha de salida" : "Departure date");
  if (blank(shipment.dateArrival))   errs.push(ES ? "Fecha de llegada (ETA)" : "Arrival date (ETA)");
  if (blank(shipment.supplierId))    errs.push(ES ? "Exportador" : "Shipper");
  if (blank(shipment.warehouseId))   errs.push(ES ? "Bodega de descarga" : "Destination warehouse");
  if (!isFobShip && blank(shipment.brokerMXId)) errs.push(ES ? "Agente aduanal MX" : "MX customs broker");
  if (!blank(shipment.lot) && (allShipments || []).some(function (s) { return s.id !== shipment.id && String(s.lot) === String(shipment.lot); })) {
    errs.push(ES ? "Lote duplicado" : "Duplicate lot");
  }
  /* Transport — required plates/economic/CAAT/ALPHA. Carrier, driver
     and phone are kept on the form but no longer block print/email/
     drive actions. Skipped entirely for FOB — there's no truck. */
  if (!isFobShip) {
    var trp = shipment.transport || {};
    [
      ["platesTruck",  ES ? "Placas tractor"           : "Truck plates"],
      ["econTruck",    ES ? "Número económico tractor" : "Truck unit #"],
      ["platesTrailer",ES ? "Placas caja"              : "Trailer plates"],
      ["econTrailer",  ES ? "Número económico caja"    : "Trailer unit #"],
      ["caat",         "CAAT"],
      ["codigoAlpha",  ES ? "Código ALPHA"             : "ALPHA code"],
    ].forEach(function (pair) { if (blank(trp[pair[0]])) errs.push(pair[1]); });
  }
  /* Lines: at least one with a productId + cases > 0. */
  var hasLine = (shipment.lines || []).some(function (l) { return l.productId && parseInt(l.cases, 10) > 0; });
  if (!hasLine) errs.push(ES ? "Al menos 1 línea de producto con cajas" : "At least 1 product line with cases");
  /* Pallet manifest: non-empty, all required fields, dupes, mismatches. */
  var realPallets = (shipment.palletManifest || []).filter(function (p) { return p.palletNum || p.productBase; });
  if (realPallets.length === 0) {
    errs.push(ES ? "Manifiesto de pallets vacío" : "Pallet manifest empty");
  } else {
    /* 2026-09-30 (Arturo: "estoy guardando un embarque de un producto
       sin etiqueta... me dice que 3 pallets tienen missing fields...
       supongo que es porque el campo label esta vacio pero no
       debiera aplicar si el producto en si no esta etiquetado") —
       labelInitials is never typed per pallet; it's derived read-only
       from the matched product's own label (see
       prefilledPalletFromLine below: `labelInitials: prod ?
       (prod.labelInitials || "") : ""`), and correctly stays blank
       for a genuine "No Label" product (2026-09-25 feature). Removed
       from the required list — a broken/unmatched product is still
       caught by the other 9 required fields (productBase, category,
       size, etc.), so this never weakens real validation, it only
       stops flagging a field that was never meant to be user-filled.
       Same fix applied to the two other copies of this exact list
       (PALLET_REQ below, and validateShipmentForSubmit's copy). */
    var palletReq = ["palletNum", "productBase", "category", "condition", "packType", "size", "unitsCase", "casesPallet", "variety"];
    var bad = realPallets.filter(function (p) { return palletReq.some(function (k) { return blank(p[k]); }); }).length;
    if (bad > 0) errs.push((ES ? bad + " pallet(s) con campos obligatorios faltantes" : bad + " pallet(s) missing required fields"));
    /* Duplicate pallet numbers */
    var pnSeen = {};
    realPallets.forEach(function (p) {
      var v = String(p.palletNum || "").trim().toUpperCase();
      if (!v) return;
      pnSeen[v] = (pnSeen[v] || 0) + 1;
    });
    Object.keys(pnSeen).forEach(function (k) {
      if (pnSeen[k] > 1) errs.push((ES ? "Pallet #" + k + " duplicado en el manifiesto — corrígelo antes de imprimir" : "Pallet #" + k + " duplicated in the manifest — fix it before printing"));
    });
    /* Cases mismatch per product */
    var bucket = function (arr, key) {
      var out = {};
      arr.forEach(function (l) {
        var name = (l.productBase || l.productName || "").trim();
        if (!name) return;
        var k = name.toLowerCase();
        if (!out[k]) out[k] = { label: name, total: 0 };
        out[k].total += parseInt(l[key], 10) || 0;
      });
      return out;
    };
    var byLine = bucket(shipment.lines || [], "cases");
    var byMan = bucket(realPallets, "casesPallet");
    var keys = {}; Object.keys(byLine).forEach(function (k) { keys[k] = true; }); Object.keys(byMan).forEach(function (k) { keys[k] = true; });
    Object.keys(keys).forEach(function (k) {
      var l = byLine[k] || { label: (byMan[k] || {}).label || k, total: 0 };
      var m = byMan[k] || { label: l.label, total: 0 };
      if (l.total === m.total) return;
      errs.push(l.label + ": " + (ES ? l.total + " en líneas vs " + m.total + " en manifiesto" : l.total + " in lines vs " + m.total + " in manifest"));
    });
    /* Pallet count must match the line total. */
    var totalLinePallets = (shipment.lines || []).reduce(function (s, l) { return s + (parseInt(l.pallets, 10) || 0); }, 0);
    if (totalLinePallets > 0 && totalLinePallets !== realPallets.length) {
      errs.push((ES ? "Pallets del Manifiesto (" + realPallets.length + ") no coinciden con Líneas (" + totalLinePallets + ")"
                    : "Pallet Manifest rows (" + realPallets.length + ") don't match Product Lines (" + totalLinePallets + ")"));
    }
  }
  /* Shipper invoice (L2 red) — printing without it would produce
     a Bill of Lading missing the buyer-side reference. */
  if (blank(shipment.shipperInvoice)) errs.push(ES ? "Número de Factura del Exportador" : "Shipper Invoice number");
  return errs;
}
function buildPrintBlockMsg(errs, lang) {
  if (!errs || errs.length === 0) return "";
  var head = lang === "es" ? "No se puede imprimir:" : "Cannot print:";
  return head + "\n" + errs.map(function (e) { return "• " + e; }).join("\n");
}

/* ──────── LOCALIZED PRINT FILENAMES ────────
   Browser print windows derive both the tab title and the default
   "Save as PDF" filename from <title>. Localize so a Spanish user
   sees "Carta_Porte_001" and an English user sees "Bill_of_Lading_001"
   in both the browser tab and the saved PDF. Underscores instead of
   spaces so the OS save dialog doesn't get awkward. */
export function docFilename(kind, shipment, lang) {
  var es = lang === "es";
  var prof = padProforma(shipment && shipment.proforma);
  if (kind === "receiving") {
    var lot = (shipment && shipment.lot) || "";
    /* FOB has no Proforma (that's an import-only concept) — filename
       uses the Vendor Invoice # (stored in shipperInvoice, same field
       the edit form relabels for FOB) instead, same reasoning as the
       doc body itself (see buildReceivingSheetHTML's own isFob
       comment). */
    if (shipment && shipment.shipmentType === "fob") {
      var vendorInv = (shipment && shipment.shipperInvoice) || "no-invoice";
      return (es ? "Lista_de_Empaque_de_Entrada_Lote_" : "Inbound_Packing_List_Lot_") + lot + (es ? "_Factura_" : "_Invoice_") + vendorInv;
    }
    return (es ? "Manifiesto_de_Recibo_Lote_" : "Receiving_Manifest_Lot_") + lot + (es ? "_Embarque_" : "_Shipment_") + prof;
  }
  if (kind === "carta-porte") return (es ? "Carta_Porte_" : "Bill_of_Lading_") + prof;
  if (kind === "dump-order") return (es ? "Orden_de_Desecho_Lote_" : "Dump_Order_Lot_") + ((shipment && shipment.lot) || "");
  return (es ? "Instrucciones_de_Embarque_" : "Shipping_Instructions_") + prof;
}
export function bulkDocFilename(kind, count, lang) {
  var es = lang === "es";
  var iso = new Date().toISOString().slice(0, 10);
  var prefix;
  if (kind === "receiving")        prefix = es ? "Manifiestos_de_Recibo"   : "Receiving_Manifests";
  else if (kind === "carta-porte") prefix = es ? "Cartas_Porte"             : "Bills_of_Lading";
  else                             prefix = es ? "Instrucciones_de_Embarque" : "Shipping_Instructions";
  return prefix + "_" + count + "_" + iso;
}

/* ──────── DOC 1 — RECEIVING SHEET (GFI-branded) ──────── */
export function buildReceivingSheetHTML(shipment, suppliers, lang) {
  /* 2026-10-02 — Arturo, correcting the FOB warehouse-document fix:
     "no necesito generar un receiving manifest para este tipo de
     embarques FOB si no un inbound packing list" + "pero este no debe
     llevar referencias a entry # ni export certificate #". A domestic
     Texas purchase never crossed a border, so Export Certificate #
     (Mexican organic import cert) and Entry # (already excluded
     entirely for FOB via the Carta Porte being hidden — Entry # only
     ever lived on that document, never here) don't apply. Retitled to
     "INBOUND PACKING LIST", Import Certificate line dropped, and the
     Shipper/Importer labels swapped to Vendor/Buyer — same philosophy
     already applied to the live Pallet Manifest edit table, which
     hides Traceability/Grower Code for FOB for the same reason. */
  var isFob = shipment.shipmentType === "fob";
  var sup = (suppliers || []).find(function (s) { return String(s.id) === String(shipment.supplierId); });
  /* 2026-10-02 — Arturo: "en shipper usa el short name" — FOB's
     Shipper field prefers shortName; the regular (non-FOB) Receiving
     Manifest's "Exportador / Shipper" field is untouched, still
     fullName-first. */
  var shipperName = sup ? (isFob ? (sup.shortName || sup.fullName || sup.name || "") : (sup.fullName || sup.shortName || sup.name || "")) : (shipment.shipperName || "");
  /* "Real" pallet = has a product AND a positive case count. A row
     with only a palletNum (or any single field) is a phantom from
     the empty-row template the form keeps for ergonomics; it must
     not bleed into the printed totals. */
  var pallets = dedupePalletsByNumber((shipment.palletManifest || []).filter(function (p) { return p && p.productBase && (parseInt(p.casesPallet, 10) || 0) > 0; }));
  var totalPallets = pallets.length;
  var totalCases = 0;
  pallets.forEach(function (p) { totalCases += parseInt(p.casesPallet, 10) || 0; });
  /* Cert number(s) inferred from product lines — never surfaced for
     FOB, computed only when it'll actually be used. */
  var certs = isFob ? [] : [...new Set((shipment.lines || []).map(function (l) { return l.exportCertNumber || l.exportCertManual || ""; }).filter(Boolean))];
  var commodities = [...new Set((shipment.lines || []).map(function (l) { return l.productBase || l.productName || ""; }).filter(Boolean))];

  /* Title used as the default PDF filename when the user prints to
     PDF. Localized via docFilename — controls both browser tab and
     the "Save as" default filename. */
  var receivingFilename = docFilename("receiving", shipment, lang);
  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + escHtml(receivingFilename) + '</title><style>';
  h += '@page{size:letter;margin:0.5in 0.6in}';
  h += '*{margin:0;padding:0;box-sizing:border-box}';
  h += 'body{font-family:"Segoe UI",Arial,sans-serif;font-size:10px;color:#1a1a1a;line-height:1.4}';
  h += '.page{max-width:730px;margin:0 auto}';
  h += '.header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:14px;border-bottom:3px solid #1B3A4B}';
  /* Bumped from 60 → 95 so "Ground-Fresh International" reads on
     the Receiving Manifest header. */
  h += '.logo img{height:95px}';
  h += '.doc-title{font-size:24px;font-weight:300;color:#1B3A4B;letter-spacing:2px;text-align:right}';
  h += '.doc-sub{font-size:10px;color:#888;text-align:right;margin-top:2px;letter-spacing:1px;text-transform:uppercase}';
  h += '.meta{display:grid;grid-template-columns:repeat(3,1fr);gap:8px 16px;margin:12px 0;padding:10px 14px;background:#f7f8fa;border-radius:6px;border-left:4px solid #1B3A4B}';
  h += '.meta .label{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700}';
  h += '.meta .value{font-size:11px;font-weight:600;color:#1a1a1a;margin-top:1px}';
  h += '.shipper-block{margin:8px 0 12px;padding:8px 14px;border:1px solid #e0e0e0;border-radius:6px;display:flex;justify-content:space-between}';
  h += '.shipper-block .label{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;margin-bottom:2px}';
  h += '.shipper-block .value{font-size:12px;font-weight:700;color:#1B3A4B}';
  h += 'table{width:100%;border-collapse:collapse}';
  h += '.items th{padding:6px 5px;font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#fff;background:#1B3A4B;font-weight:600}';
  h += '.items th:first-child{border-radius:4px 0 0 0}.items th:last-child{border-radius:0 4px 0 0}';
  h += '.items td{padding:5px 5px;border-bottom:1px solid #eee;font-size:9px;vertical-align:middle}';
  h += '.items tr:nth-child(even){background:#fafbfc}';
  h += '.r{text-align:right}.c{text-align:center}.mono{font-family:"Courier New",monospace}';
  h += '.subtotal{background:#eef4f5 !important;font-weight:700}';
  h += '.subtotal td{font-size:9px;color:#1B3A4B;padding:5px 5px;border-top:1px solid #c8d4d6}';
  h += '.grand{background:#1B3A4B;color:#fff;font-weight:800}';
  h += '.grand td{font-size:11px;color:#fff;padding:7px 5px;letter-spacing:0.5px}';
  h += '.footer{margin-top:18px;padding-top:8px;border-top:1px solid #ddd;font-size:8px;color:#999;text-align:center}';
  h += '</style></head><body><div class="page">';

  /* Header */
  h += '<div class="header"><div class="logo"><img src="' + LOGO_B64 + '" /></div>';
  h += '<div><div class="doc-title">' + (isFob ? "INBOUND PACKING LIST" : "RECEIVING MANIFEST") + '</div>';
  h += '<div class="doc-sub">Inbound Pallet Manifest</div></div></div>';

  /* Meta grid — uses Arrival ETA as the headline date (not Departure).
     The receiver cares when the truck lands, not when it left MX. */
  h += '<div class="meta">';
  h += '<div><div class="label">Arrival Date (ETA)</div><div class="value">' + escHtml(fmtDateLong(shipment.dateArrival) || "—") + '</div></div>';
  h += '<div><div class="label">Inbound (LOT)</div><div class="value">' + escHtml(shipment.lot || "—") + '</div></div>';
  h += '<div><div class="label">Pallets</div><div class="value">' + totalPallets + '</div></div>';
  if (!isFob) {
    h += '<div><div class="label">Shipment #</div><div class="value">' + escHtml(shipment.proforma || "—") + '</div></div>';
  }
  h += '<div><div class="label">Invoice #</div><div class="value">' + escHtml(shipment.shipperInvoice || "—") + '</div></div>';
  h += '<div><div class="label">' + (isFob ? "Consignee" : "Importer") + '</div><div class="value">Ground-Fresh International Inc.</div></div>';
  if (certs.length > 0) {
    h += '<div style="grid-column:span 3"><div class="label">Import Certificate # · ' + escHtml(commodities.join(", ")) + '</div><div class="value">' + escHtml(certs.join(", ")) + '</div></div>';
  }
  h += '</div>';

  /* Shipper block */
  h += '<div class="shipper-block"><div><div class="label">' + (isFob ? "Shipper" : "Exportador / Shipper") + '</div><div class="value">' + escHtml(shipperName || "—") + '</div></div>';
  h += '<div style="text-align:right"><div class="label">Total Cases</div><div class="value">' + totalCases + '</div></div></div>';

  /* Pallet table — Variety column added between Item and Size so the
     receiver can verify the cultivar against the cert. Traceability/
     Grower Code (organic import-chain columns) dropped for FOB — same
     reasoning as the "no Export Certificate #/Entry #" fix above, and
     the same 2 columns already hidden in the live Pallet Manifest edit
     table for FOB (see isFob's own comment above EMPTY_PALLET). Their
     combined 35% width is redistributed across # Cases/Type/Size so
     the FOB table doesn't end with 2 empty-looking header gaps. */
  h += '<table class="items"><thead><tr>';
  h += '<th class="c" style="width:7%">Pallet #</th>';
  h += '<th class="c" style="width:9%">Label</th>';
  h += '<th style="text-align:left;width:' + (isFob ? "28%" : "14%") + '">Item</th>';
  h += '<th class="c" style="width:' + (isFob ? "16%" : "11%") + '">Variety</th>';
  h += '<th class="c" style="width:' + (isFob ? "12%" : "8%") + '">Size</th>';
  h += '<th class="c" style="width:' + (isFob ? "14%" : "10%") + '">Type</th>';
  h += '<th class="c" style="width:' + (isFob ? "14%" : "8%") + '"># Cases</th>';
  if (!isFob) {
    h += '<th class="c" style="width:17%">Traceability</th>';
    h += '<th class="c" style="width:18%">Grower Code</th>';
  }
  h += '</tr></thead><tbody>';
  pallets.forEach(function (p) {
    var typeLabel = p.category === "OG" ? "Organic" : p.category === "CV" ? "Conventional" : (p.category || "");
    h += '<tr>';
    h += '<td class="c mono">' + escHtml(p.palletNum) + '</td>';
    h += '<td class="c mono">' + escHtml(p.labelInitials) + '</td>';
    h += '<td>' + escHtml(p.productBase) + '</td>';
    h += '<td class="c">' + escHtml(p.variety || "—") + '</td>';
    h += '<td class="c">' + escHtml(p.size) + '</td>';
    h += '<td class="c">' + escHtml(typeLabel) + '</td>';
    h += '<td class="c mono"><b>' + escHtml(p.casesPallet) + '</b></td>';
    if (!isFob) {
      h += '<td class="c mono">' + escHtml(p.traceability) + '</td>';
      h += '<td class="c mono">' + escHtml(p.growerCode) + '</td>';
    }
    h += '</tr>';
  });

  /* Subtotals per (commodity + size + type) */
  var subtotals = {};
  pallets.forEach(function (p) {
    if (!p.productBase) return;
    var key = p.productBase + " · " + (p.size || "?") + " · " + (p.category === "OG" ? "Organic" : p.category === "CV" ? "Conventional" : (p.category || "?"));
    if (!subtotals[key]) subtotals[key] = { key: key, pallets: 0, cases: 0, lb: 0 };
    var w = calcPalletWeight(p);
    subtotals[key].pallets += 1;
    subtotals[key].cases += parseInt(p.casesPallet, 10) || 0;
    subtotals[key].lb += w.lb;
  });
  /* Label-cell colspan accounts for however many columns actually
     exist (9 normally, 7 for FOB with Traceability/Grower Code
     dropped) — total must always equal the real column count:
     label + 1 (pallets) + 1 (cases) + 2 (weight). */
  var subtotalLabelSpan = isFob ? 3 : 5;
  Object.values(subtotals).forEach(function (g) {
    h += '<tr class="subtotal"><td colspan="' + subtotalLabelSpan + '" style="text-align:right">Subtotal · ' + escHtml(g.key) + '</td>';
    h += '<td class="c">' + g.pallets + ' plt</td>';
    h += '<td class="c mono">' + g.cases + '</td>';
    h += '<td colspan="2" class="r mono">' + g.lb.toFixed(1) + ' lb · ' + fmtKg(g.lb) + ' kg</td></tr>';
  });

  /* Grand total */
  var grandLb = pallets.reduce(function (s, p) { return s + calcPalletWeight(p).lb; }, 0);
  h += '<tr class="grand"><td colspan="' + subtotalLabelSpan + '" style="text-align:right">TOTAL</td>';
  h += '<td class="c">' + totalPallets + ' plt</td>';
  h += '<td class="c mono">' + totalCases + '</td>';
  h += '<td colspan="2" class="r mono">' + grandLb.toFixed(1) + ' lb · ' + fmtKg(grandLb) + ' kg</td></tr>';

  h += '</tbody></table>';

  h += '<div class="footer">Ground Fresh International, Inc. · Lindenhurst, NY, USA · ground-fresh.com</div>';
  h += '</div></body></html>';
  return h;
}

/* ──────── DOC — DUMP ORDER (GFI-branded) ────────
   2026-10-16, Arturo (urgent): "necesito generar un documento desde
   el embarque para ser enviado via email a la bodega asignada del
   mismo embarque llamado Dump Order... cuando un embarque se
   desechan cajas por repack, ellos me piden ese dump order... debe
   mencionar las cajas que yo mismo haya marcado como dump, detallando
   el numero de pallet en caso de que sean mas de un pallet, el
   producto, el lote y el pu afectado por ese dump... esto aplica
   solo para las ventas a four seasons pero el email se envia solo
   desde ground-fresh a la bodega" — sourced from the SAME per-pallet
   lostCases/lossReason notation already captured on the Pallet
   Manifest (no new capture UI needed, this is purely a document over
   existing data); PU# resolved live the same way every other status
   lookup in this app does (invoicesForPallet, saleStatusCalc.js) —
   never stored on the pallet itself. */
export function buildDumpOrderRows(shipment, invoices) {
  var pallets = (shipment.palletManifest || []).filter(function (p) { return p && (parseInt(p.lostCases, 10) || 0) > 0; });
  var allInvoices = invoices || [];
  return pallets.map(function (p) {
    var matchedInvoices = invoicesForPallet(shipment.lot, p.palletNum, allInvoices);
    var pus = [...new Set(matchedInvoices.map(function (inv) { return inv.puNumber; }).filter(Boolean))];
    /* 2026-10-16, Arturo: "solo muestras producto, debes mostrar
       descripcion del producto" — same auto-generated format
       ProductRegistry's catalog SKUs use, already relied on by
       protectionReport.js's buildProtectionReportRows for the exact
       same "[Product] [Units]/[Size] [Cat] [Label] Label" shape. */
    var productDescription = [p.productBase, (p.unitsCase || "") + "/" + (p.size || ""), p.category, p.labelInitials, "Label"].filter(Boolean).join(" ");
    return {
      palletNum: p.palletNum || "—",
      productBase: p.productBase || "—",
      productDescription: productDescription || "—",
      lot: shipment.lot || "—",
      puNumbers: pus.length > 0 ? pus.join(", ") : "—",
      casesToDump: parseInt(p.lostCases, 10) || 0,
      reason: p.lossReason === "other" ? (p.lossReasonCustom || "") : (p.lossReason || ""),
    };
  });
}

var DUMP_ORDER_REASON_LABELS = {
  en: { makeup: "Make Up", repack: "Re-Pack" },
  es: { makeup: "Make Up", repack: "Re-Pack" },
};

/* 2026-10-16, Arturo: "el mensaje al shipper debe ser en español e
   informar que ese fue el resultado de la re-empacada... informa que
   se desecharon x cajas en total, quedando x cajas y el detalle de
   cada pallet y las cajas desechadas y que vean las fotos anexas,
   pero hazlo profesional" — always Spanish regardless of the app's
   selected language (shippers are Mexican, same override every other
   shipper-facing email body in this app already uses — see
   CLAUDE.md's COMMUNICATION rules), with the real numbers computed
   live from the same data the PDF table shows, never a generic
   placeholder body. */
/* 2026-10-16, Arturo: "esta informacion [referencia] quedo al inicio
   del email... y todo esto incluyendo el detalle por pallet mostralo
   dentro de un rectangulo profesional con distintivo de colores
   informativos, arriba un banner de alerta porque no es una noticia
   agradable con el icono de alerta" — then, a turn later: "quita el
   buen dia, empieza con aviso importante en un banner de
   advertencia". Built as trusted HTML (not the plain-text editable
   `message` textarea, which EmailDialog always escapes) via
   `prependHtml` — a new EmailDialog prop (mirrors the existing
   `insertBeforeSignatureHtml`, but renders BEFORE the editable
   message instead of after) added specifically for this, so the
   styled banner+box always comes first while still leaving the
   Message field itself visible and editable for Arturo to add his
   own remarks (2026-10-16: "porque no se ve el mensaje del email
   aqui?" → "para que yo pueda editar si quisiera agregar algo, lo
   demas del preview no cambia" — the closing-remarks text now lives
   in `dumpOrderDefaultBody`'s defaultBody instead of baked into this
   HTML, so it's the one thing Arturo can see/edit before sending). */
function buildDumpOrderShipperPrependHtml(shipment, dumpOrderRows) {
  /* 2026-10-16, Arturo (real-data correction): "para sacar el total
     de cajas debes primero sumar las que se desecharon a las que
     quedaron porque ahí estás restando nuevamente... el pallet era
     por 240 originalmente, al desechar 66 quedaron 174, no 108" —
     confirmed against real data (LOT 58070, pallet 077: casesPallet
     "174", lostCases "66"): casesPallet already stores the POST-dump
     count, never the original arrival count (same "lostCases is a
     separate informational note, casesPallet always what physically
     remains" design already documented for this field). Summing
     casesPallet across the real pallets already gives the real
     remaining total — subtracting lostCases from it again double-
     counts the same dump. */
  var realPallets = (shipment.palletManifest || []).filter(function (p) { return p && p.productBase && (parseInt(p.casesPallet, 10) || 0) > 0; });
  var remaining = realPallets.reduce(function (s, p) { return s + (parseInt(p.casesPallet, 10) || 0); }, 0);
  var totalDumped = dumpOrderRows.reduce(function (s, r) { return s + r.casesToDump; }, 0);
  var reasonLabels = DUMP_ORDER_REASON_LABELS.es;
  /* 2026-10-16, Arturo: "tambien debes informar los numeros de
     referencia de ellos como numero de factura y proforma, fecha y
     hora de llegada a la bodega y fecha de salida de mexico" — the
     shipper's own reference numbers (their invoice #, GFI's proforma
     #) plus the real transit dates, so the shipper can match this
     notice to their own records without having to ask GFI which lot/
     load it refers to. "Llegada a la bodega" is the real received
     timestamp (shipment.receivedDate/receivedTime, stamped once GFI
     or Twin River actually takes the fruit in) — falls back to the
     ETA (dateArrival) only when that hasn't happened yet, so the
     email never claims an arrival that hasn't actually occurred. */
  var arrivalLabel = shipment.receivedDate ? "Fecha y hora de llegada a la bodega" : "Fecha estimada de llegada a la bodega (ETA)";
  var arrivalValue = shipment.receivedDate
    ? fmtDate(shipment.receivedDate) + (shipment.receivedTime ? " " + shipment.receivedTime : "")
    : (fmtDate(shipment.dateArrival) || "—");
  var lot = shipment.lot || "—";

  var rowsHtml = dumpOrderRows.map(function (r) {
    var reasonLabel = r.reason ? (reasonLabels[r.reason] || r.reason) : "—";
    return '<tr>'
      + '<td style="padding:6px 8px;border-bottom:1px solid #FECACA;font-size:12px">' + escHtml(r.palletNum) + '</td>'
      + '<td style="padding:6px 8px;border-bottom:1px solid #FECACA;font-size:12px">' + escHtml(r.productDescription) + '</td>'
      + '<td style="padding:6px 8px;border-bottom:1px solid #FECACA;font-size:12px">' + escHtml(reasonLabel) + '</td>'
      + '<td style="padding:6px 8px;border-bottom:1px solid #FECACA;font-size:12px;text-align:right;font-weight:700;color:#B91C1C">' + r.casesToDump + '</td>'
      + '</tr>';
  }).join("");

  var html = '';
  /* Branded header, no personal signature — Arturo: "que salga del
     email de alertas de ground-fresh formando solo Ground-Fresh y el
     logo que creaste y tienes no mi firma personal". An actual
     embedded logo image was tried and abandoned for automated alert
     emails earlier this session (WeatherWatchRegistry.js, 2026-10-13:
     never rendered in a real inbox) — this colored brand band is the
     same stand-in convention used everywhere else in the app since;
     `signatures={[]}` on the dialog is what actually keeps Arturo's
     personal signature out of the send. */
  html += '<div style="background:#0D1B2A;padding:14px 20px;border-radius:8px 8px 0 0;border-bottom:3px solid #C8A840;margin:0 0 16px 0">';
  html += '<span style="color:#fff;font-size:15px;font-weight:700;letter-spacing:0.02em">Ground-Fresh</span>';
  html += '</div>';
  /* Warning banner — "no es una noticia agradable", shown first, no
     greeting above it. */
  html += '<div style="display:flex;align-items:center;gap:10px;margin:0 0 16px 0;padding:12px 16px;background:#FEF2F2;border:1px solid #FCA5A5;border-left:5px solid #DC2626;border-radius:6px">';
  html += '<span style="font-size:22px;line-height:1">⚠️</span>';
  html += '<span style="font-size:13px;font-weight:700;color:#991B1B">AVISO IMPORTANTE — Cajas desechadas durante el re-empaque del LOTE ' + escHtml(lot) + '</span>';
  html += '</div>';

  /* Reference block + pallet detail, inside one bordered box. */
  html += '<div style="border:1px solid #d0d5dd;border-radius:8px;overflow:hidden;margin:0 0 16px 0">';
  html += '<div style="background:#B91C1C;color:#fff;padding:10px 16px;font-size:12px;font-weight:700">Orden de Desecho — LOTE ' + escHtml(lot) + '</div>';
  html += '<div style="padding:14px 16px;background:#fff">';
  html += '<table style="width:100%;border-collapse:collapse;margin-bottom:14px;font-size:12px">';
  html += '<tr>'
    + '<td style="padding:3px 8px 3px 0;color:#6b7280;font-weight:600;white-space:nowrap">Factura del Shipper:</td>'
    + '<td style="padding:3px 0;font-weight:700">' + escHtml(shipment.shipperInvoice || "—") + '</td>'
    + '<td style="padding:3px 8px 3px 24px;color:#6b7280;font-weight:600;white-space:nowrap">Proforma:</td>'
    + '<td style="padding:3px 0;font-weight:700">' + escHtml(shipment.proforma || "—") + '</td>'
    + '</tr>';
  html += '<tr>'
    + '<td style="padding:3px 8px 3px 0;color:#6b7280;font-weight:600;white-space:nowrap">Salida de México:</td>'
    + '<td style="padding:3px 0;font-weight:700">' + escHtml(fmtDate(shipment.dateDeparture)) + '</td>'
    + '<td style="padding:3px 8px 3px 24px;color:#6b7280;font-weight:600;white-space:nowrap">' + escHtml(arrivalLabel) + ':</td>'
    + '<td style="padding:3px 0;font-weight:700">' + escHtml(arrivalValue) + '</td>'
    + '</tr>';
  html += '</table>';
  html += '<div style="font-size:13px;color:#1a1a1a;line-height:1.5;margin-bottom:12px">Les informamos el resultado del re-empaque realizado en el LOTE <strong>' + escHtml(lot) + '</strong>. Como parte de este proceso se desecharon un total de <strong style="color:#B91C1C">' + totalDumped + ' caja' + (totalDumped === 1 ? "" : "s") + '</strong>, quedando <strong style="color:#15803D">' + remaining + ' caja' + (remaining === 1 ? "" : "s") + ' disponible' + (remaining === 1 ? "" : "s") + '</strong>.</div>';
  html += '<table style="width:100%;border-collapse:collapse">';
  html += '<thead><tr style="background:#FEE2E2">'
    + '<th style="text-align:left;padding:6px 8px;border-bottom:2px solid #B91C1C;font-size:11px;color:#991B1B">Pallet</th>'
    + '<th style="text-align:left;padding:6px 8px;border-bottom:2px solid #B91C1C;font-size:11px;color:#991B1B">Descripción del Producto</th>'
    + '<th style="text-align:left;padding:6px 8px;border-bottom:2px solid #B91C1C;font-size:11px;color:#991B1B">Motivo</th>'
    + '<th style="text-align:right;padding:6px 8px;border-bottom:2px solid #B91C1C;font-size:11px;color:#991B1B">Cajas desechadas</th>'
    + '</tr></thead>';
  html += '<tbody>' + rowsHtml + '</tbody>';
  html += '</table>';
  html += '</div>';
  html += '</div>';

  return html;
}

export function buildDumpOrderHTML(shipment, suppliers, invoices, lang) {
  var es = lang === "es";
  var sup = (suppliers || []).find(function (s) { return String(s.id) === String(shipment.supplierId); });
  var shipperName = sup ? (sup.fullName || sup.shortName || sup.name || "") : (shipment.shipperName || "");
  var rows = buildDumpOrderRows(shipment, invoices);
  var totalCases = rows.reduce(function (s, r) { return s + r.casesToDump; }, 0);
  var reasonLabels = DUMP_ORDER_REASON_LABELS[es ? "es" : "en"];
  var filename = docFilename("dump-order", shipment, lang);

  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + escHtml(filename) + '</title><style>';
  h += '@page{size:letter;margin:0.5in 0.6in}';
  h += '*{margin:0;padding:0;box-sizing:border-box}';
  h += 'body{font-family:"Segoe UI",Arial,sans-serif;font-size:10px;color:#1a1a1a;line-height:1.4}';
  h += '.page{max-width:730px;margin:0 auto}';
  h += '.header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:14px;border-bottom:3px solid #B91C1C}';
  h += '.logo img{height:95px}';
  h += '.doc-title{font-size:24px;font-weight:300;color:#B91C1C;letter-spacing:2px;text-align:right}';
  h += '.doc-sub{font-size:10px;color:#888;text-align:right;margin-top:2px;letter-spacing:1px;text-transform:uppercase}';
  h += '.meta{display:grid;grid-template-columns:repeat(3,1fr);gap:8px 16px;margin:12px 0;padding:10px 14px;background:#f7f8fa;border-radius:6px;border-left:4px solid #B91C1C}';
  h += '.meta .label{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700}';
  h += '.meta .value{font-size:11px;font-weight:600;color:#1a1a1a;margin-top:1px}';
  h += 'table{width:100%;border-collapse:collapse}';
  h += '.items th{padding:6px 5px;font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#fff;background:#B91C1C;font-weight:600}';
  h += '.items th:first-child{border-radius:4px 0 0 0}.items th:last-child{border-radius:0 4px 0 0}';
  h += '.items td{padding:5px 5px;border-bottom:1px solid #eee;font-size:9px;vertical-align:middle}';
  h += '.items tr:nth-child(even){background:#fafbfc}';
  h += '.r{text-align:right}.c{text-align:center}.mono{font-family:"Courier New",monospace}';
  h += '.grand{background:#B91C1C;color:#fff;font-weight:800}';
  h += '.grand td{font-size:11px;color:#fff;padding:7px 5px;letter-spacing:0.5px}';
  h += '.footer{margin-top:18px;padding-top:8px;border-top:1px solid #ddd;font-size:8px;color:#999;text-align:center}';
  h += '</style></head><body><div class="page">';

  h += '<div class="header"><div class="logo"><img src="' + LOGO_B64 + '" /></div>';
  h += '<div><div class="doc-title">' + (es ? "ORDEN DE DESECHO" : "DUMP ORDER") + '</div>';
  h += '<div class="doc-sub">' + (es ? "Cajas autorizadas para desecho" : "Cases authorized for disposal") + '</div></div></div>';

  /* 2026-10-16, Arturo: "esos numeros muestralos tambien en el PDF no
     solo en el email" — same reference numbers/dates added to the
     shipper email body (factura/proforma/fechas), now also on the
     document itself so it stands on its own if forwarded or printed
     separately from the email. Same "llegada real > ETA" fallback as
     the email body. */
  var arrivalValuePdf = shipment.receivedDate
    ? fmtDate(shipment.receivedDate) + (shipment.receivedTime ? " " + shipment.receivedTime : "")
    : (fmtDate(shipment.dateArrival) || "—");
  h += '<div class="meta">';
  h += '<div><div class="label">' + (es ? "Fecha" : "Date") + '</div><div class="value">' + escHtml(fmtDateLong(new Date().toISOString().slice(0, 10))) + '</div></div>';
  h += '<div><div class="label">' + (es ? "Lote" : "Lot") + '</div><div class="value">' + escHtml(shipment.lot || "—") + '</div></div>';
  h += '<div><div class="label">' + (es ? "Pallets Afectados" : "Pallets Affected") + '</div><div class="value">' + rows.length + '</div></div>';
  h += '<div><div class="label">' + (es ? "Proforma" : "Proforma") + '</div><div class="value">' + escHtml(shipment.proforma || "—") + '</div></div>';
  h += '<div><div class="label">' + (es ? "Factura del Shipper" : "Shipper Invoice #") + '</div><div class="value">' + escHtml(shipment.shipperInvoice || "—") + '</div></div>';
  h += '<div><div class="label">' + (es ? "Vendedor / Shipper" : "Shipper") + '</div><div class="value">' + escHtml(shipperName || "—") + '</div></div>';
  h += '<div><div class="label">' + (es ? "Salida de México" : "Departure from Mexico") + '</div><div class="value">' + escHtml(fmtDate(shipment.dateDeparture)) + '</div></div>';
  h += '<div><div class="label">' + (es ? "Llegada a la Bodega" : "Arrival at Warehouse") + '</div><div class="value">' + escHtml(arrivalValuePdf) + '</div></div>';
  h += '</div>';

  h += '<table class="items"><thead><tr>';
  h += '<th class="c" style="width:10%">' + (es ? "Pallet #" : "Pallet #") + '</th>';
  h += '<th style="text-align:left;width:28%">' + (es ? "Descripción del Producto" : "Product Description") + '</th>';
  h += '<th class="c" style="width:10%">' + (es ? "Lote" : "Lot") + '</th>';
  h += '<th class="c" style="width:16%">PU#</th>';
  h += '<th class="c" style="width:16%">' + (es ? "Motivo" : "Reason") + '</th>';
  h += '<th class="c" style="width:20%">' + (es ? "Cajas a Desechar" : "Cases to Dump") + '</th>';
  h += '</tr></thead><tbody>';
  rows.forEach(function (r) {
    h += '<tr>';
    h += '<td class="c mono">' + escHtml(r.palletNum) + '</td>';
    h += '<td>' + escHtml(r.productDescription) + '</td>';
    h += '<td class="c mono">' + escHtml(r.lot) + '</td>';
    h += '<td class="c mono">' + escHtml(r.puNumbers) + '</td>';
    h += '<td class="c">' + escHtml(reasonLabels[r.reason] || r.reason || "—") + '</td>';
    h += '<td class="c mono"><b>' + r.casesToDump + '</b></td>';
    h += '</tr>';
  });
  h += '<tr class="grand"><td colspan="5" style="text-align:right">' + (es ? "TOTAL DE CAJAS A DESECHAR" : "TOTAL CASES TO DUMP") + '</td>';
  h += '<td class="c mono">' + totalCases + '</td></tr>';
  h += '</tbody></table>';

  h += '<div class="footer">Ground Fresh International, Inc. · Lindenhurst, NY, USA · ground-fresh.com</div>';
  h += '</div></body></html>';
  return h;
}

/* ──────── DOC 2 — BILL OF LADING (master document) ────────
   Combines what used to be two separate prints (Carta Porte + Shipping
   Instructions) into one master Bill of Lading. Sections:
     1. Shipment Header (dates, shipper, invoice, export cert)
     2. Carrier Info
     3. Mercancía / Merchandise (per-tariff-item totals)
     4. Customs Info (MX + US brokers)
     5. Delivery Instructions (warehouse + consignee)
   Signature extended to take brokers + seasons (needed for Customs +
   Delivery sections). Existing fixes preserved: shipper invoice still
   in Section 1; full product description "Blackberry · 12/6oz · OG ·
   GF Label" still in the Merchandise table; US broker still surfaced. */
export function buildCartaPorteHTML(shipment, suppliers, warehouses, commodities, brokers, seasons, lang, qrDataUrl) {
  var sup = (suppliers || []).find(function (s) { return String(s.id) === String(shipment.supplierId); });
  var shipperName = sup ? (sup.fullName || sup.shortName || sup.name || "") : (shipment.shipperName || "");
  var shipperAddr = sup ? [sup.address, sup.colony, (sup.city || "") + (sup.state ? ", " + sup.state : ""), sup.zip, sup.country].filter(Boolean).join(" · ") : "";
  /* RFC stored as 3 parts in SupplierRegistry. Reconstruct on read so
     the doc shows the canonical "AAA-YYMMDD-XXX" form. */
  var shipperRfc = sup
    ? (sup.rfc || (sup.rfcLetters && sup.rfcDate ? [sup.rfcLetters, sup.rfcDate, sup.rfcHomoclave || ""].filter(Boolean).join("-") : (sup.taxId || "")))
    : "";
  var shipperFda = sup ? (sup.fdaNumber || "") : "";
  var wh = (warehouses || []).find(function (w) { return String(w.id) === String(shipment.warehouseId); });
  var whName = wh ? (wh.fullName || wh.shortName || wh.name || "") : (shipment.warehouseName || "");
  var whAddress = wh ? [wh.address, wh.city + (wh.state ? ", " + wh.state : ""), wh.zip].filter(Boolean).join(", ") : "";
  /* Broker resolution mirrors what Shipping Instructions used: prefer
     the stored stamped name (portal-accepted shipments stamp these at
     submit time), fall back to registry lookup, then manual. Keeps
     US broker visible even when registry snapshot drifts. */
  var brMx = (brokers || []).find(function (b) { return String(b.id) === String(shipment.brokerMXId); });
  var brMxName = shipment.brokerMXName || (brMx ? (brMx.fullName || brMx.shortName || brMx.name || "") : "") || shipment.brokerMXManual || "";
  var brUs = (brokers || []).find(function (b) { return String(b.id) === String(shipment.brokerUSId); });
  var brUsName = shipment.brokerUSName || (brUs ? (brUs.fullName || brUs.shortName || brUs.name || "") : "") || shipment.brokerUSManual || "";
  /* Distinct export-cert numbers from product lines so multiple certs
     across lines show in the header without duplication. */
  var exportCerts = [...new Set((shipment.lines || []).map(function (l) { return l.exportCertNumber || l.exportCertManual || ""; }).filter(Boolean))];
  void seasons; /* reserved for a future "Season" stamp in the header. */
  /* "Real" pallet = has a product AND a positive case count. A row
     with only a palletNum (or any single field) is a phantom from
     the empty-row template the form keeps for ergonomics; it must
     not bleed into the printed totals. */
  var pallets = dedupePalletsByNumber((shipment.palletManifest || []).filter(function (p) { return p && p.productBase && (parseInt(p.casesPallet, 10) || 0) > 0; }));

  /* Aggregate pallets into one row per (commodity + size + category +
     label) since the Carta Porte shows the line as a tariff item, not
     pallet by pallet. labelInitials is included in the key so e.g.
     "Blackberry 12/6oz OG GF" and "Blackberry 12/6oz OG TR" appear as
     separate rows when shipped on the same truck. Falls back to the
     shipment.lines aggregation when the manifest is empty; the line
     fallback now also pulls size/units/label from the line so the
     row still renders the full description even without pallets. */
  var groups = {};
  if (pallets.length > 0) {
    pallets.forEach(function (p) {
      var key = (p.productBase || "?") + "|" + (p.size || "") + "|" + (p.category || "") + "|" + (p.labelInitials || "");
      if (!groups[key]) groups[key] = { commodity: p.productBase || "", size: p.size || "", unitsCase: p.unitsCase || "", category: p.category || "", labelInitials: p.labelInitials || "", pallets: 0, cases: 0, lb: 0 };
      var w = calcPalletWeight(p);
      groups[key].pallets += 1;
      groups[key].cases += parseInt(p.casesPallet, 10) || 0;
      groups[key].lb += w.lb;
    });
  } else {
    /* Lines fallback only includes rows with a product AND positive
       cases — same anti-phantom rule as the pallet filter above. */
    (shipment.lines || []).filter(function (l) { return (l.productId || l.productBase) && (parseInt(l.cases, 10) || 0) > 0; }).forEach(function (l) {
      var key = (l.productBase || l.productName || "?") + "|" + (l.size || "") + "|" + (l.category || "") + "|" + (l.labelInitials || "");
      if (!groups[key]) groups[key] = { commodity: l.productBase || l.productName || "", size: l.size || "", unitsCase: l.unitsPerCase || l.unitsCase || "", category: l.category || "", labelInitials: l.labelInitials || "", pallets: 0, cases: 0, lb: 0, value: 0 };
      groups[key].pallets += parseInt(l.pallets, 10) || 0;
      groups[key].cases += parseInt(l.cases, 10) || 0;
      groups[key].value = (groups[key].value || 0) + ((parseInt(l.cases, 10) || 0) * (parseFloat(l.price) || 0));
      /* Line-fallback weight: oz per unit × units × cases. PALLET_SOZ
         (used by calcPalletWeight) is keyed on size, so reuse it here. */
      var ozPerUnit = (typeof PALLET_SOZ !== "undefined" ? PALLET_SOZ[l.size] : 0) || 0;
      var unitsPerCase = parseFloat(l.unitsPerCase || l.unitsCase) || 0;
      var lineOz = ozPerUnit * unitsPerCase * (parseInt(l.cases, 10) || 0);
      if (lineOz > 0) groups[key].lb = (groups[key].lb || 0) + (lineOz / 16);
    });
  }
  /* Per-line USD value from shipment.lines, mapped to groups by
     commodity. Filtered to real lines so phantom rows don't
     contribute zero-value entries (and so the grandValue recompute
     a few lines below sums only real values). */
  var valueByCommodity = {};
  (shipment.lines || []).filter(function (l) { return (l.productId || l.productBase) && (parseInt(l.cases, 10) || 0) > 0; }).forEach(function (l) {
    var k = (l.productBase || l.productName || "").toLowerCase();
    valueByCommodity[k] = (valueByCommodity[k] || 0) + ((parseInt(l.cases, 10) || 0) * (parseFloat(l.price) || 0));
  });

  var rows = Object.values(groups);

  /* Filename localized via docFilename — English shipper sees
     "Bill_of_Lading_001", Spanish shipper sees "Carta_Porte_001".
     Lot is intentionally never referenced — internal identifier,
     not the shipper's reference. */
  var cartaPorteFilename = docFilename("carta-porte", shipment, lang);
  /* CSS tuned for a single Letter page after Pallet Detail was
     removed. The page now has comfortable headroom, so fonts +
     paddings are bumped for legibility while still fitting:
     - body 10.5px (was 9px), line-height 1.4
     - section padding 10px 16px (was 6px 12px)
     - kv gap 6px 18px (was 3px 14px)
     - kv value 11.5px (was 10px)
     - table cells 10.5px (was 9px), heavier padding for row height
     - .value-strong class for emphasis on key facts (dates, shipper
       name, shipment #, totals) — 13px bold, accent color.
     Page uses 100% width inside the @page margins (no max-width). */
  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + escHtml(cartaPorteFilename) + '</title><style>';
  h += '@page{size:letter;margin:0.4in 0.45in}';
  h += '*{margin:0;padding:0;box-sizing:border-box}';
  h += 'body{font-family:"Segoe UI",Arial,sans-serif;font-size:10.5px;color:#1a1a1a;line-height:1.4}';
  h += '.page{width:100%;margin:0}';
  h += '.title-bar{padding:11px 18px;background:#2C3E50;color:#fff;border-radius:5px 5px 0 0}';
  h += '.title-bar h1{font-size:17px;font-weight:300;letter-spacing:2.5px;margin:0}';
  h += '.title-bar .sub{font-size:9px;letter-spacing:1px;text-transform:uppercase;opacity:0.8;margin-top:2px}';
  h += '.title-bar .doc-num-label{font-size:9px;letter-spacing:1px;text-transform:uppercase;opacity:0.7}';
  h += '.title-bar .doc-num-val{font-size:18px;font-weight:700;letter-spacing:2px;display:block;margin-top:2px}';
  h += '.section{padding:10px 16px;border:1px solid #e2e8ec;border-top:none;background:#fff}';
  h += '.section.alt{background:#f9fbfc}';
  h += '.section h2{font-size:9.5px;font-weight:700;letter-spacing:1.2px;text-transform:uppercase;color:#2C3E50;margin-bottom:7px;padding-bottom:4px;border-bottom:1px solid #e2e8ec}';
  h += '.kv{display:grid;grid-template-columns:repeat(2,1fr);gap:6px 22px}';
  h += '.kv .label{font-size:8px;text-transform:uppercase;letter-spacing:0.8px;color:#7a8693;font-weight:700;line-height:1.25}';
  h += '.kv .value{font-size:11.5px;font-weight:600;color:#1a1a1a;margin-top:1px;line-height:1.3}';
  /* Emphasis class for the values the eye should land on first. */
  h += '.kv .value-strong{font-size:13px;font-weight:700;color:#2C3E50;margin-top:1px;line-height:1.3}';
  h += 'table{width:100%;border-collapse:collapse;margin-top:5px}';
  h += '.items th{padding:6px 7px;font-size:8.5px;text-transform:uppercase;letter-spacing:0.7px;color:#fff;background:#2C3E50;font-weight:600;border-right:1px solid #34495E;line-height:1.25}';
  h += '.items th:last-child{border-right:none}';
  h += '.items td{padding:6px 7px;border-bottom:1px solid #e2e8ec;font-size:10.5px;vertical-align:middle;line-height:1.3}';
  h += '.items tr:nth-child(even){background:#f9fbfc}';
  h += '.r{text-align:right}.c{text-align:center}.mono{font-family:"Courier New",monospace}';
  h += '.tot td{font-size:12px;font-weight:800;color:#2C3E50;background:#eef4f7 !important;border-top:2px solid #2C3E50;padding-top:7px;padding-bottom:7px}';
  h += '</style></head><body><div class="page">';

  /* Title bar — title on the left, Shipment # on the right in the
     same blue bar. The number uses the larger title-bar emphasis
     style so it reads as the primary identifier on the page. LOT
     is intentionally not printed (internal GFI identifier). */
  /* Right side of the title bar: Shipment # block + optional QR.
     QR is pre-generated as a PNG data URL by printShipmentDocs
     (async) and passed in via qrDataUrl. Rendered at 80×80 so it
     scans cleanly without crowding the layout; bilingual caption
     sits below in a muted gray. When qrDataUrl is absent (e.g. a
     caller that didn't pre-generate, or QR gen failed) the block
     collapses to just the Shipment # so the title bar stays
     consistent. */
  h += '<div class="title-bar" style="display:flex;justify-content:space-between;align-items:center;gap:14px"><h1 style="margin:0">CARTA PORTE / BILL OF LADING</h1>';
  h += '<div style="display:flex;align-items:center;gap:14px">';
  h += '<div style="text-align:right"><span class="doc-num-label">Embarque # / Shipment #</span><span class="doc-num-val">' + escHtml(shipment.proforma || "—") + '</span></div>';
  if (qrDataUrl) {
    h += '<div style="display:flex;flex-direction:column;align-items:center;gap:2px">';
    /* QR rendered at 60×60 (down from 120×120) so the title bar
       doesn't grow taller than its natural height — keeps the
       Bill of Lading on a single Letter page. 2px white quiet zone
       inside the rounded background is enough at this size. */
    h += '<img src="' + qrDataUrl + '" alt="QR" width="60" height="60" style="display:block;background:#fff;padding:2px;border-radius:3px" />';
    h += '<div style="font-size:6px;color:rgba(255,255,255,0.75);letter-spacing:0.3px;text-align:center;line-height:1.15;max-width:70px;margin-top:2px">Escanear · Scan</div>';
    h += '</div>';
  }
  h += '</div></div>';

  /* SECTION 1 — Shipment Header. Compact: dates on row 1, shipper
     identity on row 2, RFC + FDA on row 3, Invoice + Export Cert on
     row 4. Shipment # intentionally not repeated here. Key facts
     (dates, shipper name, invoice, export cert) use the .value-strong
     emphasis class so the eye lands on them first. */
  h += '<div class="section"><h2>Datos del Embarque / Shipment Header</h2>';
  h += '<div class="kv">';
  /* Row 1 — dates side by side (emphasized) */
  h += '<div><div class="label">Fecha de Salida / Departure Date</div><div class="value-strong">' + escHtml(fmtDateLong(shipment.dateDeparture) || "—") + '</div></div>';
  h += '<div><div class="label">Fecha Estimada de Llegada / Estimated Arrival Date (ETA)</div><div class="value-strong">' + escHtml(fmtDateLong(shipment.dateArrival) || "—") + '</div></div>';
  /* Row 2 — shipper identity (name emphasized) */
  h += '<div style="grid-column:span 2"><div class="label">Exportador · Shipper / Razón Social · Legal Name</div><div class="value-strong">' + escHtml(shipperName || "—") + '</div></div>';
  h += '<div style="grid-column:span 2"><div class="label">Domicilio / Address</div><div class="value">' + escHtml(shipperAddr || "—") + '</div></div>';
  /* Row 3 — Mexican RFC + FDA */
  h += '<div><div class="label">RFC · Tax ID (MX)</div><div class="value">' + escHtml(shipperRfc || "—") + '</div></div>';
  h += '<div><div class="label">FDA Number</div><div class="value">' + escHtml(shipperFda || "—") + '</div></div>';
  /* Row 4 — Shipper Invoice + Export Cert (both emphasized) */
  h += '<div><div class="label">Factura del Exportador / Shipper Invoice</div><div class="value-strong">' + escHtml(shipment.shipperInvoice || "—") + '</div></div>';
  h += '<div><div class="label">Certificado de Exportación / Export Certificate</div><div class="value-strong" style="font-family:\'Courier New\',monospace">' + escHtml(exportCerts.length > 0 ? exportCerts.join(" · ") : "—") + '</div></div>';
  h += '</div></div>';

  /* Transport Data — driver, tractor/trailer, plates, ALPHA code.
     Same data the Shipping Instructions doc renders; mirrored here
     so the Bill of Lading carries the carrier identification needed
     at the border. Pulled from shipment.transport (same shape used
     across modules). Only the populated fields render — empty rows
     don't take up space. */
  var trBL = shipment.transport || {};
  var trFieldsBL = [
    ["driver", "Nombre del Chofer / Driver Name"],
    ["carrier", "Línea Transportista / Carrier"],
    ["platesTruck", "Placas Tractor / Tractor Plates"],
    ["econTruck", "No. Económico Tractor / Tractor Unit #"],
    ["platesTrailer", "Placas Caja / Trailer Plates"],
    ["econTrailer", "No. Económico Caja / Trailer Unit #"],
    ["codigoAlpha", "Código ALPHA / ALPHA Code"],
    ["caat", "CAAT"],
    ["sealNumber", "No. de Candado / Seal #"],
    ["thermograph", "No. Termógrafo / Thermograph #"],
    ["phone", "Celular / Phone"],
  ];
  h += '<div class="section"><h2>Datos del Transportista / Carrier Info</h2>';
  h += '<div class="kv" style="grid-template-columns:repeat(2,1fr)">';
  trFieldsBL.forEach(function (pair) {
    var key = pair[0], label = pair[1];
    var val = String(trBL[key] || "").trim();
    var isMono = key !== "carrier" && key !== "driver";
    h += '<div><div class="label">' + label + '</div><div class="value"' + (isMono ? ' style="font-family:\'Courier New\',monospace"' : "") + '>' + escHtml(val || "—") + '</div></div>';
  });
  h += '</div></div>';

  /* Items table — bilingual headers. */
  h += '<div class="section alt"><h2>Mercancía / Merchandise</h2>';
  h += '<table class="items"><thead><tr>';
  h += '<th class="c" style="width:14%">Fracción Arancelaria<br>HTS Code</th>';
  h += '<th class="c" style="width:14%">Clave SAT<br>SAT Code</th>';
  h += '<th style="text-align:left">Producto / Product</th>';
  h += '<th class="c" style="width:8%">Pallets</th>';
  h += '<th class="r" style="width:13%">Peso Bruto (kg)<br>Gross Weight</th>';
  h += '<th class="c" style="width:9%">Cajas<br>Cases</th>';
  h += '<th class="r" style="width:13%">Importe USD<br>Amount</th>';
  h += '</tr></thead><tbody>';
  var grandKg = 0, grandPallets = 0, grandCases = 0, grandValue = 0;
  rows.forEach(function (g) {
    var comm = findCommodity(g.commodity, commodities);
    var hts = comm ? (comm.htsCode || "") : "";
    var sat = comm ? (comm.taxCode || "") : "";
    var typeLabel = g.category === "OG" ? "OG" : g.category === "CV" ? "CV" : (g.category || "");
    /* Full presentation reading: commodity · units/size · cat · label.
       Matches the auto-generated product description format ("Blackberry
       12/6 oz OG GF Label"). Each segment only renders when populated
       so a half-configured catalog still produces a clean line. */
    var sizeBlock = "";
    if (g.unitsCase && g.size) sizeBlock = g.unitsCase + "/" + g.size;
    else if (g.size) sizeBlock = g.size;
    else if (g.unitsCase) sizeBlock = g.unitsCase + "u";
    var prodCell = escHtml(g.commodity)
      + (sizeBlock ? ' <span style="color:#7a8693">· ' + escHtml(sizeBlock) + '</span>' : "")
      + (typeLabel ? ' <span style="color:#7a8693">· ' + escHtml(typeLabel) + '</span>' : "")
      + (g.labelInitials ? ' <span style="color:#7a8693">· ' + escHtml(g.labelInitials) + ' Label</span>' : "");
    var kg = g.lb ? (g.lb * 0.453592) : 0;
    var val = g.value !== undefined ? g.value : (valueByCommodity[(g.commodity || "").toLowerCase()] || 0);
    /* When lines drive the value AND there are multiple groups for the
       same commodity, the value would be double-counted. Guard by
       splitting evenly across same-commodity groups. */
    h += '<tr>';
    h += '<td class="c mono">' + escHtml(hts || "—") + '</td>';
    h += '<td class="c mono">' + escHtml(sat || "—") + '</td>';
    h += '<td>' + prodCell + '</td>';
    h += '<td class="c mono">' + g.pallets + '</td>';
    h += '<td class="r mono">' + (kg ? kg.toFixed(1) : "—") + '</td>';
    h += '<td class="c mono">' + g.cases + '</td>';
    h += '<td class="r mono">' + (val ? "$" + val.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—") + '</td>';
    h += '</tr>';
    grandPallets += g.pallets;
    grandKg += kg;
    grandCases += g.cases;
    grandValue += val;
  });
  /* When using lines fallback, valueByCommodity already represents the
     total once — don't double-add per row. Instead recompute grandValue
     from valueByCommodity unique commodities. */
  if (rows.length > 0) {
    grandValue = Object.values(valueByCommodity).reduce(function (a, b) { return a + b; }, 0);
  }
  h += '<tr class="tot"><td colspan="3" style="text-align:right">TOTAL</td>';
  h += '<td class="c mono">' + grandPallets + '</td>';
  h += '<td class="r mono">' + grandKg.toFixed(1) + ' kg</td>';
  h += '<td class="c mono">' + grandCases + '</td>';
  h += '<td class="r mono">' + (grandValue ? "$" + grandValue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—") + '</td>';
  h += '</tr></tbody></table></div>';

  /* SECTION 4 — Customs Info. Both brokers side by side with the
     extra fields the registry stores (license, EIN/RFC, address)
     when they exist. Renders "—" gracefully when registry rows are
     bare. */
  h += '<div class="section"><h2>Datos de Aduanas / Customs Info</h2>';
  h += '<div class="kv" style="grid-template-columns:repeat(2,1fr)">';
  /* MX broker block */
  var brMxLic = brMx ? (brMx.licenseNumber || brMx.license || "") : "";
  var brMxRfc = brMx ? (brMx.rfc || brMx.taxId || "") : "";
  var brMxAddr = brMx ? [brMx.address, brMx.city + (brMx.state ? ", " + brMx.state : ""), brMx.zip].filter(Boolean).join(", ") : "";
  h += '<div><div class="label">Agente Aduanal MX / Mexican Customs Broker</div><div class="value">' + escHtml(brMxName || "—") + '</div>';
  if (brMxLic)  h += '<div class="label" style="margin-top:6px">Patente / License</div><div class="value" style="font-family:\'Courier New\',monospace">' + escHtml(brMxLic) + '</div>';
  if (brMxRfc)  h += '<div class="label" style="margin-top:6px">RFC</div><div class="value" style="font-family:\'Courier New\',monospace">' + escHtml(brMxRfc) + '</div>';
  if (brMxAddr) h += '<div class="label" style="margin-top:6px">Domicilio / Address</div><div class="value">' + escHtml(brMxAddr) + '</div>';
  h += '</div>';
  /* US broker block */
  var brUsLic = brUs ? (brUs.licenseNumber || brUs.license || "") : "";
  var brUsEin = brUs ? (brUs.ein || brUs.taxId || "") : "";
  var brUsAddr = brUs ? [brUs.address, brUs.city + (brUs.state ? ", " + brUs.state : ""), brUs.zip].filter(Boolean).join(", ") : "";
  h += '<div><div class="label">Agente Aduanal US / US Customs Broker</div><div class="value">' + escHtml(brUsName || "—") + '</div>';
  if (brUsLic)  h += '<div class="label" style="margin-top:6px">License #</div><div class="value" style="font-family:\'Courier New\',monospace">' + escHtml(brUsLic) + '</div>';
  if (brUsEin)  h += '<div class="label" style="margin-top:6px">EIN / Tax ID</div><div class="value" style="font-family:\'Courier New\',monospace">' + escHtml(brUsEin) + '</div>';
  if (brUsAddr) h += '<div class="label" style="margin-top:6px">Address</div><div class="value">' + escHtml(brUsAddr) + '</div>';
  h += '</div>';
  h += '</div></div>';

  /* SECTION 5 — Delivery Instructions. Compacted to 2 columns
     (warehouse on the left, consignee on the right) so the section
     occupies ~3 rows instead of 6 rows of full-width entries. All
     original fields kept; just denser. Tax ID + Destination Country
     share the bottom row. */
  var company = loadCompanyProfile();
  var companyAddr = [company.addressLine, company.city + (company.state ? ", " + company.state : ""), company.zip, company.country].filter(Boolean).join(", ");
  h += '<div class="section alt"><h2>Instrucciones de Entrega / Delivery Instructions</h2>';
  h += '<div class="kv">';
  /* Row 1: warehouse name | consignee name */
  h += '<div><div class="label">Descargar en / Deliver To</div><div class="value">' + escHtml(whName || "—") + '</div></div>';
  h += '<div><div class="label">Consignatario / Consignee</div><div class="value">' + escHtml(company.legalName || "—") + '</div></div>';
  /* Row 2: warehouse address | consignee address (warehouse address
     cell still renders even when whAddress is empty, so the 2-col grid
     stays aligned). */
  h += '<div><div class="label">Domicilio Bodega / Warehouse Address</div><div class="value">' + escHtml(whAddress || "—") + '</div></div>';
  h += '<div><div class="label">Domicilio / Address</div><div class="value">' + escHtml(companyAddr || "—") + '</div></div>';
  /* Row 3: Tax ID | Destination Country */
  h += '<div><div class="label">Tax ID (EIN)</div><div class="value">' + escHtml(company.taxId || "—") + '</div></div>';
  h += '<div><div class="label">País Destino / Destination Country</div><div class="value">' + escHtml(company.country || "USA") + '</div></div>';
  h += '</div></div>';

  h += '</div></body></html>';
  return h;
}

/* ──────── DOC 3 — SHIPPING INSTRUCTIONS (no GFI branding) ──────── */
export function buildShippingInstructionsHTML(shipment, suppliers, warehouses, brokers, seasons, lang) {
  var sup = (suppliers || []).find(function (s) { return String(s.id) === String(shipment.supplierId); });
  var shipperName = sup ? (sup.fullName || sup.shortName || sup.name || "") : (shipment.shipperName || "");
  var shipperFda = sup ? (sup.fdaNumber || "") : "";
  var shipperRfc = sup
    ? (sup.rfc || (sup.rfcLetters && sup.rfcDate ? [sup.rfcLetters, sup.rfcDate, sup.rfcHomoclave || ""].filter(Boolean).join("-") : (sup.taxId || "")))
    : "";
  var wh = (warehouses || []).find(function (w) { return String(w.id) === String(shipment.warehouseId); });
  var whName = wh ? (wh.fullName || wh.shortName || wh.name || "") : (shipment.warehouseName || "");
  /* Broker names: prefer the stored name when the shipment has one
     stamped (portal-accepted shipments stamp brokerUSName at submit
     time from gfi-shipper-settings), then fall back to a fresh
     registry lookup, then to a manual override. This order keeps
     the doc populated even when the registry snapshot disagrees
     with what the shipment originally carried. */
  var brMx = (brokers || []).find(function (b) { return String(b.id) === String(shipment.brokerMXId); });
  var brMxName = shipment.brokerMXName || (brMx ? (brMx.fullName || brMx.shortName || brMx.name || "") : "") || shipment.brokerMXManual || "";
  var brUs = (brokers || []).find(function (b) { return String(b.id) === String(shipment.brokerUSId); });
  var brUsName = shipment.brokerUSName || (brUs ? (brUs.fullName || brUs.shortName || brUs.name || "") : "") || shipment.brokerUSManual || "";
  var season = (seasons || []).find(function (s) { return String(s.id) === String(shipment.seasonId); });
  var seasonName = season ? (season.name || season.label || "") : "";
  /* "Real" pallet = has a product AND a positive case count. A row
     with only a palletNum (or any single field) is a phantom from
     the empty-row template the form keeps for ergonomics; it must
     not bleed into the printed totals. */
  var pallets = dedupePalletsByNumber((shipment.palletManifest || []).filter(function (p) { return p && p.productBase && (parseInt(p.casesPallet, 10) || 0) > 0; }));
  var totalPallets = pallets.length;
  var totalCases = 0; pallets.forEach(function (p) { totalCases += parseInt(p.casesPallet, 10) || 0; });
  var totalKg = pallets.reduce(function (s, p) { return s + calcPalletWeight(p).lb; }, 0) * 0.453592;
  /* Group for content breakdown — includes Variety so two pallets of
     the same commodity but different varieties get their own row. */
  var groups = {};
  pallets.forEach(function (p) {
    var key = (p.productBase || "?") + "|" + (p.size || "") + "|" + (p.category || "") + "|" + (p.labelInitials || "") + "|" + (p.variety || "");
    if (!groups[key]) groups[key] = { commodity: p.productBase || "", size: p.size || "", unitsCase: p.unitsCase || "", category: p.category || "", label: p.labelInitials || "", variety: p.variety || "", pallets: 0, cases: 0, lb: 0 };
    groups[key].pallets += 1;
    groups[key].cases += parseInt(p.casesPallet, 10) || 0;
    groups[key].lb += calcPalletWeight(p).lb;
  });
  var tr = shipment.transport || {};

  /* Filename localized via docFilename — English: "Shipping_Instructions_001",
     Spanish: "Instrucciones_de_Embarque_001". Same no-lot rule as Carta
     Porte — internal identifier, not the shipper's reference. */
  var shippingInstrFilename = docFilename("shipping-instr", shipment, lang);
  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + escHtml(shippingInstrFilename) + '</title><style>';
  h += '@page{size:letter;margin:0.5in 0.55in}';
  h += '*{margin:0;padding:0;box-sizing:border-box}';
  h += 'body{font-family:"Segoe UI",Arial,sans-serif;font-size:10px;color:#1a1a1a;line-height:1.45}';
  h += '.page{max-width:730px;margin:0 auto}';
  h += '.title-bar{padding:14px 18px;background:#1F4E79;color:#fff;border-radius:6px 6px 0 0}';
  h += '.title-bar h1{font-size:16px;font-weight:300;letter-spacing:2.5px;margin:0}';
  h += '.title-bar .sub{font-size:9px;letter-spacing:1px;text-transform:uppercase;opacity:0.75;margin-top:3px}';
  h += '.section{padding:12px 18px;border:1px solid #d6e0e8;border-top:none;background:#fff}';
  h += '.section.alt{background:#f8fafc}';
  h += '.section h2{font-size:9px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:#1F4E79;margin-bottom:8px;padding-bottom:4px;border-bottom:1px solid #d6e0e8}';
  h += '.kv{display:grid;grid-template-columns:repeat(2,1fr);gap:8px 18px}';
  h += '.kv-3{display:grid;grid-template-columns:repeat(3,1fr);gap:8px 18px}';
  h += '.label{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#7a8693;font-weight:700}';
  h += '.value{font-size:11px;font-weight:600;color:#1a1a1a;margin-top:1px;min-height:14px}';
  h += '.value.mono{font-family:"Courier New",monospace}';
  h += 'table{width:100%;border-collapse:collapse;margin-top:6px}';
  h += '.items th{padding:6px 6px;font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#fff;background:#1F4E79;font-weight:600;border-right:1px solid #2C5F8D}';
  h += '.items th:last-child{border-right:none}';
  h += '.items td{padding:6px 6px;border-bottom:1px solid #d6e0e8;font-size:10px;vertical-align:middle}';
  h += '.items tr:nth-child(even){background:#f8fafc}';
  h += '.r{text-align:right}.c{text-align:center}.mono{font-family:"Courier New",monospace}';
  h += '.tot td{font-size:11px;font-weight:800;color:#1F4E79;background:#eaf2f8 !important;border-top:2px solid #1F4E79;padding-top:7px;padding-bottom:7px}';
  h += '.notes{padding:12px 18px;background:#fffbe6;border:1px dashed #d4b842;border-radius:6px;margin-top:10px}';
  h += '.notes .lbl{font-size:8px;font-weight:700;color:#8b6914;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px}';
  h += '</style></head><body><div class="page">';

  /* Both docs are always printed bilingual (es / en) per spec —
     the sub-bar carries enough English context for US receivers. */
  var company = loadCompanyProfile();
  h += '<div class="title-bar"><h1>SHIPPING INSTRUCTIONS / INSTRUCCIONES DE EMBARQUE</h1><div class="sub">Shipper: ' + escHtml(shipperName) + (seasonName ? "  ·  " + escHtml(seasonName) : "") + '  ·  Destinatario / Consignee: ' + escHtml((company.legalName || "Ground-Fresh International Inc.").toUpperCase()) + '</div></div>';

  /* Section 1: Embarque data — bilingual labels; both dates per FIX 4. */
  h += '<div class="section"><h2>Datos del Embarque / Shipment Data</h2>';
  h += '<div class="kv-3">';
  h += '<div><div class="label">Fecha de Embarque / Shipment Date</div><div class="value">' + escHtml(fmtDateLong(shipment.dateDeparture) || "—") + '</div></div>';
  h += '<div><div class="label">Fecha Estimada de Llegada / Estimated Arrival Date (ETA)</div><div class="value">' + escHtml(fmtDateLong(shipment.dateArrival) || "—") + '</div></div>';
  h += '<div><div class="label">Embarque / Shipment #</div><div class="value">' + escHtml(shipment.proforma || "—") + '</div></div>';
  h += '<div><div class="label">Factura del Exportador / Shipper Invoice</div><div class="value mono">' + escHtml(shipment.shipperInvoice || "—") + '</div></div>';
  h += '<div style="grid-column:span 2"><div class="label">Exportador / Exporter</div><div class="value">' + escHtml(shipperName || "—") + '</div></div>';
  h += '<div><div class="label">RFC · Tax ID (MX)</div><div class="value mono">' + escHtml(shipperRfc || "—") + '</div></div>';
  h += '<div><div class="label">FDA Number</div><div class="value mono">' + escHtml(shipperFda || "—") + '</div></div>';
  h += '<div style="grid-column:span 2"><div class="label">Destinatario / Consignee</div><div class="value">' + escHtml(company.legalName || "Ground-Fresh International Inc.") + '</div></div>';
  h += '<div style="grid-column:span 3"><div class="label">Descargar en / Deliver To</div><div class="value">' + escHtml(whName || "—") + '</div></div>';
  /* Both brokers full-width so each row reads as a clear "Mexican
     broker / American broker" pairing — needed for the customs
     handoff at the border. Renders "—" only when truly empty. */
  h += '<div style="grid-column:span 3"><div class="label">Agente Aduanal MX / Mexican Customs Broker</div><div class="value">' + escHtml(brMxName || "—") + '</div></div>';
  h += '<div style="grid-column:span 3"><div class="label">Agente Aduanal US / US Customs Broker</div><div class="value">' + escHtml(brUsName || "—") + '</div></div>';
  h += '</div></div>';

  /* Section 2: Transport — bilingual labels per FIX 1. Tractor plates
     keyed as "PLACAS TRACTOR / TRACTOR PLATES" per FIX 2. */
  h += '<div class="section alt"><h2>Datos del Transportista / Carrier Info</h2>';
  h += '<div class="kv-3">';
  h += '<div><div class="label">Línea Transportista / Carrier</div><div class="value">' + escHtml(tr.carrier || "—") + '</div></div>';
  h += '<div><div class="label">Nombre del Chofer / Driver Name</div><div class="value">' + escHtml(tr.driver || "—") + '</div></div>';
  h += '<div><div class="label">Celular / Cell Phone</div><div class="value mono">' + escHtml(tr.phone || "—") + '</div></div>';
  h += '<div><div class="label">PLACAS TRACTOR / TRACTOR PLATES</div><div class="value mono">' + escHtml(tr.platesTruck || "—") + '</div></div>';
  h += '<div><div class="label">Núm. Económico Tractor / Tractor Unit #</div><div class="value mono">' + escHtml(tr.econTruck || "—") + '</div></div>';
  h += '<div><div class="label">CAAT</div><div class="value mono">' + escHtml(tr.caat || "—") + '</div></div>';
  h += '<div><div class="label">Placas Caja / Trailer Plates</div><div class="value mono">' + escHtml(tr.platesTrailer || "—") + '</div></div>';
  h += '<div><div class="label">Núm. Económico Caja / Trailer Unit #</div><div class="value mono">' + escHtml(tr.econTrailer || "—") + '</div></div>';
  h += '<div><div class="label">Código Alfa / Alpha Code</div><div class="value mono">' + escHtml(tr.codigoAlpha || "—") + '</div></div>';
  h += '<div><div class="label">No. de Candado / Seal Number</div><div class="value mono">' + escHtml(tr.sealNumber || "—") + '</div></div>';
  h += '<div><div class="label">No. de Termógrafo / Thermograph #</div><div class="value mono">' + escHtml(tr.thermograph || "—") + '</div></div>';
  h += '</div></div>';

  /* Section 3: Content breakdown — bilingual headers. */
  h += '<div class="section"><h2>Contenido del Embarque / Shipment Content</h2>';
  h += '<div class="kv-3" style="margin-bottom:10px">';
  h += '<div><div class="label">Total de Cajas / Total Cases</div><div class="value mono"><b>' + totalCases + '</b></div></div>';
  h += '<div><div class="label">Total de Pallets / Total Pallets</div><div class="value mono"><b>' + totalPallets + '</b></div></div>';
  h += '<div><div class="label">Peso Bruto (kg) / Gross Weight</div><div class="value mono"><b>' + totalKg.toFixed(1) + '</b></div></div>';
  h += '</div>';
  /* Content table — Pallets and Variety added; Consolidado removed
     (the field wasn't used and looked like a TODO row). One row per
     (commodity · size · type · label · variety) bucket. */
  h += '<table class="items"><thead><tr>';
  h += '<th style="text-align:left">Producto / Product</th>';
  h += '<th class="c" style="width:11%">Variedad / Variety</th>';
  h += '<th class="c" style="width:9%">Tamaño / Size</th>';
  h += '<th class="c" style="width:10%">Tipo / Type</th>';
  h += '<th class="c" style="width:10%">Etiqueta / Label</th>';
  h += '<th class="c" style="width:8%">Pallets</th>';
  h += '<th class="c" style="width:9%">Cajas / Cases</th>';
  h += '<th class="r" style="width:11%">Peso (kg) / Weight</th>';
  h += '</tr></thead><tbody>';
  Object.values(groups).forEach(function (g) {
    /* Bilingual type cell so both labels appear in the value too. */
    var typeLabel = g.category === "OG" ? "Orgánico / Organic" : g.category === "CV" ? "Convencional / Conventional" : g.category;
    /* Product cell mirrors Carta Porte: commodity · units/size · OG/CV ·
       LABEL Label. Composed inline so the row reads as a single
       descriptive string even when the breakdown columns happen to
       be empty for this pallet group. */
    var shortType = g.category === "OG" ? "OG" : g.category === "CV" ? "CV" : (g.category || "");
    var sizeBlock = "";
    if (g.unitsCase && g.size) sizeBlock = g.unitsCase + "/" + g.size;
    else if (g.size) sizeBlock = g.size;
    else if (g.unitsCase) sizeBlock = g.unitsCase + "u";
    var prodCell = escHtml(g.commodity)
      + (sizeBlock ? ' <span style="color:#7a8693">· ' + escHtml(sizeBlock) + '</span>' : "")
      + (shortType ? ' <span style="color:#7a8693">· ' + escHtml(shortType) + '</span>' : "")
      + (g.label ? ' <span style="color:#7a8693">· ' + escHtml(g.label) + ' Label</span>' : "");
    h += '<tr>';
    h += '<td>' + prodCell + '</td>';
    h += '<td class="c">' + escHtml(g.variety || "—") + '</td>';
    h += '<td class="c">' + escHtml(g.size || "—") + '</td>';
    h += '<td class="c">' + escHtml(typeLabel || "—") + '</td>';
    h += '<td class="c mono">' + escHtml(g.label || "—") + '</td>';
    h += '<td class="c mono"><b>' + g.pallets + '</b></td>';
    h += '<td class="c mono">' + g.cases + '</td>';
    h += '<td class="r mono">' + (g.lb * 0.453592).toFixed(1) + '</td>';
    h += '</tr>';
  });
  h += '<tr class="tot"><td colspan="5" style="text-align:right">TOTAL</td>';
  h += '<td class="c mono">' + totalPallets + '</td>';
  h += '<td class="c mono">' + totalCases + '</td>';
  h += '<td class="r mono">' + totalKg.toFixed(1) + ' kg</td>';
  h += '</tr></tbody></table></div>';

  /* Notes */
  if (shipment.notes) {
    h += '<div class="notes"><div class="lbl">Observaciones / Notes</div>' + escHtml(shipment.notes) + '</div>';
  }

  h += '</div></body></html>';
  return h;
}

/* Force a real download (fetch → blob → object URL) so files save to
   the browser's Downloads folder instead of opening a preview tab.
   Works for PDF, XML, Excel, etc. Falls back to the plain anchor click
   if the fetch fails (e.g. a CORS edge case). */
export async function forceDownload(src, filename) {
  if (!src) return;
  try {
    var resp = await fetch(src);
    if (!resp.ok) throw new Error("fetch failed");
    var blob = await resp.blob();
    var url = URL.createObjectURL(blob);
    var a = document.createElement("a");
    a.href = url;
    a.download = filename || "document";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(function () { try { URL.revokeObjectURL(url); } catch (e) {} }, 4000);
  } catch (e) {
    var a2 = document.createElement("a");
    a2.href = src;
    a2.download = filename || "document";
    a2.click();
  }
}

/* ──────── PRINT HELPER ──────── */
export function printShipmentDocs(shipments, kind, ctx, onError) {
  if (!Array.isArray(shipments) || shipments.length === 0) return;
  var suppliers = ctx && ctx.suppliers ? ctx.suppliers : [];
  var warehouses = ctx && ctx.warehouses ? ctx.warehouses : [];
  var brokers = ctx && ctx.brokers ? ctx.brokers : [];
  var commodities = ctx && ctx.commodities ? ctx.commodities : [];
  var seasons = ctx && ctx.seasons ? ctx.seasons : [];
  /* User language drives the print-window <title>, which becomes the
     browser tab label AND the default "Save as PDF" filename. */
  var lang = (ctx && ctx.lang) || "en";

  /* Open the popup SYNCHRONOUSLY inside the user-gesture chain so
     browser popup blockers don't fire. We can't wait for QR
     generation before window.open(); the QR is generated next and
     the document is written into the already-open window. A
     placeholder body is written immediately so the tab doesn't
     show "about:blank" while QR resolves. */
  var w = window.open("", "_blank");
  if (!w) { if (onError) onError("popup-blocked"); return; }
  w.document.open();
  w.document.write('<!DOCTYPE html><html><head><meta charset="utf-8"><title>...</title></head><body style="font-family:Segoe UI,Arial,sans-serif;padding:40px;text-align:center;color:#666;font-size:13px">' + (lang === "es" ? "Preparando documento..." : "Preparing document...") + '</body></html>');
  w.document.close();

  /* The Bill of Lading (carta-porte + its legacy shipping-instr
     alias) gets a per-shipment QR pre-generated. Other docs skip
     QR — receiving sheet doesn't need one per spec. */
  var needsQR = (kind === "carta-porte" || kind === "shipping-instr");
  var qrPromise = needsQR
    ? Promise.all(shipments.map(function (s) { return generateQRDataUrl(s); }))
    : Promise.resolve(shipments.map(function () { return ""; }));

  qrPromise
    .catch(function () { return shipments.map(function () { return ""; }); })
    .then(function (qrUrls) {
      var build;
      if (kind === "receiving") build = function (s) { return buildReceivingSheetHTML(s, suppliers, lang); };
      else if (kind === "carta-porte") build = function (s, i) { return buildCartaPorteHTML(s, suppliers, warehouses, commodities, brokers, seasons, lang, qrUrls[i]); };
      else if (kind === "shipping-instr") build = function (s, i) { return buildCartaPorteHTML(s, suppliers, warehouses, commodities, brokers, seasons, lang, qrUrls[i]); };
      else build = function (s) { return buildShippingInstructionsHTML(s, suppliers, warehouses, brokers, seasons, lang); };

      var htmls = shipments.map(function (s, i) { return build(s, i); });
      var styleMatch = htmls[0].match(/<style>([\s\S]*?)<\/style>/);
      var sharedCss = styleMatch ? styleMatch[1] : "";
      var bodies = htmls.map(function (html) { var m = html.match(/<body[^>]*>([\s\S]*?)<\/body>/); return m ? m[1] : ""; });

      /* Default PDF filename. Single shipment → per-doc localized name;
         bulk → localized pluralized name + count + date. */
      var titleBase = shipments.length === 1
        ? docFilename(kind, shipments[0], lang)
        : bulkDocFilename(kind, shipments.length, lang);
      var doc = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + titleBase + '</title>';
      doc += '<style>' + sharedCss + ' .doc-pagebreak{page-break-after:always;break-after:page;height:0}';
      /* Floating Print + Close bar — only path to dismiss the popup
         on mobile / PWA where the browser chrome is hidden. Stays
         pinned top-right while the operator reviews the doc and
         disappears from the actual print job via @media print. */
      doc += ' .dm-close-bar{position:fixed;top:12px;right:12px;display:flex;gap:8px;z-index:99999;font-family:Arial,sans-serif}';
      doc += ' .dm-close-bar button{padding:10px 16px;border:none;border-radius:8px;color:#fff;font-size:14px;font-weight:600;cursor:pointer;box-shadow:0 4px 12px rgba(0,0,0,0.25);font-family:inherit;letter-spacing:0.02em}';
      doc += ' .dm-close-bar button:hover{opacity:0.92}';
      doc += ' .dm-close-bar button:active{transform:translateY(1px)}';
      doc += ' .dm-print-btn{background:#2A7A6F}';
      doc += ' .dm-close-btn{background:#DC2626}';
      doc += ' @media print{.dm-close-bar{display:none !important}}';
      doc += '</style></head><body>';
      doc += '<div class="dm-close-bar">';
      doc += '<button class="dm-print-btn" onclick="window.print()">🖨 ' + (lang === 'es' ? 'Imprimir' : 'Print') + '</button>';
      doc += '<button class="dm-close-btn" onclick="window.close()">✕ ' + (lang === 'es' ? 'Cerrar' : 'Close') + '</button>';
      doc += '</div>';
      bodies.forEach(function (body, i) {
        doc += body;
        if (i < bodies.length - 1) doc += '<div class="doc-pagebreak"></div>';
      });
      doc += '</body></html>';

      try {
        w.document.open();
        w.document.write(doc);
        w.document.close();
        setTimeout(function () { try { w.focus(); w.print(); } catch (e) {} }, 600);
      } catch (e) { /* popup was closed before write finished */ }
    });
}

/* Render a single shipment doc to PDF and drop it into the shipment's
   Drive shipper subfolder. Used by the print buttons after they kick
   off the local print window — keeps Arturo's "print" gesture as the
   single action that produces both the printed copy AND the archived
   PDF on Drive. Throws on any failure so the caller can decide
   whether to toast or stay silent. */
/* Render a single shipment doc to a PDF Blob in memory. Shared by
   the Drive upload path and the Gmail attachment path so we never
   diverge between "what gets uploaded" and "what gets emailed".
   Returns { filename, blob, mimeType }. */
export async function buildShipmentDocPdf(shipment, kind, ctx) {
  if (!ctx || !ctx.suppliers) throw new Error("buildShipmentDocPdf: missing ctx");
  let html;
  if (kind === "carta-porte" || kind === "shipping-instr") {
    const qrUrl = await generateQRDataUrl(shipment).catch(function () { return ""; });
    html = buildCartaPorteHTML(
      shipment,
      ctx.suppliers,
      ctx.warehouses || [],
      ctx.commodities || [],
      ctx.brokers || [],
      ctx.seasons || [],
      ctx.lang || "en",
      qrUrl,
    );
  } else if (kind === "receiving") {
    html = buildReceivingSheetHTML(shipment, ctx.suppliers, ctx.lang || "en");
  } else if (kind === "dump-order") {
    html = buildDumpOrderHTML(shipment, ctx.suppliers, ctx.invoices || [], ctx.lang || "en");
  } else {
    throw new Error("buildShipmentDocPdf: unsupported kind " + kind);
  }
  const pdfBlob = await htmlToPdfBlob(html);
  /* docFilename() already knows the FOB-vs-import naming split (Vendor
     Invoice # instead of Proforma for FOB's Inbound Packing List) —
     reused here instead of re-deriving it so this attachment's
     filename never drifts from what the Print menu/Save-as dialog
     already show. */
  const filename = safeFilename(docFilename(kind === "shipping-instr" ? "carta-porte" : kind, shipment, ctx.lang || "en")) + ".pdf";
  return { filename: filename, blob: pdfBlob, mimeType: "application/pdf" };
}

export async function uploadShipmentDocToDrive(shipment, kind, ctx) {
  if (!ctx || !ctx.warehouses || !ctx.seasons) {
    throw new Error("uploadShipmentDocToDrive: missing ctx");
  }
  const { filename, blob } = await buildShipmentDocPdf(shipment, kind, ctx);
  /* Walk the same path Create Folder used. Drive folder caching in
     googleDrive.js makes the repeat trip cheap. */
  const company = loadCompanyProfile();
  const supplier = ctx.suppliers.find(function (s) { return String(s.id) === String(shipment.supplierId); });
  const shipperShort = (supplier && (supplier.shortName || supplier.fullName)) || shipment.shipperName || "Unknown";
  const season = ctx.seasons.find(function (s) { return String(s.id) === String(shipment.seasonId); });
  const segments = rootPathSegments(company.driveRootPath).concat([
    seasonFolderName(season),
    embarqueFolderName(shipment, shipperShort),
    shipperShort,
  ]);
  const folderId = await driveEnsurePath(segments);
  return driveUploadFile(folderId, filename, blob, { contentType: "application/pdf" });
}

/* ======================== SMALL COMPONENTS ======================== */
function Toast({ message, onClose }) {
  const [fading, setFading] = useState(false);
  useEffect(() => {
    const fadeT = setTimeout(() => setFading(true), 2700);
    const closeT = setTimeout(() => { onClose(); }, 3000);
    return () => { clearTimeout(fadeT); clearTimeout(closeT); };
  }, [message]);
  return createPortal(<div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#1a2636", color: "#fff", padding: "10px 24px", borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 100000, boxShadow: "0 8px 32px rgba(0,0,0,0.2)", animation: fading ? "fadeOut 0.3s ease forwards" : undefined }}>{message}</div>, document.body);
}

/* ======================== DETAIL PANEL ======================== */
/* Unified view+edit modal per CLAUDE.md "REGLA DE VENTANAS". View shows the
   read-only summary; Edit toggles to edit mode inside the SAME DraggableModal
   (renders ShipmentForm with embedded=true). */
function DetailPanel({ shipment, shipments, t, lang, onSave, onDelete, onClose, onCreateBill, onDuplicate, onPrintDoc, onReject, lockSupplierId, hideFinancials }) {
  const [mode, setMode] = useState("view");
  useDocumentTitle(shipment.lot ? ("Shipment LOT " + shipment.lot + (function () { var n = resolveShipperName(shipment.supplierId, shipment.shipperName); return n ? " | " + n : ""; })()) : null);
  /* "me voy a dormir" — Arturo's deferred request to make client/
     supplier names clickable app-wide. Mirrors Bill/Invoice's
     openLinkedSupplierFromBar, using the window.__gfiShipmentReopenId
     effect added to the parent ShipmentRegistry component so closing
     the Supplier record returns to this exact shipment (not just the
     Shipments list). */
  const openLinkedSupplierFromHeader = () => {
    if (!shipment.supplierId) return;
    const supplierId = shipment.supplierId;
    window.__gfiShipmentReopenId = shipment.id;
    if (window.__gfiNavigate) window.__gfiNavigate("suppliers");
    setTimeout(() => { window.__gfiReturnModule = "shipments"; if (window.__gfiSupplierOpen) window.__gfiSupplierOpen(supplierId); }, 250);
    setTimeout(() => { window.__gfiReturnModule = "shipments"; if (window.__gfiSupplierOpen) window.__gfiSupplierOpen(supplierId); }, 900);
  };
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  /* 2026-10-16, Arturo (urgent): "necesito urgente generar un
     documento... llamado Dump Order... esto aplica solo para las
     ventas a four seasons pero el email se envia solo desde
     ground-fresh a la bodega" — then, after it first shipped bundled
     into the arrival Email Warehouse dialog: "debe quedar separado de
     ese email porque ese es de cuando llega la fruta... debes asignar
     un email dump order exclusivo para eso". Own dialog, own state,
     entirely independent of emailDialogOpen above. */
  const [emailDumpOrderDialogOpen, setEmailDumpOrderDialogOpen] = useState(false);
  /* 2026-10-16, Arturo (same thread): "despues de que lo subas genera
     uno para informarle al shipper" — a SECOND, separate send of the
     same Dump Order document, this time to the SHIPPER (not the
     warehouse) so they know cases were authorized for disposal on
     their lot. Own dialog/state — never shares the warehouse one's
     recipients or subject/body. */
  const [emailDumpOrderShipperDialogOpen, setEmailDumpOrderShipperDialogOpen] = useState(false);
  /* 2026-10-16, Arturo: "para el shipper debo poder agregar fotos" —
     same multi-file + IndexedDB-persistence pattern as
     warehouseEmailFiles/qualityReportFiles above (photos of the
     dumped fruit, evidence the shipper typically asks for). Warehouse
     side's Dump Order doesn't get this — Arturo only asked for it on
     the shipper send. */
  const [dumpOrderShipperFiles, setDumpOrderShipperFiles] = useState([]);
  const handleAddDumpOrderShipperFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    setDumpOrderShipperFiles((prev) => {
      const next = [...prev, ...arr];
      saveDoc("dump-order-shipper-files-" + shipment.id, next).catch((e) => console.warn("[dump-order-shipper-files] save failed", e));
      return next;
    });
  };
  const removeDumpOrderShipperFile = (idx) => {
    setDumpOrderShipperFiles((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      saveDoc("dump-order-shipper-files-" + shipment.id, next).catch((e) => console.warn("[dump-order-shipper-files] save failed", e));
      return next;
    });
  };
  /* Manual attachments for the "Email Warehouse" send (2026-10-02,
     Arturo: "debo poder agregar un archivo al enviarlo a bodega") —
     same multi-file + IndexedDB-persistence pattern as InvoiceRegistry
     .js's Protection Notice attachments (protectionFiles), so reopening
     this dialog later doesn't lose whatever was already attached. */
  const [warehouseEmailFiles, setWarehouseEmailFiles] = useState([]);
  const handleAddWarehouseEmailFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    setWarehouseEmailFiles((prev) => {
      const next = [...prev, ...arr];
      saveDoc("warehouse-email-files-" + shipment.id, next).catch((e) => console.warn("[warehouse-email-files] save failed", e));
      return next;
    });
  };
  const removeWarehouseEmailFile = (idx) => {
    setWarehouseEmailFiles((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      saveDoc("warehouse-email-files-" + shipment.id, next).catch((e) => console.warn("[warehouse-email-files] save failed", e));
      return next;
    });
  };
  const [emailBrokerDialogOpen, setEmailBrokerDialogOpen] = useState(false);
  /* Send Quality Report (Texas QC) — 2026-09-14, Arturo. Mirrors the
     Protection Notice email pattern on the Invoice side: at least one
     mandatory attachment (the inspection team's original report, plus
     whatever photos Arturo wants to add — 2026-10-12, Arturo: "debo
     poder agregar fotos varias", changed from the original single-file
     design to the same multi-file array InvoiceRegistry.js's
     Protection Notice already uses), persisted to IndexedDB per
     shipment so it doesn't vanish on reopen. */
  const [emailQualityDialogOpen, setEmailQualityDialogOpen] = useState(false);
  const [qualityReportFiles, setQualityReportFiles] = useState([]);
  /* 2026-10-22, Arturo: "twin river me envia un reporte de calidad por
     producto... si se maneja de un mismo producto 2 CAT... se harian
     reportes por separado aunque pertenezcan al mismo lote... necesito
     que a la hora de enviar por email al shipper el reporte, me
     permita seleccionar los productos correspondientes al reporte que
     anexare, aunque tenga que enviar 2 o mas emails" — null = nothing
     narrowed yet (every product/category group selected, matching the
     old whole-shipment behavior); reset to null each time the dialog
     opens (see its trigger) so a prior send's narrowing never carries
     over to the next one. */
  const [qualitySelectedProducts, setQualitySelectedProducts] = useState(null);
  const [qualityReportPreviewOpen, setQualityReportPreviewOpen] = useState(false);
  /* Comentarios del equipo de calidad (2026-09-14, Arturo: "prefiero
     apliques el mismo criterio que aplicaste en el customer remarks
     del protection notice") — same contentEditable-with-rich-paste
     pattern as InvoiceRegistry.js's Customer Remarks, not a plain
     placeholder inside the Mensaje textarea. Still mandatory (unlike
     Customer Remarks, which is optional) — gated via sendDisabledReason
     alongside the attachment check, not EmailDialog's
     blockIfBodyContains (that only watches the Mensaje textarea,
     which this field no longer lives inside). Same split-state +
     one-time-seed-via-ref trick as Customer Remarks to avoid the
     contentEditable+dangerouslySetInnerHTML caret-reset bug. */
  const [qualityRemarksInitial, setQualityRemarksInitial] = useState("");
  const [qualityRemarks, setQualityRemarks] = useState("");
  const qualityRemarksRef = useRef(null);
  const [previewHtml, setPreviewHtml] = useState(null); /* { html, title } */
  const [driveBusy, setDriveBusy] = useState(false);
  const [driveResult, setDriveResult] = useState(null); /* { url } when success */
  const [trackingOpen, setTrackingOpen] = useState(false);

  /* For the Pending Pick Up / Pending Delivery pallet-status display
     default (see saleStatusCalc.js resolvePendingStage). */
  const [slPassingIndex, setSlPassingIndex] = useState({});
  useEffect(() => {
    let cancelled = false;
    loadFromSupabase("sweetleaf_invoices").then((sl) => {
      if (!cancelled) setSlPassingIndex(buildSlPassingIndex(sl));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  /* Build the embarque folder on Drive. Resolves season + supplier
     against localStorage caches (other modules dual-write here), then
     ensures the path /<root>/<season>/<embarque> and creates the
     subfolders FEES/{Customs,Inspection,Repack,Warehouse} + the
     shipper subfolder. Idempotent — re-running on the same shipment
     just returns the existing IDs. */
  const handleCreateDriveFolder = async () => {
    if (driveBusy) return;
    if (!driveConfigured()) { alert(t.driveNotConfigured); return; }
    setDriveBusy(true); setDriveResult(null);
    try {
      if (!driveSignedIn()) {
        try { await driveSignIn(); }
        catch (e) { alert(t.driveSignInNeeded + " (" + (e.message || String(e)) + ")"); setDriveBusy(false); return; }
      }
      const suppliers = getSuppliers();
      const seasons = getSeasons();
      const company = loadCompanyProfile();
      const sup = suppliers.find(s => String(s.id) === String(shipment.supplierId));
      const shipperShort = (sup && (sup.shortName || sup.fullName)) || shipment.shipperName || "Unknown";
      const season = seasons.find(s => String(s.id) === String(shipment.seasonId));
      /* Assemble the full path: <root>/<season folder>/<embarque folder>. */
      const segments = rootPathSegments(company.driveRootPath).concat([
        seasonFolderName(season),
        embarqueFolderName(shipment, shipperShort),
      ]);
      const embarqueId = await driveEnsurePath(segments);
      /* FEES + 4 subfolders. */
      const feesId = await driveEnsureFolder("FEES", embarqueId);
      await Promise.all([
        driveEnsureFolder("Customs", feesId),
        driveEnsureFolder("Inspection", feesId),
        driveEnsureFolder("Repack", feesId),
        driveEnsureFolder("Warehouse", feesId),
      ]);
      /* Shipper subfolder (sibling to FEES). */
      await driveEnsureFolder(shipperShort, embarqueId);
      const url = driveFolderViewUrl(embarqueId);
      setDriveResult({ url: url });
    } catch (e) {
      alert(t.driveError + ": " + (e.message || String(e)));
    } finally {
      setDriveBusy(false);
    }
  };
  /* Single-click upload of BOTH the Bill of Lading and the Receiving
     Manifest into the shipper subfolder. Different from the print
     buttons' auto-upload because it doesn't open a print window — the
     user just wants the archived PDFs on Drive without printing. */
  const [uploadBusy, setUploadBusy] = useState(false);
  const handleUploadToDrive = async () => {
    if (uploadBusy) return;
    if (!driveConfigured()) { alert(t.driveNotConfigured); return; }
    /* Same gate as the print buttons — uploading an incomplete
       shipment would produce a malformed PDF on Drive. */
    var printErrs = validateShipmentForPrint(shipment, shipments, lang);
    if (printErrs.length > 0) { alert(buildPrintBlockMsg(printErrs, lang)); return; }
    setUploadBusy(true);
    try {
      if (!driveSignedIn()) {
        try { await driveSignIn(); }
        catch (e) { alert(t.driveSignInNeeded + " (" + (e.message || String(e)) + ")"); setUploadBusy(false); return; }
      }
      const ctx = {
        suppliers: getSuppliers(),
        warehouses: getWarehouses(),
        brokers: getBrokers(),
        commodities: loadLS("gfi-commodities", []),
        seasons: getSeasons(),
        lang: lang,
      };
      /* Sequential so html2canvas only holds one bitmap at a time and
         Drive's per-file upload limit doesn't trip. Carta Porte skipped
         for FOB (2026-10-02, same reasoning as the Print menu and the
         Email Warehouse dialog above — it's a Mexican customs/
         transport document that doesn't exist for a domestic FOB
         purchase; uploading it anyway would archive a blank PDF). */
      const isFobForDrive = shipment.shipmentType === "fob";
      const uploaded = [];
      if (!isFobForDrive) uploaded.push(await uploadShipmentDocToDrive(shipment, "carta-porte", ctx));
      uploaded.push(await uploadShipmentDocToDrive(shipment, "receiving", ctx));
      alert((lang === "es" ? "Subido a Drive:\n" : "Uploaded to Drive:\n") + uploaded.map(function (u) { return "• " + u.name; }).join("\n"));
    } catch (e) {
      alert(t.driveError + ": " + (e.message || String(e)));
    } finally {
      setUploadBusy(false);
    }
  };
  /* Status-change menu — opens when the user clicks the status
     badge. Allows reverting Received → Draft (with confirm) for
     when errors are found after acceptance. */
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  useEffect(() => {
    if (!statusMenuOpen) return;
    const close = () => setStatusMenuOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [statusMenuOpen]);
  /* 2026-10-17, Arturo: "convine donde convine en el modo no
     editable, replicalo solo agregale dropdown para que se abra y se
     capture" — same inline-editable-without-entering-Edit pattern as
     the Status badge right above: view mode stays read-only for
     everything else, but this one field (who received it, GFI vs
     Twin River — f.texasQcMode) gets its own click-to-open dropdown
     here too, saving directly via onSave. */
  const [receivedByMenuOpen, setReceivedByMenuOpen] = useState(false);
  useEffect(() => {
    if (!receivedByMenuOpen) return;
    const close = () => setReceivedByMenuOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [receivedByMenuOpen]);
  /* 2026-10-17, Arturo: "shipment type a la barra de titulo despues de
     edit shipment" — the embedded ShipmentForm (rendered below when
     mode==="edit") reports its live isFob/setShipmentType here via
     onShipmentTypeApi, so THIS component's own titleLeftActions can
     render the toggle and have clicks mutate the real form state one
     level down, even though DetailPanel never touches ShipmentForm's
     internals directly. */
  const [shipmentTypeApi, setShipmentTypeApi] = useState(null);
  /* All 6 sections start expanded per spec. Track per-section open
     state so the user can collapse what they don't need without
     losing position in the modal. */
  const [open, setOpen] = useState({ header: true, transport: true, lines: true, pallets: true, documents: true, notes: true });
  const toggle = (k) => setOpen(p => ({ ...p, [k]: !p[k] }));

  /* Pull the "sent to customs" timestamp from the shipper's proforma.
     Admin doesn't set this — the shipper does when they submit. We
     look up shipper_shipments and find the proforma whose
     gfiShipmentId matches this admin shipment.id. Best-effort: if
     the fetch fails, the stamp simply doesn't render. */
  const [portalSubmittedAt, setPortalSubmittedAt] = useState("");
  useEffect(function () {
    if (!shipment || !shipment.id) return;
    var alive = true;
    loadFromSupabase("shipper_shipments", { force: true })
      .then(function (rows) {
        if (!alive) return;
        var found = "";
        (rows || []).some(function (p) {
          var pros = Array.isArray(p.proformas) ? p.proformas : [];
          var m = pros.find(function (pr) { return pr && String(pr.gfiShipmentId || "") === String(shipment.id); });
          if (m && m.submittedAt) { found = m.submittedAt; return true; }
          return false;
        });
        setPortalSubmittedAt(found);
      })
      .catch(function () {});
    return function () { alive = false; };
  }, [shipment && shipment.id]);

  const totals = calcTotals(shipment.lines);
  const st = shipment.status || "draft";
  const ss = STATUS_STYLES[st] || STATUS_STYLES.draft;
  const tr = shipment.transport || {};
  const canCreateBill = st === "received";
  const seasonName = getSeasonLabel(shipment.dateArrival || shipment.dateDeparture);
  /* All export cert numbers across product lines, deduped and joined. */
  const allCerts = [...new Set((shipment.lines || []).map(l => l.exportCertNumber || l.exportCertManual || "").filter(Boolean))].join(", ");
  /* Filter empty pallet rows just like the printed docs do. */
  const visiblePallets = (shipment.palletManifest || []).filter(p => p && (p.palletNum || p.productBase || p.casesPallet));
  /* Totals reduce over the DEDUPED list (see dedupePalletsByNumber) \u2014
     visiblePallets itself stays untouched so the row list below still
     shows every stored row, duplicates included, matching the record
     exactly. Only the aggregate cases/pallets/weight are protected
     from double-counting a repeated palletNum. */
  const palletsForTotals = dedupePalletsByNumber(visiblePallets);
  const palletGrand = palletsForTotals.reduce((acc, p) => {
    const w = calcPalletWeight(p);
    return { pallets: acc.pallets + 1, cases: acc.cases + (parseInt(p.casesPallet, 10) || 0), lb: acc.lb + w.lb };
  }, { pallets: 0, cases: 0, lb: 0 });
  /* Per (commodity \u00b7 size \u00b7 label \u00b7 variety) subtotals. Same grain as
     the Shipping Instructions content table. */
  const palletSubtotals = (() => {
    const groups = {};
    palletsForTotals.forEach(p => {
      if (!p.productBase) return;
      const key = (p.productBase || "\u2014") + " \u00b7 " + (p.size || "\u2014") + " \u00b7 " + (p.category || "\u2014") + " \u00b7 " + (p.labelInitials || "\u2014") + (p.variety ? " \u00b7 " + p.variety : "");
      if (!groups[key]) groups[key] = { key, pallets: 0, cases: 0, lb: 0 };
      const w = calcPalletWeight(p);
      groups[key].pallets += 1;
      groups[key].cases += parseInt(p.casesPallet, 10) || 0;
      groups[key].lb += w.lb;
    });
    return Object.values(groups);
  })();

  const handleSaveInline = (updated) => { onSave(updated); setMode("view"); };
  const handleDeleteInline = (id) => { onDelete(id); onClose(); };

  /* Section header \u2014 chevron + title. Default expanded. */
  /* 2026-10-17, Arturo: "todos los titulos que esten dentro de un
     rectangulo navy" \u2014 this panel's own title bar is already navy+gold
     (titleBarVariant="groundfresh" on its DraggableModal, see below);
     every section title box WITHIN it now matches the same navy
     rectangle + gold text instead of the plain gray box it used to be.
     Scoped here only \u2014 SectionHeader is a function local to this
     component, not shared with any other module's DetailPanel. */
  function SectionHeader({ id, title, count }) {
    const isOpen = open[id];
    /* 2026-10-17, Arturo: the blue-metallic rounded pill
       ("los headers... brillo metallico") got walked back one message
       later: "no me gustaron los titulos redondeados, no se ve
       minimalista, mejor un fondo tipo mac plateado con apariencia de
       metal y no redondeado" — then a screenshot of classic macOS
       Finder's brushed-metal toolbar ("me refiero a este"): added a
       faint repeating-linear-gradient hairline texture under the
       vertical gradient (same trick used for the top action strip,
       see below) to approximate that fine brushed striping, plus a
       slightly darker border/bottom inset shadow for more bevel.
       Square corners stay — Arturo was explicit about that. */
    return (
      <button onClick={() => toggle(id)} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 12px", marginTop: 14, background: "repeating-linear-gradient(180deg, rgba(0,0,0,0.025) 0px, rgba(0,0,0,0.025) 1px, transparent 1px, transparent 3px), linear-gradient(180deg, #eef0f2 0%, #d2d5d9 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08)", border: "1px solid #b9bcc0", borderRadius: 0, cursor: "pointer", textAlign: "left" }}>
        {/* 2026-10-17, Arturo: "ahora los headers de las tarjetas,
            todos" \u2014 same embossed-metal text-shadow as the window's
            title bar (LOT/Shipper/pills), applied here too. */}
        <span style={{ fontSize: 11, color: "#5a6775", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.15)", transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s", display: "inline-block", width: 10 }}>{"\u25b6"}</span>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", flex: 1 }}>{title}</span>
        {count !== undefined && <span style={{ fontSize: 10, color: "#5a6775", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.15)", fontFamily: "'JetBrains Mono', monospace" }}>{count}</span>}
      </button>
    );
  }
  /* Helper for two-column key-value field. */
  /* 2026-10-17, Arturo: "los campos de captura por ejemplo en carrier
     info... todo en una mezcla de fonts, no uses negrita en ninguna,
     todos los colores deben ser iguales... toma como referencia de
     products, blackberry description" \u2014 the value line now matches
     the Products table's Description cell exactly (fontSize 11 /
     color text-secondary / no bold / no monospace). `mono` used to
     switch the value to JetBrains Mono for ID-like fields (plates,
     econ #, etc.) \u2014 dropped per Arturo's explicit "tipo de font" ask;
     the prop stays accepted (harmless) so no call site needs touching. */
  function KV({ label, value }) {
    return (
      <div>
        <div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2, minHeight: 25 }}>{value || "\u2014"}</div>
      </div>
    );
  }

  /* 2026-10-17, Arturo: "lot se repite, ya esta en la barra de titulo,
     shipper subelo a la barra de titulo... sube los globos que estan
     debajo de shipper" \u2014 Shipper/Proforma + the 4 pills that used to
     sit below it (Sent to Customs, Status, Week, Season) move up into
     the title bar's left side via DraggableModal's titleLeftActions
     (left of the Preview/Print/\u22ee/Edit buttons, which Arturo explicitly
     said to leave where they are: "los de a lado dejalos donde
     estan"). Same logic as before, just relocated \u2014 only shown in view
     mode (edit mode's own ShipmentForm has its own fixed strip, see
     the "TOP STRIP" comment inside that function). */
  const titleLeftActions = mode === "edit" ? (
    shipmentTypeApi && (
      <div style={{ display: "inline-flex", border: "1px solid #b9bcc0" }}>
        <button type="button" onClick={() => shipmentTypeApi.setShipmentType("import")} style={{ padding: "4px 12px", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", background: !shipmentTypeApi.isFob ? "var(--color-accent)" : "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", color: !shipmentTypeApi.isFob ? "#ffffff" : "#1a2636", boxShadow: !shipmentTypeApi.isFob ? "inset 0 1px 1px rgba(255,255,255,0.3), inset 0 -1px 2px rgba(0,0,0,0.2)" : "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08)", textShadow: !shipmentTypeApi.isFob ? "0 1px 1px rgba(0,0,0,0.25)" : "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)" }}>{lang === "es" ? "Importación" : "Import"}</button>
        <button type="button" onClick={() => shipmentTypeApi.setShipmentType("fob")} style={{ padding: "4px 12px", border: "none", borderLeft: "1px solid #b9bcc0", fontSize: 11, fontWeight: 700, cursor: "pointer", background: shipmentTypeApi.isFob ? "var(--color-accent)" : "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", color: shipmentTypeApi.isFob ? "#ffffff" : "#1a2636", boxShadow: shipmentTypeApi.isFob ? "inset 0 1px 1px rgba(255,255,255,0.3), inset 0 -1px 2px rgba(0,0,0,0.2)" : "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08)", textShadow: shipmentTypeApi.isFob ? "0 1px 1px rgba(0,0,0,0.25)" : "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)" }}>{"FOB (Texas)"}</button>
      </div>
    )
  ) : (
    <>
      {/* 2026-10-17, Arturo: gold ("usa un color que combine con el
          dorado") made sense on the old navy bar; now that the bar is
          silver, he asked directly for this: "shipper + proforma negro
          no en negritas, tirandole a gris" — dark gray, not bold —
          then "agrega el efecto... grabados sobre el metal... tipo 3d",
          same embossed text-shadow as the LOT title. */}
      {/* 2026-10-17, Arturo: "el nombre del shipper dale un poco mas
          color porque con ese diseno no se distingue bien" — gray
          wasn't standing out; switched to the muted blue accent
          already established as this redesign's signature color
          (#3E6E96 — var(--color-accent), scoped to this window). */}
      {/* 2026-10-17, Arturo: "me habias mostrado otro azul un poco mas
          subido de tono" — switched from the muted #3E6E96 accent to
          the brighter blue already used for the Sent-to-Customs pill
          right next to it (#1d4ed8), more saturated as asked. */}
      <span style={{ fontSize: 12, fontWeight: 400, color: "#1d4ed8", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", whiteSpace: "nowrap" }}>
        {(resolveShipperName(shipment.supplierId, shipment.shipperName) || "\u2014") + " \u00b7 " + (shipment.proforma || "\u2014")}
      </span>
      {/* 2026-10-17, Arturo: "las burbujas despues de la proforma debes
          alinearlas al extremo derecho" \u2014 this wrapper gets
          marginLeft:"auto" so it's pushed to the far right of the
          title bar's available space (DraggableModal.js's
          .dm-title-actions now has flex:1, so "auto" here actually
          reaches the right edge, right before the maximize/close
          controls \u2014 not just floating right after Shipper). */}
      {/* 2026-10-17, Arturo: "has lo mismo pero con negritas y quita
          las burbujas en send to customs, wxx, season" \u2014 dropped the
          pill chrome (background/border/padding/radius) on these 3,
          same embossed text-shadow as LOT/Shipper, bold. Sent-to-
          Customs keeps its own blue (a real status signal); Week/
          Season go black like the title since they're now plain
          engraved text, not a colored badge. */}
      <span style={{ display: "flex", alignItems: "center", gap: 14, marginLeft: "auto" }}>
        {portalSubmittedAt && (function () {
          var d = new Date(portalSubmittedAt);
          var stamp = isNaN(d.getTime()) ? portalSubmittedAt : d.toLocaleString(lang === "es" ? "es-MX" : "en-US", { month: "2-digit", day: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
          return <span style={{ fontSize: 11, fontWeight: 700, color: "#1d4ed8", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", whiteSpace: "nowrap" }} title={lang === "es" ? "El shipper marc\u00f3 el embarque como enviado a la aduana" : "Shipper marked shipment as sent to customs"}>{"\u2708 "}{lang === "es" ? "Enviado a Aduana: " : "Sent to Customs: "}{stamp}</span>;
        })()}
        <span style={{ fontSize: 11, fontWeight: 700, color: "#1a1a1a", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", whiteSpace: "nowrap" }}>W{shipment.week || getISOWeek(shipment.dateArrival || shipment.dateDeparture)}</span>
        {seasonName && <span style={{ fontSize: 11, fontWeight: 700, color: "#1a1a1a", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", whiteSpace: "nowrap" }}>{seasonName}</span>}
      </span>
    </>
  );

  return (
    <DraggableModal title={"LOT " + (shipment.lot || "\u2014") + (mode === "edit" ? " \u00b7 " + t.edit : "")} onClose={onClose} initialWidth={Math.min(1200, Math.floor(window.innerWidth * 0.85))} initialHeight={Math.min(900, window.innerHeight - 60)} titleBarVariant="groundfresh" titleLeftActions={titleLeftActions}>
      {mode === "edit" ? (
        <ShipmentForm shipment={shipment} shipments={shipments} t={t} lang={lang} lockSupplierId={lockSupplierId} hideFinancials={hideFinancials} onSave={handleSaveInline} onDelete={handleDeleteInline} onCancel={() => setMode("view")} embedded={true} onShipmentTypeApi={setShipmentTypeApi} />
      ) : (
        <>
        {/* Top strip: action buttons only now \u2014 identity (Shipper/
            Proforma) and the status/week/season/sent-to-customs pills
            moved up to the title bar (titleLeftActions, see above);
            Preview/Print/\u22ee/Edit stay here, per Arturo: "los de a lado
            dejalos donde estan". */}
        {/* 2026-10-17, Arturo: "la seccion independiente donde se ve
            preview, received by atc dale un fondo ligeramente gris" —
            then "gris tipo metalico de apple de su sistema os" — swapped
            the flat #f7f8fa for a subtle macOS-toolbar-style brushed-
            metal gradient (light gray top-to-bottom with a thin inset
            highlight along the top edge). */}
        <div style={{ padding: "16px 22px 12px", borderBottom: "1px solid #b9bcc0", background: "repeating-linear-gradient(180deg, rgba(0,0,0,0.02) 0px, rgba(0,0,0,0.02) 1px, transparent 1px, transparent 3px), linear-gradient(180deg, #f2f3f4 0%, #dcdfe2 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
          {/* 2026-10-17, Arturo: "la seccion received by GFI esta
              gigante, necesita convivir en el renglon de preview pero
              alineado al extremo izquierdo" — was its own full-width
              card (title line + 2-column grid) below this row; now a
              single compact chip, left-aligned in the same row as
              Preview/Print/⋮/Status/Edit (which stay right-aligned,
              unchanged). Was accidentally made conditional on
              receivedDate at first ("donde quedo received by GFI?" —
              Arturo noticed it vanish on a shipment that hasn't been
              received yet); restored to always render, with "—"
              placeholders, same as the original full-size card. */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, minWidth: 0 }}>
            {/* 2026-10-17, Arturo: "te falto received y edit" — same
                embossed text-shadow as the rest of this metallic
                redesign. */}
            <span style={{ position: "relative", display: "inline-block" }} onClick={function (e) { e.stopPropagation(); }}>
              <button onClick={function () { setReceivedByMenuOpen(!receivedByMenuOpen); }} title={lang === "es" ? "Cambiar quién recibió" : "Change who received it"} style={{ padding: 0, border: "none", background: "transparent", cursor: "pointer", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-accent)", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.15)", whiteSpace: "nowrap", fontFamily: "inherit" }}>
                {shipment.texasQcMode === "clarifresh" ? (lang === "es" ? "Recepción en Twin River" : "Received by Twin River") : (lang === "es" ? "Recepción en GFI" : "Received by GFI")}
                <span style={{ marginLeft: 4, opacity: 0.6 }}>▾</span>
              </button>
              {receivedByMenuOpen && (
                /* 2026-10-20, Arturo: "dentro de un embarque en modo no
                   editable, aplica lo mismo de el diseño tipo placa a
                   todas las ventanas desplegables" — same dark-silver
                   plate as Shipment Registry/Filters, black embossed
                   text, hover lightens the plate instead of swapping
                   to a flat card color. */
                <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderRadius: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.18)", zIndex: 50, minWidth: 140, overflow: "hidden" }}>
                  {[{ v: "internal", l: lang === "es" ? "GFI" : "GFI" }, { v: "clarifresh", l: "Twin River" }].map(function (opt) {
                    return (
                      <div key={opt.v} onClick={function () { setReceivedByMenuOpen(false); if (opt.v !== (shipment.texasQcMode || "internal")) onSave({ ...shipment, texasQcMode: opt.v }); }} style={{ padding: "8px 12px", cursor: "pointer", fontSize: 11, fontWeight: 600, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", borderBottom: "1px solid #7d838c" }}
                           onMouseEnter={function (e) { e.currentTarget.style.background = "rgba(255,255,255,0.3)"; }}
                           onMouseLeave={function (e) { e.currentTarget.style.background = "transparent"; }}>
                        {opt.l}
                      </div>
                    );
                  })}
                </div>
              )}
            </span>
            <span style={{ fontWeight: 600, textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.15)", whiteSpace: "nowrap" }}>{shipment.receivedDate ? fmtDate(shipment.receivedDate) + (shipment.receivedTime ? " " + shipment.receivedTime : "") : "—"}</span>
            {shipment.receivedNotes && <span title={shipment.receivedNotes} style={{ color: "var(--color-text-muted)", cursor: "default", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 220, whiteSpace: "nowrap" }}>{"📝 " + shipment.receivedNotes}</span>}
            {/* 2026-10-20, Arturo: "en los embarques en la barra donde
                esta received by, quiero agregar LINKS: y despues
                escribir Open bill linked + numero de bill, Open
                invoices Linked + numero de factura o facturas
                ligadas al tocar el link se abre en modo vista con
                opcion a modo edicion" — one chip per linked record
                (not a single combined button), since each has to
                open its OWN record. Opens via the same navigate+open
                hook pattern Dashboard.js already uses for this exact
                purpose (__gfiBillOpen/__gfiInvoiceOpen open their
                target in view mode, with Edit still one click away
                inside that same window — REGLA DE VENTANAS). */}
            {(function () {
              var linkedBillsForLinks = loadLS("gfi-bills", []).filter(function (b) { return String(b.lot) === String(shipment.lot); });
              var linkedInvoicesForLinks = loadLS("gfi-invoices", []).filter(function (inv) {
                return String(inv.lot) === String(shipment.lot) || (inv.lines || []).some(function (l) { return String(l.lot) === String(shipment.lot); });
              });
              if (linkedBillsForLinks.length === 0 && linkedInvoicesForLinks.length === 0) return null;
              /* 2026-10-20, Arturo: "las ventanas de los links open bill
                 linked y open invoiced linked, cambiales el color como
                 shipment registry" — same dark-silver plate/black
                 embossed text as the rest of this pass. */
              /* 2026-10-20, Arturo: "las ventanas de los links
                 incrementa tamaño igual a received by" — same padding
                 as the Received-by plate button (3px 10px, not the
                 smaller 2px 8px these chips had before). */
              var linkBtnStyle = { padding: "3px 10px", borderRadius: 0, border: "1px solid #7d838c", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)", cursor: "pointer", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap", fontFamily: "inherit" };
              return (
                <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)" }}>{lang === "es" ? "Links:" : "Links:"}</span>
                  {linkedBillsForLinks.map(function (b) {
                    return (
                      <button key={"bill-" + b.id} type="button" style={linkBtnStyle} onClick={function () {
                        if (window.__gfiNavigate) window.__gfiNavigate("bills");
                        setTimeout(function () { if (window.__gfiBillOpen) window.__gfiBillOpen(b.id); }, 220);
                      }}>{/* 2026-10-20, Arturo: "si voy a bills no me
                             muestra ese numero interno... me muestra el
                             numero de lote de donde se creo, quiero que
                             muestre ese numero aunque le pongas bill
                             pero que muestre el lot" — the Bill's own
                             table identifies rows by Lot (b.lot), never
                             the internal BILL-#### consecutive id; this
                             button now matches that. */}
                      {(lang === "es" ? "Abrir Bill Vinculado · Lote " : "Open Bill Linked · Lot ") + (b.lot || b.id)}</button>
                    );
                  })}
                  {linkedInvoicesForLinks.map(function (inv) {
                    return (
                      <button key={"inv-" + inv.id} type="button" style={linkBtnStyle} onClick={function () {
                        if (window.__gfiNavigate) window.__gfiNavigate("invoices");
                        setTimeout(function () { if (window.__gfiInvoiceOpen) window.__gfiInvoiceOpen(inv.id); }, 220);
                      }}>{(lang === "es" ? "Abrir Invoice Vinculado PU " : "Open Invoice Linked PU ") + (inv.puNumber || inv.id)}</button>
                    );
                  })}
                </span>
              );
            })()}
          </div>
          {(function () {
            /* Print-time gate. Re-runs the L1+L2 checks against the
               saved shipment record. Disabled actions show the
               specific cause on hover AND on click via alert. */
            var printErrs = validateShipmentForPrint(shipment, shipments, lang);
            var canPrint = printErrs.length === 0;
            var blockMsg = buildPrintBlockMsg(printErrs, lang);
            var ctx = {
              suppliers: getSuppliers(),
              warehouses: getWarehouses(),
              brokers: getBrokers(),
              commodities: loadLS("gfi-commodities", []),
              seasons: getSeasons(),
              lang: lang,
            };
            var onPreview = function () {
              if (!canPrint) { alert(blockMsg); return; }
              /* Default Preview doc per spec: Receiving Sheet (Inbound
                 Packing List for FOB — same builder, retitled per
                 shipment.shipmentType, see buildReceivingSheetHTML). */
              var html = buildReceivingSheetHTML(shipment, ctx.suppliers, lang);
              setPreviewHtml({ html: html, title: shipment.shipmentType === "fob" ? t.printInboundPacking : t.printReceiving });
            };
            var onClickPrintKind = function (kind) {
              if (!canPrint) { alert(blockMsg); return; }
              if (onPrintDoc) onPrintDoc(shipment, kind);
            };
            var openLocal = async function () {
              var folder = buildShipmentLocalFolder(shipment);
              await openLocalFolder(folder);
              alert(t.openLocalFolderHint + "\n\n" + folder);
            };
            /* Carta Porte is a Mexican customs/transport document that
               reads broker + transport data \u2014 doesn't exist/apply for
               an FOB (domestic) shipment, so the option is hidden
               entirely rather than left to fail/print blank. */
            var isFobShip = shipment.shipmentType === "fob";
            var printItems = [
              { key: "receiving", label: isFobShip ? t.printInboundPacking : t.printReceiving, icon: "\ud83d\udda8", onClick: function () { onClickPrintKind("receiving"); } },
            ].concat(isFobShip ? [] : [
              { key: "carta-porte", label: t.printCartaPorte, icon: "\ud83d\udda8", onClick: function () { onClickPrintKind("carta-porte"); } },
            ]);
            /* Same fix as InvoiceRegistry.js's Protection Notice
               (2026-09-14) \u2014 loadCompanyProfile() only reads the
               synchronous in-memory cache, which stays empty
               (DEFAULT_COMPANY fallback: no accounts, no signatures)
               whenever primeCatalogs()'s boot-time fetch never ran or
               raced. Awaiting a fresh (TTL-cached, so normally
               instant) refetch before opening any of these email
               dialogs guarantees company.emailAccounts/emailSignatures
               are actually populated by render time. */
            var ensureCompanyProfileFresh = function () { return loadFromSupabase("company_profile").catch(function () {}); };
            /* 2026-10-16, Arturo: Dump Order \u2014 only offered when this
               lot sold to Four Seasons AND has at least one pallet
               with dumped cases captured (lostCases>0, see the
               existing Pallet Manifest notation). Own kebab item, own
               dialog \u2014 never bundled into the arrival "Email
               Warehouse" item above (that one fires when the truck
               lands; this one fires later, after repack). */
            var dumpOrderRows = buildDumpOrderRows(shipment, freshInvoicesShp());
            var isFourSeasonsLotForDump = freshInvoicesShp().some(function (inv) {
              return String(inv.lot) === String(shipment.lot) && String(inv.clientName || "").toLowerCase().indexOf("four seasons") >= 0;
            });
            var showDumpOrderItem = isFourSeasonsLotForDump && dumpOrderRows.length > 0;
            var kebabItems = [
              { key: "email", label: t.emailInboundACS, icon: "\u2709", disabled: !canPrint, onClick: function () {
                if (!canPrint) { alert(blockMsg); return; }
                if (!shipment.warehouseId) { alert(t.emailNoWarehouse); return; }
                loadDoc("warehouse-email-files-" + shipment.id).then(function (stored) { setWarehouseEmailFiles(stored || []); }).catch(function () { setWarehouseEmailFiles([]); });
                ensureCompanyProfileFresh().then(function () { setEmailDialogOpen(true); });
              }},
            ].concat(showDumpOrderItem ? [
              { key: "email-dump-order", label: t.sendDumpOrder, icon: "\ud83d\uddd1", disabled: !canPrint, onClick: function () {
                if (!canPrint) { alert(blockMsg); return; }
                if (!shipment.warehouseId) { alert(t.emailNoWarehouse); return; }
                ensureCompanyProfileFresh().then(function () { setEmailDumpOrderDialogOpen(true); });
              }},
              { key: "email-dump-order-shipper", label: t.sendDumpOrderShipper, icon: "\ud83d\uddd1", disabled: !canPrint, onClick: function () {
                if (!canPrint) { alert(blockMsg); return; }
                loadDoc("dump-order-shipper-files-" + shipment.id).then(function (stored) { setDumpOrderShipperFiles(stored || []); }).catch(function () { setDumpOrderShipperFiles([]); });
                ensureCompanyProfileFresh().then(function () { setEmailDumpOrderShipperDialogOpen(true); });
              }},
            ] : []).concat(isFobShip ? [] : [
              { key: "email-broker", label: t.emailCustomsBroker, icon: "\u2709", disabled: !canPrint, onClick: function () {
                if (!canPrint) { alert(blockMsg); return; }
                if (!shipment.brokerMXId) { alert(t.emailNoBrokerMX); return; }
                ensureCompanyProfileFresh().then(function () { setEmailBrokerDialogOpen(true); });
              }},
            ]).concat([
              { key: "email-quality", label: t.emailQualityReport, icon: "\ud83e\uddea", disabled: !canPrint, onClick: function () {
                if (!canPrint) { alert(blockMsg); return; }
                var hasTexasGrades = (shipment.palletManifest || []).some(function (p) { return p && (p.texasQuality || p.texasCondition || p.texasSevereCondition); });
                if (!hasTexasGrades) { alert(t.emailQualityNoGrades); return; }
                var sup5 = getSuppliers().find(function (s) { return String(s.id) === String(shipment.supplierId); });
                if (!sup5 || !Array.isArray(sup5.contacts) || sup5.contacts.filter(function (c) { return c && c.email; }).length === 0) {
                  alert(t.emailQualityNoShipperContacts); return;
                }
                /* `stored` may still be a single File object from before
                   the 2026-10-12 multi-file change — wrap it into an
                   array so an old in-progress draft doesn't vanish. */
                loadDoc("quality-report-file-" + shipment.id).then(function (stored) { setQualityReportFiles(Array.isArray(stored) ? stored : (stored ? [stored] : [])); }).catch(function () { setQualityReportFiles([]); });
                loadDoc("quality-remarks-" + shipment.id).then(function (stored) {
                  setQualityRemarksInitial(stored || "");
                  setQualityRemarks(stored || "");
                }).catch(function () { setQualityRemarksInitial(""); setQualityRemarks(""); });
                setQualitySelectedProducts(null);
                ensureCompanyProfileFresh().then(function () { setEmailQualityDialogOpen(true); });
              }},
              { key: "drive-folder", label: t.driveCreateFolder, icon: "\ud83d\udcc1", onClick: handleCreateDriveFolder },
              { key: "drive-upload", label: t.driveUploadDocs, icon: "\u2b06", disabled: !canPrint, onClick: function () { if (!canPrint) { alert(blockMsg); return; } handleUploadToDrive(); } },
              { key: "open-folder", label: t.openLocalFolder, icon: "\ud83d\udcc2", onClick: openLocal },
              { key: "tracking-link", label: t.sendTrackingLink, icon: "\ud83d\udce1", onClick: function () { setTrackingOpen(true); } },
              { key: "duplicate", label: t.duplicate, icon: "\u2398", onClick: function () { onDuplicate(shipment); } },
            ]);
            if (canCreateBill) {
              kebabItems.unshift({ key: "create-bill", label: t.createBill, icon: "\u2192", onClick: function () { onCreateBill(shipment); } });
            }
            return (
              <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                {/* 2026-10-17, Arturo: "preview, print, los 3 puntos y
                    edit, dales un relieve 3d tambien" — same
                    brushed-metal silver gradient + inset bevel already
                    used for the section headers and Download buttons.
                    ButtonMenu is a SHARED component (used by other
                    modules too) — its own default stays untouched;
                    these pass their own `style` override instead,
                    which ButtonMenu already merges in on top of its
                    default (see its `style` prop). */}
                <button
                  type="button"
                  onClick={onPreview}
                  disabled={!canPrint}
                  title={canPrint ? t.preview : blockMsg}
                  style={{ padding: "7px 12px", borderRadius: 0, border: "1px solid #7d838c", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)", color: canPrint ? "#000000" : "var(--color-text-muted)", textShadow: canPrint ? "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" : "none", cursor: canPrint ? "pointer" : "not-allowed", fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6, opacity: canPrint ? 1 : 0.5 }}
                >👁 {t.preview}</button>
                {/* 2026-10-17, Arturo: "las palabras en preview, print y
                    el icono, toda esa seccion, los 3 botones" — same
                    embossed text-shadow, inherited down from each
                    button's own style into its trigger text (Preview's
                    label is inline already; Print/⋮ pass `style` to
                    ButtonMenu, which text-shadow still inherits through
                    into the trigger content it renders). */}
                {/* 2026-10-20, Arturo: "dentro de un embarque en modo no
                    editable, aplica lo mismo de el diseño tipo placa a
                    todas las ventanas desplegables" — Print PDF/⋮ are
                    ButtonMenu (shared component, also used by Invoices/
                    Bills — its own defaults stay untouched); these pass
                    the same dark-silver plate via `style`/`menuStyle`/
                    `itemHoverBg` (new optional props, see ButtonMenu.js)
                    instead of the lighter trigger-only gradient they had
                    before, matching Filters/kebab in the Shipments list
                    and the Received-by/Status dropdowns above. */}
                <ButtonMenu
                  trigger={<><span>🖨</span><span>{t.printPdf}</span><span style={{ fontSize: 9, opacity: 0.7 }}>▾</span></>}
                  items={printItems}
                  disabled={!canPrint}
                  title={canPrint ? t.printPdf : blockMsg}
                  style={{ borderRadius: 0, border: "1px solid #7d838c", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" }}
                  menuStyle={{ background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderRadius: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.18)" }}
                  itemHoverBg="rgba(255,255,255,0.3)"
                  itemTextColor="#000000"
                  itemTextShadow="0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)"
                />
                <ButtonMenu
                  trigger={<span style={{ fontSize: 16, fontWeight: 700 }}>⋮</span>}
                  items={kebabItems}
                  title={t.more}
                  style={{ padding: "4px 10px", borderRadius: 0, border: "1px solid #7d838c", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" }}
                  menuStyle={{ background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderRadius: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.18)" }}
                  itemHoverBg="rgba(255,255,255,0.3)"
                  itemTextColor="#000000"
                  itemTextShadow="0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)"
                />
                {shipment.portalEmbarqueId && shipment.portalProformaId && onReject && (
                  <button onClick={() => onReject(shipment)} title={lang === "es" ? "Eliminar de GFI y devolver al exportador con motivo" : "Delete from GFI and return to the exporter with a reason"} style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid rgba(220,38,38,0.35)", background: "rgba(220,38,38,0.06)", color: "#DC2626", cursor: "pointer", fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>✕ {lang === "es" ? "Rechazar" : "Reject"}</button>
                )}
                {/* 2026-10-17, Arturo: "billed como es un campo
                    editable, cambialo de ahi antes de edit dentro de
                    un rectangulo, si es billed color azul claro...
                    si es draft color gris" — moved back out of the
                    title bar (it's a clickable control, not plain
                    identity like Shipper/Week/Season) into this
                    action row, right before Edit. Billed gets the same
                    light blue already used for the Sent-to-Customs
                    pill; Draft stays its existing gray; Received keeps
                    its existing green (not mentioned, left as-is). */}
                {(function () {
                  var options = [];
                  if (st === "draft") options = ["received"];
                  else if (st === "received") options = ["draft"];
                  else if (st === "billed") options = ["received", "draft"];
                  var changeStatus = function (next) {
                    setStatusMenuOpen(false);
                    if (next === st) return;
                    var needsConfirm = (st === "received" || st === "billed") && next === "draft";
                    if (needsConfirm) {
                      var msg = lang === "es" ? "¿Regresar embarque a borrador?" : "Return shipment to draft?";
                      if (!window.confirm(msg)) return;
                    }
                    onSave({ ...shipment, status: next });
                  };
                  var curSty = st === "billed"
                    ? { bg: "rgba(59,130,246,0.1)", text: "#1d4ed8", border: "rgba(59,130,246,0.3)" }
                    : st === "draft"
                      ? { bg: "rgba(107,114,128,0.1)", text: "#6B7280", border: "rgba(107,114,128,0.3)" }
                      : ss;
                  return (
                    <span style={{ position: "relative", display: "inline-block" }} onClick={function (e) { e.stopPropagation(); }}>
                      {/* 2026-10-17, Arturo: "billed tambien pero en su
                          color" — same 3D bevel (inset highlight/shadow)
                          as Preview/Print/⋮/Edit, but keeping this
                          button's own semantic color (blue for billed,
                          gray for draft, green for received) instead of
                          swapping to the gray-metal fill those use. */}
                      <button onClick={function () { setStatusMenuOpen(!statusMenuOpen); }} title={lang === "es" ? "Cambiar estado" : "Change status"} style={{ padding: "7px 14px", borderRadius: 0, fontSize: 12, fontWeight: 700, background: curSty.bg, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 2px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.12)", color: curSty.text, border: "1px solid " + curSty.border, cursor: options.length > 0 ? "pointer" : "default", fontFamily: "inherit" }}>
                        {t[st]}{options.length > 0 && <span style={{ marginLeft: 4, opacity: 0.6 }}>{"▾"}</span>}
                      </button>
                      {statusMenuOpen && options.length > 0 && (
                        /* 2026-10-20, Arturo: "dentro de un embarque en
                           modo no editable, aplica lo mismo de el diseño
                           tipo placa a todas las ventanas desplegables"
                           — plate background/border on the panel; each
                           option's own semantic status color (text +
                           hover tint) stays untouched, that's meaningful
                           data, not generic chrome. */
                        <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderRadius: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.18)", zIndex: 50, minWidth: 140, overflow: "hidden" }}>
                          {options.map(function (opt) {
                            var optStyle = opt === "billed"
                              ? { bg: "rgba(59,130,246,0.1)", text: "#1d4ed8" }
                              : opt === "draft"
                                ? { bg: "rgba(107,114,128,0.1)", text: "#6B7280" }
                                : (STATUS_STYLES[opt] || STATUS_STYLES.draft);
                            return (
                              <div key={opt} onClick={function () { changeStatus(opt); }} style={{ padding: "8px 12px", cursor: "pointer", fontSize: 11, fontWeight: 600, color: optStyle.text, textShadow: "0 1px 0 rgba(255,255,255,0.5)", display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid #7d838c" }}
                                   onMouseEnter={function (e) { e.currentTarget.style.background = optStyle.bg; }}
                                   onMouseLeave={function (e) { e.currentTarget.style.background = "transparent"; }}>
                                <span style={{ width: 8, height: 8, borderRadius: 4, background: optStyle.text, flexShrink: 0 }} />
                                <span>{"→ "}{t[opt]}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </span>
                  );
                })()}
                <button onClick={() => setMode("edit")} style={{ padding: "7px 14px", borderRadius: 0, border: "1px solid #7d838c", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.editBtn}</button>
              </div>
            );
          })()}
        </div>

        {/* Shipper-submission gate — when a shipment arrives from the
            Shipper Portal it carries shipperStatus='submitted'. Admin
            decides accept / reject here; once flipped, the shipment
            enters the regular admin lifecycle. Rejected shipments
            return to the shipper as editable with the rejection note
            attached. */}
        {(function () {
          var sStatus = shipment.shipperStatus;
          var shipperMode = !!lockSupplierId;
          /* No banner at all when the admin opens a draft (the historical
             behavior). In shipperMode we DO want to surface the submit
             control for draft / rejected records so the shipper can push
             it to GFI. */
          if (!shipperMode && (!sStatus || sStatus === "draft")) return null;

          var accept = function () {
            onSave(Object.assign({}, shipment, {
              shipperStatus: "accepted",
              acceptedAt: new Date().toISOString(),
              rejectionNote: "",
            }));
          };
          var reject = function () {
            var msg = window.prompt(lang === "es" ? "Motivo del rechazo:" : "Reason for rejection:", shipment.rejectionNote || "");
            if (msg == null) return;
            onSave(Object.assign({}, shipment, {
              shipperStatus: "rejected",
              rejectedAt: new Date().toISOString(),
              rejectionNote: String(msg || "").trim(),
            }));
          };
          var submitToGFI = function () {
            var ok = window.confirm(lang === "es"
              ? "¿Enviar este embarque a GFI para revisión? No podrás editarlo hasta que GFI responda."
              : "Submit this shipment to GFI for review? You won't be able to edit it until GFI responds.");
            if (!ok) return;
            onSave(Object.assign({}, shipment, {
              shipperStatus: "submitted",
              submittedAt: new Date().toISOString(),
              rejectionNote: "",
            }));
          };
          var pill = function (color, bg, label) {
            return <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: 10, fontWeight: 700, background: bg, color: color, border: "1px solid " + color + "33" }}>{label}</span>;
          };

          /* Shipper view, draft / rejected / empty → Submit-to-GFI banner. */
          if (shipperMode && (!sStatus || sStatus === "draft" || sStatus === "rejected")) {
            var rejectedBefore = sStatus === "rejected";
            return (
              <div style={{ padding: "10px 22px", borderBottom: "1px solid var(--color-border)", background: rejectedBefore ? "rgba(220,38,38,0.05)" : "rgba(42,122,111,0.05)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  {rejectedBefore
                    ? pill("#DC2626", "rgba(220,38,38,0.13)", lang === "es" ? "Rechazado — edita y reenvía" : "Rejected — edit and resubmit")
                    : pill("#2A7A6F", "rgba(42,122,111,0.13)", lang === "es" ? "Borrador" : "Draft")}
                  {rejectedBefore && shipment.rejectionNote && <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{lang === "es" ? "Motivo:" : "Reason:"} <strong>{shipment.rejectionNote}</strong></span>}
                </div>
                <button onClick={submitToGFI} style={{ padding: "7px 14px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
                  {lang === "es" ? "📤 Enviar a GFI" : "📤 Submit to GFI"}
                </button>
              </div>
            );
          }
          if (sStatus === "submitted") {
            return (
              <div style={{ padding: "10px 22px", borderBottom: "1px solid var(--color-border)", background: "rgba(245,158,11,0.06)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {pill("#B45309", "rgba(245,158,11,0.13)", lang === "es" ? "Enviado por el shipper" : "Submitted by shipper")}
                  <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{shipperMode ? (lang === "es" ? "Esperando revisión de GFI." : "Waiting on GFI review.") : (lang === "es" ? "Revísalo y acéptalo o recházalo." : "Review and accept or reject.")}</span>
                </div>
                {!shipperMode && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={reject} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid rgba(220,38,38,0.35)", background: "rgba(220,38,38,0.06)", color: "#DC2626", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>{lang === "es" ? "✕ Rechazar" : "✕ Reject"}</button>
                    <button onClick={accept} style={{ padding: "7px 14px", borderRadius: 6, border: "none", background: "#059669", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>{lang === "es" ? "✓ Aceptar" : "✓ Accept"}</button>
                  </div>
                )}
              </div>
            );
          }
          if (sStatus === "accepted") {
            return (
              <div style={{ padding: "8px 22px", borderBottom: "1px solid var(--color-border)", display: "flex", alignItems: "center", gap: 10, background: "rgba(16,185,129,0.05)" }}>
                {pill("#059669", "rgba(16,185,129,0.13)", shipperMode ? (lang === "es" ? "Aceptado por GFI" : "Accepted by GFI") : (lang === "es" ? "Aceptado del shipper" : "Accepted from shipper"))}
              </div>
            );
          }
          if (sStatus === "rejected") {
            return (
              <div style={{ padding: "8px 22px", borderBottom: "1px solid var(--color-border)", display: "flex", alignItems: "center", gap: 10, background: "rgba(220,38,38,0.05)", flexWrap: "wrap" }}>
                {pill("#DC2626", "rgba(220,38,38,0.13)", lang === "es" ? "Rechazado al shipper" : "Rejected to shipper")}
                {shipment.rejectionNote && <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{lang === "es" ? "Motivo:" : "Reason:"} <strong>{shipment.rejectionNote}</strong></span>}
              </div>
            );
          }
          return null;
        })()}

        <div style={{ flex: 1, overflowY: "auto", padding: "8px 22px 22px" }}>
          <RejectionDetailsReadOnly invoices={freshInvoicesShp()} lot={shipment.lot} lang={lang} />
          <ProtectionResponsesReadOnly invoices={freshInvoicesShp()} lot={shipment.lot} lang={lang} />

          {/* 2026-10-17, Arturo: "240 cases, 1 pallet y price ref, no
              son necesarios ahi" — Cases/Pallets/Price-Ref headline
              chips removed; the numbers are already visible further
              down in Pallet Manifest / product lines. totals.cases/
              .pallets/.value stay computed above (other code may still
              reference them), only this display block is gone. */}

          {/* 2026-10-17, Arturo: "la seccion received by GFI esta
              gigante, necesita convivir en el renglon de preview pero
              alineado al extremo izquierdo" \u2014 this used to be its own
              full-width card here; now a compact chip in the top
              strip's action row, left-aligned (see above, right after
              the DetailPanel's top <div>). */}

          {/* 2026-10-17, Arturo: "las tarjetas de shipper, carrier etc
              tienen un color blanco y el titulo gris, detras de estas
              tarjetas es completamente blanco" \u2014 all 6 section-body
              containers below (Header/Transport/Products/Pallet
              Manifest/Documents/Notes) got the same soft gray
              background (#f7f8fa, same tone used elsewhere in this
              redesign) so the white-on-white KV fields read as a
              grouped card instead of blending into the page. */}

          {/* 2026-10-20, Arturo: "shiomment header y carrier info...
              una al lado de otra de manera horizontal, shipment del
              lado izquierdo y carrier info del lado derecho
              dividiendo exactamente a la mitad esa seccion de la
              ventana" — two independent collapsible sections (each
              keeps its own open.header/open.transport toggle state,
              unaffected by the other), side by side in a 50/50 flex
              row instead of stacked. Header's KV grid dropped from 4
              to 2 columns so its 14 fields stay readable at half
              width; Transport keeps its original 3-column grid (short
              values — phone, truck#, plates — still fit fine at
              half width). */}
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            {/* SECTION 1 — HEADER */}
            <div style={{ flex: "1 1 50%", minWidth: 0 }}>
              <SectionHeader id="header" title={lang === "es" ? "Datos del Embarque" : "Shipment Header"} />
              {open.header && (
                <div style={{ padding: "10px 12px 4px", background: "#f7f8fa", border: "1px solid var(--color-border)", borderTop: "none", borderRadius: 0, marginTop: -1 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px 14px" }}>
                    <KV label={t.lot} value={shipment.lot} mono />
                    <KV label={lang === "es" ? "Temporada" : "Season"} value={seasonName} />
                    <KV label={t.status} value={t[st]} />
                    <KV label={t.weekNumber} value={"W" + (shipment.week || getISOWeek(shipment.dateArrival || shipment.dateDeparture))} mono />
                    <KV label={t.dateDeparture} value={fmtDate(shipment.dateDeparture)} />
                    <KV label={t.dateArrival} value={fmtDate(shipment.dateArrival)} />
                    {shipment.supplierId ? (
                      <div>
                        <div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>{t.shipper}</div>
                        <div onClick={openLinkedSupplierFromHeader} title={lang === "es" ? "Abrir proveedor" : "Open supplier"} style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2, minHeight: 25, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}>{resolveShipperName(shipment.supplierId, shipment.shipperName) || "—"}</div>
                      </div>
                    ) : (
                      <KV label={t.shipper} value={resolveShipperName(shipment.supplierId, shipment.shipperName)} />
                    )}
                    <KV label={t.proformaNumber} value={shipment.proforma} mono />
                    <KV label={t.shipperInvoice} value={shipment.shipperInvoice} mono />
                    <KV label={t.entryNumber} value={shipment.entryNumber} mono />
                    <KV label={t.exportCert} value={allCerts} mono />
                    <KV label={t.warehouse} value={shipment.warehouseName} />
                    <KV label={t.brokerMX} value={shipment.brokerMXName || shipment.brokerMXManual} />
                    <KV label={t.brokerUS} value={shipment.brokerUSName || shipment.brokerUSManual} />
                  </div>
                </div>
              )}
            </div>

            {/* SECTION 2 — TRANSPORT DATA */}
            <div style={{ flex: "1 1 50%", minWidth: 0 }}>
              <SectionHeader id="transport" title={t.transport} />
              {open.transport && (
                <div style={{ padding: "14px 14px 4px", background: "#f7f8fa", border: "1px solid var(--color-border)", borderTop: "none", borderRadius: 0, marginTop: -1 }}>
                  {/* Row 1: carrier / driver / phone */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px 18px", marginBottom: 12 }}>
                    <KV label={t.carrier} value={tr.carrier} />
                    <KV label={t.driver} value={tr.driver} />
                    <KV label={t.phone} value={tr.phone} mono />
                  </div>
                  {/* Row 2: paired columns — same layout as the edit form */}
                  {/* 2026-10-20, Arturo: "caat debe ir abajo y alpha code
                      arriba, remueve seal y temp recorder, no son
                      necesarios" — same reorder/removal as the edit form. */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px 18px" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      <KV label={t.econTruck} value={tr.econTruck} mono />
                      <KV label={t.platesTruck} value={tr.platesTruck} mono />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      <KV label={t.econTrailer} value={tr.econTrailer} mono />
                      <KV label={t.platesTrailer} value={tr.platesTrailer} mono />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      <KV label={t.codigoAlpha} value={tr.codigoAlpha} mono />
                      <KV label={t.caat} value={tr.caat} mono />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* SECTION 3 \u2014 PRODUCT LINES */}
          <SectionHeader id="lines" title={t.products} count={(shipment.lines || []).filter(l => l.productName || l.productBase).length} />
          {open.lines && (
            <div style={{ padding: "8px 0", background: "#f7f8fa", border: "1px solid var(--color-border)", borderTop: "none", borderRadius: 0, marginTop: -1 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                {/* 2026-10-17, Arturo: "replica en font, tamaño, color
                    de carrier name en los titulos de products y pallet
                    manifest" — same KV-label typography (fontSize 9 /
                    text-muted / weight 600 / uppercase) already applied
                    to Pallet Manifest's headers above, now matched here
                    too (replacing fontWeight 700 / fontSize 10 /
                    text-secondary, with no uppercase before). */}
                <thead><tr style={{ borderBottom: "2px solid var(--color-border)" }}>
                  <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.product}</th>
                  <th style={{ padding: "8px 6px", textAlign: "left", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.presentation}</th>
                  <th style={{ padding: "8px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.category}</th>
                  <th style={{ padding: "8px 4px", textAlign: "right", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.cases}</th>
                  <th style={{ padding: "8px 4px", textAlign: "right", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.pallets}</th>
                  <th style={{ padding: "8px 4px", textAlign: "right", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.linePrice}</th>
                  <th style={{ padding: "8px 4px", textAlign: "right", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.lineTotal}</th>
                  <th style={{ padding: "8px 6px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.saleStatus}</th>
                  <th style={{ padding: "8px 6px", textAlign: "left", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.exportCert}</th>
                </tr></thead>
                {/* 2026-10-17, Arturo: "los campos de captura... todo en
                    una mezcla de fonts, no uses negrita en ninguna,
                    todos los colores deben ser iguales... toma como
                    referencia de products, blackberry description" —
                    every data cell in this row now matches the
                    Description cell's own style exactly (fontSize 11 /
                    color text-secondary / no bold / no monospace) —
                    that cell was already the reference. The only
                    carved-out exception, per Arturo: "la burbuja de OG
                    no cambia" — the Category badge keeps its own
                    color-coded pill untouched. */}
                <tbody>
                  {(shipment.lines || []).filter(l => l.productName || l.productBase).map((l, i) => {
                    const lineTotal = (parseFloat(l.cases) || 0) * (parseFloat(l.price) || 0);
                    return (
                      <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                        <td style={{ padding: "7px 10px", fontSize: 11, color: "var(--color-text-secondary)" }}>{l.productName || l.productBase}</td>
                        <td style={{ padding: "7px 6px", fontSize: 11, color: "var(--color-text-secondary)" }}>{l.productDescription || "\u2014"}</td>
                        <td style={{ padding: "7px 4px", textAlign: "center" }}>{l.category && <span style={{ padding: "2px 7px", borderRadius: 8, fontSize: 9, fontWeight: 700, background: l.category === "OG" ? "var(--color-badge-og-bg)" : "var(--color-badge-cv-bg)", color: l.category === "OG" ? "var(--color-badge-og-text)" : "var(--color-badge-cv-text)" }}>{l.category}</span>}</td>
                        <td style={{ padding: "7px 4px", textAlign: "right", fontSize: 11, color: "var(--color-text-secondary)" }}>{l.cases}</td>
                        <td style={{ padding: "7px 4px", textAlign: "right", fontSize: 11, color: "var(--color-text-secondary)" }}>{l.pallets}</td>
                        <td style={{ padding: "7px 4px", textAlign: "right", fontSize: 11, color: "var(--color-text-secondary)" }}>{l.price ? fmtMoney(l.price) : "\u2014"}</td>
                        <td style={{ padding: "7px 4px", textAlign: "right", fontSize: 11, color: "var(--color-text-secondary)" }}>{lineTotal > 0 ? fmtMoney(lineTotal) : "\u2014"}</td>
                        <td style={{ padding: "7px 6px", textAlign: "center", fontSize: 11, color: "var(--color-text-secondary)" }}>{l.saleStatus ? (t[l.saleStatus] || l.saleStatus) : "\u2014"}</td>
                        <td style={{ padding: "7px 6px", fontSize: 11, color: "var(--color-text-secondary)" }}>{l.exportCertNumber || l.exportCertManual || "\u2014"}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: "var(--color-bg)", fontWeight: 700, borderTop: "2px solid var(--color-accent)" }}>
                    <td colSpan={3} style={{ padding: "8px 10px", textAlign: "right", textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 10, color: "var(--color-accent)" }}>{lang === "es" ? "Total" : "Total"}</td>
                    <td style={{ padding: "8px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "var(--color-accent)" }}>{totals.cases}</td>
                    <td style={{ padding: "8px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "var(--color-accent)" }}>{totals.pallets}</td>
                    <td colSpan={1}></td>
                    <td style={{ padding: "8px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "var(--color-accent)" }}>{totals.value > 0 ? fmtMoney(totals.value) : ""}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* SECTION 4 \u2014 PALLET MANIFEST */}
          <SectionHeader id="pallets" title={t.palletManifest} count={visiblePallets.length + " plt"} />
          {open.pallets && (
            <div style={{ padding: "8px 0", background: "#f7f8fa", border: "1px solid var(--color-border)", borderTop: "none", borderRadius: 0, marginTop: -1, overflowX: "auto" }}>
              {visiblePallets.length === 0 ? (
                <div style={{ padding: "20px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 12, fontStyle: "italic" }}>{lang === "es" ? "Sin pallets registrados" : "No pallets registered"}</div>
              ) : (
                <table style={{ width: "100%", minWidth: 1200, borderCollapse: "collapse", fontSize: 11 }}>
                  {/* 2026-10-17, Arturo: "los titulos no concuerdan con
                      los titulos de por ejemplo carrier info, los fonts
                      de pallet manifest parecen estar en negritas y mas
                      grandes, copia el estilo de carrier name" — matched
                      to the KV label style used throughout this panel
                      (fontSize 9 / color text-muted / weight 600).
                      Setting fontWeight/fontSize on the <tr> alone
                      wasn't enough — <th> has its own browser-default
                      bold (wins over inheritance from the parent row),
                      AND App.css has a global "th, td { font-size:
                      12px; }" rule (line ~136) that, with no inline
                      font-size of its own to compete against, beats
                      inheritance too (a real stylesheet rule always
                      wins over inherited-from-parent, regardless of
                      specificity, when the child sets nothing itself)
                      — confirmed live via getComputedStyle (9px on the
                      <tr>, 12px on its own <th> children). Each of the
                      16 <th> below now sets fontWeight: 600 AND
                      fontSize: 9 explicitly. */}
                  <thead><tr style={{ borderBottom: "2px solid var(--color-border)", color: "var(--color-text-muted)", fontWeight: 600, fontSize: 9, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    <th style={{ padding: "6px 6px", textAlign: "center", fontWeight: 600, fontSize: 9 }}>{t.palletNum}</th>
                    <th style={{ padding: "6px 6px", textAlign: "center", fontWeight: 600, fontSize: 9 }}>{t.tagId}</th>
                    <th title={t.actualShipperInitialsHint} style={{ padding: "6px 4px", textAlign: "center", fontWeight: 600, fontSize: 9 }}>{t.actualShipperInitialsShort}</th>
                    <th style={{ padding: "6px 6px", textAlign: "left", fontWeight: 600, fontSize: 9 }}>{t.product}</th>
                    <th style={{ padding: "6px 4px", textAlign: "center", fontWeight: 600, fontSize: 9 }}>{t.label}</th>
                    <th style={{ padding: "6px 4px", textAlign: "left", fontWeight: 600, fontSize: 9 }}>{t.variety}</th>
                    <th style={{ padding: "6px 4px", textAlign: "center", fontWeight: 600, fontSize: 9 }}>{t.category}</th>
                    <th style={{ padding: "6px 4px", textAlign: "left", fontWeight: 600, fontSize: 9 }}>{t.condition}</th>
                    <th style={{ padding: "6px 4px", textAlign: "left", fontWeight: 600, fontSize: 9 }}>{t.packType}</th>
                    <th style={{ padding: "6px 4px", textAlign: "center", fontWeight: 600, fontSize: 9 }}>{t.presentation}</th>
                    <th style={{ padding: "6px 4px", textAlign: "right", fontWeight: 600, fontSize: 9 }}>{t.unitsCase}</th>
                    <th style={{ padding: "6px 4px", textAlign: "right", fontWeight: 600, fontSize: 9 }}>{t.casesPallet}</th>
                    <th style={{ padding: "6px 4px", textAlign: "left", fontWeight: 600, fontSize: 9 }}>{t.traceability}</th>
                    <th style={{ padding: "6px 4px", textAlign: "left", fontWeight: 600, fontSize: 9 }}>{t.growerCode}</th>
                    <th style={{ padding: "6px 4px", textAlign: "right", fontWeight: 600, fontSize: 9 }}>{t.weight}</th>
                    {/* 2026-10-21, Arturo: "las columnas de calidad en
                        los embarques que son 2 si es mi qc o 3 si es
                        twin river, solo se ven en modo edicion, quiero
                        que tambien se vean en modo no edicion, copia
                        el mismo formato de colores, tamaños etc" —
                        same 2-vs-3-column gate (shipment.texasQcMode)
                        and same TEXAS_QC_COLORS/TEXAS_QC_BEVEL badge
                        recipe as the edit-mode ShipmentForm table,
                        placed in the same relative position (right
                        after Weight). Read-only here (view mode never
                        edits anything) — just the colored pill, no
                        SearchSelect. */}
                    <th style={{ padding: "6px 4px", textAlign: "center", fontWeight: 600, fontSize: 9 }}>{t.texasQuality}</th>
                    <th style={{ padding: "6px 4px", textAlign: "center", fontWeight: 600, fontSize: 9 }}>{t.texasCondition}</th>
                    {shipment.texasQcMode === "clarifresh" && <th style={{ padding: "6px 4px", textAlign: "center", fontWeight: 600, fontSize: 9 }}>{t.texasSevereCondition}</th>}
                    <th style={{ padding: "6px 4px", textAlign: "center", fontWeight: 600, fontSize: 9 }}>{lang === "es" ? "Status" : "Status"}</th>
                    <th style={{ padding: "6px 4px", textAlign: "center", fontWeight: 600, fontSize: 9 }}>{t.casesLost}</th>
                  </tr></thead>
                  <tbody>
                    {(() => {
                      /* 2026-09-12 — per-pallet status, read live from
                         whichever invoice(s) currently allocate this
                         pallet (Arturo: the pallet's status is only ever
                         set on the Invoice, never here — this is
                         read-only). Self-contained color map (not the
                         list view's SS_COLORS, out of scope in this
                         component) matching the same values used
                         everywhere else in the app. */
                      const PALLET_STATUS_COLORS = { goodDelivery: { bg: "rgba(16,185,129,0.12)", text: "#059669", border: "rgba(16,185,129,0.3)" }, protection: { bg: "rgba(239,68,68,0.12)", text: "#DC2626", border: "rgba(239,68,68,0.3)" }, pas: { bg: "rgba(245,158,11,0.12)", text: "#D97706", border: "rgba(245,158,11,0.3)" }, partialProtection: { bg: "rgba(217,119,6,0.10)", text: "#B45309", border: "rgba(217,119,6,0.25)" }, pendingPickUp: { bg: "rgba(100,116,139,0.10)", text: "#64748B", border: "rgba(100,116,139,0.25)" }, pendingDelivery: { bg: "rgba(37,99,235,0.10)", text: "#2563EB", border: "rgba(37,99,235,0.25)" } };
                      const PALLET_STATUS_LABELS = lang === "es"
                        ? { goodDelivery: "Buena Entrega", protection: "Protección", pas: "PAS", partialProtection: "Protección Parcial", pendingPickUp: "Pendiente de Recolección", pendingDelivery: "Pendiente de Entrega" }
                        : { goodDelivery: "Good Delivery", protection: "Protection", pas: "PAS", partialProtection: "Partial Protection", pendingPickUp: "Pending Pick Up", pendingDelivery: "Pending Delivery" };
                      const allInvoicesForPalletStatus = loadLS("gfi-invoices", []);
                      /* Same duplicate-palletNum detector as the edit
                         form (pnCounts/pnDupe) \u2014 view mode had none, so
                         a repeated palletNum here just looked like the
                         "TOTAL" row mysteriously not matching the row
                         count once totals started excluding the extra
                         copy. Flag it here too instead of leaving that
                         unexplained. */
                      const pnCounts = {};
                      visiblePallets.forEach(p => {
                        const v = String(p.palletNum || "").trim().toUpperCase();
                        if (v) pnCounts[v] = (pnCounts[v] || 0) + 1;
                      });
                      return visiblePallets.map((p, i) => {
                      const w = calcPalletWeight(p);
                      const pnKey = String(p.palletNum || "").trim().toUpperCase();
                      const pnDupe = !!pnKey && (pnCounts[pnKey] || 0) > 1;
                      const qc = p.qcDefects || {};
                      const qcCatsPresent = ["severeCondition", "condition", "quality", "findings"].filter(function (cat) {
                        return Array.isArray(qc[cat]) && qc[cat].length > 0;
                      });
                      /* 2026-10-17, Arturo: "los campos de captura...
                         todo en una mezcla de fonts, no uses negrita en
                         ninguna, todos los colores deben ser iguales...
                         toma como referencia de products, blackberry
                         description" \u2014 every plain data cell below now
                         matches that reference (fontSize 11 / color
                         text-secondary / no bold / no monospace).
                         Colored badges (Category/Status/Lost Cases) and
                         the duplicate-pallet warning are left alone \u2014
                         same exception Arturo named for the OG bubble,
                         applied to every other color-coded pill here. */
                      const cellSty = { fontSize: 11, color: "var(--color-text-secondary)" };
                      const mainRow = (
                        <tr key={i} style={{ borderBottom: qcCatsPresent.length > 0 ? "none" : "1px solid var(--color-border)" }}>
                          <td style={{ padding: "6px 6px", textAlign: "center" }}>
                            <span style={{ ...cellSty, color: pnDupe ? "#B91C1C" : cellSty.color }}>{p.palletNum || "\u2014"}</span>
                            {pnDupe && <div style={{ fontSize: 9, color: "#B91C1C", fontWeight: 700 }}>{t.duplicatePalletNum}</div>}
                          </td>
                          <td style={{ padding: "6px 6px", textAlign: "center", ...cellSty }}>{p.tagId || "\u2014"}</td>
                          <td style={{ padding: "6px 4px", textAlign: "center", ...cellSty }}>{p.actualShipperInitials || shipment.shipperInitials || "\u2014"}</td>
                          <td style={{ padding: "6px 6px", ...cellSty }}>{p.productBase || "\u2014"}</td>
                          <td style={{ padding: "6px 4px", textAlign: "center", ...cellSty }}>{p.labelInitials || "\u2014"}</td>
                          <td style={{ padding: "6px 4px", ...cellSty }}>{p.variety || "\u2014"}</td>
                          <td style={{ padding: "6px 4px", textAlign: "center" }}>{p.category && <span style={{ padding: "2px 5px", borderRadius: 5, fontSize: 9, fontWeight: 700, background: p.category === "OG" ? "var(--color-badge-og-bg)" : "var(--color-badge-cv-bg)", color: p.category === "OG" ? "var(--color-badge-og-text)" : "var(--color-badge-cv-text)" }}>{p.category}</span>}</td>
                          <td style={{ padding: "6px 4px", ...cellSty }}>{p.condition || "\u2014"}</td>
                          <td style={{ padding: "6px 4px", ...cellSty }}>{p.packType ? ({ clamshell: "Clamshell", bulk: lang === "es" ? "Granel" : "Bulk", bag: lang === "es" ? "Bolsa" : "Bag", case_pack: lang === "es" ? "Caja" : "Case Pack" }[p.packType] || p.packType) : "\u2014"}</td>
                          <td style={{ padding: "6px 4px", textAlign: "center", ...cellSty }}>{p.size || "\u2014"}</td>
                          <td style={{ padding: "6px 4px", textAlign: "right", ...cellSty }}>{p.unitsCase || "\u2014"}</td>
                          <td style={{ padding: "6px 4px", textAlign: "right", ...cellSty }}>{p.casesPallet || "\u2014"}</td>
                          <td style={{ padding: "6px 4px", ...cellSty }}>{p.traceability || "\u2014"}</td>
                          <td style={{ padding: "6px 4px", ...cellSty }}>{p.growerCode || "\u2014"}</td>
                          <td style={{ padding: "6px 4px", textAlign: "right", ...cellSty }}>{w.lb > 0 ? <div><div style={cellSty}>{w.lb.toFixed(1)} lb</div><div style={{ color: "var(--color-text-muted)", fontSize: 9 }}>{w.kg.toFixed(1)} kg</div></div> : "\u2014"}</td>
                          {/* Texas QC tier badges \u2014 read-only mirror of
                              the edit-mode ShipmentForm table's own
                              Quality/Condition/[Severe Condition]
                              columns, same TEXAS_QC_COLORS/
                              TEXAS_QC_BEVEL recipe. */}
                          {["texasQuality", "texasCondition"].concat(shipment.texasQcMode === "clarifresh" ? ["texasSevereCondition"] : []).map(function (field) {
                            var val = p[field];
                            var c = val ? TEXAS_QC_COLORS[val] : null;
                            return (
                              <td key={field} style={{ padding: "6px 4px", textAlign: "center" }}>
                                {c
                                  ? <span style={{ padding: "2px 7px", borderRadius: 0, fontSize: 10, fontWeight: 700, background: c.bg, color: c.text, border: "1px solid " + c.border, boxShadow: TEXAS_QC_BEVEL, whiteSpace: "nowrap" }}>{t["texasQc" + val.charAt(0).toUpperCase() + val.slice(1)]}</span>
                                  : <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{"\u2014"}</span>}
                              </td>
                            );
                          })}
                          <td style={{ padding: "6px 4px", textAlign: "center" }}>{(() => {
                            const pStatus = twinRiverPalletStatus(shipment, p.palletNum) || derivePalletStatus(shipment.lot, p.palletNum, allInvoicesForPalletStatus) || resolvePendingStage(invoicesForPallet(shipment.lot, p.palletNum, allInvoicesForPalletStatus), slPassingIndex);
                            const pSc = PALLET_STATUS_COLORS[pStatus];
                            // 2026-10-21, Arturo: "tambien el status
                            // tiene forma obalada en modo no edicion de
                            // los embarques, emula el formato del modo
                            // edicion" \u2014 rectangular (borderRadius 0),
                            // same squared convention edit mode uses
                            // everywhere in this table (see the Texas
                            // QC SearchSelect pickers above), not the
                            // oval/rounded pill this used to be.
                            return pSc
                              ? <span style={{ padding: "2px 7px", borderRadius: 0, fontSize: 9, fontWeight: 700, background: pSc.bg, color: pSc.text, border: "1px solid " + pSc.border }}>{PALLET_STATUS_LABELS[pStatus] || pStatus}</span>
                              : <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{"\u2014"}</span>;
                          })()}</td>
                          <td style={{ padding: "6px 4px", textAlign: "center" }}>
                            {p.lostCases ? (
                              <span title={p.lossReason === "other" ? (p.lossReasonCustom || "") : ""} style={{ padding: "2px 7px", borderRadius: 8, fontSize: 9, fontWeight: 700, background: "rgba(217,119,6,0.12)", color: "#B45309", border: "1px solid rgba(217,119,6,0.3)" }}>
                                {p.lostCases} {t.lossNotedBadge}{p.lossReason ? " \u2014 " + (t["lossReason" + p.lossReason.charAt(0).toUpperCase() + p.lossReason.slice(1)] || p.lossReason) : ""}
                              </span>
                            ) : <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{"\u2014"}</span>}
                          </td>
                        </tr>
                      );
                      /* 2026-10-15, Arturo (16th pass, view-mode
                         DetailPanel): "no lo quiero ahi [la columna al
                         final], quiero que se quede debajo del
                         renglon del pallet manteniendo sus propios
                         colores pero no editable solo visible... los
                         defectos solo aparecen visibles cuando se
                         capturan" \u2014 same "one colored rectangle per
                         category" treatment as the locked edit-mode
                         row (ShipmentForm's defectsRow), but here it's
                         ALWAYS read-only (view mode never edits
                         anything) and only renders at all when this
                         pallet actually has captured defects \u2014 no
                         empty "no defects" placeholder row cluttering
                         every pallet that hasn't been inspected yet. */
                      /* 2026-10-15, Arturo (GFI Team/internal mode):
                         same read-only summary sub-row, now also for
                         non-ClariFresh shipments — internal mode only
                         ever populates qc.quality (Condition stays a
                         plain tier grade, no per-defect breakdown), so
                         this naturally renders just the one category. */
                      const defectsSubRow = qcCatsPresent.length > 0 ? (
                        <tr key={i + "-defects"} style={{ borderBottom: "1px solid var(--color-border)" }}>
                          {/* +2 for the new Quality/Condition columns
                              (+1 more when clarifresh adds Severe
                              Condition) so this sub-row still spans the
                              full, now-wider table width. */}
                          {/* 2026-10-23 — +1 base for the new
                              actualShipperInitials column. */}
                          <td colSpan={17 + (shipment.texasQcMode === "clarifresh" ? 3 : 2)} style={{ padding: "2px 6px 6px" }}>
                            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                              {/* 2026-10-15, Arturo (17th pass): "hay
                                  bastante espacio para que empezando
                                  debajo del numero de pallet muestres
                                  defects y en seguida todo lo
                                  capturado" — same leading "Defects"
                                  label the edit-mode row has, right
                                  under Pallet#, before the colored
                                  category chips (not just the chips
                                  alone). */}
                              <span style={{ fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{t.qcDefects}</span>
                              {qcCatsPresent.map(function (cat) {
                                var entries = qc[cat];
                                /* 2026-10-15, Arturo (GFI Team/internal
                                   mode): per-defect severity coloring
                                   instead of one flat category color —
                                   applies to both real categories
                                   (Condition/Quality) now that internal
                                   mode files defects under their actual
                                   category instead of one flat bucket.
                                   Trailing total (sum of the category's
                                   captured %s) so Arturo can check it
                                   against the real report at a glance —
                                   "debo cotejarlo con el reporte que me
                                   mandan". ClariFresh keeps its existing
                                   single-color-per-category look
                                   untouched. */
                                if ((cat === "quality" || cat === "condition") && shipment.texasQcMode !== "clarifresh") {
                                  var catTotal = entries.reduce(function (s, e) { var n = parseFloat(e.pct); return s + (n >= 0 ? n : 0); }, 0);
                                  return (
                                    <span key={cat} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                      <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--color-text-muted)" }}>{t["qcCat" + cat.charAt(0).toUpperCase() + cat.slice(1)]}:</span>
                                      {entries.map(function (e, ei) {
                                        var lbl = internalDefectLabel(e, t);
                                        var pct = e.pct !== undefined && e.pct !== null && e.pct !== "" ? " " + e.pct + "%" : "";
                                        var dc = internalQualityDefectColor(e.key);
                                        return (
                                          <span key={e.key + ei} style={{ padding: "2px 8px", borderRadius: 5, fontSize: 9, fontWeight: 600, background: dc.bg, color: dc.text, border: "1px solid " + dc.border }}>{lbl + pct}</span>
                                        );
                                      })}
                                      <span style={{ fontSize: 9, fontWeight: 700, color: "var(--color-text-secondary)" }}>{"(" + t.qcDefectTotal + ": " + catTotal.toFixed(2) + "%)"}</span>
                                    </span>
                                  );
                                }
                                var c = TOLERANCE_CATEGORY_COLORS[cat];
                                var summaryText = entries.map(function (e) {
                                  var lbl = e.key === "__custom__" ? (e.custom || t.qcTolCustom) : t["qcTol" + e.key.charAt(0).toUpperCase() + e.key.slice(1)];
                                  return lbl + (e.pct !== undefined && e.pct !== null && e.pct !== "" ? " " + e.pct + "%" : "");
                                }).join(", ");
                                return (
                                  <span key={cat} style={{ padding: "2px 8px", borderRadius: 5, fontSize: 9, fontWeight: 600, background: c.bg, color: c.text, border: "1px solid " + c.border }}>
                                    <span style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>{t["qcCat" + cat.charAt(0).toUpperCase() + cat.slice(1)]}</span>
                                    {summaryText && (": " + summaryText)}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      ) : null;
                      return defectsSubRow ? [mainRow, defectsSubRow] : mainRow;
                      });
                    })()}
                  </tbody>
                  <tfoot>
                    {palletSubtotals.map(g => (
                      <tr key={g.key} style={{ background: "var(--color-bg)", fontSize: 10 }}>
                        <td colSpan={10} style={{ padding: "6px 8px", fontWeight: 600, color: "var(--color-text-secondary)" }}>{t.palletSubtotal + " \u00b7 " + g.key}</td>
                        <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>{g.cases}</td>
                        <td colSpan={2} style={{ padding: "6px 4px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>{g.pallets} plt</td>
                        <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>{g.lb.toFixed(1) + " lb \u00b7 " + (g.lb * 0.453592).toFixed(1) + " kg"}</td>
                      </tr>
                    ))}
                    {/* 2026-10-17, Arturo: "la parte inferior tiene un
                        color verde, sustituyelo pero arrastra ese
                        color hasta el extremo derecho, ahora termina
                        en grower code" \u2014 2 fixes: (1) the hardcoded
                        teal rgba(42,122,111,...) swapped for the
                        scoped muted blue, matching the rest of this
                        redesign (text/border already read
                        var(--color-accent), only the raw background
                        hex was still teal); (2) the thead has 16
                        columns but this row's colSpans only summed to
                        13 (9+1+2+1), so the tr's background had no
                        <td> left to paint for the last 3 columns
                        (Weight/Status/Cases Lost) \u2014 visually stopping
                        right at Grower Code. Last cell's colSpan
                        widened from 1 to 4 to reach the true right
                        edge. */}
                    <tr style={{ background: "rgba(62,110,150,0.08)", borderTop: "2px solid var(--color-accent)", fontSize: 11 }}>
                      <td colSpan={10} style={{ padding: "8px 8px", textAlign: "right", fontWeight: 700, color: "var(--color-accent)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.palletGrandTotal}</td>
                      <td style={{ padding: "8px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }}>{palletGrand.cases}</td>
                      <td colSpan={2} style={{ padding: "8px 4px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }}>{palletGrand.pallets} plt</td>
                      {/* +2 for the new Quality/Condition columns above
                          (+1 more when clarifresh adds Severe
                          Condition) so this row's accent background
                          still reaches the true right edge \u2014 same
                          "widen the last cell's colSpan" fix already
                          applied here once before for a different
                          column-count change. */}
                      <td colSpan={4 + (shipment.texasQcMode === "clarifresh" ? 3 : 2)} style={{ padding: "8px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }}>{palletGrand.lb.toFixed(1) + " lb \u00b7 " + (palletGrand.lb * 0.453592).toFixed(1) + " kg"}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
              {shipment.texasQcMode === "clarifresh" && (shipment.lotRegressionPct !== undefined && shipment.lotRegressionPct !== null && shipment.lotRegressionPct !== "") && (
                <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 6, background: "rgba(217,119,6,0.06)", border: "1px solid rgba(217,119,6,0.25)", fontSize: 11, color: "#B45309" }}>
                  {t.lotRegressionLabel + " " + shipment.lotRegressionPct + "%"}
                </div>
              )}
            </div>
          )}

          {/* SECTION 5 \u2014 DOCUMENTS. Lists every attached file from
              the shipment (Arturo's own uploads + portal-inherited
              files stamped with `source: "portal"`). Each card shows
              name + size + uploaded date + per-file Download; a
              "Download All" button at the top loops through every
              doc and triggers a sequential download. */}
          <SectionHeader id="documents" title={t.documents} count={(shipment.documents || []).length} />
          {open.documents && (
            <div style={{ padding: "14px", background: "#f7f8fa", border: "1px solid var(--color-border)", borderTop: "none", borderRadius: 0, marginTop: -1 }}>
              {(shipment.documents || []).length === 0 ? (
                <div style={{ fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic" }}>{lang === "es" ? "No hay documentos adjuntos" : "No attached documents"}</div>
              ) : (
                <>
                  {/* Download All \u2014 small stagger between downloads
                      so the browser doesn't drop simultaneous triggers. */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{(shipment.documents || []).length} {lang === "es" ? "archivo(s)" : "file(s)"}</div>
                    <button onClick={async () => {
                      var docs = shipment.documents || [];
                      for (var i = 0; i < docs.length; i++) {
                        await forceDownload(docs[i].url || docs[i].data, docs[i].name || ("document-" + i));
                        await new Promise(function (r) { setTimeout(r, 250); });
                      }
                    }} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #b9bcc0", background: "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.15)", color: "#1a2636", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{"\u2b07 "}{lang === "es" ? "Descargar Todos" : "Download All"}</button>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {shipment.documents.map((doc, i) => {
                      var uploaded = doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) : "";
                      return (
                        <div key={doc.id || i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 6, background: "var(--color-bg)", border: "1px solid var(--color-border)", fontSize: 12 }}>
                          <span style={{ fontSize: 16 }}>{"\ud83d\udcc4"}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: "var(--color-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.name}</div>
                            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2, display: "flex", gap: 10, flexWrap: "wrap" }}>
                              {doc.size && <span>{doc.size}</span>}
                              {doc.type && <span>{doc.type}</span>}
                              {uploaded && <span>{lang === "es" ? "Subido " : "Uploaded "}{uploaded}</span>}
                              {doc.source === "portal" && <span style={{ padding: "1px 6px", borderRadius: 3, background: "rgba(42,122,111,0.12)", color: "var(--color-accent)", fontWeight: 700, fontSize: 9, textTransform: "uppercase", letterSpacing: "0.04em" }}>{lang === "es" ? "Portal" : "Portal"}</span>}
                              {doc.level === "shipment" && <span style={{ padding: "1px 6px", borderRadius: 3, background: "rgba(59,130,246,0.12)", color: "#2563EB", fontWeight: 700, fontSize: 9, textTransform: "uppercase", letterSpacing: "0.04em" }}>{lang === "es" ? "Embarque" : "Shipment"}</span>}
                              {doc.level === "proforma" && <span style={{ padding: "1px 6px", borderRadius: 3, background: "rgba(217,119,6,0.14)", color: "#B45309", fontWeight: 700, fontSize: 9, textTransform: "uppercase", letterSpacing: "0.04em" }}>{lang === "es" ? "Proforma" : "Proforma"}</span>}
                            </div>
                          </div>
                          {/* 2026-10-17, Arturo: "agregales el color
                              gris de mac pero hazlos 3d... como si
                              fueran una placa metalica" \u2014 same
                              brushed-metal silver gradient + inset
                              bevel already used for the section headers
                              and the Download All button above. */}
                          <button onClick={() => { forceDownload(doc.url || doc.data, doc.name || ("document-" + i)); }} style={{ padding: "5px 12px", borderRadius: 5, border: "1px solid #b9bcc0", background: "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.15)", color: "#1a2636", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{"\u2193 "}{lang === "es" ? "Descargar" : "Download"}</button>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* SECTION 6 \u2014 NOTES */}
          <SectionHeader id="notes" title={t.notes} />
          {open.notes && (
            <div style={{ padding: "14px", background: "#f7f8fa", border: "1px solid var(--color-border)", borderTop: "none", borderRadius: 0, marginTop: -1 }}>
              {shipment.notes
                ? <div style={{ fontSize: 12, color: "var(--color-text-secondary)", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{shipment.notes}</div>
                : <div style={{ fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic" }}>{lang === "es" ? "Sin notas" : "No notes"}</div>}
            </div>
          )}

        </div>
        </>
      )}
      {emailDialogOpen && (() => {
        /* Look up the assigned warehouse + supplier so we can
           pre-populate the dialog with the warehouse's distribution
           list and a sensible default subject/body. */
        const wh = getWarehouses().find(w => String(w.id) === String(shipment.warehouseId));
        const sup = getSuppliers().find(s => String(s.id) === String(shipment.supplierId));
        const company = loadCompanyProfile();
        const distList = (wh && wh.distributionList) || [];
        /* All distribution-list recipients pre-checked per spec — the
           per-doc-kind toggles in the contact card stay as
           "metadata" about which docs they CAN receive; the dialog
           always opens them ticked and lets the user uncheck.
           2026-10-02 — Arturo: "quiero enviar... a 5 brothers pero al
           enviar el email no me muestra el correo de 5 brothers". This
           dialog only ever read `wh.distributionList` — a separate
           section from the warehouse's own "Contacts" list, easy to
           never set up (same gap already found/fixed for client
           contacts earlier this session). Five Brothers has contacts
           configured, just never added to its own Distribution List.
           Falls back to `wh.contacts`, filtered by the "shipment"
           email category (same `filterContactsByCategory` helper
           Supplier/Broker/Client contacts already use — a contact
           with no emailPreferences configured counts as opted into
           everything by default, so this works out of the box). */
        const whContactsFallback = filterContactsByCategory((wh && wh.contacts) || [], "shipment")
          .map(c => ({ name: [c.firstName, c.lastName].filter(Boolean).join(" "), email: c.email, checked: true }));
        const recipients = distList.length > 0
          ? distList.filter(c => c && c.email).map(c => ({ name: c.name || "", email: c.email, checked: true }))
          : whContactsFallback;
        /* 2026-10-02 — Arturo: "en los casos de producto comprado FOB
           debo generar un documento para la bodega de llegada". This
           dialog always offered Carta Porte (Bill of Lading) as a
           checked default — a Mexican customs/transport document that
           doesn't exist for an FOB (domestic Texas) purchase, same
           reasoning already applied to the Print menu above
           ("doesn't exist/apply for an FOB shipment, hidden entirely
           rather than left to fail/print blank"). FOB shipments now
           only get the Receiving Manifest — the one document that
           actually applies, and the one the warehouse needs to know
           product is arriving.
           2026-10-02 — Arturo corrected the document itself: "no
           necesito generar un receiving manifest para este tipo de
           embarques FOB si no un inbound packing list" — label swapped
           to match; buildReceivingSheetHTML (same builder, same "receiving"
           key — no new doc kind) retitles its own PDF the same way
           whenever shipment.shipmentType is "fob". */
        const isFobShip = shipment.shipmentType === "fob";
        /* 2026-10-16, Arturo: "debe quedar separado de ese email porque
           ese es de cuando llega la fruta" — Dump Order used to live
           here as a 3rd doc option, but this dialog is specifically
           the ARRIVAL email (Bill of Lading/Receiving Manifest, sent
           when the truck lands); Dump Order is a later, unrelated
           event (after repack, once cases are confirmed for disposal)
           — moved to its own dedicated "Send Dump Order" kebab item
           and dialog below, never bundled in here again. */
        const docOptions = isFobShip
          ? [{ key: "receiving", label: t.emailWarehouseDocInboundPacking, checked: true }]
          : [
              { key: "carta-porte", label: t.emailWarehouseDocBoL, checked: true },
              { key: "receiving",   label: t.emailWarehouseDocReceiving, checked: true },
            ];
        const tpl = (s) => String(s || "")
          .replace("{lot}", shipment.lot || "—")
          .replace("{proforma}", shipment.proforma || "—")
          .replace("{shipper}", (sup && sup.shortName) || shipment.shipperName || "—")
          .replace("{eta}", shipment.dateArrival || shipment.dateDeparture || "—");
        const ctx = {
          suppliers: getSuppliers(),
          warehouses: getWarehouses(),
          brokers: getBrokers(),
          commodities: loadLS("gfi-commodities", []),
          seasons: getSeasons(),
          invoices: freshInvoicesShp(),
          lang: lang,
        };
        const buildAttachments = async (kinds) => {
          const out = [];
          for (const k of kinds) {
            const att = await buildShipmentDocPdf(shipment, k, ctx);
            out.push(att);
          }
          /* Manual files Arturo added for this send (2026-10-02 —
             "debo poder agregar un archivo al enviarlo a bodega"),
             same shape the generated doc attachments use. */
          warehouseEmailFiles.forEach((file) => {
            out.push({ filename: file.name, blob: file, mimeType: file.type || "application/octet-stream" });
          });
          return out;
        };
        /* Inline doc embed for email body — short product summary
           only, using inline styles so every email client (Gmail,
           Outlook, etc.) renders it without stripping. Replaces the
           earlier "embed the full PDF HTML" approach which broke in
           clients that sanitise <style> blocks. */
        const buildEmbedHtml = async (/* kinds */) => {
          const lines = (shipment.lines || []).filter(l => l && (l.productName || l.productBase));
          if (lines.length === 0) return "";
          const esc = (s) => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
          const totals = { pallets: 0, cases: 0 };
          const rows = lines.map(l => {
            const p = parseInt(l.pallets, 10) || 0;
            const c = parseInt(l.cases, 10) || 0;
            totals.pallets += p; totals.cases += c;
            return {
              product: l.productBase || l.productName || "",
              desc: l.productDescription || "",
              pallets: p,
              cases: c,
              uom: l.uom || "Flats",
            };
          });
          const th = 'style="text-align:left;padding:8px 10px;border-bottom:1px solid #d0d5dd;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;color:#1a1a1a"';
          const thNum = 'style="text-align:right;padding:8px 10px;border-bottom:1px solid #d0d5dd;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;color:#1a1a1a"';
          const thCtr = 'style="text-align:center;padding:8px 10px;border-bottom:1px solid #d0d5dd;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;color:#1a1a1a"';
          let html = '<div style="font-family:Arial,sans-serif;font-size:13px;color:#1a1a1a;line-height:1.4">';
          html += '<table cellspacing="0" cellpadding="0" style="border-collapse:collapse;width:100%;border:1px solid #d0d5dd;background:#ffffff">';
          html += '<thead><tr style="background:#f7f8fa">';
          html += '<th ' + th + '>Product</th>';
          html += '<th ' + th + '>Description</th>';
          html += '<th ' + thNum + '>Pallets</th>';
          html += '<th ' + thNum + '>Fill (Cases)</th>';
          html += '<th ' + thCtr + '>UOM</th>';
          html += '</tr></thead><tbody>';
          rows.forEach(r => {
            html += '<tr style="border-bottom:1px solid #eef0f3">';
            html += '<td style="padding:7px 10px;font-weight:600">' + esc(r.product) + '</td>';
            html += '<td style="padding:7px 10px;color:#444">' + esc(r.desc) + '</td>';
            html += '<td style="padding:7px 10px;text-align:right;font-family:Courier New,monospace">' + r.pallets + '</td>';
            html += '<td style="padding:7px 10px;text-align:right;font-family:Courier New,monospace;font-weight:600">' + r.cases + '</td>';
            html += '<td style="padding:7px 10px;text-align:center">' + esc(r.uom) + '</td>';
            html += '</tr>';
          });
          html += '<tr style="background:#f7f8fa;font-weight:700">';
          html += '<td colspan="2" style="padding:9px 10px;text-align:right;font-size:11px;text-transform:uppercase;letter-spacing:0.04em">Totals</td>';
          html += '<td style="padding:9px 10px;text-align:right;font-family:Courier New,monospace">' + totals.pallets + '</td>';
          html += '<td style="padding:9px 10px;text-align:right;font-family:Courier New,monospace">' + totals.cases + '</td>';
          html += '<td></td>';
          html += '</tr></tbody></table></div>';
          return html;
        };
        /* Pull the doc-kind-aware template defaults from
           CompanyProfile. Falls back to the inline templates if the
           user hasn't customized them. */
        const tplCfg = pickEmailTemplate(company, "receiving");
        const tplValues = {
          pu: "", po: "", lot: shipment.lot || "",
          arrival: shipment.dateArrival || shipment.dateDeparture || "",
          shipper: (sup && sup.shortName) || shipment.shipperName || "",
          client: "",
          warehouse: (wh && (wh.shortName || wh.fullName)) || shipment.warehouseName || "",
        };
        const tplBody = composeEmailBody(tplCfg, tplValues);
        /* Shipment emails always go from Ground-Fresh (never SweetLeaf —
           that's for invoices to Four Seasons). Pick the Ground-Fresh
           account from company.emailAccounts so EmailDialog resolves the
           maskedFrom alias automatically.
           2026-10-02 — Arturo: "ese email a bodega debe salir de la
           cuenta de alertas de ground-fresh solo los FOB" — FOB
           shipments send from the dedicated "GFI Automated"
           (alerts@gfinexus.com) account instead, same account/
           resolution pattern already used for export-cert and
           Protection Status Reminder automated sends (InvoiceRegistry
           .js). Import shipments are untouched — still the regular
           Ground-Fresh account. Falls back to the regular account if
           "GFI Automated" hasn't been configured yet, so this never
           breaks a send before Arturo sets it up. */
        const isFobForFrom = shipment.shipmentType === "fob";
        const gfiAutomatedAcctForWh = isFobForFrom
          ? (company.emailAccounts || []).find(function (a) { return String((a && a.maskedFrom) || "").toLowerCase() === "alerts@gfinexus.com"; })
            || (company.emailAccounts || []).find(function (a) { return (a.name || "").toLowerCase().indexOf("automat") >= 0; })
          : null;
        const groundFreshAcct = gfiAutomatedAcctForWh || (company.emailAccounts || []).find(function (a) {
          return /ground.?fresh/i.test(a.name || "");
        }) || (company.emailAccounts || [])[0] || null;
        const shipmentFromEmail = groundFreshAcct ? groundFreshAcct.email : (company.outboundEmail || "");
        const shipmentEmailAccounts = groundFreshAcct ? [groundFreshAcct] : [];
        /* 2026-10-02 — Arturo: "necesito preview el documento que
           enviare a bodega". EmailDialog's own built-in "Preview"
           button only renders the email BODY, not the attached PDFs —
           same gap the Protection Notice's own "Vista previa" button
           was added for earlier this session. Reuses the exact same
           builders buildAttachments calls at send time (buildReceiving
           SheetHTML / buildCartaPorteHTML), so what's previewed here
           is guaranteed to match what actually gets attached — never a
           second, possibly-drifted copy of the doc logic. One button
           per doc currently offered (so FOB shipments only see the
           Receiving Manifest button, matching docOptions above). */
        const previewDoc = async (kind) => {
          if (kind === "receiving") {
            setPreviewHtml({ html: buildReceivingSheetHTML(shipment, ctx.suppliers, lang), title: isFobShip ? t.emailWarehouseDocInboundPacking : t.emailWarehouseDocReceiving });
          } else if (kind === "carta-porte") {
            const qrUrl = await generateQRDataUrl(shipment).catch(function () { return ""; });
            setPreviewHtml({ html: buildCartaPorteHTML(shipment, ctx.suppliers, ctx.warehouses, ctx.commodities, ctx.brokers, ctx.seasons, lang, qrUrl), title: t.emailWarehouseDocBoL });
          }
        };
        return (
          <EmailDialog
            lang={lang}
            title={t.emailWarehouseTitle + " — " + (wh ? (wh.shortName || wh.fullName) : "—")}
            recipients={recipients}
            docOptions={docOptions}
            defaultSubject={tpl(t.emailWarehouseDefaultSubject)}
            defaultBody={tplBody || tpl(t.emailWarehouseDefaultBody)}
            fromEmail={shipmentFromEmail}
            emailAccounts={shipmentEmailAccounts}
            /* 2026-10-02 — Arturo: "sin firma personal" for the FOB
               alerts@gfinexus.com send — empty signatures list means
               no signature gets preselected (same pattern already used
               for InvoiceRegistry.js's own clientProtectionReminder
               kind, which sends from the same automated account). */
            signatures={isFobForFrom ? [] : (company.emailSignatures || [])}
            postProcessHtmlBody={(html, resolvedFrom) => appendDisclosureByAccount(html, resolvedFrom, company, readSlSettingsForDisclosure())}
            buildAttachments={buildAttachments}
            buildEmbedHtml={buildEmbedHtml}
            topContent={
              <>
              <div style={{ marginBottom: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                {docOptions.map(function (d) {
                  return (
                    <div key={d.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg)" }}>
                      <span style={{ fontSize: 11, fontWeight: 700 }}>📄 {d.label}</span>
                      <button type="button" onClick={function () { previewDoc(d.key); }} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid var(--color-accent)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                        {t.preview}
                      </button>
                    </div>
                  );
                })}
              </div>
              {/* 2026-10-02 — Arturo: "debo poder agregar un archivo al
                 enviarlo a bodega". Manual attachments, on top of the
                 generated doc(s) above — e.g. a packing diagram, a
                 vendor BOL, photos — merged into buildAttachments at
                 send time, same multi-file pattern as InvoiceRegistry
                 .js's Protection Notice attachments. */}
              <div style={{ marginBottom: 12, padding: "10px 12px", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg)" }}>
                <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{lang === "es" ? "Archivos adicionales" : "Additional files"}</div>
                <FileDropZone lang={lang} multiple={true} onFiles={handleAddWarehouseEmailFiles} style={{ width: "100%" }}>
                  <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "8px 14px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600, width: "100%", boxSizing: "border-box" }}>
                    <span style={{ fontSize: 14, lineHeight: 1 }}>+</span> {lang === "es" ? "Agregar archivos" : "Add files"}
                    <input type="file" multiple style={{ display: "none" }} onChange={function (e) { handleAddWarehouseEmailFiles(e.target.files); e.target.value = ""; }} />
                  </label>
                </FileDropZone>
                {warehouseEmailFiles.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
                    {warehouseEmailFiles.map(function (file, idx) {
                      return (
                        <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "var(--color-bg-card)", borderRadius: 6, border: "1px solid var(--color-border)" }}>
                          <span style={{ fontSize: 13 }}>📎</span>
                          <span style={{ flex: 1, fontSize: 11, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                          <span style={{ fontSize: 10, color: "var(--color-text-muted)", whiteSpace: "nowrap" }}>{(file.size / 1024).toFixed(0)} KB</span>
                          <button type="button" onClick={function () { removeWarehouseEmailFile(idx); }} style={{ fontSize: 11, color: "#ef4444", fontWeight: 600, cursor: "pointer", background: "none", border: "none", padding: 0 }}>✕</button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              </>
            }
            onClose={() => setEmailDialogOpen(false)}
            onSent={() => {
              /* Stamp warehouseEmailSentAt so the shipment list can
                 show a "✉ sent" indicator. Persists via the parent's
                 onSave (same path the rest of DetailPanel uses) so
                 Supabase + LS stay in sync. */
              try { onSave(Object.assign({}, shipment, { warehouseEmailSentAt: new Date().toISOString() })); }
              catch (e) { console.warn("[shipments] could not stamp warehouseEmailSentAt:", e && e.message); }
            }}
          />
        );
      })()}
      {/* 2026-10-16, Arturo: "debes asignar un email dump order
         exclusivo para eso" — fully separate dialog, never shares
         state with the arrival "Email Warehouse" dialog above; same
         recipient-resolution pattern (distribution list → contacts
         fallback), same Ground-Fresh-only from-account (REGLA DE
         ENVÍO DE EMAILS — never SweetLeaf, this is always a
         shipper/warehouse-side send), single fixed attachment. */}
      {emailDumpOrderDialogOpen && (() => {
        const company = loadCompanyProfile();
        const wh = getWarehouses().find(w => String(w.id) === String(shipment.warehouseId));
        const distList = (wh && wh.distributionList) || [];
        const whContactsFallback = filterContactsByCategory((wh && wh.contacts) || [], "shipment")
          .map(c => ({ name: [c.firstName, c.lastName].filter(Boolean).join(" "), email: c.email, checked: true }));
        const recipients = distList.length > 0
          ? distList.filter(c => c && c.email).map(c => ({ name: c.name || "", email: c.email, checked: true }))
          : whContactsFallback;
        const docOptions = [{ key: "dump-order", label: t.sendDumpOrder, checked: true }];
        const tpl = (s) => String(s || "").replace("{lot}", shipment.lot || "—");
        const invoicesForDump = freshInvoicesShp();
        const buildAttachments = async () => {
          return [await buildShipmentDocPdf(shipment, "dump-order", { suppliers: getSuppliers(), invoices: invoicesForDump, lang: lang })];
        };
        const groundFreshAcct = (company.emailAccounts || []).find(function (a) { return /ground.?fresh/i.test(a.name || ""); }) || (company.emailAccounts || [])[0] || null;
        const dumpOrderFromEmail = groundFreshAcct ? groundFreshAcct.email : (company.outboundEmail || "");
        const dumpOrderEmailAccounts = groundFreshAcct ? [groundFreshAcct] : [];
        return (
          <EmailDialog
            lang={lang}
            title={t.dumpOrderTitle.replace("{warehouse}", (wh ? (wh.shortName || wh.fullName) : "—"))}
            recipients={recipients}
            docOptions={docOptions}
            defaultSubject={tpl(t.dumpOrderDefaultSubject)}
            defaultBody={tpl(t.dumpOrderDefaultBody)}
            fromEmail={dumpOrderFromEmail}
            emailAccounts={dumpOrderEmailAccounts}
            signatures={company.emailSignatures || []}
            postProcessHtmlBody={(html, resolvedFrom) => appendDisclosureByAccount(html, resolvedFrom, company, readSlSettingsForDisclosure())}
            buildAttachments={buildAttachments}
            topContent={
              <div style={{ marginBottom: 10, padding: "8px 12px", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 11, fontWeight: 700 }}>📄 {t.sendDumpOrder}</span>
                <button type="button" onClick={function () { setPreviewHtml({ html: buildDumpOrderHTML(shipment, getSuppliers(), invoicesForDump, lang), title: t.sendDumpOrder }); }} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid var(--color-accent)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                  {t.preview}
                </button>
              </div>
            }
            onClose={() => setEmailDumpOrderDialogOpen(false)}
            onSent={() => {
              try { onSave(Object.assign({}, shipment, { dumpOrderSentAt: new Date().toISOString() })); }
              catch (e) { console.warn("[shipments] could not stamp dumpOrderSentAt:", e && e.message); }
            }}
          />
        );
      })()}
      {/* 2026-10-16, Arturo: "despues de que lo subas genera uno para
         informarle al shipper" — same document, second independent
         send, this time to the shipper (quality_report contact
         category, same one Texas QC reports already use) instead of
         the warehouse. Always from Ground-Fresh too (shippers are
         never a SweetLeaf recipient, REGLA DE ENVÍO DE EMAILS). */}
      {emailDumpOrderShipperDialogOpen && (() => {
        const company = loadCompanyProfile();
        const sup = getSuppliers().find(s => String(s.id) === String(shipment.supplierId));
        const supName = sup ? (sup.shortName || sup.fullName || "Shipper") : "Shipper";
        const recipients = filterContactsByCategory(sup && sup.contacts, "quality_report")
          .map(c => ({ name: [c.firstName, c.lastName].filter(Boolean).join(" ") || supName, email: c.email, checked: true }));
        const docOptions = [{ key: "dump-order", label: t.sendDumpOrder, checked: true }];
        const tpl = (s) => String(s || "").replace("{lot}", shipment.lot || "—");
        const invoicesForDump = freshInvoicesShp();
        const dumpOrderRowsForBody = buildDumpOrderRows(shipment, invoicesForDump);
        const buildAttachments = async () => {
          var out = [await buildShipmentDocPdf(shipment, "dump-order", { suppliers: getSuppliers(), invoices: invoicesForDump, lang: lang })];
          dumpOrderShipperFiles.forEach(function (file) { out.push({ filename: file.name, blob: file, mimeType: file.type || "application/octet-stream" }); });
          return out;
        };
        /* 2026-10-16, Arturo: "solo cambia que salga del email de
           alertas de ground-fresh formando solo Ground-Fresh y el
           logo que creaste y tienes no mi firma personal" — same
           dedicated "GFI Automated" account (alerts@gfinexus.com)
           already used for FOB warehouse sends in this very file
           (see gfiAutomatedAcctForWh above) and for every other
           automated/no-personal-signature send in the app
           (InvoiceRegistry.js's clientProtectionReminder,
           WeatherWatchRegistry.js's weather alerts) — never Arturo's
           own personal Ground-Fresh masked account/signature.
           refreshAccountsBeforeSend reuses this exact resolution at
           send time so EmailDialog's own default refetch-and-match-
           by-email never runs (documented real incident, 2026-10-13:
           "GFI Automated" shares its real .email with the separate
           personal "Ground-Fresh" account, so a plain refetch silently
           picks whichever one comes first in company_profile — the
           personal one). A real embedded logo image was tried and
           dropped for automated alert emails earlier this session
           (WeatherWatchRegistry.js, 2026-10-13: never rendered in any
           real inbox) — the branded colored header below stands in
           for it, same convention used everywhere else in the app
           since. */
        const resolveGfAutomatedAccounts = async function () {
          await loadFromSupabase("company_profile", { force: true }).catch(function () {});
          var fresh = loadCompanyProfile();
          var acct = (fresh.emailAccounts || []).find(function (a) { return String((a && a.maskedFrom) || "").toLowerCase() === "alerts@gfinexus.com"; })
            || (fresh.emailAccounts || []).find(function (a) { return (a.name || "").toLowerCase().indexOf("automat") >= 0; })
            || (fresh.emailAccounts || []).find(function (a) { return /ground.?fresh/i.test(a.name || ""); })
            || (fresh.emailAccounts || [])[0] || null;
          return acct ? [acct] : [];
        };
        const dumpOrderAutomatedAcct = (company.emailAccounts || []).find(function (a) { return String((a && a.maskedFrom) || "").toLowerCase() === "alerts@gfinexus.com"; })
          || (company.emailAccounts || []).find(function (a) { return (a.name || "").toLowerCase().indexOf("automat") >= 0; })
          || (company.emailAccounts || []).find(function (a) { return /ground.?fresh/i.test(a.name || ""); })
          || (company.emailAccounts || [])[0] || null;
        const dumpOrderFromEmail = dumpOrderAutomatedAcct ? dumpOrderAutomatedAcct.email : (company.outboundEmail || "");
        const dumpOrderEmailAccounts = dumpOrderAutomatedAcct ? [dumpOrderAutomatedAcct] : [];
        return (
          <EmailDialog
            lang={lang}
            title={t.dumpOrderShipperTitle.replace("{shipper}", supName)}
            recipients={recipients}
            docOptions={docOptions}
            defaultSubject={tpl(t.dumpOrderDefaultSubject)}
            defaultBody={"Favor de revisar las fotografías adjuntas como evidencia del proceso.\n\nQuedamos atentos a cualquier comentario."}
            prependHtml={buildDumpOrderShipperPrependHtml(shipment, dumpOrderRowsForBody)}
            fromEmail={dumpOrderFromEmail}
            emailAccounts={dumpOrderEmailAccounts}
            refreshAccountsBeforeSend={resolveGfAutomatedAccounts}
            signatures={[]}
            postProcessHtmlBody={(html, resolvedFrom) => appendDisclosureByAccount(html, resolvedFrom, company, readSlSettingsForDisclosure())}
            buildAttachments={buildAttachments}
            topContent={
              <>
              <div style={{ marginBottom: 10, padding: "8px 12px", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 11, fontWeight: 700 }}>📄 {t.sendDumpOrderShipper}</span>
                <button type="button" onClick={function () { setPreviewHtml({ html: buildDumpOrderHTML(shipment, getSuppliers(), invoicesForDump, lang), title: t.sendDumpOrderShipper }); }} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid var(--color-accent)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                  {t.preview}
                </button>
              </div>
              {/* 2026-10-16, Arturo: "para el shipper debo poder
                 agregar fotos" — same multi-file pattern already used
                 for Email Warehouse/Quality Report sends. */}
              <div style={{ marginBottom: 12, padding: "10px 12px", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg)" }}>
                <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{lang === "es" ? "Archivos adicionales" : "Additional files"}</div>
                <FileDropZone lang={lang} multiple={true} onFiles={handleAddDumpOrderShipperFiles} style={{ width: "100%" }}>
                  <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "8px 14px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600, width: "100%", boxSizing: "border-box" }}>
                    <span style={{ fontSize: 14, lineHeight: 1 }}>+</span> {lang === "es" ? "Agregar archivos" : "Add files"}
                    <input type="file" multiple style={{ display: "none" }} onChange={function (e) { handleAddDumpOrderShipperFiles(e.target.files); e.target.value = ""; }} />
                  </label>
                </FileDropZone>
                {dumpOrderShipperFiles.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
                    {dumpOrderShipperFiles.map(function (file, idx) {
                      return (
                        <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "var(--color-bg-card)", borderRadius: 6, border: "1px solid var(--color-border)" }}>
                          <span style={{ fontSize: 13 }}>📎</span>
                          <span style={{ flex: 1, fontSize: 11, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                          <span style={{ fontSize: 10, color: "var(--color-text-muted)", whiteSpace: "nowrap" }}>{(file.size / 1024).toFixed(0)} KB</span>
                          <button type="button" onClick={function () { removeDumpOrderShipperFile(idx); }} style={{ fontSize: 11, color: "#ef4444", fontWeight: 600, cursor: "pointer", background: "none", border: "none", padding: 0 }}>✕</button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              </>
            }
            onClose={() => setEmailDumpOrderShipperDialogOpen(false)}
            onSent={() => {
              try { onSave(Object.assign({}, shipment, { dumpOrderShipperSentAt: new Date().toISOString() })); }
              catch (e) { console.warn("[shipments] could not stamp dumpOrderShipperSentAt:", e && e.message); }
            }}
          />
        );
      })()}
      {emailBrokerDialogOpen && (() => {
        /* US customs broker (import) — NOT the Mexican agent
           (brokerMXId, export side; see buildShipmentDocPdf's own
           "Agente Aduanal MX" section for that one). This modal is
           "Enviar embarque a la Aduana" — it must resolve
           shipment.brokerUSId, not brokerMXId. Previously this block
           read brokerMXId here too (copy-paste from the same pattern
           used elsewhere in this file for the Mexican broker), so it
           silently showed the WRONG agent's contacts whenever a US
           broker was actually assigned — fix-dashboard.md.
           Resolution order: shipment.brokerUSId (assigned on this
           specific shipment) → this shipper's configured
           default_us_broker_id (gfi-shipper-settings[supplierId]
           .defaultUSBroker — same field ShipperPortal.js's
           resolveDefaultUSBroker reads) → none (empty recipients,
           same as before). */
        const brUsById = getBrokers().find(function (b) { return String(b.id) === String(shipment.brokerUSId); });
        const shipperSettings = (loadLS("gfi-shipper-settings", {}) || {})[String(shipment.supplierId)] || {};
        const brUsDefault = (!brUsById && shipperSettings.defaultUSBroker)
          ? getBrokers().find(function (b) { return String(b.id) === String(shipperSettings.defaultUSBroker) || String(b._uuid) === String(shipperSettings.defaultUSBroker); })
          : null;
        const brUs = brUsById || brUsDefault || null;
        const sup = getSuppliers().find(function (s) { return String(s.id) === String(shipment.supplierId); });
        const company = loadCompanyProfile();
        /* Broker contacts live in brUs.contacts (same shape as
           Suppliers), not a top-level email/contact/distributionList —
           there is no such field on the broker record. Name format
           follows agentType (mexican → nombre/apPaterno/apMaterno,
           american → firstName/middleName/lastName), same helper
           CustomsBrokerRegistry uses internally as getContactName. */
        const brokerContactName = function (c) {
          return brUs && brUs.agentType === "american"
            ? [c.firstName, c.middleName, c.lastName].filter(Boolean).join(" ")
            : [c.nombre, c.apPaterno, c.apMaterno].filter(Boolean).join(" ");
        };
        /* Filtered by "shipment" emailPreferences category, same
           opt-in pattern SupplierRegistry.js's contacts use (Fase 2) —
           contactReceivesCategory defaults an unconfigured contact to
           "receives everything", so a broker that's never had
           preferences set behaves exactly as before this change. */
        const recipients = filterContactsByCategory(brUs && brUs.contacts, "shipment")
          .map(function (c) { return { name: brokerContactName(c) || "", email: c.email, checked: true }; });
        /* Documents: generated Bill of Lading + Receiving Sheet + each
           uploaded shipment doc individually so user can pick which to
           attach. Uploaded docs go under "shipment" kind namespace. */
        const uploadedDocs = (shipment.documents || []).filter(function (d) { return d && (d.url || d.data); });
        const docOptions = [
          { key: "carta-porte", label: t.emailBrokerDocBoL, checked: true },
          { key: "receiving",   label: t.emailBrokerDocReceiving, checked: true },
        ].concat(uploadedDocs.map(function (d, i) {
          return { key: "uploaded:" + i, label: d.name || ("Doc " + (i + 1)), checked: true };
        }));
        const tpl = function (s) { return String(s || "")
          .replace("{lot}", shipment.lot || "—")
          .replace("{proforma}", shipment.proforma || "—")
          .replace("{shipper}", (sup && sup.shortName) || shipment.shipperName || "—")
          .replace("{eta}", shipment.dateArrival || shipment.dateDeparture || "—"); };
        const ctx = { suppliers: getSuppliers(), warehouses: getWarehouses(), brokers: getBrokers(), commodities: loadLS("gfi-commodities", []), seasons: getSeasons(), lang: lang };
        const buildAttachments = async function (kinds) {
          const out = [];
          for (const k of kinds) {
            if (k === "carta-porte" || k === "receiving") {
              const att = await buildShipmentDocPdf(shipment, k, ctx);
              out.push(att);
            } else if (k.indexOf("uploaded:") === 0) {
              const idx = parseInt(k.split(":")[1], 10);
              const doc = uploadedDocs[idx];
              if (doc && doc.url) {
                try {
                  const r = await fetch(doc.url);
                  const blob = await r.blob();
                  out.push({ filename: doc.name || ("document-" + idx), blob: blob, mimeType: doc.type || blob.type || "application/octet-stream" });
                } catch (e) { console.warn("Could not fetch doc:", doc.name, e && e.message); }
              } else if (doc && doc.data) {
                /* Legacy base64 payload. */
                try {
                  const b64 = doc.data.split(",")[1] || doc.data;
                  const byteChars = atob(b64);
                  const bytes = new Uint8Array(byteChars.length);
                  for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
                  const blob = new Blob([bytes], { type: doc.type || "application/octet-stream" });
                  out.push({ filename: doc.name || ("document-" + idx), blob: blob, mimeType: doc.type || "application/octet-stream" });
                } catch (e) { console.warn("Could not decode legacy doc:", doc.name, e && e.message); }
              }
            }
          }
          return out;
        };
        const groundFreshAcct = (company.emailAccounts || []).find(function (a) { return /ground.?fresh/i.test(a.name || ""); }) || (company.emailAccounts || [])[0] || null;
        const shipmentFromEmail = groundFreshAcct ? groundFreshAcct.email : (company.outboundEmail || "");
        const shipmentEmailAccounts = groundFreshAcct ? [groundFreshAcct] : [];
        return (
          <EmailDialog
            lang={lang}
            title={t.emailBrokerTitle + " — " + (brUs ? (brUs.shortName || brUs.fullName) : "—")}
            recipients={recipients}
            docOptions={docOptions}
            defaultSubject={tpl(t.emailBrokerDefaultSubject)}
            defaultBody={tpl(t.emailBrokerDefaultBody)}
            fromEmail={shipmentFromEmail}
            emailAccounts={shipmentEmailAccounts}
            signatures={company.emailSignatures || []}
            postProcessHtmlBody={function (html, resolvedFrom) { return appendDisclosureByAccount(html, resolvedFrom, company, readSlSettingsForDisclosure()); }}
            buildAttachments={buildAttachments}
            onClose={function () { setEmailBrokerDialogOpen(false); }}
            onSent={function (payload) {
              /* 2026-09-14 — Arturo: "el envío de ese lote a los
                 aduanales no se propagó al shipper y aparece en su
                 portal como si no se hubiera enviado... debió quedar
                 huella digital del envío". brokerEmailSentAt alone
                 only ever lived on the GFI-side shipment — the
                 shipper's portal shows this exact send as its own
                 "BOL enviado al aduanal" block (embarque.bolSentAt/
                 bolSentTo, see ShipperPortal.js), which nothing here
                 ever stamped. Cross-reference the dialog's actual sent-
                 to addresses (payload.to — final list after any manual
                 add/remove) against the pre-send `recipients` (which
                 carry names) to build the same {name,email}[] shape
                 the portal already renders; both fields ride along on
                 the shipment save so handleSave's existing "portal
                 full propagate" step (matched by gfiShipmentId) can
                 carry them into the shipper's shipper_shipments row. */
              var sentTo = ((payload && payload.to) || []).map(function (email) {
                var match = recipients.find(function (r) { return r.email === email; });
                return { name: match ? match.name : "", email: email };
              });
              try { onSave(Object.assign({}, shipment, { brokerEmailSentAt: new Date().toISOString(), bolSentAt: new Date().toISOString(), bolSentTo: sentTo })); }
              catch (e) { console.warn("[shipments] could not stamp brokerEmailSentAt:", e && e.message); }
            }}
          />
        );
      })()}
      {/* Send Quality Report (Texas QC) — 2026-09-14, Arturo: "quiero
         enviarlo por la app al shipper... son internos entre GF y el
         shipper... aqui no aplica ni el bill ni el invoice, es
         especificamente el embarque". Mirrors InvoiceRegistry.js's
         Protection Notice dialog: mandatory single attachment, a
         mandatory "observaciones" placeholder the user must actually
         edit before Send unlocks (blockIfBodyContains), an
         auto-generated PDF (qualityReport.js) always attached, and a
         preview button before sending. */}
      {emailQualityDialogOpen && (function () {
        var company = loadCompanyProfile();
        var sup = getSuppliers().find(function (s) { return String(s.id) === String(shipment.supplierId); });
        var supName = sup ? (sup.shortName || sup.fullName || "Shipper") : "Shipper";
        var contactDisplayName = function (c) {
          return c.name || [c.firstName, c.lastName].filter(Boolean).join(" ") || [c.nombre, c.apPaterno].filter(Boolean).join(" ") || "";
        };
        /* Same "never show zero recipients" fallback as Protection
           Notice — a supplier whose contacts predate the
           "quality_report" preference category would otherwise come
           back empty (see InvoiceRegistry.js's identical comment). */
        /* 2026-09-25: the "never show zero recipients" fallback that used
           to sit here was REMOVED — when no contact was opted into
           quality_report it re-added every contact, overriding explicit
           opt-outs (Arturo: Terey's billing email, opted into only Sales
           Report + Payment Confirmation, showed checked). Zero recipients
           is now shown as-is; extra emails can still be typed in. */
        var recipientSource = filterContactsByCategory(sup && sup.contacts, "quality_report");
        var recipients = recipientSource.map(function (c) { return { name: (contactDisplayName(c) ? contactDisplayName(c) + " · " : "") + supName, email: c.email, checked: true }; });

        /* Product/category picker — see shipmentQualityProductGroups
           comment in qualityReport.js. Only shown when the shipment
           genuinely has more than one group; a single-product shipment
           behaves exactly as before (no picker, report covers
           everything). qualitySelectedProducts stays null (="all")
           until the user actually unchecks something. */
        var qualityProductGroups = shipmentQualityProductGroups(shipment);
        var effectiveProducts = qualitySelectedProducts === null
          ? qualityProductGroups.map(function (g) { return g.key; })
          : qualitySelectedProducts;
        var toggleQualityProduct = function (key) {
          setQualitySelectedProducts(function (prev) {
            var base = prev === null ? qualityProductGroups.map(function (g) { return g.key; }) : prev;
            return base.indexOf(key) >= 0 ? base.filter(function (k) { return k !== key; }) : base.concat([key]);
          });
        };
        var isNarrowed = qualityProductGroups.length > 1 && effectiveProducts.length !== qualityProductGroups.length;
        var narrowedLabel = isNarrowed
          ? qualityProductGroups.filter(function (g) { return effectiveProducts.indexOf(g.key) >= 0; }).map(function (g) { return g.label; }).join(" / ")
          : "";

        var subject = "Reporte de Calidad Texas — Lote# " + (shipment.lot || "—") + (narrowedLabel ? " — " + narrowedLabel : "");
        var body = "A todos:\n\nAdjunto encontrarán el reporte de inspección de calidad del lote# " + (shipment.lot || "—") + (narrowedLabel ? " (" + narrowedLabel + ")" : "") + ".\n\nSaludos,";
        /* Comentarios del equipo de calidad — same rich-paste
           contentEditable treatment as Customer Remarks (Arturo:
           "prefiero apliques el mismo criterio"), inserted into the
           outgoing email between the message and the signature.
           Still MANDATORY (unlike Customer Remarks) — the plain-text
           check strips tags so an empty-looking "<br>" or "<div><br>
           </div>" (what a browser leaves behind after you type then
           delete everything) doesn't count as real content. */
        /* 2026-09-14 — Arturo: the rule above the heading only marked
           where this block STARTS — nothing marked where it ENDS, so
           it read as attached to whatever came after instead of
           clearly bounded on both sides. Same closing rule added below
           the pasted content (same fix applied to Customer Remarks in
           InvoiceRegistry.js). */
        var qualityRemarksPlainText = String(qualityRemarks || "").replace(/<[^>]+>/g, "").trim();
        var qualityRemarksBlock = qualityRemarksPlainText
          ? '<div style="margin-top:20px;padding-top:14px;border-top:1px solid #d0d5dd;"><div style="font-weight:700;font-size:12px;color:#333;margin-bottom:8px;">Comentarios del Equipo de Calidad</div><div style="font-size:13px;color:#1a1a1a;line-height:1.5;">' + qualityRemarks + '</div><div style="margin-top:14px;padding-top:14px;border-top:1px solid #d0d5dd;"></div></div>'
          : "";

        var QUALITY_FILE_KEY = "quality-report-file-" + shipment.id;
        var ALLOWED_QUALITY_EXT = ["pdf", "jpg", "jpeg", "png", "gif", "webp", "doc", "docx", "xls", "xlsx"];
        var handleQualityFile = function (fileList) {
          var accepted = [];
          var rejected = [];
          Array.from(fileList || []).forEach(function (f) {
            var ext = (f.name.split(".").pop() || "").toLowerCase();
            if (ALLOWED_QUALITY_EXT.indexOf(ext) >= 0) accepted.push(f); else rejected.push(f.name);
          });
          if (accepted.length > 0) {
            setQualityReportFiles(function (prev) {
              var next = prev.concat(accepted);
              saveDoc(QUALITY_FILE_KEY, next).catch(function (e) { console.warn("[quality-report-file] save failed", e); });
              return next;
            });
          }
          if (rejected.length > 0) alert((lang === "es" ? "Tipo de archivo no permitido: " : "File type not allowed: ") + rejected.join(", "));
        };
        var removeQualityFile = function (idx) {
          setQualityReportFiles(function (prev) {
            var next = prev.filter(function (_, i) { return i !== idx; });
            saveDoc(QUALITY_FILE_KEY, next).catch(function (e) { console.warn("[quality-report-file] save failed", e); });
            return next;
          });
        };
        /* 2026-10-08, Arturo: "quisiera que al enviar los reportes de
           calidad de twin river, siempre se anexara la hoja de
           tolerancias" — this dialog is per-SHIPMENT (to the shipper),
           not per-client, but a lot's fruit is only "for Twin River"
           once an invoice actually sells it there, so the gate is
           "does any invoice linked to this shipment's lot belong to
           Twin River", same lot-lookup convention used elsewhere in
           this file (freshInvoicesShp — prefers the live Supabase
           cache over the localStorage mirror). The tolerance sheet
           itself is a fixed reference document bundled with the app
           rendered fresh through the same htmlToPdfBlob pipeline every
           other document in this app uses — see
           blackberrySpecAtOrigin.js. */
        /* 2026-10-08, Arturo: "ese anexo solo se debe agregar si la
           factura o embarque contiene blackberry en sus productos
           porque esa hoja de calidad es para blackberry, si fuera
           solo arandano o frambuesa, no se anexa" — the spec sheet is
           Blackberry-specific, so Twin River alone isn't enough; the
           shipment's own product lines must actually include it. */
        /* 2026-10-22, Arturo: "la hoja de defectos que se agrega al
           email debe ser de acuerdo al producto que se esta
           reportando... solo agrega la Blackberry Spec-At-Origin.pdf
           ... al seleccionar solo blueberry, la elimina pero la de
           blueberry no aparece" — this used to be Blackberry-only
           (the Blueberry equivalent didn't exist yet); generalized to
           a small per-product registry so each product attaches its
           OWN tolerance sheet, gated on the groups actually going into
           THIS send (not the whole shipment) — narrowing to just
           Blueberry drops the Blackberry sheet and attaches the
           Blueberry one instead, same Twin River gate for both. */
        var SPEC_SHEETS_BY_PRODUCT = [
          { match: "blackberry", filename: BLACKBERRY_SPEC_FILENAME, build: buildBlackberrySpecAtOriginBlob },
          { match: "blueberry", filename: BLUEBERRY_SPEC_FILENAME, build: buildBlueberrySpecAtOriginBlob },
        ];
        var isTwinRiverLot = freshInvoicesShp().some(function (inv) {
          return String(inv.lot) === String(shipment.lot) && String(inv.clientName || "").toLowerCase().indexOf("twin river") >= 0;
        });
        var specSheetsToAttach = isTwinRiverLot ? SPEC_SHEETS_BY_PRODUCT.filter(function (spec) {
          return qualityProductGroups.some(function (g) {
            return effectiveProducts.indexOf(g.key) >= 0 && String(g.productBase || "").toLowerCase().indexOf(spec.match) >= 0;
          });
        }) : [];
        var buildAttachments = async function () {
          var atts = [];
          qualityReportFiles.forEach(function (file) { atts.push({ filename: file.name, blob: file, mimeType: file.type || "application/octet-stream" }); });
          try {
            var reportBlob = await buildQualityReportBlob(shipment, sup, company, effectiveProducts);
            atts.push({ filename: qualityReportFilename(shipment, sup, narrowedLabel), blob: reportBlob, mimeType: "application/pdf" });
          } catch (e) { console.error("[quality-report] build failed", e); }
          for (var si = 0; si < specSheetsToAttach.length; si++) {
            var spec = specSheetsToAttach[si];
            try {
              var specBlob = await spec.build();
              atts.push({ filename: spec.filename, blob: specBlob, mimeType: "application/pdf" });
            } catch (e) { console.error("[quality-report] tolerance sheet attach failed", spec.filename, e); }
          }
          return atts;
        };
        /* Ground-Fresh only, same pattern as the broker block above
           (groundFreshAcct) — a single-account list means EmailDialog
           never renders a "from" picker, so SweetLeaf can't be chosen
           even by accident. */
        var gfAcct = (company.emailAccounts || []).find(function (a) { return /ground.?fresh/i.test(a.name || ""); }) || (company.emailAccounts || []).filter(function (a) { return (a.name || "").toLowerCase().indexOf("sweetleaf") < 0; })[0] || null;
        var gfAccounts = gfAcct ? [gfAcct] : [];
        var gfSignature = (company.emailSignatures || []).find(function (s) { return s && (s.name || "").toLowerCase().indexOf("ground") >= 0; })
          || (company.emailSignatures || []).find(function (s) { return s && s.isDefault; });
        /* 2026-10-01 — Arturo: "los Quality team comments no son
           indispensables, debo poder enviar el email sin esos
           remarks" — dropped that requirement; the attachment is
           still required (he didn't ask to remove that one). */
        var sendDisabledReason = qualityReportFiles.length === 0
          ? t.emailQualityAttachRequired
          : (effectiveProducts.length === 0 ? (lang === "es" ? "Selecciona al menos un producto" : "Select at least one product") : "");

        return (
          <EmailDialog
            lang={lang}
            title={t.emailQualityReport + " — " + supName}
            recipients={recipients}
            docOptions={[]}
            defaultSubject={subject}
            defaultBody={body}
            fromEmail={gfAcct ? gfAcct.email : (company.outboundEmail || "")}
            emailAccounts={gfAccounts}
            signatures={company.emailSignatures || []}
            defaultSignatureId={gfSignature ? gfSignature.id : undefined}
            sendDisabledReason={sendDisabledReason}
            alwaysBuildAttachments={true}
            buildAttachments={buildAttachments}
            insertBeforeSignatureHtml={qualityRemarksBlock}
            postProcessHtmlBody={function (html, resolvedFrom) {
              var withDisclosure = appendQualityReportDisclosure(html, company, "es");
              return appendDisclosureByAccount(withDisclosure, resolvedFrom, company, readSlSettingsForDisclosure());
            }}
            topContent={
              <>
              {/* 2026-10-22, Arturo: Twin River (and his own team, for any
                 client) sends one quality report PER product, even per
                 product+category within the same lot — so a multi-
                 product shipment needs separate emails, each scoped to
                 the products this specific send covers. Hidden entirely
                 for a single-product shipment (nothing to narrow). */}
              {qualityProductGroups.length > 1 && (
                <div style={{ marginBottom: 10, padding: "10px 12px", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg)" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{lang === "es" ? "Productos incluidos en este reporte" : "Products included in this report"}</div>
                  <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 8 }}>
                    {lang === "es"
                      ? "Este embarque tiene varios productos — desmarca los que no apliquen para enviar un reporte/correo distinto por cada uno."
                      : "This shipment has multiple products — uncheck the ones that don't apply to send a separate report/email per product."}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {qualityProductGroups.map(function (g) {
                      var checked = effectiveProducts.indexOf(g.key) >= 0;
                      return (
                        <label key={g.key} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, cursor: "pointer" }}>
                          <input type="checkbox" checked={checked} onChange={function () { toggleQualityProduct(g.key); }} />
                          {g.label}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <div style={{ marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg)" }}>
                <span style={{ fontSize: 11, fontWeight: 700 }}>📄 {lang === "es" ? "Reporte de Calidad (PDF, se anexa automáticamente)" : "Quality Report (PDF, auto-attached)"}</span>
                <button type="button" onClick={function () { setQualityReportPreviewOpen(true); }} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid var(--color-accent)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                  {lang === "es" ? "Vista previa" : "Preview"}
                </button>
              </div>
              {specSheetsToAttach.map(function (spec) {
                return (
                  <div key={spec.filename} style={{ marginBottom: 10, padding: "8px 12px", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg)" }}>
                    <span style={{ fontSize: 11, fontWeight: 700 }}>📄 {spec.filename} {lang === "es" ? "(hoja de tolerancias, se anexa automáticamente — Twin River)" : "(tolerance sheet, auto-attached — Twin River)"}</span>
                  </div>
                );
              })}
              <div style={{ marginBottom: 12, padding: "10px 12px", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg)" }}>
                <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{lang === "es" ? "Reporte de inspección y fotos (al menos un archivo)" : "Inspection report and photos (at least one file)"}</div>
                <FileDropZone lang={lang} multiple={true} onFiles={handleQualityFile} style={{ width: "100%" }}>
                  <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "8px 14px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600, width: "100%", boxSizing: "border-box" }}>
                    <span style={{ fontSize: 14, lineHeight: 1 }}>+</span> {lang === "es" ? "Agregar archivos" : "Add files"}
                    <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx" style={{ display: "none" }} onChange={function (e) { handleQualityFile(e.target.files); e.target.value = ""; }} />
                  </label>
                </FileDropZone>
                {qualityReportFiles.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
                    {qualityReportFiles.map(function (file, idx) {
                      return (
                        <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "var(--color-bg-card)", borderRadius: 6, border: "1px solid var(--color-border)" }}>
                          <span style={{ fontSize: 13 }}>📎</span>
                          <span style={{ flex: 1, fontSize: 11, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                          <span style={{ fontSize: 10, color: "var(--color-text-muted)", whiteSpace: "nowrap" }}>{(file.size / 1024).toFixed(0)} KB</span>
                          <button type="button" onClick={function () { removeQualityFile(idx); }} style={{ fontSize: 11, color: "#ef4444", fontWeight: 600, cursor: "pointer", background: "none", border: "none", padding: 0 }}>✕</button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Comentarios del equipo de calidad — same contentEditable
                 pattern as InvoiceRegistry.js's Customer Remarks (rich
                 paste survives intact), but mandatory here: the ✱ and
                 the sendDisabledReason gate above both key off
                 qualityRemarksPlainText being non-empty. */}
              <div style={{ marginBottom: 12, padding: "10px 12px", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg)" }}>
                <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{lang === "es" ? "Comentarios del equipo de calidad (opcional)" : "Quality team comments (optional)"}</div>
                <div
                  contentEditable
                  suppressContentEditableWarning
                  ref={function (el) {
                    qualityRemarksRef.current = el;
                    if (el && !el.dataset.seeded) {
                      el.innerHTML = qualityRemarksInitial;
                      el.dataset.seeded = "1";
                    }
                  }}
                  onInput={function () { setQualityRemarks(qualityRemarksRef.current ? qualityRemarksRef.current.innerHTML : ""); }}
                  onPaste={function () { setTimeout(function () { setQualityRemarks(qualityRemarksRef.current ? qualityRemarksRef.current.innerHTML : ""); }, 0); }}
                  onBlur={function () {
                    saveDoc("quality-remarks-" + shipment.id, qualityRemarksRef.current ? qualityRemarksRef.current.innerHTML : "").catch(function (e) { console.warn("[quality-remarks] save failed", e); });
                  }}
                  style={{ minHeight: 70, maxHeight: 220, overflowY: "auto", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--color-border)", background: "var(--color-bg-input)", fontSize: 12, lineHeight: 1.5, outline: "none" }}
                />
              </div>
              </>
            }
            onClose={function () { setEmailQualityDialogOpen(false); }}
            onSent={function () {
              try { onSave(Object.assign({}, shipment, { qualityReportSentAt: new Date().toISOString() })); }
              catch (e) { console.warn("[shipments] could not stamp qualityReportSentAt:", e && e.message); }
            }}
          />
        );
      })()}
      {qualityReportPreviewOpen && (function () {
        var company2 = loadCompanyProfile();
        var sup2 = getSuppliers().find(function (s) { return String(s.id) === String(shipment.supplierId); });
        /* Same narrowing as the send dialog — "Vista previa" must show
           exactly what Send will actually attach, never the whole
           shipment when the user already unchecked a product. */
        var groups2 = shipmentQualityProductGroups(shipment);
        var effective2 = qualitySelectedProducts === null
          ? groups2.map(function (g) { return g.key; })
          : qualitySelectedProducts;
        return (
          <QualityReportPreview
            shipment={shipment}
            supplier={sup2}
            company={company2}
            productFilter={effective2}
            onClose={function () { setQualityReportPreviewOpen(false); }}
          />
        );
      })()}
      {previewHtml && (
        <DocPreviewModal
          title={previewHtml.title}
          html={previewHtml.html}
          lang={lang}
          onClose={() => setPreviewHtml(null)}
        />
      )}
      {trackingOpen && (
        <TrackingLinkModal
          lang={lang}
          payload={shipment}
          onClose={() => setTrackingOpen(false)}
        />
      )}
    </DraggableModal>
  );
}

/* ======================== COMPACT FORM ======================== */
function ShipmentForm({ shipment, shipments, t, lang, onSave, onDelete, onCancel, embedded, lockSupplierId, hideFinancials, onShipmentTypeApi }) {
  const isNew = !shipment;
  const suppliers = useMemo(() => getSuppliers(), []);
  const clients = useMemo(() => getClients(), []);
  const warehouses = useMemo(() => getActiveWarehouses(), []);
  /* getBrokers() reads localStorage["gfi-brokers"], which is only
     dual-written by CustomsBrokerRegistry.js's own screen — a user who
     opens a shipment without having visited Brokers in this browser
     session sees a stale/empty cache regardless of what's actually in
     Supabase (same root cause documented in ExpenseBillRegistry.js's
     allVendorOptions fix). This bit Arturo directly: Godinez is really
     saved with agentType "american" in Supabase, but a stale cached
     copy (from before that field existed, or before it was set) still
     showed it as Mexican, so after splitting brokerOptions by
     agentType the US dropdown fell back to only the manual placeholder
     entries while the MX dropdown absorbed it. Fetch fresh from
     Supabase on mount and bump brokersVersion so `brokers` (and the
     memoized MX/US option lists) recompute with real data. */
  const [brokersVersion, setBrokersVersion] = useState(0);
  useEffect(() => {
    let alive = true;
    loadFromSupabase("brokers").then(rows => {
      if (!alive) return;
      try { localStorage.setItem("gfi-brokers", JSON.stringify(rows)); } catch (e) {}
      setBrokersVersion(v => v + 1);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const brokers = useMemo(() => getBrokers(), [brokersVersion]);

  /* 2026-10-26 — Arturo, dictating a Shipment from his phone: "los
     certificados de exportación de cada línea de producto no me
     despliega los que deben de tener". Same bug class documented
     several times in this file already (brokers above, company
     profile, etc.): getExportCerts()/getAvailableExportCerts() read
     ONLY the localStorage mirror `gfi-export-certs`, which is written
     by ExportCertificateRegistry.js's own load effect — a device that
     has never opened that module (his phone) has that key empty, so
     the per-line cert dropdown always has zero options there, even
     though the real data exists in Supabase `export_certs`. Fetch
     fresh here too, independent of whether that other module has ever
     run on this device, and bump a version so dependent useMemos
     (certFilterOptions/certIdToNum below) recompute. */
  const [, setCertsVersion] = useState(0);
  useEffect(() => {
    let alive = true;
    loadFromSupabase("export_certs", { force: true }).then(rows => {
      if (!alive) return;
      try { localStorage.setItem("gfi-export-certs", JSON.stringify(rows)); } catch (e) {}
      setCertsVersion(v => v + 1);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const baseProducts = useMemo(() => getBaseProducts(), []);

  /* Label options for the pallet manifest. Pulled from existing
     gfi-products labelInitials (deduped). Falls back to the canonical
     in-house set if the catalog is empty. The manifest stores only
     the initials — the catalog provides the full label name. */
  const palletLabelOptions = useMemo(() => {
    const m = new Map();
    loadLS("gfi-products", []).forEach(p => {
      if (p && p.labelInitials) {
        const init = String(p.labelInitials).toUpperCase();
        if (!m.has(init)) m.set(init, p.label || init);
      }
    });
    if (m.size === 0) ["GF", "TR", "GB", "HH", "SL", "FS", "AS"].forEach(i => m.set(i, i));
    return Array.from(m.entries()).map(([init, name]) => ({ value: init, label: init + (name && name !== init ? " — " + name : "") }));
  }, []);

  /* Commodities catalog (gfi-commodities) drives the per-pallet
     Variety dropdown. Each commodity row has a `varieties` string
     array — match by commodity name in either language. */
  const commoditiesCatalog = useMemo(() => loadLS("gfi-commodities", []), []);
  const varietiesFor = useCallback((commodityName) => {
    if (!commodityName) return [];
    const n = String(commodityName).trim().toLowerCase();
    const c = commoditiesCatalog.find(x => (x.nameEn || "").toLowerCase() === n || (x.nameEs || "").toLowerCase() === n);
    return (c && Array.isArray(c.varieties)) ? c.varieties : [];
  }, [commoditiesCatalog]);

  /* Split by agentType (CustomsBrokerRegistry.js's "mexican"/"american"
     field, default "mexican") so the MX dropdown only offers Mexican
     agents and the US dropdown only American ones — previously both
     fields shared one combined list, so the "Agente MX" dropdown also
     showed American brokers (Godinez, etc.), which is why it always
     had more entries than the Mexican-agents catalog. Arturo, 2026-09-16:
     "dropdown de agente aduanal mexicano, muestra mas de los que tengo
     en catalogos". */
  const buildBrokerOptions = useCallback((agentType) => {
    const real = brokers.filter(b => (b.agentType || "mexican") === agentType).map(b => ({ id: b.id, name: b.shortName || b.fullName || "" }));
    real.push({ id: "tmp-other", name: lang === "es" ? "Otro (manual)" : "Other (manual)" });
    return real;
  }, [brokers, lang]);
  const brokerOptionsMX = useMemo(() => buildBrokerOptions("mexican"), [buildBrokerOptions]);
  const brokerOptionsUS = useMemo(() => buildBrokerOptions("american"), [buildBrokerOptions]);

  const [f, setF] = useState(() => {
    if (shipment) {
      const prods = getProducts();
      const lines = (shipment.lines || []).map(l => {
        const merged = { ...EMPTY_LINE, ...l };
        /* Resolve missing category from product registry */
        if (!merged.category && merged.productId) {
          const prod = prods.find(p => String(p.id) === String(merged.productId));
          if (prod) {
            const rawCat = (prod.category || "").toString().toLowerCase();
            merged.category = (rawCat === "organic" || rawCat === "og" || rawCat.includes("org")) ? "OG" : "CV";
          }
        }
        return merged;
      });
      /* Hydrate pallet manifest. Existing pallets keep their values;
         each gets a stable shape via EMPTY_PALLET spread. New
         shipments get a single empty row to start. */
      var hydratedPallets = (shipment.palletManifest || []).map(function (p) { return { ...EMPTY_PALLET, ...p }; });
      if (hydratedPallets.length === 0) hydratedPallets = [{ ...EMPTY_PALLET }];
      var _hydratedTransport = { ...EMPTY_TRANSPORT, ...(shipment.transport || {}) };
      if (!(_hydratedTransport.carrier || "").trim() && shipment.supplierId) {
        var _sups = getSuppliers();
        var _foundSup = _sups.find(function (s) { return String(s.id) === String(shipment.supplierId) || String(s._uuid) === String(shipment.supplierId); });
        if (_foundSup && (_foundSup.shortName || _foundSup.fullName || "").toLowerCase().indexOf("gomez") >= 0) {
          _hydratedTransport.carrier = "AGSA Logistics";
        }
      }
      return { ...shipment, lines, documents: [...(shipment.documents || [])], transport: _hydratedTransport, palletManifest: hydratedPallets };
    }
    /* Shipper Portal: seed the new shipment with the locked supplier
       so the form opens already pointing at the right shipper. The
       picker further down is also disabled when lockSupplierId is
       set, so the value can't drift after the seed. */
    var _seedSupplierId = lockSupplierId || "";
    var _seedShipperName = "";
    var _seedShipperInitials = "";
    if (lockSupplierId) {
      var _sup = suppliers.find(function (s) { return String(s.id) === String(lockSupplierId); });
      if (_sup) {
        _seedShipperName = _sup.shortName || _sup.fullName || _sup.name || "";
        _seedShipperInitials = _sup.initials || "";
      }
    }
    return {
      id: "", lot: nextLot(shipments), dateDeparture: "", dateArrival: "",
      week: "", supplierId: _seedSupplierId, shipperName: _seedShipperName, shipperInitials: _seedShipperInitials,
      /* 2026-10-01 — Arturo: domestic Texas purchases ("FOB") need no
         truck/transport data, export cert, entry #, or customs broker
         of any kind — only vendor name + their invoice # + a lot he
         assigns himself, same product/pallet/QC capture as an import.
         Default "import" so every existing shipment (none of which
         have this field) behaves exactly as before. */
      shipmentType: "import",
      /* 2026-10-01 — Arturo: 2 kinds of Texas QC report, see the
         TEXAS_QC_TIERS_* comment above. Default "internal" (GFI's own
         team) so every existing shipment behaves exactly as before. */
      texasQcMode: "internal",
      proforma: "", shipperInvoice: "", entryNumber: "", vendorReference: "",
      brokerMXId: "", brokerMXName: "", brokerMXManual: "",
      brokerUSId: (function() { var b = brokers.find(function(x) { return (x.shortName || x.fullName || x.name || "").toLowerCase().includes("godinez"); }); return b ? b.id : ""; })(),
      brokerUSName: (function() { var b = brokers.find(function(x) { return (x.shortName || x.fullName || x.name || "").toLowerCase().includes("godinez"); }); return b ? (b.shortName || b.fullName || b.name || "") : ""; })(),
      brokerUSManual: "",
      warehouseId: (function() { var w = warehouses.find(function(x) { return (x.shortName || x.fullName || x.name || "").toLowerCase().includes("american"); }); return w ? w.id : ""; })(),
      warehouseName: (function() { var w = warehouses.find(function(x) { return (x.shortName || x.fullName || x.name || "").toLowerCase().includes("american"); }); return w ? (w.shortName || w.fullName || w.name || "") : ""; })(),
      seasonId: "", status: "draft",
      transport: { ...EMPTY_TRANSPORT, carrier: (function() { var name = (_seedShipperName || "").toLowerCase(); return name.indexOf("gomez") >= 0 ? "AGSA Logistics" : ""; })() },
      /* 2026-10-17, Arturo: "productos por default aparecen 3, solo
         necesito uno por default" — new shipments start with 1 empty
         Product line instead of 3; "+ Add line" still adds more. */
      lines: [{ ...EMPTY_LINE }],
      documents: [], notes: "",
      palletManifest: [{ ...EMPTY_PALLET }],
    };
  });

  /* 2026-10-23, Arturo: "pero no manual, un drop con los shippers que
     tengo, muestra el nombre completo en el drop pero en el campo
     devuelve solo las iniciales" — same full-name-in-dropdown/
     initials-stored pattern as palletLabelOptions above (`triggerLabel`
     collapses to just the initials once picked, `label` shows the full
     name only while the dropdown is open), sourced from the real
     Suppliers catalog. 2026-10-23 follow-up: "necesito me muestre
     todos los shippers porque hay embarques donde toda la fruta es de
     GB pero la exporta TA" — ALL shippers, including the shipment's
     own official one (e.g. an embarque officially under Terey Avocados
     whose fruit all actually belongs to Gomez Berries, or vice versa —
     no case can be assumed irrelevant). */
  const actualShipperOptions = useMemo(() => {
    return getSuppliers()
      .filter(s => s && s.initials)
      .map(s => {
        var nm = s.shortName || s.fullName || s.initials;
        return { value: s.initials, label: nm + " (" + s.initials + ")", triggerLabel: s.initials, search: nm + " " + s.initials };
      });
  }, []);

  const [shipperSource, setShipperSource] = useState(function() {
    /* The Shipments module is for imports only — the source is always
       a shipper. The toggle exists only for legacy records where the
       buyer was manually flagged as a client; everything else (new
       embarques, portal-imported embarques whose stored source is
       "portal") defaults to "shipper" so the correct dropdown
       renders and the shipper auto-loads from supplierId. */
    if (shipment && shipment.shipperSource === "client") return "client";
    return "shipper";
  });
  const [errors, setErrors] = useState({});
  const [showDel, setShowDel] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  /* 2026-10-15, Arturo: "sigue sin guardar" — handleSave's pending-
     items warning used a native window.confirm(), which the shared
     Claude Browser pane this session runs in suppresses by design
     (confirmed via console: "Page dialog suppressed (confirm)...
     confirm() returned false") — a Save with ANY yellow/red/orange
     item (e.g. a line missing its organic certificate) silently did
     nothing, with no visible error, exactly what Arturo reported.
     Replaced with the same custom confirm-banner pattern already
     used for showExitConfirm below — works in any browser, not just
     ones where window.confirm() actually prompts. Holds the built
     warning text (null = hidden). */
  const [saveWarnText, setSaveWarnText] = useState(null);
  /* Tolerance-sheet defects — which pallet rows have their expand
     panel open (UI-only, never persisted), and the in-progress pick
     in each category's "+ Add defect" SearchSelect, keyed by
     "{palletIdx}-{category}" like InvoiceRegistry.js's
     pendingDefectKey. */
  const [qcDefectsOpen, setQcDefectsOpen] = useState({});
  /* Sequential capture (2026-10-15, Arturo: "quiero seleccionar con un
     drop que muestre las 3 condiciones... y conforme seleccione cada
     una me muestre un drop con los defectos de cada una y al
     seleccionar un defecto me abra un campo para el porcentaje")
     — both keyed by palletIdx only (one in-progress pick at a time
     per pallet, unlike the old per-category keying this replaces). */
  const [qcPendingCategory, setQcPendingCategory] = useState({});
  const [pendingPalletDefectKey, setPendingPalletDefectKey] = useState({});
  /* 2026-10-15, Arturo (5th pass): "cada que selecciono una condicion
     por ejemplo quality, debe tener un boton para agregarla al
     renglon, una condicion no se puede capturar dos veces" — picking
     a category in the dropdown is NOT enough by itself to add its
     badge to the row anymore; a category only becomes an active
     "column" in the row (its uppercase badge + a defect picker)
     once explicitly confirmed via the +Add button below. Keyed by
     palletIdx → array of category keys already added for that
     pallet (not yet necessarily holding any defect — a category can
     be added empty and filled in after). */
  const [qcActiveCategories, setQcActiveCategories] = useState({});
  /* 2026-10-15, Arturo (7th pass): "el cuadro add defect esta muy
     grande, solo un boton pequeño con el simbolo de +" — the
     category/defect SearchSelects used to sit inline, always
     visible, at 150-170px wide; now they stay collapsed behind a
     small icon-only "+" button and only expand (narrow) once that
     button is clicked. Category toggle keyed by palletIdx; defect
     toggle keyed by "{palletIdx}-{category}" since several
     categories can each have their own add-picker open at once. */
  const [qcCategoryPickerOpen, setQcCategoryPickerOpen] = useState({});
  const [qcDefectPickerOpen, setQcDefectPickerOpen] = useState({});
  /* 2026-10-01 — Arturo: "nunca lo habia usado, lo use y no hace
     nada" (Clear Form). Replaced the native window.confirm() with the
     same inline Yes/No bar already used for Delete just below — a
     native confirm() dialog can be silently auto-dismissed by some
     browsers/extensions (always resolving false), which would explain
     the button appearing to do nothing with zero visible feedback.
     This in-app pattern is already proven working in this exact form. */
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  /* 2026-10-20, Arturo: "carrier info siempre aparece colapsado,
     cambia eso ahora" — matches openEdit's header/lines/pallets/
     documents/notes, all of which already default open; Transport
     was the only section still starting collapsed. */
  const [showTransport, setShowTransport] = useState(true);

  /* Growers scoped to the shipment's shipper — feeds the per-pallet
     Grower Code picker in the pallet manifest, mirroring the
     ShipperPortal pattern. Reloads when the shipper changes.
     2026-10-08, Arturo: "debo ver los growers asignados al shipper
     para seleccionar de ahi pero tambien agregar manualmente" —
     "ambos" — so this feeds a SearchSelect with allowCustom, not a
     catalog-only picker. */
  var [growers, setGrowers] = useState([]);
  useEffect(function () {
    var suppId = f.supplierId || "";
    if (!suppId) { setGrowers([]); return; }
    var alive = true;
    loadFromSupabase("growers")
      .then(function (rows) {
        if (!alive) return;
        var mine = (rows || []).filter(function (g) {
          return String(g.shipperId) === String(suppId) && g.isActive !== false;
        });
        setGrowers(mine);
      })
      .catch(function () { if (alive) setGrowers([]); });
    return function () { alive = false; };
  }, [f.supplierId]);

  const set = (k, v) => { setF(prev => ({ ...prev, [k]: v })); setDirty(true); setErrors(prev => ({ ...prev, [k]: false })); };
  const setTransport = (k, v) => { setF(prev => ({ ...prev, transport: { ...prev.transport, [k]: v } })); setDirty(true); };
  /* Yellow tint (#FFF8E1) for required-but-empty fields — same palette
     and rule as the portal. Required sets mirror the L1 blocking lists
     in validate() so the user sees yellow on exactly the fields that
     would block submit. Helpers return undefined when filled so the
     default input bg shows through. */
  const TRANSPORT_REQ = ["econTruck", "platesTruck", "econTrailer", "platesTrailer", "caat", "codigoAlpha"];
  /* 2026-09-30 — labelInitials removed, see validateShipmentForPrint's
     comment above for why (derived read-only field, correctly blank
     for a "No Label" product). */
  const PALLET_REQ = ["palletNum", "productBase", "category", "condition", "packType", "size", "unitsCase", "casesPallet", "variety"];
  const LINE_REQ = ["productBase", "productId", "cases", "pallets", "price"];
  const isBlank = v => (v === undefined || v === null || String(v).trim() === "");
  const trBg   = (field, value) => (TRANSPORT_REQ.indexOf(field) >= 0 && isBlank(value)) ? "#FFF8E1" : undefined;
  const palBg  = (field, value) => (PALLET_REQ.indexOf(field) >= 0     && isBlank(value)) ? "#FFF8E1" : undefined;
  const lineBg = (field, value) => (LINE_REQ.indexOf(field) >= 0       && isBlank(value)) ? "#FFF8E1" : undefined;

  /* ---------- Product Lines → Pallet Manifest auto-sync ----------
     Pallet Manifest rows are derived from the line `pallets` count.
     Each line emits N pre-filled rows (product / cat / condition=
     Fresh / pack / size / units/case / label). Manual fields stay
     editable per pallet (palletNum / casesPallet / variety / trace
     / grower). Same matching strategy as the portal: bucket existing
     rows by (productBase|size|category|label) and reuse in FIFO so
     manual edits survive when the user nudges the pallet count. */
  const prefilledPalletFromLine = (line) => {
    const prod = getProducts().find(p => String(p.id) === String(line.productId));
    const rawCat = prod ? (prod.category || prod.cat || "").toString().toLowerCase() : "";
    const catFromProd = (rawCat === "organic" || rawCat === "og" || rawCat.includes("org")) ? "OG" : (rawCat ? "CV" : "");
    return {
      productBase: line.productBase || line.productName || "",
      category: line.category || catFromProd || "",
      condition: "Fresh",
      packType: prod ? (prod.packType || "") : "",
      size: prod ? (prod.size || "") : "",
      unitsCase: prod ? String(prod.unitsCase || prod.unitsPerCase || "") : "",
      labelInitials: prod ? (prod.labelInitials || "") : "",
    };
  };
  const palKeyOf = (p) =>
    (p.productBase || "") + "|" + (p.size || "") + "|" + (p.category || "") + "|" + (p.labelInitials || "");
  const projectManifestFromLines = (lines, currentManifest) => {
    const bucket = {};
    (currentManifest || []).forEach(p => {
      const k = palKeyOf(p);
      (bucket[k] = bucket[k] || []).push(p);
    });
    const next = [];
    (lines || []).forEach(line => {
      const n = parseInt(line.pallets, 10) || 0;
      if (!line.productBase || n <= 0) return;
      const tpl = prefilledPalletFromLine(line);
      const k = palKeyOf(tpl);
      const existing = bucket[k] || [];
      for (let i = 0; i < n; i++) {
        const prev = existing.shift();
        if (prev) {
          next.push({
            ...tpl,
            palletNum: prev.palletNum || "",
            casesPallet: prev.casesPallet || "",
            variety: prev.variety || "",
            traceability: prev.traceability || "",
            julianCode: prev.julianCode || "",
            growerCode: prev.growerCode || "",
            /* Carry the multi-producer breakdown forward too — a
               re-sync from Product Lines must not silently drop it. */
            producers: prev.producers || [],
          });
        } else {
          next.push({ ...EMPTY_PALLET, ...tpl });
        }
      }
    });
    return next;
  };
  const manifestRowHasManualData = (p) =>
    !!(p && (p.palletNum || p.casesPallet || p.variety || p.traceability || p.julianCode || p.growerCode || (p.producers && p.producers.length > 0)));
  const countLossyDropped = (current, projected) => {
    const room = {};
    (projected || []).forEach(p => { const k = palKeyOf(p); room[k] = (room[k] || 0) + 1; });
    let lost = 0;
    (current || []).forEach(p => {
      const k = palKeyOf(p);
      if ((room[k] || 0) > 0) { room[k]--; return; }
      if (manifestRowHasManualData(p)) lost++;
    });
    return lost;
  };

  /* --- Shipper change => auto proforma WITHOUT needing date --- */
  const handleShipperChange = (supplierId) => {
    const sup = suppliers.find(s => String(s.id) === String(supplierId));
    var supName = "";
    if (sup) {
      supName = sup.shortName || sup.fullName || sup.name || sup.companyName || "";
      if (!supName && (sup.ownerNombre || sup.nombre)) supName = [sup.ownerNombre || sup.nombre, sup.ownerApPaterno || sup.apPaterno, sup.ownerApMaterno || sup.apMaterno].filter(Boolean).join(" ");
    }
    const initials = sup ? (sup.initials || (supName || "XX").substring(0, 2).toUpperCase()) : "";
    /* Use date if available, otherwise use current season */
    const season = f.dateDeparture ? detectSeason(f.dateDeparture) : getCurrentSeason();
    /* 2026-10-01 — Arturo: "[el campo de factura] siempre debe estar
       vacio porque el proveedor... me asignara un numero de factura"
       — then, qualifying it — "pero solo en FOB". Proforma and the
       default US broker are GFI's OWN numbers/defaults (fine to keep
       auto-filling for Importación), but none of the three apply to
       FOB at all (no Proforma concept, no broker, and the invoice
       number always comes from the vendor, never from GFI) — skip all
       three here when the shipment is already set to FOB. */
    const isFobNow = f.shipmentType === "fob";
    const proforma = (sup && !isFobNow) ? reserveProformaFromSettings(supplierId, initials, shipments, season ? season.id : null) : "";
    /* Shipper-invoice prefix + default US customs broker, both from
       Shipper Settings when configured for this supplier — replaces
       the old Gomez-only hardcode with the real per-shipper setting
       (Gomez kept as an ultimate fallback for suppliers with nothing
       configured yet, same behavior as before for them). */
    const settingsInvoicePrefix = (sup && !isFobNow) ? invoicePrefixFromSettings(supplierId) : "";
    const settingsUsBroker = (sup && !isFobNow) ? defaultUsBrokerFromSettings(supplierId) : "";
    const usBrokerObj = settingsUsBroker ? brokers.find(b => String(b.id) === String(settingsUsBroker)) : null;
    setF(prev => ({
      ...prev, supplierId,
      shipperName: sup ? (sup.shortName || sup.fullName || sup.name || sup.companyName || "") : "",
      shipperInitials: initials,
      shipperSource: "shipper",
      proforma,
      shipperInvoice: isFobNow ? prev.shipperInvoice : (prev.shipperInvoice ? prev.shipperInvoice
        : settingsInvoicePrefix ? settingsInvoicePrefix
        : supName.toLowerCase().includes("gomez") ? "GB-A-" : prev.shipperInvoice),
      brokerUSId: isFobNow ? prev.brokerUSId : (usBrokerObj ? usBrokerObj.id : prev.brokerUSId),
      brokerUSName: isFobNow ? prev.brokerUSName : (usBrokerObj ? (usBrokerObj.shortName || usBrokerObj.fullName || usBrokerObj.name || "") : prev.brokerUSName),
      seasonId: season ? season.id : prev.seasonId,
      lines: prev.lines.map(l => ({ ...l, exportCertId: "", exportCertNumber: "", exportCertManual: "" }))
    }));
    setDirty(true);
  };

  const handleClientAsShipper = (clientId) => {
    var cl = clients.find(c => String(c.id) === String(clientId));
    var clName = cl ? (cl.shortName || cl.fullName || cl.name || cl.companyName || "") : "";
    var initials = cl ? (cl.initials || (clName || "XX").substring(0, 2).toUpperCase()) : "";
    var season = f.dateDeparture ? detectSeason(f.dateDeparture) : getCurrentSeason();
    /* 2026-10-01 — Arturo: same invoice-prefix behavior as
       handleShipperChange above, mirrored here — "pero solo en FOB"
       qualifies the "always empty" rule: Importación still gets the
       Shipper-Settings prefix default (same as picking via the
       "Shipper" toggle), FOB never does, because for FOB the invoice
       number always comes from the vendor, never from GFI. */
    var isFobNow = f.shipmentType === "fob";
    var settingsInvoicePrefix = (cl && !isFobNow) ? invoicePrefixFromSettings(clientId) : "";
    setF(prev => ({
      ...prev, supplierId: clientId, shipperName: clName, shipperInitials: initials, shipperSource: "client",
      shipperInvoice: isFobNow ? prev.shipperInvoice : (prev.shipperInvoice ? prev.shipperInvoice : (settingsInvoicePrefix || prev.shipperInvoice)),
      seasonId: season ? season.id : prev.seasonId,
    }));
    setDirty(true);
  };

  const handleDateChange = (field, value) => {
    const updates = { [field]: value };
    if (field === "dateDeparture" && value) {
      /* 2026-10-01 — Arturo: "el numero de semana es en relacion a la
         fecha de llegada no a la fecha de salida" — week comes from
         Arrival, never Departure. Only fall back to Departure here
         when Arrival hasn't been entered yet, so a week number still
         shows while the shipment is in transit. */
      if (!f.dateArrival) updates.week = getISOWeek(value);
      const season = detectSeason(value);
      if (season) {
        updates.seasonId = season.id;
        /* Recalc proforma with season if shipper selected */
        if (f.supplierId) updates.proforma = reserveProformaFromSettings(f.supplierId, f.shipperInitials, shipments, season.id);
      }
    }
    if (field === "dateArrival" && value) {
      updates.week = getISOWeek(value);
    }
    setF(prev => ({ ...prev, ...updates })); setDirty(true);
  };

  const handleBrokerChange = (type, id) => {
    const isOther = id === "tmp-other";
    const broker = (type === "MX" ? brokerOptionsMX : brokerOptionsUS).find(b => b.id === id);
    if (type === "MX") {
      setF(prev => ({ ...prev, brokerMXId: id, brokerMXName: isOther ? "" : (broker ? broker.name : ""), brokerMXManual: isOther ? prev.brokerMXManual : "" }));
    } else {
      setF(prev => ({ ...prev, brokerUSId: id, brokerUSName: isOther ? "" : (broker ? broker.name : ""), brokerUSManual: isOther ? prev.brokerUSManual : "" }));
    }
    setDirty(true);
  };

  const handleWarehouseChange = (whId) => {
    const wh = warehouses.find(w => String(w.id) === String(whId));
    const whName = wh ? (wh.shortName || wh.fullName || wh.name || wh.companyName || "") : "";
    /* 2026-10-26, Arturo: "normalmente si la bodega seleccionada en
       shipper header es 5 Brothers, received by debe ser twin river
       ... si la bodega es american cold storage, received by debe
       ser gfi" — then, same day: "esta condicion debe funcionar
       dinamica, es decir, que la condicion no sea el nombre de la
       bodega si no la bodega de recepcion por default asignada a GFI
       o a TR... si algun dia cambio de bodega no necesito crear
       codigo para cambiar esto si no debe leer siempre de la bodega
       que tengo asignada en mi perfil y si tr tambien cambia de
       bodega debe hacer lo mismo" — no warehouse NAME is compared
       here at all anymore. Twin River's side reads live from its own
       Client record (ClientRegistry.js's `warehouseId`, "Client's
       designated warehouse" — the same field Arturo already
       maintains there); GFI's side reads live from whichever
       warehouse is flagged `isDefault` in the Warehouses catalog
       (WarehouseRegistry.js, "Used as default for new shipments").
       Changing either of those settings alone, with zero code
       changes, changes this default too. Still only a default — the
       "Received by" dropdown (f.texasQcMode) stays fully editable for
       the stated exceptions. */
    const twinRiverClient = clients.find(c => ((c.shortName || c.fullName || "").toLowerCase()).indexOf("twin river") >= 0);
    const gfiDefaultWarehouse = warehouses.find(w => w.isDefault);
    var qcDefault = null;
    if (twinRiverClient && twinRiverClient.warehouseId && String(twinRiverClient.warehouseId) === String(whId)) qcDefault = "clarifresh";
    else if (gfiDefaultWarehouse && String(gfiDefaultWarehouse.id) === String(whId)) qcDefault = "internal";
    setF(prev => ({ ...prev, warehouseId: whId, warehouseName: whName, texasQcMode: qcDefault || prev.texasQcMode }));
    setDirty(true);
  };

  /* When the user types a cert number into the manual input, try to
     resolve it against gfi-export-certs. If the typed value matches an
     existing cert AND that cert is eligible for this shipment (same
     shipper + same commodity + Available > 0 OR already assigned to
     this shipment's invoice), upgrade the line from "manual" to a
     proper cert assignment. Otherwise keep it as exportCertManual so
     the typed text isn't lost. */
  const applyManualCertEntry = (idx, typedVal) => {
    setF(prev => {
      var ln = prev.lines[idx];
      if (!ln) return prev;
      var allCerts = getExportCerts();
      var shipments = loadLS("gfi-shipments", []);
      var sup = getSuppliers().find(s => String(s.id) === String(prev.supplierId));
      var shipperName = sup ? (sup.shortName || sup.fullName || "") : "";
      var wantedCommodity = extractCommodity(ln.productBase || ln.productName);
      var typedKey = nrmCertNum(typedVal);
      var billsForManual = loadLS("gfi-bills", []);
      var matched = typedKey ? allCerts.find(c => {
        if (nrmCertNum(c.certNumber) !== typedKey) return false;
        if (c.shipperName !== shipperName && String(c.supplierId) !== String(prev.supplierId)) return false;
        var certCommodity = extractCommodity(c.productName);
        if (!wantedCommodity || !certCommodity || certCommodity !== wantedCommodity) return false;
        if (!isCertAvailableForShipment(c, shipments, prev.shipperInvoice, prev.id, billsForManual)) return false;
        /* Same "expired certs stop being pickable" rule as
           getAvailableExportCerts above — a typed cert number that
           matches an expired cert shouldn't silently auto-link it. */
        if (isCertExpired(c)) {
          var usageM = computeCertUsage(c, shipments, billsForManual);
          var alreadyLinkedM = usageM.some(function (u) {
            if (prev.shipperInvoice && u.shipperInvoice === prev.shipperInvoice) return true;
            if (!prev.shipperInvoice && prev.id && String(u.shipmentId) === String(prev.id)) return true;
            return false;
          });
          if (!alreadyLinkedM) return false;
        }
        return true;
      }) : null;
      var nl;
      if (matched) {
        nl = prev.lines.map((l, i) => i !== idx ? l : { ...l, exportCertId: matched.id, exportCertNumber: matched.certNumber, exportCertManual: "" });
      } else {
        nl = prev.lines.map((l, i) => i !== idx ? l : { ...l, exportCertManual: typedVal, exportCertId: typedVal ? "manual" : "", exportCertNumber: typedVal });
      }
      return { ...prev, lines: nl };
    });
    setDirty(true);
  };

  /* --- Two-step product line update ---
     Also auto-syncs the Pallet Manifest when the field affects the
     row count or the pre-fill template (pallets / productId /
     productBase). Confirms before dropping rows with manual data;
     cancel reverts the line change too so counts stay consistent. */
  const updateLine = (idx, field, value) => {
    /* Compute the new lines snapshot OUTSIDE setF so we can use it
       for the manifest projection BEFORE committing. */
    const newLines = f.lines.map((l, i) => {
      if (i !== idx) return l;
      const updated = { ...l, [field]: value };
      if (field === "productBase") {
        updated.productName = value;
        updated.productId = ""; updated.productDescription = ""; updated.category = "";
        updated.exportCertId = ""; updated.exportCertNumber = ""; updated.exportCertManual = "";
      }
      if (field === "productId" && value) {
        const prod = getProducts().find(p => String(p.id) === String(value));
        if (prod) {
          const rawCat = (prod.category || prod.cat || "").toString().toLowerCase();
          const cat = (rawCat === "organic" || rawCat === "og" || rawCat.includes("org")) ? "OG" : "CV";
          updated.productDescription = prod.description || (prod.size || "") + " " + (prod.unitsCase || "") + "u " + cat + " " + (prod.labelInitials || "");
          updated.category = cat;
          /* Switching to a CV product invalidates any export cert
             that was attached — those are USDA Organic certs only. */
          if (cat === "CV") {
            updated.exportCertId = "";
            updated.exportCertNumber = "";
            updated.exportCertManual = "";
          }
        }
      }
      if (field === "cases" || field === "price") {
        const c = field === "cases" ? (parseInt(value, 10) || 0) : (parseInt(updated.cases, 10) || 0);
        const p = field === "price" ? (parseFloat(value) || 0) : (parseFloat(updated.price) || 0);
        updated.total = (c * p).toFixed(2);
      }
      return updated;
    });

    if (field === "pallets" || field === "productId" || field === "productBase") {
      const projected = projectManifestFromLines(newLines, f.palletManifest || []);
      const lossy = countLossyDropped(f.palletManifest || [], projected);
      if (lossy > 0 && !window.confirm(t.confirmLoseManifestRows.replace("{n}", String(lossy)))) {
        return;
      }
      setF(prev => ({ ...prev, lines: newLines, palletManifest: projected }));
    } else {
      setF(prev => ({ ...prev, lines: newLines }));
    }
    setDirty(true);
  };

  const addLine = () => { setF(prev => ({ ...prev, lines: [...prev.lines, { ...EMPTY_LINE }] })); setDirty(true); };
  const removeLine = (idx) => {
    if (f.lines.length <= 1) return;
    const newLines = f.lines.filter((_, i) => i !== idx);
    const projected = projectManifestFromLines(newLines, f.palletManifest || []);
    const lossy = countLossyDropped(f.palletManifest || [], projected);
    if (lossy > 0 && !window.confirm(t.confirmLoseManifestRows.replace("{n}", String(lossy)))) {
      return;
    }
    setF(prev => ({ ...prev, lines: newLines, palletManifest: projected }));
    setDirty(true);
  };

  /* Pallet manifest handlers. addPallet caps at MAX_PALLETS (30 per
     spec). Updates do partial merges so other fields keep their
     values. removePallet keeps at least one row visible — empty rows
     are stripped at save time, not on remove. */
  const addPallet = () => {
    setF(prev => {
      var current = prev.palletManifest || [];
      if (current.length >= MAX_PALLETS) return prev;
      return { ...prev, palletManifest: [...current, { ...EMPTY_PALLET }] };
    });
    setDirty(true);
  };
  const removePallet = (idx) => {
    var current = f.palletManifest || [];
    if (current.length <= 1) {
      alert(lang === "es" ? "Debe quedar al menos 1 pallet en el manifiesto." : "At least 1 pallet must remain in the manifest.");
      return;
    }
    var target = current[idx];
    var label = target && target.palletNum ? "#" + target.palletNum : "#" + (idx + 1);
    var msg = lang === "es" ? "¿Eliminar pallet " + label + "?" : "Delete pallet " + label + "?";
    if (!window.confirm(msg)) return;
    setF(prev => ({ ...prev, palletManifest: (prev.palletManifest || []).filter((_, i) => i !== idx) }));
    setDirty(true);
  };
  const updatePallet = (idx, field, value) => {
    setF(prev => {
      var next = (prev.palletManifest || []).map((p, i) => i === idx ? { ...p, [field]: value } : p);
      /* Clear size + variety when product changes — both are
         commodity-specific and the previous selections may not exist
         for the new product. */
      if (field === "productBase") {
        next = next.map((p, i) => i === idx ? { ...p, size: "", variety: "" } : p);
      }
      return { ...prev, palletManifest: next };
    });
    setDirty(true);
  };
  /* Tolerance-sheet defects (2026-10-15) — same add/remove/set-pct
     pattern as InvoiceRegistry.js's addAllocDefect/removeAllocDefect/
     setAllocDefectPct for Protection defects, adapted to 3 categories
     per pallet instead of 1 flat list per pallet allocation. cat is
     one of TOLERANCE_DEFECTS_BY_PRODUCT's 3 fixed categories
     (severeCondition/condition/quality — the per-product list is only
     the DEFECTS inside each one). */
  const addPalletDefect = (idx, cat, key) => {
    setF(prev => {
      var next = (prev.palletManifest || []).map((p, i) => {
        if (i !== idx) return p;
        var qc = p.qcDefects || { severeCondition: [], condition: [], quality: [] };
        var cur = Array.isArray(qc[cat]) ? qc[cat] : [];
        if (cur.some(function (e) { return e.key === key; })) return p;
        return { ...p, qcDefects: { ...qc, [cat]: cur.concat([{ key: key, pct: undefined, custom: "" }]) } };
      });
      return { ...prev, palletManifest: next };
    });
    setDirty(true);
  };
  const removePalletDefect = (idx, cat, key) => {
    setF(prev => {
      var next = (prev.palletManifest || []).map((p, i) => {
        if (i !== idx) return p;
        var qc = p.qcDefects || { severeCondition: [], condition: [], quality: [] };
        var cur = Array.isArray(qc[cat]) ? qc[cat] : [];
        return { ...p, qcDefects: { ...qc, [cat]: cur.filter(function (e) { return e.key !== key; }) } };
      });
      return { ...prev, palletManifest: next };
    });
    setDirty(true);
  };
  const setPalletDefectPct = (idx, cat, key, pct) => {
    setF(prev => {
      var next = (prev.palletManifest || []).map((p, i) => {
        if (i !== idx) return p;
        var qc = p.qcDefects || { severeCondition: [], condition: [], quality: [] };
        var cur = Array.isArray(qc[cat]) ? qc[cat] : [];
        return { ...p, qcDefects: { ...qc, [cat]: cur.map(function (e) { return e.key === key ? { ...e, pct: pct } : e; }) } };
      });
      return { ...prev, palletManifest: next };
    });
    setDirty(true);
  };
  const setPalletDefectCustom = (idx, cat, key, custom) => {
    setF(prev => {
      var next = (prev.palletManifest || []).map((p, i) => {
        if (i !== idx) return p;
        var qc = p.qcDefects || { severeCondition: [], condition: [], quality: [] };
        var cur = Array.isArray(qc[cat]) ? qc[cat] : [];
        return { ...p, qcDefects: { ...qc, [cat]: cur.map(function (e) { return e.key === key ? { ...e, custom: custom } : e; }) } };
      });
      return { ...prev, palletManifest: next };
    });
    setDirty(true);
  };
  /* 2026-10-15, Arturo (10th pass): "si me equivocara y seleccionara
     una condicion por ejemplo severe condition y quisiera removerla
     porque no presento ese defecto, no hay forma de quitarla, una vez
     agregada no se puede remover" — removing individual defect chips
     was already possible (✕ per chip), but the category badge itself
     (added via qcActiveCategories, which can exist with zero defects
     in it) had no way back out. Drops it from qcActiveCategories AND
     clears any defects already captured under it — removing the
     whole category means none of it applies to this pallet. */
  const removePalletCategory = (idx, cat) => {
    setQcActiveCategories(function (prev) {
      var cur = prev[idx] || [];
      return { ...prev, [idx]: cur.filter(function (c) { return c !== cat; }) };
    });
    setF(prev => {
      var next = (prev.palletManifest || []).map((p, i) => {
        if (i !== idx) return p;
        var qc = p.qcDefects || { severeCondition: [], condition: [], quality: [] };
        return { ...p, qcDefects: { ...qc, [cat]: [] } };
      });
      return { ...prev, palletManifest: next };
    });
    setDirty(true);
  };
  /* Multi-producer pallets (2026-09-14, Arturo): "ocasionalmente un
     pallet podria estar compuesto por fruta de mas de un productor...
     agregar sub-renglones para cajas, trazabilidad y codigo del
     productor son los unicos datos que podrian ser distintos en un
     pallet... la suma al final debe coincidir con el total". Texas
     QC status and client-reported delivery status stay pallet-wide
     (Arturo, same day: "no aplica a nivel productor sino a nivel
     pallet completo") — only cases/traceability/growerCode move into
     `p.producers[]`. `casesPallet` stays the single source of truth
     everywhere else in the app (weight calc, subtotals, the invoice
     allocation ceiling, the 2 PDF report builders — see the research
     pass before this change), so it's kept in sync as the SUM of
     producers[] here rather than becoming a derived/computed field
     that every one of those call sites would need to learn about.
     "si solo es uno no se agregan sub-renglones" — producers stays []
     (no sub-rows shown) for the default single-producer case; splitting
     seeds producers with the pallet's CURRENT cases/traceability/grower
     as producer #1 (so existing data isn't lost) plus a blank #2, and
     dropping back down to 1 producer collapses that survivor's values
     back onto the pallet-level fields and clears producers to []. */
  const splitPalletProducers = (idx) => {
    setF(prev => {
      var next = (prev.palletManifest || []).map((p, i) => {
        if (i !== idx) return p;
        var existing = { cases: p.casesPallet || "", traceability: p.traceability || "", growerCode: p.growerCode || "" };
        return { ...p, producers: [existing, { cases: "", traceability: "", growerCode: "" }] };
      });
      return { ...prev, palletManifest: next };
    });
    setDirty(true);
  };
  const updateProducer = (palletIdx, prodIdx, field, value) => {
    setF(prev => {
      var next = (prev.palletManifest || []).map((p, i) => {
        if (i !== palletIdx) return p;
        var producers = (p.producers || []).map((pr, j) => j === prodIdx ? { ...pr, [field]: value } : pr);
        var patch = { producers: producers };
        if (field === "cases") patch.casesPallet = producers.reduce(function (s, pr) { return s + (parseInt(pr.cases, 10) || 0); }, 0);
        return { ...p, ...patch };
      });
      return { ...prev, palletManifest: next };
    });
    setDirty(true);
  };
  const addProducer = (palletIdx) => {
    setF(prev => {
      var next = (prev.palletManifest || []).map((p, i) => i === palletIdx ? { ...p, producers: (p.producers || []).concat([{ cases: "", traceability: "", growerCode: "" }]) } : p);
      return { ...prev, palletManifest: next };
    });
    setDirty(true);
  };
  const mergeProducersBack = (palletIdx) => {
    setF(prev => {
      var next = (prev.palletManifest || []).map((p, i) => {
        if (i !== palletIdx) return p;
        var producers = p.producers || [];
        var survivor = producers[0] || { cases: p.casesPallet || "", traceability: "", growerCode: "" };
        return { ...p, producers: [], casesPallet: survivor.cases || p.casesPallet || "", traceability: survivor.traceability || "", growerCode: survivor.growerCode || "" };
      });
      return { ...prev, palletManifest: next };
    });
    setDirty(true);
  };
  const removeProducer = (palletIdx, prodIdx) => {
    setF(prev => {
      var next = (prev.palletManifest || []).map((p, i) => {
        if (i !== palletIdx) return p;
        var producers = (p.producers || []).filter((pr, j) => j !== prodIdx);
        if (producers.length <= 1) {
          /* Collapse back to single-producer mode — the survivor's
             values (if any) become the pallet-level fields again. */
          var survivor = producers[0] || { cases: p.casesPallet || "", traceability: "", growerCode: "" };
          return { ...p, producers: [], casesPallet: survivor.cases || p.casesPallet || "", traceability: survivor.traceability || "", growerCode: survivor.growerCode || "" };
        }
        var casesPallet = producers.reduce(function (s, pr) { return s + (parseInt(pr.cases, 10) || 0); }, 0);
        return { ...p, producers: producers, casesPallet: casesPallet };
      });
      return { ...prev, palletManifest: next };
    });
    setDirty(true);
  };
  /* Drag-reorder for product lines. */
  const dndLines = useDragReorder(f.lines, function(next) { setF(prev => ({ ...prev, lines: next })); setDirty(true); });

  /* Admin-side doc upload now goes to Supabase Storage (bucket
     `shipment-docs`) instead of inlining base64 in the JSONB row —
     LOT 58026 hit 1.5MB of inline base64 and the localStorage
     dual-write blew past the 5MB cap. Path scheme matches the
     shipper-portal pattern: {supplier_id}/{lot|shipmentId}/{filename}
     so admin uploads and portal-promoted docs live side-by-side
     under the same shipment folder.

     The persisted document object carries only { name, size, type,
     storagePath, url, uploadedAt }. The legacy `.data` field is no
     longer written. Reads still fall back to `.data` for historical
     rows that haven't been migrated yet (see the Tools → Migrate
     Shipment Docs to Storage button). */
  const processDocs = async (files) => {
    const arr = Array.from(files || []); if (arr.length === 0) return;
    const supSeg  = safeStoragePathSegment(f.supplierId || f.shipperName || "no-shipper");
    const lotSeg  = safeStoragePathSegment(f.lot || f.id || "no-lot");
    for (let i = 0; i < arr.length; i++) {
      const file = arr[i];
      const fid = Date.now() + "-" + Math.floor(Math.random() * 1000000);
      const ext = extFromShipDoc(file);
      const safeName = safeStoragePathSegment(file.name || ("doc-" + fid));
      const path = supSeg + "/" + lotSeg + "/" + fid + "-" + safeName + (safeName.endsWith("." + ext) ? "" : "." + ext);
      try {
        const up = await uploadFileToStorage(SHIPMENT_DOCS_BUCKET, path, file);
        const entry = {
          id: fid,
          name: file.name,
          size: (file.size / 1024).toFixed(1) + " KB",
          type: file.type || "",
          storagePath: up.path,
          url: up.publicUrl,
          uploadedAt: new Date().toISOString(),
        };
        setF(prev => ({ ...prev, documents: [...(prev.documents || []), entry] }));
        setDirty(true);
      } catch (err) {
        const msg = (err && err.message) ? err.message : String(err);
        const label = (lang === "es" ? "⚠ Error al subir " : "⚠ Upload failed for ") + (file.name || "?") + ": " + msg;
        if (typeof window !== "undefined" && window.__gfiToast) window.__gfiToast(label);
        else if (typeof window !== "undefined" && window.alert) window.alert(label);
      }
    }
  };
  const handleFileUpload = (e) => { processDocs(e.target.files); e.target.value = ""; };
  const removeDoc = (idx) => {
    setF(prev => {
      const next = (prev.documents || []).slice();
      const removed = next.splice(idx, 1)[0];
      /* Fire-and-forget Storage cleanup so we don't leak orphan
         objects when a doc is removed mid-edit. Legacy docs without
         a storagePath are no-ops on the helper. */
      if (removed && removed.storagePath) removeFromStorage(SHIPMENT_DOCS_BUCKET, removed.storagePath);
      return { ...prev, documents: next };
    });
    setDirty(true);
  };

  /* Three-level validation, mirrored from the Shipper Portal so the
     two modules apply the same rules:
       blocking — refuses save; lists every Level 1 problem.
       red     — Level 2 (shipper invoice missing — SAT can fail,
                 ship anyway).
       orange  — Level 3 (export certs / trace / grower codes
                 missing — arrive in transit).
     setErrors() retains a few legacy keys (lot/dupLot/dateDeparture/
     supplierId/lines/manifestEmpty/manifestMismatch) so existing red-
     border styling in the form keeps working; the new `blocking`
     array drives the user-facing summary alert. */
  const validate = (opts) => {
    const ES = lang === "es";
    const blank = v => v == null || String(v).trim() === "";
    const errs = {};
    const blocking = []; const red = []; const orange = [];
    if (blank(f.lot)) { errs.lot = true; blocking.push(ES ? "Lote" : "Lot"); }
    if (blank(f.dateDeparture)) { errs.dateDeparture = true; blocking.push(ES ? "Fecha de salida" : "Departure date"); }
    if (blank(f.dateArrival))   blocking.push(ES ? "Fecha de llegada (ETA)" : "Arrival date (ETA)");
    if (blank(f.supplierId))    { errs.supplierId = true; blocking.push(ES ? "Exportador" : "Shipper"); }
    if (blank(f.warehouseId))   blocking.push(ES ? "Bodega de descarga" : "Destination warehouse");
    if (!isFob && blank(f.brokerMXId)) blocking.push(ES ? "Agente aduanal MX" : "MX customs broker");
    if (!blank(f.lot) && shipments.some(s => s.id !== f.id && String(s.lot) === String(f.lot))) { errs.dupLot = true; blocking.push(ES ? "Lote duplicado" : "Duplicate lot"); }
    if (!(f.lines || []).some(l => l.productId && parseInt(l.cases, 10) > 0)) { errs.lines = true; blocking.push(ES ? "Al menos 1 línea de producto con cajas" : "At least 1 product line with cases"); }
    /* Transport — both plates / both econ / CAAT / ALPHA. Carrier,
       driver and phone are kept on the form but no longer block save.
       Skipped entirely for FOB — there's no truck crossing a border. */
    if (!isFob) {
      const trp = f.transport || {};
      [
        ["platesTruck",  ES ? "Placas tractor"           : "Truck plates"],
        ["econTruck",    ES ? "Número económico tractor" : "Truck unit #"],
        ["platesTrailer",ES ? "Placas caja"              : "Trailer plates"],
        ["econTrailer",  ES ? "Número económico caja"    : "Trailer unit #"],
        ["caat",         ES ? "CAAT"                     : "CAAT"],
        ["codigoAlpha",  ES ? "Código ALPHA"             : "ALPHA code"],
      ].forEach(pair => { if (blank(trp[pair[0]])) blocking.push(pair[1]); });
    }
    /* Pallet manifest: non-empty, all fields (except trace + grower
       code) filled, totals match line totals per product. */
    const realPallets = (f.palletManifest || []).filter(p => p.palletNum || p.productBase);
    if (realPallets.length === 0) {
      errs.manifestEmpty = true;
      blocking.push(ES ? "Manifiesto de pallets vacío" : "Pallet manifest empty");
    } else {
      /* 2026-09-30 — labelInitials removed, see
         validateShipmentForPrint's comment near the top of this file
         for why (derived read-only field, correctly blank for a "No
         Label" product). */
      const palletReq = ["palletNum", "productBase", "category", "condition", "packType", "size", "unitsCase", "casesPallet", "variety"];
      const bad = realPallets.filter(p => palletReq.some(k => blank(p[k]))).length;
      if (bad > 0) blocking.push((ES ? bad + " pallet(s) con campos obligatorios faltantes" : bad + " pallet(s) missing required fields"));
      const bucket = (arr, key) => {
        const out = {};
        arr.forEach(l => {
          const name = (l.productBase || l.productName || "").trim();
          if (!name) return;
          const k = name.toLowerCase();
          if (!out[k]) out[k] = { label: name, total: 0 };
          out[k].total += parseInt(l[key], 10) || 0;
        });
        return out;
      };
      const byLine = bucket(f.lines || [], "cases");
      const byMan = bucket(realPallets, "casesPallet");
      const keys = {}; Object.keys(byLine).forEach(k => keys[k] = true); Object.keys(byMan).forEach(k => keys[k] = true);
      const mm = [];
      Object.keys(keys).forEach(k => {
        const l = byLine[k] || { label: (byMan[k] || {}).label || k, total: 0 };
        const m = byMan[k] || { label: l.label, total: 0 };
        if (l.total === m.total) return;
        const diff = l.total - m.total;
        const msg = lang === "es"
          ? l.label + ": " + l.total + " cajas en líneas, " + m.total + " en manifiesto — " + Math.abs(diff) + (diff > 0 ? " faltan" : " sobran")
          : l.label + ": " + l.total + " cases in lines, " + m.total + " in manifest — " + Math.abs(diff) + (diff > 0 ? " missing" : " extra");
        mm.push(msg);
      });
      if (mm.length > 0) { errs.manifestMismatch = mm; blocking.push(ES ? "Las cajas del manifiesto no coinciden con las líneas" : "Pallet manifest cases don't match lines"); mm.forEach(msg => blocking.push("  · " + msg)); }
      /* L1: pallet numbers must be unique within the manifest. */
      const pnSeen = {};
      let dupePnCount = 0;
      realPallets.forEach(p => {
        const v = String(p.palletNum || "").trim().toUpperCase();
        if (!v) return;
        pnSeen[v] = (pnSeen[v] || 0) + 1;
      });
      Object.keys(pnSeen).forEach(k => { if (pnSeen[k] > 1) dupePnCount++; });
      if (dupePnCount > 0) {
        blocking.push(ES ? dupePnCount + " número(s) de pallet duplicado(s) en el manifiesto"
                         : dupePnCount + " duplicate pallet number(s) in the manifest");
      }
      /* L1: total pallets across all lines must equal the manifest
         row count. Auto-sync should keep these in lockstep; this
         check catches any drift (e.g., a legacy record loaded
         pre-sync). */
      const totalLinePallets = (f.lines || []).reduce((s, l) => s + (parseInt(l.pallets, 10) || 0), 0);
      if (totalLinePallets > 0 && totalLinePallets !== realPallets.length) {
        blocking.push((ES ? "Pallets del Manifiesto (" + realPallets.length + ") no coinciden con Líneas de Producto (" + totalLinePallets + ")"
                          : "Pallet Manifest rows (" + realPallets.length + ") don't match Product Lines pallet total (" + totalLinePallets + ")"));
      }
    }
    /* Level 2 — shipper invoice. Pending allowed; ship can't wait
       for SAT. */
    if (blank(f.shipperInvoice)) red.push(ES ? "Número de Factura del Exportador — Pendiente" : "Shipper Invoice number — Pending");
    /* Level 3 — export certs (OG only) + trace/grower. USDA Organic
       export certs don't apply to Conventional (CV) products, so CV
       lines are intentionally skipped — the validator only flags
       missing certs on Organic lines. */
    /* Export cert / traceability / grower code — none of these apply
       to an FOB (domestic) purchase, so neither warning fires for it. */
    if (!isFob) {
      var noCert = 0;
      (f.lines || []).filter(l => (l.productBase || l.productId) && l.category === "OG").forEach(l => {
        var hasCert = (l.exportCertId && l.exportCertId !== "__manual__" && l.exportCertId !== "tmp-other") || !blank(l.exportCertNumber) || !blank(l.exportCertManual);
        if (!hasCert) noCert++;
      });
      if (noCert > 0) orange.push((ES ? noCert + " línea(s) orgánica(s) sin certificado — Pendiente en tránsito" : noCert + " organic line(s) without certificate — Pending in transit"));
      var noTrace = 0;
      /* Multi-producer pallets (2026-09-14) — check EACH producer's own
         cases/traceability/growerCode instead of the pallet-level
         fields, which are no longer authoritative once producers[] has
         more than 1 entry (see splitPalletProducers). */
      realPallets.forEach(p => {
        if (p.producers && p.producers.length > 1) {
          var incomplete = p.producers.some(function (pr) { return !pr || (parseInt(pr.cases, 10) || 0) <= 0 || (blank(pr.traceability) || blank(pr.growerCode)); });
          if (incomplete) noTrace++;
          return;
        }
        if (blank(p.traceability) && blank(p.julianCode)) noTrace++;
        else if (blank(p.growerCode)) noTrace++;
      });
      if (noTrace > 0) orange.push((ES ? noTrace + " pallet(s) sin trazabilidad o código de productor" : noTrace + " pallet(s) missing traceability or grower code"));
    }
    setErrors(errs);
    /* Block when there's anything in blocking. When `opts.surfaceWarnings`
       is set, the caller wants the warning summary to confirm — we hand
       back the full triple so they can render it. */
    if (opts && opts.returnTriple) return { ok: blocking.length === 0, blocking, red, orange };
    return blocking.length === 0;
  };

  /* Clear all editable fields. The lot + id consecutives are
     preserved (the user shouldn't lose the assigned number just
     because they want to redo the entry); the seasonId is also
     preserved so the date-derived season classification stays. */
  const doClearForm = () => {
    setShowClearConfirm(false);
    setF(prev => ({
      ...prev,
      /* Reset all data fields. Keeping id, lot, seasonId, plus the
         shipment status (so a "received"/"billed" shipment doesn't
         silently downgrade just because the user cleared the form). */
      dateDeparture: "", dateArrival: "", week: "",
      supplierId: "", shipperName: "", shipperInitials: "",
      proforma: "", shipperInvoice: "", entryNumber: "", vendorReference: "",
      brokerMXId: "", brokerMXName: "", brokerMXManual: "",
      brokerUSId: "", brokerUSName: "", brokerUSManual: "",
      warehouseId: "", warehouseName: "",
      transport: { ...EMPTY_TRANSPORT },
      lines: [{ ...EMPTY_LINE }],
      palletManifest: [{ ...EMPTY_PALLET }],
      documents: [],
      notes: "",
    }));
    setErrors({});
    setDirty(true);
  };

  const handleSave = () => {
    const v = validate({ returnTriple: true });
    /* Saving is always allowed — the user can save an incomplete
       embarque and finish it later. Print and submit are the actions
       that refuse on validation errors. If there are warnings, show
       a soft summary so the user knows what's still pending but
       don't block. */
    if (v.blocking.length > 0 || v.red.length > 0 || v.orange.length > 0) {
      let warn = lang === "es" ? "El embarque tiene pendientes (se guardará de todos modos):\n\n" : "The shipment has pending items (will save anyway):\n\n";
      if (v.blocking.length > 0) warn += (lang === "es" ? "AMARILLO (obligatorios):\n" : "YELLOW (required):\n") + v.blocking.map(m => "  • " + m).join("\n") + "\n\n";
      if (v.red.length > 0)      warn += (lang === "es" ? "ROJO (pendientes):\n" : "RED (pending):\n") + v.red.map(m => "  • " + m).join("\n") + "\n\n";
      if (v.orange.length > 0)   warn += (lang === "es" ? "NARANJA (en tránsito):\n" : "ORANGE (in transit):\n") + v.orange.map(m => "  • " + m).join("\n") + "\n\n";
      warn += lang === "es" ? "¿Continuar guardando?" : "Continue saving?";
      setSaveWarnText(warn);
      return;
    }
    doSaveConfirmed();
  };
  const doSaveConfirmed = () => {
    setSaveWarnText(null);
    const toSave = {
      ...f, id: f.id || nextId(shipments), week: f.week || getISOWeek(f.dateArrival || f.dateDeparture),
      /* brokerXxName resolves from brokerXxManual when the user picked
         "Other (manual)" OR when they typed into the empty-catalog
         fallback input without picking anything from the dropdown
         (brokerUSId stays blank, brokerUSManual carries the name). */
      brokerMXName: (f.brokerMXId === "tmp-other" || (!f.brokerMXId && f.brokerMXManual)) ? f.brokerMXManual : f.brokerMXName,
      brokerUSName: (f.brokerUSId === "tmp-other" || (!f.brokerUSId && f.brokerUSManual)) ? f.brokerUSManual : f.brokerUSName,
      lines: f.lines.filter(l => l.productId || l.productBase).map(l => ({
        ...l,
        exportCertNumber: l.exportCertNumber || l.exportCertManual || "",
      })),
      /* Drop fully-empty pallet rows on save so the persisted manifest
         only carries real entries. A row counts as non-empty when ANY
         field is set (palletNum, product, cases, etc.) — being lenient
         keeps a half-typed row from disappearing on accidental save. */
      palletManifest: (f.palletManifest || []).filter(function (p) {
        return (p.palletNum && String(p.palletNum).trim())
          || p.productBase
          || p.size
          || (p.casesPallet && parseFloat(p.casesPallet) > 0)
          || (p.unitsCase && parseFloat(p.unitsCase) > 0)
          || p.labelInitials
          || p.traceability
          || p.growerCode;
      }),
    };
    /* Advance the shared Settings proforma counter past whatever this
       save used, so the NEXT shipment (here or in the Shipper Portal)
       for this supplier doesn't reuse the same number. Idempotent —
       safe to call on every save, not just brand-new shipments. */
    commitProformaToSettings(toSave.supplierId, toSave.proforma);
    onSave(toSave);
  };

  const tryCancel = () => { if (dirty) setShowExitConfirm(true); else onCancel(); };
  const isMexican = useMemo(() => isSupplierMexican(f.supplierId), [f.supplierId]);
  /* 2026-10-01 — Arturo: domestic Texas purchase ("FOB"), see the
     shipmentType comment on the new-shipment seed above. */
  const isFob = f.shipmentType === "fob";
  const setShipmentType = (v) => {
    setF(prev => {
      var next = { ...prev, shipmentType: v };
      if (v === "fob") {
        /* Clear import-only fields so none of them silently ride
           along on an FOB record once their inputs disappear.
           2026-10-01 — Arturo found the real gap: a shipment opens
           defaulted to Importación, so picking a vendor BEFORE
           switching to FOB already baked in that vendor's Shipper-
           Settings invoice prefix (e.g. Terey Avocados' "TA-A") — and
           since nothing cleared it on switching type, it then "se
           queda fijo aunque cambie de vendor" (handleShipperChange/
           handleClientAsShipper correctly skip re-adding a prefix
           once already in FOB, but were never clearing a prefix that
           leaked in from before the switch). Clearing it here closes
           that gap — the invoice number is never GFI's to assign for
           FOB, so switching to FOB always blanks it out. */
        next.shipperInvoice = "";
        next.proforma = ""; next.entryNumber = "";
        next.brokerMXId = ""; next.brokerMXName = ""; next.brokerMXManual = "";
        next.brokerUSId = ""; next.brokerUSName = ""; next.brokerUSManual = "";
        next.transport = { ...EMPTY_TRANSPORT };
        /* Traceability/Grower Code (and any producer split, which only
           exists to capture those two per producer) don't apply to a
           domestic purchase either — Arturo: "tampoco es necesario
           tracebolity, grower code". */
        next.palletManifest = (next.palletManifest || []).map(function (p) {
          return { ...p, traceability: "", growerCode: "", producers: [] };
        });
      }
      return next;
    });
    setDirty(true);
  };
  /* 2026-10-17, Arturo: "shipment type a la barra de titulo despues de
     edit shipment" — when embedded inside DetailPanel's own modal
     (view mode's "Edit" toggle, the common path), ShipmentForm doesn't
     render its own title bar — DetailPanel's does. Reports the toggle's
     live state + setter up to whichever parent asked for it
     (onShipmentTypeApi), so that parent can render the SAME toggle
     inside ITS OWN titleLeftActions and have clicks there actually
     mutate this component's real f.shipmentType — not a disconnected
     copy. No-op when no parent is listening (e.g. the standalone
     new-shipment modal, which renders its own titleLeftActions with
     direct access to isFob/setShipmentType, no lifting needed there). */
  useEffect(() => {
    if (onShipmentTypeApi) onShipmentTypeApi({ isFob, setShipmentType });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFob]);
  /* 2026-10-01 — Arturo: "necesito poder distinguir... 2 columnas o
     de tres" (ClariFresh, Twin River) — texasQcMode controls ONLY the
     3rd column (Severe Condition); the tier dropdown options are the
     same full TEXAS_QC_TIERS list in every mode (see that constant's
     comment — "los conceptos de calidad pueden convivir juntos sin
     importar de quien es el reporte"). Switching modes doesn't clear
     anything: a tier value that no longer has a visible column (e.g.
     Severe Condition picked under ClariFresh, then switched back to
     internal) just stops rendering until switched back, same
     non-destructive pattern as the rest of this form. */
  const isClariFresh = f.texasQcMode === "clarifresh";
  const setTexasQcMode = (v) => { set("texasQcMode", v); };
  const errS = { borderColor: "#ef4444" };
  /* 2026-10-17, Arturo: "todos los campos del shipper header excepto
     lot y broker us deben aparecen en amarillo" — then: "ese amarillo
     no, el mismo de products, hazlo uniforme" — matches lineBg's exact
     yellow (#FFF8E1, used for empty-required Product line fields)
     instead of a separately invented amber tint.
     2026-10-20, Arturo: "dejaste el color amarillo fijo, si tiene
     datos cambia a blanco" — was a static object (always yellow,
     regardless of value), unlike its siblings trBg/palBg/lineBg above
     (all conditional on isBlank) — a real bug from that 2026-10-17
     pass. Now a function, same conditional pattern as those three:
     yellow only while empty, otherwise falls back to the field's own
     background. Also squares the corners ("ningún campo de captura
     debe tener esquinas redondeadas") for every field that uses it. */
  const reqBg = (value) => ({ background: isBlank(value) ? "#FFF8E1" : undefined, borderRadius: 0, minHeight: 25, fontSize: 10 });
  /* 2026-10-17, Arturo: "movieras [la tarjeta de recepcion] a la
     seccion del renglon donde esta received al extremo izquierdo
     replicando como esta en el modo no editable, pero le agregarias
     un dropdown para hacer la captura de fecha hora y notas" — the
     standalone colored card (texasQcMode/date/time/notes) collapses
     into a single left-aligned label + popover in the status row,
     same compact-chip pattern as the view-mode DetailPanel. */
  const [receivedPopoverOpen, setReceivedPopoverOpen] = useState(false);
  useEffect(() => {
    if (!receivedPopoverOpen) return;
    const close = () => setReceivedPopoverOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [receivedPopoverOpen]);
  /* 2026-10-17, Arturo: "ahora si secciona como en el modo no
     editable con sus tarjetas, primera seccion shipmment header etx,
     replica el modo no editable aqui" — same collapsible-card shell
     as the view-mode DetailPanel (SectionHeader there), now local to
     this form too. Transport keeps its own pre-existing showTransport
     state (just re-skinned to match, see below) — everything else
     (header/lines/pallets/documents/notes) gets a fresh entry here,
     all starting open like the view-mode spec. */
  const [openEdit, setOpenEdit] = useState({ header: true, lines: true, pallets: true, documents: true, notes: true });
  const toggleEdit = (id) => setOpenEdit(o => ({ ...o, [id]: !o[id] }));
  function EditSectionHeader({ id, title, count, embedded }) {
    const isOpen = openEdit[id];
    /* 2026-10-17, Arturo: "no se ve correcto, no tiene margenes a los
       lados tampoco products" — this form's scrollable body has no
       outer side padding (view mode's does, "8px 22px 22px"), and
       every pre-existing row in this form supplies its own 20px side
       margin individually rather than relying on a parent — this
       header needed the same, explicit. */
    /* 2026-10-20, Arturo: "replica [shipment header lado a lado con
       carrier info] en el modo edicion" — `embedded` drops the
       hardcoded 20px side margin + calc(100%-40px) width so this same
       button can sit inside a half-width flex column instead (the
       column's own parent row supplies the 20px outer gutter). Every
       other caller (Products/Pallet Manifest/Documents/Notes) is
       unaffected — they just don't pass the prop. */
    return (
      <button type="button" onClick={() => toggleEdit(id)} style={embedded
        ? { display: "flex", alignItems: "center", gap: 8, width: "100%", margin: "14px 0 0", padding: "10px 12px", background: "repeating-linear-gradient(180deg, rgba(0,0,0,0.025) 0px, rgba(0,0,0,0.025) 1px, transparent 1px, transparent 3px), linear-gradient(180deg, #eef0f2 0%, #d2d5d9 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08)", border: "1px solid #b9bcc0", borderRadius: 0, cursor: "pointer", textAlign: "left" }
        : { display: "flex", alignItems: "center", gap: 8, width: "calc(100% - 40px)", margin: "14px 20px 0", padding: "10px 12px", background: "repeating-linear-gradient(180deg, rgba(0,0,0,0.025) 0px, rgba(0,0,0,0.025) 1px, transparent 1px, transparent 3px), linear-gradient(180deg, #eef0f2 0%, #d2d5d9 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08)", border: "1px solid #b9bcc0", borderRadius: 0, cursor: "pointer", textAlign: "left" }}>
        <span style={{ fontSize: 11, color: "#5a6775", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.15)", transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s", display: "inline-block", width: 10 }}>{"▶"}</span>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", flex: 1 }}>{title}</span>
        {count !== undefined && <span style={{ fontSize: 10, color: "#5a6775", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.15)", fontFamily: "'JetBrains Mono', monospace" }}>{count}</span>}
      </button>
    );
  }

  /* 2026-10-17, Arturo: first asked to move this up to the window's
     own title bar ("la burbuja de today 290 subela hasta la barra de
     titulo"), then reversed: "no subas today 290 hasta arriva,
     colocalo ahi en la barra de la tarjeta de pallet manifest antes
     de 1/30" — lives in the Pallet Manifest card's own EditSectionHeader
     bar instead, right before the "n/30" count. Defined up here (not
     after `if (embedded) return body`) so it's available in both the
     embedded and standalone render paths — EditSectionHeader itself is
     used from within `body`, shared by both. */
  const julianBadge = (function () {
    var today = new Date();
    var start = new Date(today.getFullYear(), 0, 0);
    var diff = today - start;
    var julian = Math.floor(diff / 86400000);
    return (
      /* 2026-10-17, Arturo: "today cambialo remueve el icono, agrega
         julian day y dale formato como pallet manifest como impreso
         en metal" — no 📅, no pill/background, "Julian Day" wording,
         same embossed-on-metal text treatment as the EditSectionHeader
         titles (dark text + light-below/dark-above text-shadow),
         instead of the old blue badge pill. */
      <span title={lang === "es" ? "Día Juliano actual (día del año)" : "Today's Julian day (day of year)"} style={{ fontSize: 10, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.15)", fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap" }}>
        {lang === "es" ? "Día Juliano" : "Julian Day"}: <span style={{ fontWeight: 800 }}>{julian}</span>
      </span>
    );
  })();

  const body = (
    <>
        {/* TOP STRIP — 2026-10-17, Arturo: "mueve a la barra de titulo
            el shipment type, y el status del embarque (draft) para que
            no se pierda el shipment type" + "vamos a reordenar, de
            arriba hacia abajo" + "rectangulos con relieves 3d y el
            color metalico" — Shipment Type used to be the first thing
            INSIDE the scrollable BODY div below, so it scrolled out of
            view on a long form; moved up here as a fixed sibling next
            to the status sub-header (this flex-column modal only lets
            the BODY div below scroll), top to bottom: Shipment Type
            first, Status/actions below. 2026-10-17, Arturo: "aplica
            este mismo diseño al modo edicion del embarque" — updated
            from the first (gold/gunmetal) metallic pass to match the
            design that actually landed on the view-mode window:
            brushed-metal silver strip (same repeating-linear-gradient
            texture as the section headers/toolbar), square corners (no
            border-radius, per "no me gustaron los titulos redondeados"),
            the muted blue accent for the toggle's active state instead
            of gold, and embossed text-shadow throughout. */}
        <div style={{ padding: "10px 20px", borderBottom: "1px solid #b9bcc0", background: "repeating-linear-gradient(180deg, rgba(0,0,0,0.02) 0px, rgba(0,0,0,0.02) 1px, transparent 1px, transparent 3px), linear-gradient(180deg, #f2f3f4 0%, #dcdfe2 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)" }}>
          {/* 2026-10-17, Arturo: "shipment type a la barra de titulo
              despues de edit shipment" — moved out of this strip
              entirely: lives in titleLeftActions now, either on this
              form's own DraggableModal (standalone/new-shipment path)
              or on DetailPanel's (embedded/edit-existing path, via the
              onShipmentTypeApi lift above). */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {/* 2026-10-20, Arturo: "received by esta ligeramente mas
              abajo que open bill y open invoice" — this wrapper was
              `display:inline-block` while its sibling ("Links:" span,
              a few lines below) is `inline-flex` with its own
              alignItems:center; matching that display mode here too
              (its child button already has a fixed height) removes
              the sub-pixel vertical drift between the two. */}
          <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }} onClick={function (e) { e.stopPropagation(); }}>
            {/* 2026-10-17, Arturo: tried "Shipment Arrival" as a fixed
                label, then reverted: "revierte shipment arrival como
                estaba" — back to the dynamic GFI/Twin River text,
                same as view mode. Then: "receive by agregale tambien
                un rectangulo 3d silver con relieve metalico" — same
                brushed-metal plate as the other buttons here. */}
            {/* 2026-10-20, Arturo: "ahora lo mismo en modo edit... nada
                redondeado" — same dark-silver plate trigger/panel as the
                view-mode Received-by dropdown, square corners, black
                embossed text, and the inner fields get the same sunken
                Mac-silver look as the Filters popover (filterFieldPlate)
                instead of the plain white Filters-style inputs. */}
            {/* 2026-10-20, Arturo: "no estan bien alineadas, esta off
                por muy poco" — line-height alone left ~1-2px of drift
                between a <span> and <button>s at different font/UA
                defaults. Switched to an explicit fixed height +
                inline-flex centering (here and on every sibling in
                this row: linkBtnStyleEdit, the Billed badge, and the
                3 status-toggle buttons below) so they're pixel-exact
                regardless of element type. */}
            <button type="button" onClick={() => setReceivedPopoverOpen(!receivedPopoverOpen)} style={{ height: 21, boxSizing: "border-box", display: "inline-flex", alignItems: "center", padding: "0 10px", borderRadius: 0, border: "1px solid #7d838c", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)", cursor: "pointer", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)", fontFamily: "inherit" }}>
              {isClariFresh ? (lang === "es" ? "Recepción en Twin River" : "Received by Twin River") : (lang === "es" ? "Recepción en GFI" : "Received by GFI")}
              {f.receivedDate && <span style={{ marginLeft: 6, fontWeight: 600, color: "#1a2636", textTransform: "none", letterSpacing: "normal" }}>{fmtDate(f.receivedDate) + (f.receivedTime ? " " + f.receivedTime : "")}</span>}
              <span style={{ marginLeft: 4, opacity: 0.6 }}>▾</span>
            </button>
            {receivedPopoverOpen && (
              <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, padding: 12, background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderRadius: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.18)", zIndex: 50, width: 320 }}>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 9, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.5)", fontWeight: 600, marginBottom: 2 }}>{lang === "es" ? "Recibido por" : "Received by"}</div>
                  <SearchSelect
                    value={f.texasQcMode || "internal"}
                    onChange={setTexasQcMode}
                    options={[
                      { value: "internal", label: "GFI" },
                      { value: "clarifresh", label: "Twin River" },
                    ]}
                    style={filterFieldPlate}
                  />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                  <div><div style={{ fontSize: 9, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.5)", fontWeight: 600, marginBottom: 2 }}>{lang === "es" ? "Fecha de recepción" : "Received date"}</div><input type="date" value={f.receivedDate || ""} onChange={e => set("receivedDate", e.target.value)} style={Object.assign({}, inp, filterFieldPlate)} /></div>
                  <div><div style={{ fontSize: 9, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.5)", fontWeight: 600, marginBottom: 2 }}>{lang === "es" ? "Hora de recepción" : "Received time"}</div><input type="time" value={f.receivedTime || ""} onChange={e => set("receivedTime", e.target.value)} style={Object.assign({}, inp, filterFieldPlate)} /></div>
                </div>
                <div><div style={{ fontSize: 9, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.5)", fontWeight: 600, marginBottom: 2 }}>{lang === "es" ? "Notas de recepción" : "Receiving notes"}</div><textarea value={f.receivedNotes || ""} onChange={e => set("receivedNotes", e.target.value)} rows={2} style={Object.assign({}, inp, filterFieldPlate, { resize: "vertical", fontSize: 12, lineHeight: 1.4 })} /></div>
              </div>
            )}
          </span>
          {/* 2026-10-20, Arturo — see DetailPanel's matching "Links:"
              block (view mode) for the full comment; same feature,
              same markup, just reading f.lot instead of shipment.lot
              since this is the editable form. */}
          {(function () {
            var linkedBillsForLinksEdit = loadLS("gfi-bills", []).filter(function (b) { return String(b.lot) === String(f.lot); });
            var linkedInvoicesForLinksEdit = loadLS("gfi-invoices", []).filter(function (inv) {
              return String(inv.lot) === String(f.lot) || (inv.lines || []).some(function (l) { return String(l.lot) === String(f.lot); });
            });
            if (linkedBillsForLinksEdit.length === 0 && linkedInvoicesForLinksEdit.length === 0) return null;
            /* 2026-10-20, Arturo: "open bill y open invoice deben ser
               de la misma altura que received by" — explicit
               lineHeight matches "Received by..."'s natural height
               (same fix as the status badge/toggle buttons above). */
            var linkBtnStyleEdit = { height: 21, boxSizing: "border-box", display: "inline-flex", alignItems: "center", padding: "0 10px", borderRadius: 0, border: "1px solid #7d838c", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)", cursor: "pointer", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap", fontFamily: "inherit" };
            return (
              <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)" }}>{lang === "es" ? "Links:" : "Links:"}</span>
                {linkedBillsForLinksEdit.map(function (b) {
                  return (
                    <button key={"bill-" + b.id} type="button" style={linkBtnStyleEdit} onClick={function () {
                      if (window.__gfiNavigate) window.__gfiNavigate("bills");
                      setTimeout(function () { if (window.__gfiBillOpen) window.__gfiBillOpen(b.id); }, 220);
                    }}>{(lang === "es" ? "Abrir Bill Vinculado · Lote " : "Open Bill Linked · Lot ") + (b.lot || b.id)}</button>
                  );
                })}
                {linkedInvoicesForLinksEdit.map(function (inv) {
                  return (
                    <button key={"inv-" + inv.id} type="button" style={linkBtnStyleEdit} onClick={function () {
                      if (window.__gfiNavigate) window.__gfiNavigate("invoices");
                      setTimeout(function () { if (window.__gfiInvoiceOpen) window.__gfiInvoiceOpen(inv.id); }, 220);
                    }}>{(lang === "es" ? "Abrir Invoice Vinculado PU " : "Open Invoice Linked PU ") + (inv.puNumber || inv.id)}</button>
                  );
                })}
              </span>
            );
          })()}
          </div>
          <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
            {/* Status badge — not clickable, just shows current */}
            {/* 2026-10-17, Arturo: "haz lo mismo con status del
                embarque... boton con relieve 3d metalico, en el color
                que corresponda a cada status" — same full gradient +
                bevel + embossed-text treatment as Clear Form/Cancel/
                Save, built from this status's own semantic color
                (sty.text) instead of a flat bg + simple shadow. */}
            {/* 2026-10-17, Arturo (follow-up): "draft es plata, los
                demas no recuerdo que color son" — Draft gets the same
                silver brushed-metal plate as every other inactive/
                neutral control in this redesign (not a gray-tinted
                gradient); Received keeps its own semantic color as a
                gradient, since Arturo confirmed those stay as
                whatever STATUS_STYLES already defines for them. */}
            {/* 2026-10-20, Arturo: "copia el billed (color y diseño)
                del modo no edicion... es verde y no me gusta" → "dale
                el mismo formato tipo placa... pero en azul" → "debe
                ser asi" (screenshot of view mode's own status button)
                — Billed now matches that button exactly: a FLAT pale-
                blue background (not a 2-stop gradient like Draft's
                silver plate or Received's solid fill) with the same
                bevel boxShadow already used for view mode's status
                button a few hundred lines up in this file. */}
            {/* 2026-10-20, Arturo: "no son de la misma altura" — a
                <span> and the <button>s next to it render at different
                heights even with identical padding/border/font-size
                (UA default line-height differs between inline spans
                and buttons). Explicit lineHeight on both sides
                (here + the status-change buttons right below) forces
                them to match. */}
            {(() => { const isBilled = f.status === "billed"; const sty = STATUS_STYLES[f.status] || STATUS_STYLES.draft; const isDraft = (f.status || "draft") === "draft"; const billedSty = { bg: "rgba(59,130,246,0.1)", text: "#1d4ed8", border: "rgba(59,130,246,0.3)" }; return <span style={{ height: 21, boxSizing: "border-box", display: "inline-flex", alignItems: "center", padding: "0 10px", borderRadius: 0, border: "1px solid " + (isDraft ? "#b9bcc0" : isBilled ? billedSty.border : sty.text), background: isDraft ? "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)" : isBilled ? billedSty.bg : "linear-gradient(180deg, " + sty.text + "cc 0%, " + sty.text + " 55%, " + sty.text + "ee 100%)", boxShadow: isDraft || isBilled ? "inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 2px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.12)" : "inset 0 1px 1px rgba(255,255,255,0.4), inset 0 -1px 2px rgba(0,0,0,0.2), 0 1px 2px rgba(0,0,0,0.15)", color: isDraft ? "#1a2636" : isBilled ? billedSty.text : "#ffffff", textShadow: isDraft ? "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)" : isBilled ? "none" : "0 1px 1px rgba(0,0,0,0.3)", fontSize: 10, fontWeight: 700 }}>{t[f.status] || t.draft}</span>; })()}

            {/* 2026-10-17, Arturo: "usa el mismo formato de los botones
                preview y usalos para received, draft y create bill" \u2014
                same brushed-metal silver + embossed text used for the
                view-mode Preview button, instead of each having its
                own flat colored pill. */}
            {f.status === "draft" && !isNew && (
              <button onClick={() => set("status", "received")} style={{ height: 21, boxSizing: "border-box", display: "inline-flex", alignItems: "center", padding: "0 10px", borderRadius: 0, border: "1px solid #b9bcc0", background: "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.15)", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", cursor: "pointer", fontSize: 10, fontWeight: 600, marginLeft: 6 }}>{"\u2192"} {t.received}</button>
            )}
            {f.status === "received" && (
              <button onClick={() => set("status", "draft")} style={{ height: 21, boxSizing: "border-box", display: "inline-flex", alignItems: "center", padding: "0 10px", borderRadius: 0, border: "1px solid #b9bcc0", background: "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.15)", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", cursor: "pointer", fontSize: 10, fontWeight: 600, marginLeft: 6 }}>{"\u2190"} {t.draft}</button>
            )}
            {f.status === "billed" && (
              <button onClick={() => set("status", "received")} style={{ height: 21, boxSizing: "border-box", display: "inline-flex", alignItems: "center", padding: "0 10px", borderRadius: 0, border: "1px solid #b9bcc0", background: "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.15)", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", cursor: "pointer", fontSize: 10, fontWeight: 600, marginLeft: 6 }}>{"\u2190"} {t.received}</button>
            )}

            {/* Create Bill — only when Received */}
            {!isNew && f.status === "received" && (<>
              <div style={{ width: 1, height: 18, background: "var(--color-border)", margin: "0 6px" }}></div>
              <button onClick={async () => {
                /* 2026-10-14, Arturo: "si creo un bill desde dentro del
                   embarque, el status no cambia a billed, y tengo que
                   volverlo a cambiar manualmente" \u2014 real race condition:
                   onSave(shipmentData) was fired WITHOUT awaiting it
                   (onSave here resolves to the outer async handleSave,
                   which itself does 2 awaited Supabase round-trips for
                   the proforma-collision check before it even writes
                   the shipment), then navigation to Bills + bill
                   creation fired on a fixed 100ms/200ms timer \u2014 far
                   less than the real save could take. Bill creation
                   (or a subsequent read) could land before the
                   status:"billed" write actually committed, so the
                   next read of this shipment still showed "received".
                   Awaiting the save first (Promise.resolve wraps it so
                   this works whether onSave is sync or async,
                   depending on which call site rendered this form)
                   guarantees the status is actually persisted before
                   anything downstream can read a stale copy. */
                var shipmentData = { ...f, status: "billed" };
                await Promise.resolve(onSave(shipmentData));
                if (window.__gfiNavigate) window.__gfiNavigate("bills");
                setTimeout(function() { if (window.__gfiBillCreate) window.__gfiBillCreate(shipmentData); }, 200);
              }} style={{ padding: "3px 10px", borderRadius: 6, border: "1px solid #b9bcc0", background: "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.15)", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", cursor: "pointer", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>{"\u2192"} {t.createBill}</button>
            </>)}
          </div>
          </div>
        </div>

        {/* BODY */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {(errors.dupLot || errors.lines || errors.manifestEmpty || errors.manifestMismatch) && (
            <div style={{ margin: "10px 20px 0", padding: "7px 12px", borderRadius: 6, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", color: "#ef4444", fontSize: 11 }}>
              {errors.dupLot ? t.dupLot
                : errors.manifestEmpty ? (lang === "es" ? "Captura al menos 1 pallet en el Manifiesto de Pallets." : "Add at least 1 pallet in the Pallet Manifest.")
                : errors.manifestMismatch ? (
                    <div>
                      <div style={{ fontWeight: 700, marginBottom: 2 }}>{lang === "es" ? "Las cajas del Manifiesto no coinciden con las líneas de producto:" : "Pallet Manifest cases don't match the product lines:"}</div>
                      {errors.manifestMismatch.map(function (msg, i) { return <div key={i} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>· {msg}</div>; })}
                    </div>
                  )
                : t.fillRequired}
            </div>
          )}

          {/* 2026-10-20, Arturo: "replica [shipment header lado a lado
              con carrier info] en el modo edicion" — same 50/50 flex
              row as view mode. Header's EditSectionHeader is rendered
              `embedded` (drops its hardcoded 20px side margin/width
              calc, see that component) since this row's own padding
              now supplies the outer gutter instead. Transport keeps
              its own showTransport toggle state, untouched; it's only
              ever absent for FOB (no truck/trailer/border-crossing
              data), in which case Header just takes the full row. */}
          <div style={{ display: "flex", gap: isFob ? 0 : 14, padding: "0 20px" }}>
            <div style={{ flex: isFob ? "1 1 100%" : "1 1 50%", minWidth: 0 }}>
          {/* 2026-10-17, Arturo: "ahora si secciona como en el modo no
              editable con sus tarjetas, primera seccion shipmment
              header etx, replica el modo no editable aqui" — ROW 1-3
              (Lot/Dates/Shipper/Week/Proforma/Invoice/Entry#/Brokers/
              Warehouse) wrapped in the same collapsible card shell as
              view mode's "Datos del Embarque" section. */}
          <EditSectionHeader id="header" title={lang === "es" ? "Datos del Embarque" : "Shipment Header"} embedded />
          {/* 2026-10-17, Arturo: "shipmment header desborda" — the
              Fragment had no bordered/background container, unlike
              view mode's section bodies (a distinct boxed card right
              below the metal header bar) — content just floated below
              with no visual boundary. Real div with the same card
              styling now wraps it. */}
          {openEdit.header && (<div style={{ margin: "-1px 0 0", padding: "14px 14px 4px", background: "#f7f8fa", border: "1px solid var(--color-border)", borderTop: "none", borderRadius: 0 }}>
          {/* ROW 1 */}
          <div style={{ padding: "12px 0 0", display: "grid", gridTemplateColumns: "75px 1fr 1fr", gap: 8 }}>
            <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }}>{t.lotNumber}*</div><input value={f.lot} onChange={e => set("lot", e.target.value)} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, ...(errors.lot ? errS : {}) }} /></div>
            <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }} title={t.dateDepartureSub}>{t.dateDeparture}* <span style={{ fontWeight: 400, opacity: 0.65 }}>({t.dateDepartureSub})</span></div><input type="date" value={f.dateDeparture} onChange={e => handleDateChange("dateDeparture", e.target.value)} style={{ ...inp, ...reqBg(f.dateDeparture), ...(errors.dateDeparture ? errS : {}) }} /></div>
            <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }} title={t.dateArrivalSub}>{t.dateArrival} <span style={{ fontWeight: 400, opacity: 0.65 }}>({t.dateArrivalSub})</span></div><input type="date" value={f.dateArrival || ""} onChange={e => handleDateChange("dateArrival", e.target.value)} style={{ ...inp, ...reqBg(f.dateArrival) }} /></div>
          </div>

          {/* 2026-10-20, Arturo: "shipper y week muevelo... porque
              ahora se encima con lo demas" — Shipper/Week used to
              share ROW 1 with Lot/Departure/Arrival (5 columns), fine
              at full width but too cramped once this card went to
              50% width (the Shipper plate-toggle + SearchSelect was
              overlapping the Week badge at the row's edge). Split into
              their own row instead of trying to fit 5 columns in half
              the space. */}
          <div style={{ padding: "6px 0 0", display: "grid", gridTemplateColumns: "1fr 70px", gap: 8 }}>
            <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }}>{isFob ? (lang === "es" ? "Vendedor" : "Vendor") : t.shipper}*</div><div style={{ display: "flex", gap: 4 }}>
              {/* 2026-10-17, Arturo: "los campos shipper y client en
                  shipment header, dales tambien relieve 3d en azul al
                  seleccionado el plata el no seleccionado, como si
                  fueran placas" — same metallic-plate pair pattern as
                  the Shipment Type toggle (blue accent gradient when
                  active, silver brushed-metal when not). */}
              <div style={{ display: "flex", border: "1px solid #b9bcc0" }}>
                <button type="button" onClick={function() { setShipperSource("shipper"); }} style={{ padding: "5px 8px", border: "none", fontSize: 9, fontWeight: 700, cursor: "pointer", background: shipperSource === "shipper" ? "var(--color-accent)" : "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", color: shipperSource === "shipper" ? "#ffffff" : "#1a2636", boxShadow: shipperSource === "shipper" ? "inset 0 1px 1px rgba(255,255,255,0.3), inset 0 -1px 2px rgba(0,0,0,0.2)" : "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08)", textShadow: shipperSource === "shipper" ? "0 1px 1px rgba(0,0,0,0.25)" : "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)" }}>{t.sourceShipper}</button>
                <button type="button" onClick={function() { setShipperSource("client"); }} style={{ padding: "5px 8px", border: "none", borderLeft: "1px solid #b9bcc0", fontSize: 9, fontWeight: 700, cursor: "pointer", background: shipperSource === "client" ? "var(--color-accent)" : "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", color: shipperSource === "client" ? "#ffffff" : "#1a2636", boxShadow: shipperSource === "client" ? "inset 0 1px 1px rgba(255,255,255,0.3), inset 0 -1px 2px rgba(0,0,0,0.2)" : "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08)", textShadow: shipperSource === "client" ? "0 1px 1px rgba(0,0,0,0.25)" : "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)" }}>{t.sourceClient}</button>
              </div>
              {shipperSource === "shipper" ? <SearchSelect value={f.supplierId} onChange={handleShipperChange} disabled={!!lockSupplierId} placeholder={t.selectShipper} style={{ flex: 1, ...reqBg(f.supplierId) }} error={!!errors.supplierId} options={suppliers.map(s => ({ value: s.id, label: s.shortName || s.fullName || s.name || s.companyName || s.id }))} /> : <SearchSelect value={f.supplierId} onChange={handleClientAsShipper} disabled={!!lockSupplierId} placeholder={t.selectClient} style={{ flex: 1, ...reqBg(f.supplierId) }} error={!!errors.supplierId} options={clients.map(cl => ({ value: cl.id, label: cl.shortName || cl.fullName || cl.name || cl.companyName || cl.id }))} />}</div></div>
            {/* 2026-10-17, Arturo: "campo wk escribe week y el campo
                dale relieve 3d como una placa" — label renamed via
                t.weekNumber (Wk/Sem → Week/Semana, shared with view
                mode's KV too). This one read-only field keeps the
                metallic plate treatment (unlike the plain "campos de
                captura" look applied everywhere else) since it's a
                computed badge, not something Arturo types into. */}
            <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }}>{t.weekNumber}</div><input value={f.week ? "W" + f.week : ""} readOnly style={{ ...inp, background: "repeating-linear-gradient(180deg, rgba(0,0,0,0.025) 0px, rgba(0,0,0,0.025) 1px, transparent 1px, transparent 3px), linear-gradient(180deg, #eef0f2 0%, #d2d5d9 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08)", border: "1px solid #b9bcc0", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, textAlign: "center", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.15)" }} /></div>
          </div>

          {/* 2026-10-17, Arturo: "te dije que lo movieras a la seccion
              del renglon donde esta received al extremo izquierdo
              replicando como esta en el modo no editable, pero le
              agregarias un dropdown para hacer la captura de fecha
              hora y notas" — the standalone colored card is gone; its
              fields (texasQcMode/date/time/notes) now live in a
              popover opened from a label at the far left of the
              status row above (same row as the Status badge/action
              buttons), matching view mode's compact single-line chip
              instead of a permanently-expanded box. See that row. */}

          {/* ROW 2 */}
          <div style={{ padding: "6px 0 0", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {!isFob && <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }}>{t.proformaNumber}</div><input value={f.proforma} onChange={e => set("proforma", e.target.value)} style={{ ...inp, ...reqBg(f.proforma), fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: f.proforma ? "var(--color-accent)" : "var(--color-text)" }} /></div>}
            <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }}>{isFob ? (lang === "es" ? "Factura del Proveedor" : "Vendor Invoice #") : t.shipperInvoiceNum}</div><input value={f.shipperInvoice || ""} onChange={e => set("shipperInvoice", e.target.value)} style={{ ...inp, ...reqBg(f.shipperInvoice), fontFamily: "'JetBrains Mono', monospace" }} /></div>
            {/* 2026-10-01 — Arturo: the invoice field alone spanned the
                full row once Proforma/Entry# dropped out for FOB; added
                a second field ("Vendor Reference") next to it instead
                of leaving it stretched full-width. */}
            {isFob && <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }}>{lang === "es" ? "Referencia del Proveedor" : "Vendor Reference"}</div><input value={f.vendorReference || ""} onChange={e => set("vendorReference", e.target.value)} style={{ ...inp, ...reqBg(f.vendorReference), fontFamily: "'JetBrains Mono', monospace" }} /></div>}
            {!isFob && <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }}>{t.entryNumber}</div><input value={f.entryNumber || ""} onChange={e => set("entryNumber", e.target.value)} style={{ ...inp, ...reqBg(f.entryNumber), fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }} /></div>}
          </div>

          {/* ROW 3 */}
          <div style={{ padding: "6px 0 4px", display: "grid", gridTemplateColumns: isFob ? "1fr" : "1fr 1fr 1fr", gap: 8 }}>
            {!isFob && (
            <div>
              <div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }}>{t.brokerMX} <span style={{ fontSize: 9 }}>({t.optionalField})</span></div>
              <SearchSelect value={f.brokerMXId || ""} onChange={v => handleBrokerChange("MX", v)} placeholder={t.selectBroker} style={reqBg(f.brokerMXId)} options={brokerOptionsMX.map(b => ({ value: b.id, label: b.name }))} />
              {f.brokerMXId === "tmp-other" && <input value={f.brokerMXManual || ""} onChange={e => set("brokerMXManual", e.target.value)} placeholder={lang === "es" ? "Nombre" : "Name"} style={{ ...inp, ...reqBg(f.brokerMXManual), marginTop: 3, borderStyle: "dashed" }} />}
            </div>
            )}
            {!isFob && (
            <div>
              <div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }}>{t.brokerUS}</div>
              <SearchSelect value={f.brokerUSId || ""} onChange={v => handleBrokerChange("US", v)} placeholder={t.selectBroker} options={brokerOptionsUS.map(b => ({ value: b.id, label: b.name }))} style={{ minHeight: 25, fontSize: 10 }} />
              {/* Manual text-entry fallback. Shown when (a) the user
                  explicitly picks "Other (manual)" from the dropdown,
                  OR (b) the brokers catalog is empty / hasn't loaded
                  yet — in that case the dropdown only has the 3 temp
                  entries + "Other (manual)" and the user might not
                  recognize their actual broker; the always-visible
                  text input lets them type it directly without
                  hunting through the dropdown. */}
              {(f.brokerUSId === "tmp-other" || brokers.length === 0) && (
                <input value={f.brokerUSManual || ""} onChange={e => set("brokerUSManual", e.target.value)} placeholder={lang === "es" ? "Nombre del agente (manual)" : "Broker name (manual)"} style={{ ...inp, marginTop: 3, borderStyle: "dashed" }} />
              )}
            </div>
            )}
            <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }}>{t.warehouse}</div><SearchSelect value={f.warehouseId || ""} onChange={handleWarehouseChange} placeholder={t.selectWarehouse} style={reqBg(f.warehouseId)} options={warehouses.map(w => ({ value: w.id, label: w.shortName || w.fullName || w.name || w.companyName || w.id }))} /></div>
          </div>
          </div>)}
            </div>

            {/* TRANSPORT (collapsible) — Importación only; FOB has no
                truck/trailer/border-crossing data at all. */}
            {!isFob && <div style={{ flex: "1 1 50%", minWidth: 0 }}>
            {/* 2026-10-17, Arturo: "secciona como en el modo no
                editable con sus tarjetas" — same metallic-plate look as
                EditSectionHeader above, keeping this toggle's own
                pre-existing showTransport state (no behavior change,
                just re-skinned). */}
            <button onClick={() => setShowTransport(!showTransport)} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 12px", marginTop: 14, background: "repeating-linear-gradient(180deg, rgba(0,0,0,0.025) 0px, rgba(0,0,0,0.025) 1px, transparent 1px, transparent 3px), linear-gradient(180deg, #eef0f2 0%, #d2d5d9 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08)", border: "1px solid #b9bcc0", borderRadius: 0, cursor: "pointer", textAlign: "left" }}>
              <span style={{ fontSize: 11, color: "#5a6775", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.15)", transition: "transform 0.2s", transform: showTransport ? "rotate(90deg)" : "rotate(0deg)", display: "inline-block", width: 10 }}>{"▶"}</span>
              {/* 2026-10-17, Arturo: "remueve carrier and truck
                  details del header de la tarjeta carrier info" */}
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", flex: 1 }}>{t.transport}</span>
            </button>
            {/* 2026-10-20, Arturo: "carrier info quedo sin ventana
                detras, debes replicar lo mismo de shipper header" —
                same enclosing card as Shipment Header's body (margin
                -1px to sit flush under the header's own bottom border,
                same background/border, square corners). */}
            {showTransport && (
              <div style={{ margin: "-1px 0 0", padding: "14px 14px 12px", background: "#f7f8fa", border: "1px solid var(--color-border)", borderTop: "none", borderRadius: 0 }}>
                {/* Carrier row */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
                  <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }}>{t.carrier} *</div><input value={f.transport.carrier || ""} onChange={e => setTransport("carrier", e.target.value)} style={{ ...inp, background: trBg("carrier", f.transport.carrier) || inp.background }} /></div>
                  <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }}>{t.driver} *</div><input value={f.transport.driver || ""} onChange={e => setTransport("driver", e.target.value)} style={{ ...inp, background: trBg("driver", f.transport.driver) || inp.background }} /></div>
                  {(function () {
                    /* Cell phone rendered as country + area + local
                       with auto-dash on the 7-digit local. Stored
                       concatenated as "+DIAL-AREA-000-0000" on
                       transport.phone — print docs and exports keep
                       seeing a single string. Defaults to MX (+52). */
                    var parsed = parsePhone(f.transport.phone || "");
                    var opts = buildPhoneCountryOptions(lang);
                    var bg = trBg("phone", f.transport.phone) || inp.background;
                    var commit = (country, area, local) => setTransport("phone", serializePhone(country, area, local));
                    return (
                      <div>
                        {/* 2026-10-20, Arturo: "cell phone no es
                            obligatorio, puedo cerrar el embarque sin
                            que me marque que falta eso" — matches
                            validate()'s own comment just above
                            (TRANSPORT_REQ/blocking list never included
                            phone to begin with), the asterisk here was
                            just a leftover visual claiming otherwise. */}
                        <div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }}>{t.phone}</div>
                        <div style={{ display: "flex", gap: 4 }}>
                          <div style={{ width: 70, flexShrink: 0 }}>
                            <SearchSelect
                              value={parsed.country}
                              onChange={v => commit(v, parsed.area, parsed.local)}
                              options={opts}
                              placeholder="—"
                              style={{ background: bg, borderRadius: 0, minHeight: 25, fontSize: 10 }}
                            />
                          </div>
                          <input
                            value={parsed.area}
                            onChange={e => commit(parsed.country, e.target.value.replace(/\D/g, "").slice(0, 3), parsed.local)}
                            placeholder={lang === "es" ? "Área" : "Area"}
                            maxLength={3}
                            style={{ ...inp, width: 44, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", background: bg }}
                          />
                          <input
                            value={parsed.local}
                            onChange={e => commit(parsed.country, parsed.area, e.target.value)}
                            placeholder="000-0000"
                            maxLength={8}
                            style={{ ...inp, flex: 1, fontFamily: "'JetBrains Mono', monospace", background: bg }}
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>
                {/* Compact paired-column grid: each column stacks two
                    related fields (truck / trailer / caat+alpha /
                    seal+temp recorder). Replaces the old TRUCK INFO /
                    TRAILER INFO headers — labels alone carry meaning. */}
                {/* 2026-10-20, Arturo: "caat debe ir abajo y alpha code
                    arriba, remueve seal y temp recorder, no son
                    necesarios" — swapped Alpha Code/CAAT order in their
                    shared column, dropped the Seal/Thermograph column
                    entirely (data stays in the record if any old
                    shipment has it, just no longer shown/editable
                    here). Grid goes from 4 columns to 3. */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }}>{t.econTruck} *</div><input value={f.transport.econTruck || ""} onChange={e => setTransport("econTruck", e.target.value.toUpperCase())} style={{ ...inp, textTransform: "uppercase", background: trBg("econTruck", f.transport.econTruck) || inp.background }} /></div>
                    <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }}>{t.platesTruck} *</div><input value={f.transport.platesTruck || ""} onChange={e => setTransport("platesTruck", e.target.value.toUpperCase())} style={{ ...inp, textTransform: "uppercase", background: trBg("platesTruck", f.transport.platesTruck) || inp.background }} /></div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }}>{t.econTrailer} *</div><input value={f.transport.econTrailer || ""} onChange={e => setTransport("econTrailer", e.target.value.toUpperCase())} style={{ ...inp, textTransform: "uppercase", background: trBg("econTrailer", f.transport.econTrailer) || inp.background }} /></div>
                    <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }}>{t.platesTrailer} *</div><input value={f.transport.platesTrailer || ""} onChange={e => setTransport("platesTrailer", e.target.value.toUpperCase())} style={{ ...inp, textTransform: "uppercase", background: trBg("platesTrailer", f.transport.platesTrailer) || inp.background }} /></div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }}>{t.codigoAlpha} *</div><input value={f.transport.codigoAlpha || ""} onChange={e => setTransport("codigoAlpha", e.target.value.toUpperCase())} style={{ ...inp, textTransform: "uppercase", background: trBg("codigoAlpha", f.transport.codigoAlpha) || inp.background }} /></div>
                    <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 600, marginBottom: 2 }}>{t.caat} *</div><input value={f.transport.caat || ""} onChange={e => setTransport("caat", e.target.value.toUpperCase())} style={{ ...inp, textTransform: "uppercase", background: trBg("caat", f.transport.caat) || inp.background }} /></div>
                  </div>
                </div>
              </div>
            )}
            </div>}
          </div>

          {/* 2026-10-17, Arturo: "secciona como en el modo no editable
              con sus tarjetas" — Products wrapped in the same
              collapsible card shell as view mode. */}
          <EditSectionHeader id="lines" title={t.products} count={(f.lines || []).filter(l => l.productName || l.productBase).length} />
          {/* 2026-10-17, Arturo: "shipmment header desborda... products
              tambien" — same fix: real bordered/background card
              instead of a bare Fragment. */}
          {openEdit.lines && (<div style={{ margin: "-1px 20px 0", padding: "10px 14px 4px", background: "#f7f8fa", border: "1px solid var(--color-border)", borderTop: "none", borderRadius: 0 }}>
          {/* PRODUCT LINES TABLE */}
          <div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
              <thead><tr style={{ borderBottom: "2px solid var(--color-border)" }}>
                <th style={{ width: "3%" }}></th>
                {/* 2026-10-20, Arturo: "has lo mismo en products pallet
                    manifest y manifest (excepto palabras abreviadas
                    como CAT" — these headers were forced all-caps via
                    CSS textTransform, independent of the underlying
                    string's own casing; dropped so the real string
                    (already sentence case, "Cat" stays an abbreviation
                    as-is) is what actually renders. */}
                <th style={{ padding: "9px 4px", textAlign: "left", fontWeight: 400, fontSize: 9, color: "var(--color-text-muted)", letterSpacing: "0.05em", width: "13%" }}>{t.product}</th>
                <th style={{ padding: "9px 4px", textAlign: "left", fontWeight: 400, fontSize: 9, color: "var(--color-text-muted)", letterSpacing: "0.05em", width: "31%" }}>{t.presentation}</th>
                {/* 2026-10-20, Arturo: "cat y og centralos" — reverts
                    the earlier left-align pass for this column only;
                    every other Products header stays left. */}
                <th style={{ padding: "9px 3px", textAlign: "center", fontWeight: 400, fontSize: 9, color: "var(--color-text-muted)", letterSpacing: "0.05em", width: "5%" }}>{t.category}</th>
                {/* 2026-10-20, Arturo: "pallets, price y total muevelos
                    hacia la izquierda junto a sus numeros... quedaron
                    muy espaciados" — these 4 columns are narrower now
                    (matching their narrowed inputs), the freed width
                    went to Product Description above; numbers left-
                    aligned too so they sit flush under their already-
                    left headers instead of floating at the far right
                    of a column wider than they need. */}
                <th style={{ padding: "9px 3px", textAlign: "left", fontWeight: 400, fontSize: 9, color: "var(--color-text-muted)", letterSpacing: "0.05em", width: "6%" }}>{t.lineCases}</th>
                <th style={{ padding: "9px 3px", textAlign: "left", fontWeight: 400, fontSize: 9, color: "var(--color-text-muted)", letterSpacing: "0.05em", width: "5%" }}>{t.linePallets}</th>
                {!hideFinancials && <th style={{ padding: "9px 3px", textAlign: "left", fontWeight: 400, fontSize: 9, color: "var(--color-text-muted)", letterSpacing: "0.05em", width: "6%" }}>{t.linePrice}</th>}
                {!hideFinancials && <th style={{ padding: "9px 3px", textAlign: "left", fontWeight: 400, fontSize: 9, color: "var(--color-text-muted)", letterSpacing: "0.05em", width: "7%" }}>{t.lineTotal}</th>}
                <th style={{ padding: "9px 4px", textAlign: "left", fontWeight: 400, fontSize: 9, color: "var(--color-text-muted)", letterSpacing: "0.05em", width: "21%" }}>{t.exportCert}</th>
                <th style={{ width: "3%" }}></th>
              </tr></thead>
              <tbody>
                {f.lines.map((line, idx) => {
                  const presentations = getPresentations(line.productBase || line.productName);
                  const isOrganic = line.category === "OG";
                  const hasProduct = !!(line.productBase || line.productName || line.productId);
                  const availCerts = (hasProduct && isMexican && !isFob) ? getAvailableExportCerts(f.supplierId, line.productBase || line.productName, f.shipperInvoice, f.id) : [];
                  const rp = dndLines.rowProps(idx);
                  return (
                    <tr key={idx} {...rp} style={{ borderBottom: "1px solid var(--color-border)", ...(rp.style || {}) }}>
                      <td style={{ padding: "5px 0", textAlign: "center" }}><DragHandle handleProps={dndLines.handleProps()} /></td>
                      <td style={{ padding: "5px 3px" }}>
                        <SearchSelect
                          value={line.productBase || line.productName || ""}
                          onChange={v => updateLine(idx, "productBase", v)}
                          options={baseProducts.map(p => ({ value: p, label: p }))}
                          placeholder={t.selectProduct}
                          style={{ background: lineBg("productBase", line.productBase || line.productName), borderRadius: 0, minHeight: 25, fontSize: 10 }}
                        />
                      </td>
                      <td style={{ padding: "5px 3px" }}>
                        <SearchSelect
                          value={line.productId || ""}
                          onChange={v => updateLine(idx, "productId", v)}
                          options={presentations.map(p => {
                            var cat = (p.category === "Organic" || p.category === "OG") ? "OG" : "CV";
                            var label = (p.description || ((p.size || "") + " " + (p.unitsCase || "") + "u " + cat + " " + (p.labelInitials || ""))).trim();
                            return { value: p.id, label: label };
                          })}
                          placeholder={t.selectPresentation}
                          disabled={!line.productBase && !line.productName}
                          style={{ background: lineBg("productId", line.productId), borderRadius: 0, minHeight: 25, fontSize: 10 }}
                        />
                      </td>
                      {/* 2026-10-20, Arturo: "cat y og centralos" —
                          back to centered, matching the header. */}
                      <td style={{ padding: "5px 2px", textAlign: "center" }}>{line.category && <span style={{ padding: "2px 5px", borderRadius: 6, fontSize: 9, fontWeight: 400, background: line.category === "OG" ? "var(--color-badge-og-bg)" : "var(--color-badge-cv-bg)", color: line.category === "OG" ? "var(--color-badge-og-text)" : "var(--color-badge-cv-text)" }}>{line.category}</span>}</td>
                      {/* 2026-10-20, Arturo: "el ancho del campo
                          sigue igual en los 3" — `inp` sets
                          width:"100%", which always fills the column
                          regardless of the column's own width; an
                          explicit px width on the input itself is
                          what actually shrinks the box (header/column
                          width stay as reverted). */}
                      <td style={{ padding: "5px 3px", textAlign: "right" }}><input type="number" min="0" className="gfi-num-input shp-line-cases-input" value={line.cases} onChange={e => updateLine(idx, "cases", e.target.value)} style={{ ...inp, width: 52, padding: "7px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, background: lineBg("cases", line.cases) || inp.background }} /></td>
                      <td style={{ padding: "5px 3px", textAlign: "right" }}><input type="number" min="0" className="gfi-num-input shp-line-pallets-input" value={line.pallets} onChange={e => updateLine(idx, "pallets", e.target.value)} style={{ ...inp, width: 32, padding: "7px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, background: lineBg("pallets", line.pallets) || inp.background }} /></td>
                      {/* 2026-10-20, Arturo: "el 23 de price por ser
                          moneda, alinealo a la derecha del campo" —
                          currency convention, right-aligned within the
                          field (unlike Cases/Pallets, which stay left
                          per his earlier instruction). */}
                      {/* 2026-10-20, Arturo: "agregale por default
                          decimales... y signo de moneda" — "$" prefix
                          overlaid (type=number can't hold a literal
                          "$" character inside its own value), 2
                          decimals applied on blur, same convention
                          already used elsewhere in the app for price
                          fields (never reformatted on every keystroke
                          — that fights the user mid-type). */}
                      {!hideFinancials && <td style={{ padding: "5px 3px", textAlign: "right" }}>
                        <span style={{ position: "relative", display: "inline-block", width: 52 }}>
                          <span style={{ position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: "var(--color-text-muted)", pointerEvents: "none" }}>$</span>
                          <input type="number" min="0" step="0.01" className="gfi-num-input" value={line.price} onChange={e => updateLine(idx, "price", e.target.value)} onBlur={e => { if (e.target.value !== "") updateLine(idx, "price", Number(e.target.value).toFixed(2)); }} style={{ ...inp, width: 52, paddingLeft: 14, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, background: lineBg("price", line.price) || inp.background }} />
                        </span>
                      </td>}
                      {/* 2026-10-20, Arturo: "el total (numeros)
                          alinealo a la izquierda" */}
                      {!hideFinancials && <td style={{ padding: "5px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 400, color: "var(--color-accent)" }}>{parseFloat(line.total) > 0 ? fmtMoney(line.total) : ""}</td>}
                      <td style={{ padding: "5px 3px" }}>
                        {hasProduct ? (isFob ? (
                          /* FOB (domestic Texas purchase) — no export cert concept at all. */
                          <SearchSelect value="" onChange={function () {}} placeholder={lang === "es" ? "N/A (FOB)" : "N/A (FOB)"} options={[]} disabled style={{ borderRadius: 0, minHeight: 25, fontSize: 10 }} />
                        ) : line.category === "CV" ? (
                          /* CV product — USDA Organic certs don't apply. Grayed out + locked. */
                          <SearchSelect value="" onChange={function () {}} placeholder={lang === "es" ? "N/A (Convencional)" : "N/A (Conventional)"} options={[]} disabled style={{ borderRadius: 0, minHeight: 25, fontSize: 10 }} />
                        ) : (
                          <div>
                            {availCerts.length > 0 ? (
                              <SearchSelect value={line.exportCertId || ""} onChange={val => {
                                if (val === "manual") { updateLine(idx, "exportCertId", "manual"); }
                                else if (val === "pending") { updateLine(idx, "exportCertId", "pending"); }
                                else {
                                  var cert = getExportCerts().find(c => c.id === val);
                                  updateLine(idx, "exportCertId", val);
                                  if (cert) { setF(prev => { var nl = [...prev.lines]; nl[idx] = { ...nl[idx], exportCertNumber: cert.certNumber }; return { ...prev, lines: nl }; }); }
                                }
                              }} placeholder={t.selectCert} style={{ borderRadius: 0, minHeight: 25, fontSize: 10 }} options={[
                                { value: "pending", label: "\u23f3 " + t.certPending },
                                { value: "manual", label: "\u270f\ufe0f " + t.certManual },
                                ...availCerts.map(c => {
                                  /* Live availability from gfi-shipments + gfi-bills — see exportCertUsage.js. */
                                  var av = availableForCert(c, loadLS("gfi-shipments", []), loadLS("gfi-bills", []));
                                  return { value: c.id, label: c.certNumber + " (" + av + " " + (lang === "es" ? "disp" : "avail") + ")" };
                                })
                              ]} />
                            ) : (
                              <input value={line.exportCertManual || line.exportCertNumber || ""} onChange={e => applyManualCertEntry(idx, e.target.value)} placeholder="840-..." style={{ ...inp, fontSize: 9, fontFamily: "'JetBrains Mono', monospace" }} />
                            )}
                            {line.exportCertId === "manual" && availCerts.length > 0 && <input value={line.exportCertManual || ""} onChange={e => applyManualCertEntry(idx, e.target.value)} placeholder="840-..." style={{ ...inp, marginTop: 3, fontSize: 9, fontFamily: "'JetBrains Mono', monospace", borderStyle: "dashed" }} />}
                          </div>
                        )) : null}
                      </td>
                      <td style={{ padding: "5px 1px", textAlign: "center" }}>{f.lines.length > 1 && <button onClick={() => removeLine(idx)} style={{ background: "none", border: "none", color: "var(--color-text-muted)", cursor: "pointer", fontSize: 12, padding: 1 }}>{"\u2715"}</button>}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot><tr>
                {/* 2026-10-20, Arturo: "al boton add line agregale
                    placa metallica con relieve" — same Brushed Steel
                    silver plate as the rest of this redesign, square
                    corners, embossed text. */}
                {/* 2026-10-20, Arturo: "los totales alinealos debajo
                    de sus columnas y agrega total debajo de og" — this
                    row's colSpan bookkeeping was short by one cell (9
                    column-units covering 10 real columns), so every
                    value after Description silently sat one column to
                    the left of where its header actually was. Fixed
                    cell-by-cell against the real header list (drag,
                    Product, Description, Cat, Cases, Pallets, Price,
                    Total, Export Cert, remove); the Cat slot now shows
                    a "Total" label, same role as the Pallet Manifest's
                    own Subtotal/Total rows. */}
                <td style={{ padding: "8px 4px" }} colSpan={2}><button onClick={addLine} style={{ padding: "5px 12px", borderRadius: 0, border: "1px solid #b9bcc0", background: "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.15)", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", cursor: "pointer", fontSize: 11, fontWeight: 400 }}>+ {t.addLine}</button></td>
                <td></td>
                <td style={{ padding: "8px 3px", textAlign: "center", fontSize: 10, color: "var(--color-text-secondary)" }}>{t.palletGrandTotal}</td>
                <td style={{ padding: "8px 6px 8px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 400 }}>{calcTotals(f.lines).cases || ""}</td>
                {/* 2026-10-20, Arturo: "1 de pallets no esta alineado
                    con pallets" — cell position was already correct
                    (measured against the header, same left offset);
                    what he meant was the text centered within the
                    field, not left-hugging it. */}
                <td style={{ padding: "8px 6px 8px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 400 }}>{calcTotals(f.lines).pallets || ""}</td>
                {!hideFinancials && <td></td>}
                {!hideFinancials && <td style={{ padding: "8px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 400, color: "var(--color-accent)" }}>{calcTotals(f.lines).value > 0 ? fmtMoney(calcTotals(f.lines).value) : ""}</td>}
                <td colSpan={2}></td>
              </tr></tfoot>
            </table>
          </div>
          </div>)}

          {/* ─────────────── PALLET MANIFEST ───────────────
              One row per physical pallet (max 30). Each row is fully
              independent — the system tries to match the chosen
              product+size+units+category+label against gfi-products
              for a "Catalog match" hint, but never auto-creates a
              product. Empty rows are stripped on save. */}
          {/* 2026-10-17, Arturo: "si received by es gfi muestra las 2
              columnas de qc report, si es twin river muestra las 3,
              haciendolo asi, removemos texas qc report mas las
              burbujas 2 columnas y 3 columnas asi como esas lineas que
              encierran esas columnas, con ello puedes agrandar la
              barra de titulo de pallet manifest hasta el extremo
              ezquierdo como las demas tarjetas" — the manual 2/3
              column toggle was fully redundant once "Received by"
              (GFI/Twin River, in the popover above) already drives
              f.texasQcMode; removed it, the "Texas QC Report" bracket
              header row above Quality/Condition(/Severe Condition),
              AND its border lines. Column count still switches
              automatically (isClariFresh), just from one source now
              instead of two. Title bar is the standard EditSectionHeader,
              same full-width look as every other card. Julian day badge
              + pallet count moved into the collapsible body, just
              above the table, instead of living in the header row. */}
          <EditSectionHeader id="pallets" title={t.palletManifest} count={<>{julianBadge}<span style={{ marginLeft: 8 }}>{(f.palletManifest || []).length + "/" + MAX_PALLETS}</span></>} />
          {/* 2026-10-17, Arturo: "la burbuja de today 290 subela hasta
              la barra de titulo al extremo derecho antes del cuadro de
              agrandar ventana" — Julian day badge moved out of this
              card entirely, up to the modal's own title bar
              (titleBarActions, right before maximize/close — see the
              DraggableModal calls below/in DetailPanel). */}
          {openEdit.pallets && (<div style={{ margin: "-1px 20px 0", padding: "10px 14px 4px", background: "#f7f8fa", border: "1px solid var(--color-border)", borderTop: "none", borderRadius: 0 }}>
            <div style={{ overflowX: "auto", width: "100%" }}>
              <table style={{ width: "100%", minWidth: 1200, borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                  {/* 2026-10-17, Arturo: "los titulos del pallet
                      manifest los dejaste igual, no cambio nada... si
                      te digo emula es repite el mismo formato" — exact
                      same fix already applied to the view-mode Pallet
                      Manifest headers: fontSize 9 / color text-muted /
                      weight 600 on the <tr>, AND explicitly on every
                      <th> too (a bare <th> ignores an inherited
                      font-size — loses to App.css's global
                      "th, td { font-size: 12px }" — and browsers bold
                      <th> by default regardless of the parent's
                      font-weight). */}
                  {/* 2026-10-20, Arturo: "has lo mismo en products
                      pallet manifest y manifest (excepto palabras
                      abreviadas como CAT" — same removal as Products'
                      header row, letting each column's own string
                      (already sentence case) render as-is. */}
                  <tr style={{ borderBottom: "2px solid var(--color-border)", color: "var(--color-text-muted)", fontWeight: 600, fontSize: 9, letterSpacing: "0.05em" }}>
                  <th style={{ padding: "6px 4px", textAlign: "left", fontSize: 9, fontWeight: 600, minWidth: 55, whiteSpace: "nowrap" }}>{t.palletNum}</th>
                  <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 55, whiteSpace: "nowrap", fontSize: 9, fontWeight: 600 }}>{t.tagId}</th>
                  <th title={t.actualShipperInitialsHint} style={{ padding: "6px 4px", textAlign: "left", minWidth: 55, whiteSpace: "nowrap", fontSize: 9, fontWeight: 600 }}>{t.actualShipperInitialsShort}</th>
                  <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 110, fontSize: 9, fontWeight: 600 }}>{t.product}</th>
                  <th style={{ padding: "6px 4px", textAlign: "left", fontSize: 9, fontWeight: 600 }}>{t.category}</th>
                  <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 95, fontSize: 9, fontWeight: 600 }}>{t.condition}</th>
                  <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 90, fontSize: 9, fontWeight: 600 }}>{t.packType}</th>
                  <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 90, fontSize: 9, fontWeight: 600 }}>{t.presentation}</th>
                  {/* 2026-10-20, Arturo: "units/case en 2 renglones...
                      u reduce el campo a 7 caracteres" — forced break
                      right at the "/" (natural wrap isn't reliable at
                      narrow widths) instead of just narrowing and
                      hoping the browser breaks cleanly. */}
                  <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 50, whiteSpace: "normal", lineHeight: 1.2, fontSize: 9, fontWeight: 600 }}>{t.unitsCase.split("/")[0]}/<br />{t.unitsCase.split("/")[1]}</th>
                  {/* 2026-10-20, Arturo: "igual en Cases/Pallet" — same
                      forced break at "/" as Units/Case. */}
                  <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 50, whiteSpace: "normal", lineHeight: 1.2, fontSize: 9, fontWeight: 600 }}>{t.casesPallet.split("/")[0]}/<br />{t.casesPallet.split("/")[1]}</th>
                  <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 50, whiteSpace: "nowrap", fontSize: 9, fontWeight: 600 }}>{t.label}</th>
                  <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 65, whiteSpace: "nowrap", fontSize: 9, fontWeight: 600 }}>{t.variety}</th>
                  {/* 2026-10-20, Arturo: "traceability (julian) a dos
                      renglones igual y reduce a 10 caracteres" — same
                      forced-break pattern as Units/Case, splitting
                      right before the "(Julian)" part. */}
                  {!isFob && <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 80, whiteSpace: "normal", lineHeight: 1.2, fontSize: 9, fontWeight: 600 }}>{t.traceability.split(" (")[0]}<br />({t.traceability.split(" (")[1]}</th>}
                  {!isFob && <th style={{ padding: "6px 4px", textAlign: "left", fontSize: 9, fontWeight: 600 }}>{t.growerCode}</th>}
                  <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 60, whiteSpace: "nowrap", fontSize: 9, fontWeight: 600 }}>{t.weight}</th>
                  {/* 2026-10-17, Arturo: "esas lineas que estan antes
                      de quality y despues de condition, eliminalas" —
                      leftover blue border remnants from the removed
                      Texas QC Report bracket (the <tr> itself was
                      already deleted, but these per-cell borderLeft/
                      borderRight were still bracketing the columns). */}
                  <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 100, fontSize: 9, fontWeight: 600 }}>{t.texasQuality}</th>
                  <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 100, fontSize: 9, fontWeight: 600 }}>{t.texasCondition}</th>
                  {isClariFresh && <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 100, fontSize: 9, fontWeight: 600 }}>{t.texasSevereCondition}</th>}
                  {/* 2026-10-20, Arturo: "reduce cases lost a 5
                      caracteres" — too narrow for "Cases lost" on one
                      line, so it breaks at the space like the other
                      2-word headers. */}
                  {/* 2026-10-20, Arturo: "ahora a 2 renglones Cases /
                      Lost" — both words capitalized this time, unlike
                      the sentence-case rule used everywhere else in
                      this header row. */}
                  <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 52, whiteSpace: "normal", lineHeight: 1.2, fontSize: 9, fontWeight: 600 }}>{lang === "es" ? "Cajas" : "Cases"}<br />{lang === "es" ? "Perdidas" : "Lost"}</th>
                  {/* 2026-10-20, Arturo: "texas work 2 renglones y
                      reduce tamaño a 7 caracteres" — same pattern as
                      Cases/Lost, both words capitalized. */}
                  <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 45, whiteSpace: "normal", lineHeight: 1.2, fontSize: 9, fontWeight: 600 }}>{lang === "es" ? "Trabajo" : "Texas"}<br />{lang === "es" ? "Texas" : "Work"}</th>
                  {/* 2026-10-15, Arturo: "twin river delivery status
                     cambialo a despues de texas work y siempre debe
                     ser visible" — its own always-visible column in
                     the main row (not tucked inside the collapsible
                     Defects sub-row below), right after Texas Work
                     (lossReason). ClariFresh-only, same gate as the
                     tolerance-sheet capture itself.
                     2026-10-15 (follow-up): "esa columna de twin
                     river delivery status es lo mismo que la
                     columna status, ya existe" — this IS the same
                     pallet delivery status shown everywhere else in
                     the app (view-mode DetailPanel, Bill), just
                     editable here; header reuses the plain t.status
                     label instead of a distinct "Twin River" name so
                     it doesn't read as a second, separate concept. */}
                  {/* 2026-10-20, Arturo: "redice status a 158
                     caracteres" — read as 15 (values shown here are
                     "Protection"/"Good Delivery", both still fit). */}
                  {isClariFresh && <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 98, whiteSpace: "nowrap", fontSize: 9, fontWeight: 600 }}>{t.status}</th>}
                </tr></thead>
                <tbody>{(function () {
                  /* Map of pallet number → occurrence count. Any row
                     whose palletNum appears more than once is flagged
                     with red bg + inline error message. Trim + upper
                     for stable comparison across casing/whitespace. */
                  const pnCounts = {};
                  (f.palletManifest || []).forEach(p => {
                    const v = String(p.palletNum || "").trim().toUpperCase();
                    if (!v) return;
                    pnCounts[v] = (pnCounts[v] || 0) + 1;
                  });
                  return (f.palletManifest || []).map((p, idx) => {
                  const sizes = PALLET_SIZES_BY_PRODUCT[p.productBase] || [];
                  const w = calcPalletWeight(p);
                  const catalog = findCatalogMatch(p);
                  const pnKey = String(p.palletNum || "").trim().toUpperCase();
                  const pnDupe = !!pnKey && (pnCounts[pnKey] || 0) > 1;
                  const isSplit = !!(p.producers && p.producers.length > 1);
                  const mainRow = (
                    /* 2026-10-17, Arturo: "al agregar tu catalog match
                       empuja el campo product hacia arriba y lo
                       desalinea... tambien sucede en cases/pallet el
                       split by producer" — browser default for <td>
                       is vertical-align: middle; a cell that grows
                       taller (hint/link text added below the field)
                       centers its own content, nudging the field up,
                       while every OTHER cell in the row re-centers
                       within the now-taller row too — misaligning the
                       whole row. verticalAlign on the <tr> cascades to
                       every <td> that doesn't set its own, pinning all
                       of them to the top regardless of content height. */
                    <tr key={idx} style={{ borderBottom: isSplit ? "none" : "1px solid var(--color-border)", verticalAlign: "top" }}>
                      {/* Editable: Pallet # — red when duplicated. */}
                      <td style={{ padding: "5px 3px" }}><input value={p.palletNum || ""} onChange={e => updatePallet(idx, "palletNum", e.target.value)} onBlur={e => { var val = e.target.value.replace(/\D/g, ""); if (val && val.length < 3) { val = val.padStart(3, "0"); updatePallet(idx, "palletNum", val); } }} title={pnDupe ? t.duplicatePalletNum : ""} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", textAlign: "center", fontWeight: 600, background: pnDupe ? "#FFEBEE" : (palBg("palletNum", p.palletNum) || inp.background), borderColor: pnDupe ? "#DC2626" : "var(--color-border)", color: pnDupe ? "#B91C1C" : "var(--color-text)" }} />{pnDupe && <div style={{ fontSize: 9, color: "#B91C1C", marginTop: 2, fontWeight: 700, textAlign: "center" }}>{t.duplicatePalletNum}</div>}</td>
                      {/* 2026-10-26, Arturo: "cuando el embarque es
                          para twin river el pallet manifest no
                          necesita tag id" — Twin River pallets never
                          carry a GFI warehouse Tag ID, so the field
                          is disabled/blanked out instead of inviting
                          him to fill in something that doesn't apply.
                          Any value already saved on an older pallet
                          still shows through (not wiped), just can't
                          be edited while in ClariFresh mode. */}
                      <td style={{ padding: "5px 3px" }}>{isClariFresh ? (
                        <div style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", textAlign: "center", color: "var(--color-text-muted)", background: "var(--color-bg)" }}>{p.tagId || "—"}</div>
                      ) : (
                        <input value={p.tagId || ""} onChange={e => updatePallet(idx, "tagId", e.target.value)} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", textAlign: "center" }} />
                      )}</td>
                      <td style={{ padding: "5px 3px" }} title={t.actualShipperInitialsHint}>
                        {/* 2026-10-26, Arturo: "por defecto debe
                            mostrar las iniciales del shipper
                            seleccionado en shipper header" — falls
                            back to the shipment's own shipper
                            (f.shipperInitials) whenever this pallet
                            was never explicitly set to a different
                            one. Picking any option here still saves
                            an explicit value on the pallet itself,
                            same as before. */}
                        <SearchSelect
                          value={p.actualShipperInitials || f.shipperInitials || ""}
                          onChange={v => updatePallet(idx, "actualShipperInitials", v)}
                          options={actualShipperOptions}
                          placeholder="—"
                          style={{ background: "transparent", borderRadius: 0, borderStyle: "dashed", minHeight: 25, fontSize: 10 }}
                        />
                      </td>
                      {/* Read-only Product — driven by Product Lines. */}
                      <td style={{ padding: "5px 3px" }}>
                        <div style={readOnlyCell}>{p.productBase || "—"}</div>
                        {(p.productBase && p.size && p.category) && (
                          <div style={{ fontSize: 9, marginTop: 2, color: catalog ? "#059669" : "var(--color-text-muted)", fontStyle: catalog ? "normal" : "italic" }}>
                            {catalog ? "✓ " + t.palletProdMatch : "○ " + t.palletProdNew}
                          </div>
                        )}
                      </td>
                      {/* Read-only pre-fills from Product Lines. */}
                      <td style={{ padding: "5px 3px" }}><div style={readOnlyCell}>{p.category || "—"}</div></td>
                      <td style={{ padding: "5px 3px" }}><div style={readOnlyCell}>{p.condition || "Fresh"}</div></td>
                      <td style={{ padding: "5px 3px" }}><div style={readOnlyCell}>{p.packType ? ({ clamshell: "Clamshell", bulk: lang === "es" ? "Granel" : "Bulk", bag: lang === "es" ? "Bolsa" : "Bag", case_pack: lang === "es" ? "Caja" : "Case Pack" }[p.packType] || p.packType) : "—"}</div></td>
                      <td style={{ padding: "5px 3px" }}><div style={readOnlyCell}>{p.size || "—"}</div></td>
                      <td style={{ padding: "5px 3px" }}><div style={{ ...readOnlyCell, textAlign: "right", fontFamily: "'Courier New',monospace" }}>{p.unitsCase || "—"}</div></td>
                      {/* Cases/Pallet — a plain editable input for the
                         common single-producer case; once split, this
                         becomes a read-only SUM (see updateProducer/
                         removeProducer, which keep it in sync) so
                         every other reader of casesPallet in the app
                         (weight calc, subtotals, invoice-allocation
                         ceiling, the 2 PDF reports) keeps working
                         unchanged. */}
                      <td style={{ padding: "5px 3px" }}>
                        {isSplit ? (
                          <div style={{ ...readOnlyCell, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>{p.casesPallet || 0}</div>
                        ) : (
                          <>
                            {/* 2026-10-20, Arturo: "en cases remueve
                                las flechas" — same .gfi-num-input
                                treatment as Cases Lost. */}
                            <input type="number" min="0" className="gfi-num-input" value={p.casesPallet || ""} onChange={e => updatePallet(idx, "casesPallet", e.target.value)} style={{ ...inp, padding: "7px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, background: palBg("casesPallet", p.casesPallet) || inp.background }} />
                            {/* Split-by-producer exists only to capture
                                per-producer traceability/grower code —
                                both hidden for FOB, so this doesn't apply. */}
                            {p.productBase && !isFob && (
                              <button type="button" onClick={() => splitPalletProducers(idx)} title={lang === "es" ? "Este pallet tiene fruta de mas de un productor" : "This pallet has fruit from more than one producer"} style={{ marginTop: 3, padding: "1px 4px", fontSize: 9, border: "none", background: "none", color: "var(--color-accent)", cursor: "pointer", textDecoration: "underline", display: "block", width: "100%", textAlign: "right" }}>{t.splitByProducer}</button>
                            )}
                          </>
                        )}
                      </td>
                      {/* Read-only pre-fill: Label */}
                      <td style={{ padding: "5px 3px" }}><div style={readOnlyCell}>{p.labelInitials || "—"}</div></td>
                      {/* Variety dropdown — sourced from gfi-commodities
                          for the chosen commodity. Disabled when the
                          commodity has no varieties registered or no
                          product is selected yet. */}
                      <td style={{ padding: "5px 3px" }}>{(function() { var vs = varietiesFor(p.productBase); return <SearchSelect value={p.variety || ""} onChange={v => updatePallet(idx, "variety", v)} placeholder={vs.length === 0 ? "—" : "—"} disabled={vs.length === 0} limit={30} options={vs.map(function(v) { return { value: v, label: v }; })} style={{ background: palBg("variety", p.variety), borderRadius: 0, minHeight: 25, fontSize: 10 }} />; })()}</td>
                      {/* Trazabilidad / Grower Code — 2026-09-14, Arturo:
                         "ocasionalmente un pallet podria estar compuesto
                         por fruta de mas de un productor... agregar
                         sub-renglones para cajas, trazabilidad y codigo
                         del productor". Once split (producers.length>1),
                         these 2 cells stop being editable here — the
                         real values live in the sub-rows below, one per
                         producer; a small note + the "merge back" button
                         (rendered in the sub-rows block) replace them.
                         2026-10-08, Arturo: "debo ver los growers
                         asignados al shipper para seleccionar de ahi
                         pero tambien agregar manualmente" — "ambos" —
                         SearchSelect with allowCustom: picks from the
                         shipper's Growers catalog same as before, but
                         typing something not in that list and pressing
                         Enter (or clicking "+ Add") keeps the typed
                         text as the value instead of forcing a catalog
                         pick. `nowrap` per "el dropdown... debe verse
                         el nombre horizontalmente no vertical". */}
                      {!isFob && (isSplit ? (
                        <td colSpan={2} style={{ padding: "5px 3px" }}>
                          <div style={{ fontSize: 10, color: "var(--color-accent)", fontStyle: "italic", textAlign: "center" }}>{t.multipleProducersNote}</div>
                        </td>
                      ) : (
                        <>
                          <td style={{ padding: "5px 3px" }}><input value={p.traceability || ""} onChange={e => updatePallet(idx, "traceability", e.target.value)} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} /></td>
                          <td style={{ padding: "5px 3px" }}>
                            <SearchSelect
                              value={p.growerCode || ""}
                              onChange={function (v) { updatePallet(idx, "growerCode", v); }}
                              placeholder="—"
                              limit={30}
                              allowCustom
                              nowrap
                              panelMinWidth={320}
                              options={(growers || []).map(function (g) {
                                var nm = g.personType === "moral" ? (g.razonSocial || "—") : ([g.nombre, g.apPaterno, g.apMaterno].filter(Boolean).join(" ") || "—");
                                var code = g.producerCode || "";
                                return { value: code, label: (code || "—") + " - " + nm, triggerLabel: code, search: nm + " " + code };
                              }).filter(function (o) { return !!o.value; })}
                              style={{ borderRadius: 0, minHeight: 25, fontSize: 10 }}
                            />
                          </td>
                        </>
                      ))}
                      {/* 2026-10-20, Arturo: "alinea el peso real no el
                          header a la izquierda libras y kg" — the
                          header already went left earlier; this is the
                          actual lb/kg value cell, still right-aligned. */}
                      {/* 2026-10-20, Arturo: "usa el mismo font y
                          tamaño para ambos, el de kg" — lb was bold/
                          fontSize 10, kg was plain/9; both now match
                          kg's own lighter, smaller style. */}
                      <td style={{ padding: "5px 3px", textAlign: "left", fontFamily: "'JetBrains Mono', monospace", fontSize: 9 }}>
                        {w.lb > 0 ? (
                          <div>
                            <div style={{ color: "var(--color-text-muted)" }}>{w.lb.toFixed(1)} lb</div>
                            <div style={{ color: "var(--color-text-muted)" }}>{w.kg.toFixed(1)} kg</div>
                          </div>
                        ) : "—"}
                      </td>
                      {/* Texas QC Report (2026-09-14, Arturo) — GF's own
                         inspection on arrival. Never shown to the
                         client/SL side (see TEXAS_QC_TIERS_* comment
                         above EMPTY_PALLET). Background color follows
                         the selected tier, same pattern as palBg() a
                         few cells to the left, so a full row reads at
                         a glance. Tier options + whether the 3rd
                         (Severe Condition) column renders at all
                         depend on texasQcMode (2026-10-01). */}
                      <td style={{ padding: "5px 3px" }}>
                        <SearchSelect
                          value={p.texasQuality || ""}
                          onChange={function (v) { updatePallet(idx, "texasQuality", v); }}
                          placeholder="—"
                          options={TEXAS_QC_TIERS.map(function (tier) { return { value: tier, label: t["texasQc" + tier.charAt(0).toUpperCase() + tier.slice(1)] }; })}
                          style={p.texasQuality ? { background: TEXAS_QC_COLORS[p.texasQuality].bg, color: TEXAS_QC_COLORS[p.texasQuality].text, border: "1px solid " + TEXAS_QC_COLORS[p.texasQuality].border, boxShadow: TEXAS_QC_BEVEL, fontWeight: 700, borderRadius: 0, minHeight: 25, fontSize: 10 } : { borderRadius: 0, minHeight: 25, fontSize: 10 }}
                        />
                      </td>
                      <td style={{ padding: "5px 3px" }}>
                        <SearchSelect
                          value={p.texasCondition || ""}
                          onChange={function (v) { updatePallet(idx, "texasCondition", v); }}
                          placeholder="—"
                          options={TEXAS_QC_TIERS.map(function (tier) { return { value: tier, label: t["texasQc" + tier.charAt(0).toUpperCase() + tier.slice(1)] }; })}
                          style={p.texasCondition ? { background: TEXAS_QC_COLORS[p.texasCondition].bg, color: TEXAS_QC_COLORS[p.texasCondition].text, border: "1px solid " + TEXAS_QC_COLORS[p.texasCondition].border, boxShadow: TEXAS_QC_BEVEL, fontWeight: 700, borderRadius: 0, minHeight: 25, fontSize: 10 } : { borderRadius: 0, minHeight: 25, fontSize: 10 }}
                        />
                      </td>
                      {isClariFresh && (
                      <td style={{ padding: "5px 3px" }}>
                        <SearchSelect
                          value={p.texasSevereCondition || ""}
                          onChange={function (v) { updatePallet(idx, "texasSevereCondition", v); }}
                          placeholder="—"
                          options={TEXAS_QC_TIERS.map(function (tier) { return { value: tier, label: t["texasQc" + tier.charAt(0).toUpperCase() + tier.slice(1)] }; })}
                          style={p.texasSevereCondition ? { background: TEXAS_QC_COLORS[p.texasSevereCondition].bg, color: TEXAS_QC_COLORS[p.texasSevereCondition].text, border: "1px solid " + TEXAS_QC_COLORS[p.texasSevereCondition].border, boxShadow: TEXAS_QC_BEVEL, fontWeight: 700, borderRadius: 0, minHeight: 25, fontSize: 10 } : { borderRadius: 0, minHeight: 25, fontSize: 10 }}
                        />
                      </td>
                      )}
                      {/* Case-loss notation (2026-09-21, Arturo) — a
                         separate note, never subtracted from
                         casesPallet (see PALLET_LOSS_REASONS comment
                         above EMPTY_PALLET). Amber highlight when a
                         count is captured, same visual language as
                         palBg()'s capture-pending fields elsewhere. */}
                      <td style={{ padding: "5px 3px" }}>
                        {/* 2026-10-20, Arturo: "de cases lost remueve
                            esas flechas, es captura manual" — native
                            number-input spinner hidden via the shared
                            .gfi-num-input class (App.css), same used
                            elsewhere in this app for manual-entry
                            number fields. */}
                        <input type="number" min="0" className="gfi-num-input" value={p.lostCases || ""} onChange={e => updatePallet(idx, "lostCases", e.target.value)} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, background: p.lostCases ? "rgba(217,119,6,0.10)" : inp.background }} />
                      </td>
                      <td style={{ padding: "5px 3px" }}>
                        {/* 2026-10-20, Arturo: "el drop de texas work
                            debe mostrar ventana completa al abrir,
                            alineacion horizontal" — the trigger itself
                            is narrow (7-character column), but the
                            open panel uses panelMinWidth to widen
                            past the trigger, and nowrap keeps each
                            option's label on one line instead of
                            wrapping at that narrow width. */}
                        <SearchSelect
                          value={p.lossReason || ""}
                          onChange={function (v) { updatePallet(idx, "lossReason", v); }}
                          placeholder="—"
                          options={PALLET_LOSS_REASONS.map(function (r) { return { value: r, label: t["lossReason" + r.charAt(0).toUpperCase() + r.slice(1)] }; })}
                          style={{ borderRadius: 0, minHeight: 25, fontSize: 10 }}
                          nowrap
                          panelMinWidth={150}
                        />
                        {p.lossReason === "other" && <input value={p.lossReasonCustom || ""} onChange={function (e) { updatePallet(idx, "lossReasonCustom", e.target.value); }} placeholder={t.lossReasonCustomPlaceholder} style={{ ...inp, marginTop: 3, fontSize: 10, borderStyle: "dashed" }} />}
                      </td>
                      {/* Twin River delivery status (2026-10-15,
                         Arturo) — a deliberate exception to REGLA DE
                         STATUS DE PALLET: Twin River inspects at their
                         own warehouse and reports Protection directly
                         off this same report, so for ClariFresh-mode
                         shipments the pallet's real Good Delivery/
                         Protection comes from HERE, not the Invoice's
                         Pallet Allocations. Deliberately NOT derived
                         from the grades/defects below (they can
                         legitimately disagree — see t.qcTwinRiverHint).
                         Always visible/editable in the main row now —
                         "despues de texas work y siempre debe ser
                         visible" — no longer tucked inside the
                         collapsible Defects sub-row. */}
                      {isClariFresh && (
                        <td style={{ padding: "5px 3px" }}>
                          {/* 2026-10-20, Arturo: "cada font tiene el
                              color que le corresponde... haz lo mismo
                              con status" — same tinted-plate treatment
                              (border + bevel) just applied to
                              Quality/Condition/Severe condition,
                              colors unchanged. */}
                          <SearchSelect
                            value={p.twinRiverStatus || ""}
                            onChange={function (v) { updatePallet(idx, "twinRiverStatus", v); }}
                            placeholder="—"
                            options={[{ value: "goodDelivery", label: t.qcTwinRiverGood }, { value: "protection", label: t.qcTwinRiverProtection }]}
                            style={p.twinRiverStatus ? { background: p.twinRiverStatus === "protection" ? "rgba(220,38,38,0.1)" : "rgba(16,185,129,0.1)", color: p.twinRiverStatus === "protection" ? "#B91C1C" : "#059669", border: "1px solid " + (p.twinRiverStatus === "protection" ? "rgba(220,38,38,0.4)" : "rgba(16,185,129,0.4)"), boxShadow: TEXAS_QC_BEVEL, fontWeight: 700, borderRadius: 0, minHeight: 25, fontSize: 10 } : { borderRadius: 0, minHeight: 25, fontSize: 10 }}
                            title={t.qcTwinRiverHint}
                          />
                        </td>
                      )}
                      {/* Per-row remove button intentionally omitted:
                          rows are driven by Product Lines pallet count. */}
                    </tr>
                  );
                  /* Tolerance-sheet defects (2026-10-15, redesigned
                     per Arturo: "no quiero ver ahi los 3 cuadros,
                     quiero seleccionar con un drop que muestre las 3
                     condiciones... y conforme seleccione cada una me
                     muestre un drop con los defectos de cada una y al
                     seleccionar un defecto me abra un campo para el
                     porcentaje y eso se vaya guardando" — sequential
                     Category → Defect pick, auto-added the instant a
                     defect is chosen (addPalletDefect, same setter as
                     before), then both pickers reset so the next pick
                     starts clean; the %, once a defect exists, lives
                     on its own chip below (editable any time, not
                     just at the moment of adding).

                     "quiero que esa linea de captura se contraiga una
                     vez que asigne delivery status y capture
                     defectos... solo muestre un renglon debajo del
                     pallet... paralelo al renglon del pallet" — once
                     there's something captured (hasData, same flag
                     the DEFECTS button cell above uses), the collapsed
                     STEADY STATE is a one-line read-only summary
                     sub-row (summaryRow), not the picker UI. The ✎
                     button toggles into the picker (editRow) to add
                     more / change the status; qcDefectsOpen[idx] drives
                     which of the two actually renders.

                     colSpan matches the row's real column count: the
                     same (isFob?12:14) span used by the QC bracket
                     header, +2 (texasQuality/texasCondition) +1
                     (texasSevereCondition, ClariFresh-only) +2
                     (casesLost/lossReason) +1 (the always-visible
                     Twin River status column, also ClariFresh-only —
                     this sub-row only ever renders when isClariFresh
                     is already true, so both extra columns always
                     apply here). */
                  /* +2 texasQuality/texasCondition, +2 casesLost/
                     lossReason always; +1 texasSevereCondition and +1
                     the always-visible Twin River status column, both
                     ClariFresh-only (internal mode has neither
                     column). */
                  /* 2026-10-23 — +1 for the new actualShipperInitials
                     column (see totalCols above, same base count). */
                  var qcColSpan = (isFob ? 12 : 14) + 1 + (isClariFresh ? 6 : 4);
                  var qc = p.qcDefects || { severeCondition: [], condition: [], quality: [] };
                  /* 2026-10-15, Arturo (2nd pass on this same feature):
                     "el selector defects no lo quiero ahi sino debajo
                     de weight mas discreto y ahi hacia la derecha la
                     siguiente columna es un drop con las condiciones,
                     la siguiente los defectos de esa condicion y la
                     ultima el porcentaje y eso se vaya agregando de
                     manera horizontal iniciando debajo de la columna
                     pallet con el titulo defects y ahi los distribuya
                     hacia la derecha en el renglon como en el reporte
                     de twin river" — ONE always-present sub-row per
                     pallet (ClariFresh mode), left-aligned starting
                     right under the Pallet # column: a plain "Defects"
                     label, then Category → Defect → % pickers, then
                     already-added entries continue horizontally to the
                     right of those pickers as compact read-only chips
                     (never a boxed/grouped layout, never centered —
                     matches ClariFresh's own left-to-right "Defect
                     group / Percentage / Defects / Grade" table). The
                     row still collapses to just the label + chips
                     (no active pickers) until clicked, per Arturo's
                     earlier "que se contraiga" ask — same row either
                     way, just with or without the 2 pickers visible. */
                  /* 2026-10-15, Arturo (6th pass, reverses the 3rd
                     pass above): "cambiemos la dinamica... el drop
                     defects y lo demas cambialo de posicion hacia el
                     extremo izquierdo del renglon justo debajo del
                     pallet number" — back to starting at column 1
                     (Pallet #), no leading spacer; qcColSpan alone
                     covers the full row width from the start. */
                  var pendingCat = qcPendingCategory[idx] || "";
                  /* 2026-10-15, Arturo (4th pass): "del lado izquierdo
                     al inicio del renglon... se escriba Defects,
                     despues a la derecha de defects QUALITY, con
                     mayusculas y en un rectangulo con un color...
                     cuando agregue los defectos de quality se agreguen
                     hacia la derecha de ese rectangulo con su
                     porcentaje... cuando termine quality y agrego otra
                     condicion... se agregara de la misma manera
                     despues de la columna de defectos que ya captura y
                     con otro rectangulo usando el mismo color que ya
                     habias elegido para esa condicion... un pallet
                     podria tener defectos en una, dos o las 3
                     condiciones... al guardar y cerrar la captura de
                     defectos solo debe mostrarme... los defectos
                     capturados alineados horizontalmente como en el
                     reporte de calidad" — one UPPERCASE colored badge
                     per category (fixed order: Severe Condition/
                     Condition/Quality, same as ClariFresh's own
                     report), shown as soon as that category is picked
                     (even with 0 defects yet) or has any entry,
                     immediately followed by its own chips — multiple
                     categories' badges+chips sit side by side in the
                     SAME row, left to right. Collapsed (closed) state
                     renders the identical badges+chips, just without
                     the ability to edit/remove — "solo debe mostrarme
                     los defectos capturados". */
                  var activeCats = qcActiveCategories[idx] || [];
                  /* 2026-10-15, Arturo (11th pass): "las condiciones
                     deben agregarse a la derecha de lo ya capturado no
                     a la izquierda, asi es muy confuso" — a fixed
                     severeCondition/condition/quality taxonomy order
                     was re-sorting a just-added category to wherever
                     it falls in that list, even when it lands before
                     something already on screen. qcActiveCategories is
                     UI-only (never persisted) so it starts empty on
                     every reload — any category with saved entries
                     from before this session is shown first, in the
                     fixed taxonomy order, since there's no real
                     "added order" for those; anything added THIS
                     session (via the "+" button) is appended after,
                     in the actual order it was clicked — always to
                     the right of whatever was already there. */
                  /* 2026-10-15, Arturo (GFI Team/internal mode): first
                     pass forced a single flat "Quality" bucket for
                     internal mode (Condition staying a plain tier,
                     never broken into defects) — Arturo corrected this
                     after comparing captured totals against the real
                     report: internal mode genuinely has 2 categories
                     (Condition/Quality, see TOLERANCE_DEFECTS_INTERNAL),
                     same pick-a-category-then-pick-a-defect flow as
                     ClariFresh, just without Severe Condition. Both
                     modes now share one formula — only the category
                     universe differs. */
                  /* 2026-10-21, Arturo: "Findings" — always available in
                     both modes, appended after the mode's own tolerance
                     categories (never replacing/reordering them). */
                  var catUniverse = (isClariFresh ? ["severeCondition", "condition", "quality"] : ["condition", "quality"]).concat(["findings"]);
                  var catsToShow = catUniverse
                    .filter(function (cat) { return (Array.isArray(qc[cat]) ? qc[cat] : []).length > 0 && activeCats.indexOf(cat) === -1; })
                    .concat(activeCats);
                  /* 2026-10-15, Arturo (8th pass): "el cuadro add
                     defect esta muy grande, solo un boton pequeño con
                     el simbolo de +" — both add-pickers (category and
                     per-category defect) now stay collapsed behind a
                     tiny icon-only "+" button; clicking it reveals a
                     narrow SearchSelect right there, and picking a
                     value commits immediately and collapses it back
                     to the "+" button (qcCategoryPickerOpen /
                     qcDefectPickerOpen, both UI-only). */
                  var qcPlusBtn = { width: 16, height: 16, borderRadius: "50%", border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 10, fontWeight: 700, lineHeight: 1, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" };
                  /* 2026-10-15, Arturo (9th pass): "al final del
                     renglon de defectos 2 botones uno para guardar y
                     otro para editar, al guardar se inhabilita la
                     edicion y los botones de agregar defectos o
                     condiciones desaparecen dejando solo lo
                     capturado" — qcDefectsOpen[idx] is now driven
                     exclusively by these 2 explicit buttons (the old
                     click-the-label toggle is gone); Save closes it
                     (locks the row to a plain read summary, same look
                     as a freshly-loaded/never-opened pallet), Edit
                     reopens it (remove ✕'s + add "+" buttons come
                     back). */
                  var defectsRow = (
                    <tr key={idx + "-defects"} style={{ background: "rgba(220,38,38,0.025)", borderBottom: "1px solid var(--color-border)" }}>
                      <td colSpan={qcColSpan} style={{ padding: "4px 6px" }}>
                        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                          <span style={{ fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                            {t.qcDefects}
                          </span>
                          {catsToShow.length === 0 && <span style={{ fontSize: 10, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.qcDefectsNone}</span>}
                          {catsToShow.map(function (cat) {
                            var entries = Array.isArray(qc[cat]) ? qc[cat] : [];
                            var c = TOLERANCE_CATEGORY_COLORS[cat];
                            var defectPickerKey = idx + "-" + cat;
                            /* 2026-10-15, Arturo (12th pass): "al dar
                               save quiero que remueva rectangulos
                               individuales y que deje... quality y sus
                               defectos en un solo rectangulo que
                               abarque condicion y defectos y un solo
                               color perteneciente a la condicion...
                               son rectangulos individuales y que nada
                               sea editable, es informacion de lectura"
                               — locked (Save'd) state collapses a
                               whole category + its defects into ONE
                               rectangle (plain text, comma-joined,
                               nothing input-shaped) instead of one
                               little bordered box per defect chip.
                               Editing (qcDefectsOpen[idx] true) keeps
                               the per-chip editable layout below,
                               since that one genuinely needs separate
                               controls per defect. */
                            if (!qcDefectsOpen[idx]) {
                              /* 2026-10-15, Arturo (GFI Team/internal
                                 mode): per-defect severity coloring —
                                 same exception as the view-mode
                                 DetailPanel above, see
                                 internalQualityDefectColor. */
                              if ((cat === "quality" || cat === "condition") && !isClariFresh) {
                                var lockedCatTotal = entries.reduce(function (s, e) { var n = parseFloat(e.pct); return s + (n >= 0 ? n : 0); }, 0);
                                return (
                                  <span key={cat} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                    <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--color-text-muted)" }}>{t["qcCat" + cat.charAt(0).toUpperCase() + cat.slice(1)]}:</span>
                                    {entries.map(function (e, ei) {
                                      var lbl = internalDefectLabel(e, t);
                                      var pct = e.pct !== undefined && e.pct !== null && e.pct !== "" ? " " + e.pct + "%" : "";
                                      var dc = internalQualityDefectColor(e.key);
                                      return (
                                        <span key={e.key + ei} style={{ padding: "2px 8px", borderRadius: 0, fontSize: 9, fontWeight: 600, background: dc.bg, color: dc.text, border: "1px solid " + dc.border }}>{lbl + pct}</span>
                                      );
                                    })}
                                    <span style={{ fontSize: 9, fontWeight: 700, color: "var(--color-text-secondary)" }}>{"(" + t.qcDefectTotal + ": " + lockedCatTotal.toFixed(2) + "%)"}</span>
                                  </span>
                                );
                              }
                              var summaryText = entries.map(function (e) {
                                var lbl = e.key === "__custom__" ? (e.custom || t.qcTolCustom) : t["qcTol" + e.key.charAt(0).toUpperCase() + e.key.slice(1)];
                                return lbl + (e.pct !== undefined && e.pct !== null && e.pct !== "" ? " " + e.pct + "%" : "");
                              }).join(", ");
                              return (
                                <span key={cat} style={{ padding: "2px 8px", borderRadius: 5, fontSize: 9, fontWeight: 600, background: c.bg, color: c.text, border: "1px solid " + c.border }}>
                                  <span style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>{t["qcCat" + cat.charAt(0).toUpperCase() + cat.slice(1)]}</span>
                                  {summaryText && (": " + summaryText)}
                                </span>
                              );
                            }
                            return (
                              <div key={cat} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 4px 2px 7px", borderRadius: 0, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", background: c.bg, color: c.text, border: "1px solid " + c.border }}>
                                  {t["qcCat" + cat.charAt(0).toUpperCase() + cat.slice(1)]}
                                  <button type="button" title={t.qcDefectsRemoveCategory} onClick={function () { removePalletCategory(idx, cat); }} style={{ padding: 0, border: "none", background: "transparent", color: c.text, cursor: "pointer", fontSize: 11, fontWeight: 700, lineHeight: 1 }}>✕</button>
                                </span>
                                {entries.map(function (e) {
                                  var label = e.key === "__custom__" ? null : (!isClariFresh ? internalDefectLabel(e, t) : t["qcTol" + e.key.charAt(0).toUpperCase() + e.key.slice(1)]);
                                  /* 2026-10-15, Arturo (GFI Team/internal
                                     mode): each defect chip gets its own
                                     severity color here too — the
                                     category pill above stays Quality's
                                     own color (identifies the category),
                                     but the value itself (border/text/
                                     % input/✕) reflects whether this
                                     specific defect means the fruit is a
                                     total loss. */
                                  /* Findings are always medium-high severity,
                                     in both modes — never routed through
                                     internalQualityDefectColor's
                                     severe-vs-condition split. */
                                  var dc = cat === "findings" ? TOLERANCE_CATEGORY_COLORS.findings : (!isClariFresh ? internalQualityDefectColor(e.key) : c);
                                  return (
                                    <div key={e.key} style={{ display: "flex", alignItems: "center", gap: 3, padding: "2px 5px", borderRadius: 0, border: "1px solid " + dc.border, background: "rgba(255,255,255,0.5)" }}>
                                      {e.key === "__custom__" ? (
                                        <input value={e.custom || ""} onChange={function (ev) { setPalletDefectCustom(idx, cat, e.key, ev.target.value); }} placeholder={t.qcDefectCustomPlaceholder} style={{ ...inp, fontSize: 9, padding: "1px 3px", width: 90 }} />
                                      ) : (
                                        <span style={{ fontSize: 10, color: dc.text }}>{label}</span>
                                      )}
                                      {/* 2026-10-15, Arturo (14th
                                          pass, reverses the 13th):
                                          "los campos de captura de
                                          porcentaje son muy pequeños,
                                          remueve el signo de
                                          porcentaje al capturar pero
                                          muestralo en modo no
                                          editable" — the static "%"
                                          suffix is gone again while
                                          editing (back to just the
                                          "%" placeholder, which is
                                          fine since it only needs to
                                          show on an empty field); the
                                          width that freed up goes
                                          back into the input itself
                                          so it's easier to tap/type
                                          into. The locked/read-only
                                          summary (built above, before
                                          this editable branch even
                                          renders) already appends
                                          "%" as real text — that one
                                          is untouched. */}
                                      <input type="number" min="0" max="100" step="1" placeholder="%"
                                        value={e.pct === undefined || e.pct === null ? "" : e.pct}
                                        onChange={function (ev) { setPalletDefectPct(idx, cat, e.key, ev.target.value === "" ? undefined : Number(ev.target.value)); }}
                                        style={{ width: 38, padding: "1px 2px", borderRadius: 0, border: "1px solid " + dc.border, background: "rgba(255,255,255,0.7)", color: dc.text, fontSize: 9, fontWeight: 700, textAlign: "center" }} />
                                      <button type="button" onClick={function () { removePalletDefect(idx, cat, e.key); }} style={{ padding: "0 2px", border: "none", background: "transparent", color: dc.text, cursor: "pointer", fontSize: 11, fontWeight: 700, lineHeight: 1 }}>✕</button>
                                    </div>
                                  );
                                })}
                                {/* 2026-10-15, Arturo (GFI Team/
                                    internal mode): "la suma total de
                                    cada condicion necesito verla en
                                    vivo al capturar porque debo
                                    cotejar vs el reporte actual, no
                                    quiero salir y despues entrar si no
                                    cuadra" — live running total,
                                    recomputed from `entries` (the real
                                    live state) on every keystroke, no
                                    need to lock/save first. */}
                                {!isClariFresh && (
                                  <span style={{ fontSize: 9, fontWeight: 700, color: "var(--color-text-secondary)" }}>
                                    {/* 2026-10-15, Arturo: "elimina el
                                        simbolo de porcentaje del
                                        campo, es muy molesto tenerlo
                                        ahi, que sea visible al
                                        guardar" — no "%" suffix while
                                        actively capturing (same
                                        convention as each defect's own
                                        % input); the locked/saved
                                        summary above already appends
                                        "%" as real text. */}
                                    {"(" + t.qcDefectTotal + ": " + entries.reduce(function (s, e) { var n = parseFloat(e.pct); return s + (n >= 0 ? n : 0); }, 0).toFixed(2) + ")"}
                                  </span>
                                )}
                                {/* 2026-10-21, Arturo: "si quiero
                                    agregar un defecto a un pallet que
                                    ya fue capturado, aunque lo cambie
                                    a modo edit, el signo + no
                                    funciona, debo borrar por lo menos
                                    uno para poder recapturar" — root
                                    cause: this gate required `cat` to
                                    be in `activeCats` (qcActiveCategories,
                                    UI-only state that starts empty on
                                    every mount/edit-toggle — see its
                                    own comment above), but a category
                                    with data loaded from a PRIOR
                                    session is shown via catsToShow's
                                    OTHER branch ("has entries") and
                                    was therefore never in activeCats —
                                    so its "+" button could never
                                    render until the user emptied the
                                    category entirely and re-added it
                                    through the separate "add category"
                                    picker (which does populate
                                    activeCats). Every `cat` reaching
                                    this point already came through
                                    catsToShow's own filter (has data
                                    OR is active), so re-checking
                                    activeCats here was redundant and
                                    wrong — dropped. */}
                                {qcDefectsOpen[idx] && (function () {
                                  var currentKeys = entries.map(function (e) { return e.key; });
                                  var catDefectList = cat === "findings" ? QC_FINDINGS_OPTIONS : (isClariFresh ? toleranceDefectsForProduct(p.productBase)[cat] : TOLERANCE_DEFECTS_INTERNAL[cat]);
                                  var defectOptions = catDefectList.filter(function (k) { return currentKeys.indexOf(k) < 0; })
                                    .map(function (k) { return { value: k, label: k === "__custom__" ? t.qcTolCustom : t["qcTol" + k.charAt(0).toUpperCase() + k.slice(1)] }; });
                                  if (!qcDefectPickerOpen[defectPickerKey]) {
                                    return (
                                      <button type="button" title={t.qcDefectAddPlaceholder} disabled={defectOptions.length === 0}
                                        onClick={function () { setQcDefectPickerOpen(function (prev) { return { ...prev, [defectPickerKey]: true }; }); }}
                                        style={{ ...qcPlusBtn, borderColor: c.border, color: c.text, opacity: defectOptions.length === 0 ? 0.4 : 1, cursor: defectOptions.length === 0 ? "not-allowed" : "pointer" }}>+</button>
                                    );
                                  }
                                  return (
                                    <SearchSelect
                                      value={pendingPalletDefectKey[idx] || ""}
                                      onChange={function (v) {
                                        addPalletDefect(idx, cat, v);
                                        setPendingPalletDefectKey(function (prev) { return { ...prev, [idx]: "" }; });
                                        setQcDefectPickerOpen(function (prev) { return { ...prev, [defectPickerKey]: false }; });
                                      }}
                                      placeholder={t.qcDefectAddPlaceholder}
                                      options={defectOptions}
                                      style={{ width: 130, fontSize: 10, borderRadius: 0, minHeight: 25 }}
                                    />
                                  );
                                })()}
                              </div>
                            );
                          })}
                          {qcDefectsOpen[idx] && (function () {
                            /* 2026-10-15, Arturo (5th pass): "cada que
                               selecciono una condicion... debe tener
                               un boton para agregarla al renglon, una
                               condicion no se puede capturar dos
                               veces" — already-active categories are
                               excluded from the dropdown's own
                               options, so the same one can never be
                               added twice; opening the picker via the
                               "+" button IS the explicit add action,
                               picking a value commits it right away.
                               2026-10-15 (GFI Team/internal mode): now
                               available in both modes — catUniverse
                               already excludes Severe Condition for
                               internal mode. */
                            var catOptions = catUniverse.filter(function (cat) { return catsToShow.indexOf(cat) < 0; }).map(function (cat) { return { value: cat, label: t["qcCat" + cat.charAt(0).toUpperCase() + cat.slice(1)] }; });
                            if (!qcCategoryPickerOpen[idx]) {
                              return (
                                <button type="button" title={t.qcCategoryPlaceholder} disabled={catOptions.length === 0}
                                  onClick={function () { setQcCategoryPickerOpen(function (prev) { return { ...prev, [idx]: true }; }); }}
                                  style={{ ...qcPlusBtn, opacity: catOptions.length === 0 ? 0.4 : 1, cursor: catOptions.length === 0 ? "not-allowed" : "pointer" }}>+</button>
                              );
                            }
                            return (
                              <SearchSelect
                                value={pendingCat}
                                onChange={function (v) {
                                  setQcActiveCategories(function (prev) {
                                    var cur = prev[idx] || [];
                                    if (cur.indexOf(v) !== -1) return prev;
                                    return { ...prev, [idx]: cur.concat([v]) };
                                  });
                                  setQcPendingCategory(function (prev) { return { ...prev, [idx]: "" }; });
                                  setQcCategoryPickerOpen(function (prev) { return { ...prev, [idx]: false }; });
                                }}
                                placeholder={t.qcCategoryPlaceholder}
                                options={catOptions}
                                style={{ width: 130, fontSize: 10, borderRadius: 0, minHeight: 25 }}
                              />
                            );
                          })()}
                          <span style={{ flex: "1 1 auto" }} />
                          {/* 2026-10-15, Arturo (15th pass): this button
                              used to ONLY lock the row's UI, a purely
                              local toggle with nothing persisted — made
                              it also call handleSave() (the full
                              shipment save) so defects captured here
                              couldn't be lost by navigating away before
                              hitting the real window Save.
                              2026-10-22, Arturo (correcting that):
                              "al guardar guarda el lote completo, y me
                              regresa toda la ventana a modo no edicion,
                              los saves de cada renglon son individuales
                              para ese renglon, solo el save de la
                              ventana guarda todos los cambios" — calling
                              handleSave() here saves the ENTIRE shipment
                              and (via the onSave prop's own
                              setMode("view")) kicks the whole window
                              back to read-only, interrupting whatever
                              else he was mid-editing. Reverted to a
                              purely local toggle — the captured defects
                              already live in `f` (this form's draft
                              state) the instant they're added, same as
                              every other field in this form; they're
                              only ever persisted for real when Arturo
                              clicks the window's own Save, exactly like
                              everything else here. */}
                          <button type="button" disabled={!qcDefectsOpen[idx]} onClick={function () { setQcDefectsOpen(function (prev) { return { ...prev, [idx]: false }; }); }}
                            style={{ padding: "2px 8px", borderRadius: 0, border: "none", background: qcDefectsOpen[idx] ? "var(--color-accent)" : "var(--color-border)", color: qcDefectsOpen[idx] ? "var(--color-accent-text)" : "var(--color-text-muted)", cursor: qcDefectsOpen[idx] ? "pointer" : "not-allowed", opacity: qcDefectsOpen[idx] ? 1 : 0.5, fontSize: 9, fontWeight: 700 }}>
                            {t.qcDefectsSave}
                          </button>
                          <button type="button" disabled={!!qcDefectsOpen[idx]} onClick={function () { setQcDefectsOpen(function (prev) { return { ...prev, [idx]: true }; }); }}
                            style={{ padding: "2px 8px", borderRadius: 0, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: qcDefectsOpen[idx] ? "not-allowed" : "pointer", opacity: qcDefectsOpen[idx] ? 0.5 : 1, fontSize: 9, fontWeight: 700 }}>
                            {t.qcDefectsEdit}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                  if (!isSplit) return defectsRow ? [mainRow, defectsRow] : mainRow;
                  /* Producer sub-rows — only cases/traceability/grower
                     differ per producer; everything else about the
                     pallet is shown once, on mainRow above. Indented
                     under a light tint so the grouping reads clearly
                     even though it's several <tr>s. total/target lets
                     the user see at a glance whether the split still
                     adds up to what casesPallet shows (it always does
                     by construction, but a producer left at 0 cases
                     mid-edit is still worth flagging visually). */
                  var producerRows = (p.producers || []).map(function (pr, pIdx) {
                    return (
                      <tr key={idx + "-p" + pIdx} style={{ background: "rgba(42,122,111,0.04)", borderBottom: pIdx === p.producers.length - 1 ? "1px solid var(--color-border)" : "none" }}>
                        {/* Spacer covering Pallet#..Units/Case (9 cols,
                           +1 for the new actualShipperInitials column)
                           so the producer's Cases input lands directly
                           under the Cases/Pallet column of the row
                           above — same alignment trick for Traceability/
                           Grower Code further down this row. */}
                        <td colSpan={9}></td>
                        <td style={{ padding: "4px 3px" }}>
                          <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginBottom: 2, whiteSpace: "nowrap" }}>↳ {t.producerCases} #{pIdx + 1}</div>
                          <input type="number" min="0" value={pr.cases || ""} onChange={e => updateProducer(idx, pIdx, "cases", e.target.value)} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }} />
                        </td>
                        <td colSpan={2}></td>
                        <td style={{ padding: "4px 3px" }}><input value={pr.traceability || ""} onChange={e => updateProducer(idx, pIdx, "traceability", e.target.value)} placeholder={t.producerTraceability} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }} /></td>
                        <td style={{ padding: "4px 3px" }}>
                          <SearchSelect
                            value={pr.growerCode || ""}
                            onChange={function (v) { updateProducer(idx, pIdx, "growerCode", v); }}
                            placeholder="—"
                            limit={30}
                            allowCustom
                            nowrap
                            panelMinWidth={320}
                            options={(growers || []).map(function (g) {
                              var nm = g.personType === "moral" ? (g.razonSocial || "—") : ([g.nombre, g.apPaterno, g.apMaterno].filter(Boolean).join(" ") || "—");
                              var code = g.producerCode || "";
                              return { value: code, label: (code || "—") + " - " + nm, triggerLabel: code, search: nm + " " + code };
                            }).filter(function (o) { return !!o.value; })}
                            style={{ fontSize: 10, borderRadius: 0, minHeight: 25 }}
                          />
                        </td>
                        <td colSpan={5} style={{ padding: "4px 6px", textAlign: "right" }}>
                          <button type="button" onClick={() => removeProducer(idx, pIdx)} title={t.removeProducerRow} style={{ padding: "2px 6px", fontSize: 10, border: "1px solid rgba(220,38,38,0.3)", borderRadius: 4, background: "transparent", color: "#DC2626", cursor: "pointer" }}>✕</button>
                        </td>
                      </tr>
                    );
                  });
                  var addRow = (
                    <tr key={idx + "-add"} style={{ background: "rgba(42,122,111,0.04)", borderBottom: "2px solid var(--color-border)" }}>
                      <td colSpan={19} style={{ padding: "4px 6px", textAlign: "right" }}>
                        <button type="button" onClick={() => addProducer(idx)} style={{ padding: "2px 8px", fontSize: 10, border: "1px dashed var(--color-accent)", borderRadius: 4, background: "transparent", color: "var(--color-accent)", cursor: "pointer", marginRight: 6 }}>{t.addAnotherProducer}</button>
                        <button type="button" onClick={() => mergeProducersBack(idx)} style={{ padding: "2px 8px", fontSize: 10, border: "1px solid var(--color-border)", borderRadius: 4, background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer" }}>{t.removeProducerSplit}</button>
                      </td>
                    </tr>
                  );
                  return [mainRow].concat(defectsRow ? [defectsRow] : [], producerRows, [addRow]);
                  });
                })()}</tbody>
                {/* Subtotals (per commodity+size+label group) and grand
                    total. Both fold the per-pallet weight calc so the
                    weight column adds up without a second source of truth. */}
                {(function() {
                  /* Reduce over the deduped list — the row list above
                     still shows every stored row (including the ones
                     pnDupe already flags in red), only these aggregate
                     totals need protecting from double-counting a
                     repeated palletNum. */
                  var groups = {};
                  var grand = { pallets: 0, cases: 0, lb: 0 };
                  dedupePalletsByNumber(f.palletManifest).forEach(p => {
                    var cases = parseFloat(p.casesPallet) || 0;
                    var w = calcPalletWeight(p);
                    grand.pallets += (p.productBase || cases > 0) ? 1 : 0;
                    grand.cases += cases;
                    grand.lb += w.lb;
                    if (!p.productBase) return;
                    var key = (p.productBase || "—") + " · " + (p.size || "—") + " · " + (p.labelInitials || "—");
                    if (!groups[key]) groups[key] = { key: key, pallets: 0, cases: 0, lb: 0 };
                    groups[key].pallets += 1;
                    groups[key].cases += cases;
                    groups[key].lb += w.lb;
                  });
                  var rows = Object.values(groups);
                  if (rows.length === 0 && grand.pallets === 0) return null;
                  /* 2026-10-17, Arturo: "los colores de fondo de
                     subtotal y total llegan hasta donde aparece el
                     peso, debe cubrir todo hasta el final de la
                     tarjeta" — same fixed-colSpan-vs-dynamic-column-
                     count bug already fixed once in view mode: these
                     2 rows' fixed cells (5+1+1+1+3=11) never accounted
                     for the real, variable column count (14/16 base ±
                     Traceability+GrowerCode for FOB, + Quality/
                     Condition/Severe Condition/Cases Lost/Texas Work/
                     Status depending on isFob/isClariFresh) — so the
                     trailing colored band always stopped short. Last
                     cell's colSpan now computed to always reach the
                     true last column. */
                  /* 2026-10-23 — +1 for the new "actualShipperInitials"
                     column (internal-only, see translation key above),
                     always present regardless of isFob/isClariFresh. */
                  var totalCols = (isFob ? 12 : 14) + 1 + 2 + (isClariFresh ? 1 : 0) + 2 + (isClariFresh ? 1 : 0);
                  var lastColSpan = Math.max(1, totalCols - 12);
                  return (
                    <tfoot>
                      {/* 2026-10-20, Arturo: "subtotales, totales no
                          negritas incluyendo sus valores tanto en
                          pallets cajas y peso" — fontWeight removed
                          from the label AND every value cell (cases/
                          pallets/lb-kg) on both the subtotal and grand
                          total rows. */}
                      {rows.map(g => (
                        <tr key={g.key} style={{ background: "var(--color-bg)", fontSize: 10 }}>
                          <td colSpan={6} style={{ padding: "6px 8px", color: "var(--color-text-secondary)" }}>{t.palletSubtotal} · {g.key}</td>
                          <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-muted)" }}>—</td>
                          <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{g.cases}</td>
                          <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{g.pallets} plt</td>
                          <td colSpan={3}></td>
                          <td colSpan={lastColSpan} style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{g.lb.toFixed(1)} lb / {(g.lb * 0.453592).toFixed(1)} kg</td>
                        </tr>
                      ))}
                      {/* 2026-10-17, Arturo: same hardcoded-teal bug
                          already fixed in the view-mode total row —
                          swapped for the scoped muted blue here too. */}
                      <tr style={{ background: "rgba(62,110,150,0.08)", fontSize: 11, borderTop: "2px solid var(--color-accent)" }}>
                        <td colSpan={7} style={{ padding: "8px 4px", color: "var(--color-accent)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.palletGrandTotal}</td>
                        <td style={{ padding: "8px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "var(--color-accent)" }}>{grand.cases}</td>
                        <td style={{ padding: "8px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "var(--color-accent)" }}>{grand.pallets} plt</td>
                        <td colSpan={3}></td>
                        <td colSpan={lastColSpan} style={{ padding: "8px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "var(--color-accent)" }}>{grand.lb.toFixed(1)} lb / {(grand.lb * 0.453592).toFixed(1)} kg</td>
                      </tr>
                    </tfoot>
                  );
                })()}
              </table>
            </div>
            {/* 2026-10-15, Arturo: "la regression no me la reportan
                a nivel pallet, si no a nivel el total de la carga...
                ayudame a construir la idea: 3 pallets con un
                comentario como total regression shown in this load
                was ___" — Twin River only grades Regression as one
                combined % for the whole load, never broken out per
                pallet (unlike every other Blackberry defect). Lives
                as its own shipment-level field (f.lotRegressionPct,
                not on any one pallet), captured once, right under
                the manifest table — never inside a pallet's own
                defects row. Blackberry/ClariFresh only.
                2026-10-20, Arturo: "amplíala hasta el extremo
                derecho" — moved out from inside the table's own
                overflowX:auto wrapper (where its real width matched
                the 1200px scrollable table, invisible past the
                card's visible edge) to a sibling of that wrapper, so
                it spans the full visible card width instead. */}
            {isClariFresh && f.palletManifest.some(function (p) { return p.productBase === "Blackberry"; }) && (
              <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 6, background: "rgba(217,119,6,0.06)", border: "1px solid rgba(217,119,6,0.25)", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, color: "#B45309" }}>{t.lotRegressionLabel}</span>
                <input type="number" min="0" max="100" step="0.1" placeholder="%" value={f.lotRegressionPct === undefined || f.lotRegressionPct === null ? "" : f.lotRegressionPct}
                  onChange={function (e) { setF(function (prev) { return { ...prev, lotRegressionPct: e.target.value === "" ? undefined : Number(e.target.value) }; }); setDirty(true); }}
                  style={{ width: 60, padding: "3px 6px", borderRadius: 4, border: "1px solid rgba(217,119,6,0.35)", background: "#fff", color: "#B45309", fontSize: 11, fontWeight: 700, textAlign: "center" }} />
                <span style={{ fontSize: 11, color: "#B45309" }}>%</span>
              </div>
            )}
          </div>)}

          {/* 2026-10-17, Arturo: "secciona como en el modo no editable
              con sus tarjetas" \u2014 Documents/Notes titles are now real
              clickable toggles (were static labels). */}
          <EditSectionHeader id="documents" title={t.documents} count={(f.documents || []).length} />
          {openEdit.documents && (
          <div style={{ padding: "8px 20px 6px" }}>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {f.documents.map((doc, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", borderRadius: 5, background: "var(--color-bg)", border: "1px solid var(--color-border)", fontSize: 11 }}>
                  <span>{"\ud83d\udcc4"}</span>
                  <span style={{ fontWeight: 600, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.name}</span>
                  <button onClick={() => removeDoc(i)} style={{ background: "none", border: "none", color: "var(--color-text-muted)", cursor: "pointer", fontSize: 11, padding: 0 }}>{"\u2715"}</button>
                </div>
              ))}
              <FileDropZone onFiles={processDocs}>
                <label style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 5, border: "1px dashed var(--color-border)", fontSize: 11, color: "var(--color-text-muted)", cursor: "pointer" }}>
                  {"\ud83d\udcce"} {t.uploadDoc}
                  <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" onChange={handleFileUpload} style={{ display: "none" }} />
                </label>
              </FileDropZone>
            </div>
          </div>
          )}

          <EditSectionHeader id="notes" title={t.notes} />
          {openEdit.notes && (
          <div style={{ padding: "8px 20px 12px" }}>
            <textarea value={f.notes || ""} onChange={e => set("notes", e.target.value)} rows={2} style={{ ...inp, resize: "vertical", fontSize: 11 }} />
          </div>
          )}
        </div>

        {/* FOOTER */}
        <div style={{ padding: "10px 20px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 6 }}>
            {!isNew && !showDel && <button onClick={() => setShowDel(true)} style={{ padding: "5px 12px", borderRadius: 0, border: "1px solid rgba(239,68,68,0.3)", background: "transparent", color: "#ef4444", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.delete}</button>}
            {showDel && (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 11, color: "#ef4444" }}>{t.confirmDel}</span>
                <button onClick={() => onDelete(shipment.id)} style={{ padding: "4px 10px", borderRadius: 0, border: "none", background: "#ef4444", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.yes}</button>
                <button onClick={() => setShowDel(false)} style={{ padding: "4px 10px", borderRadius: 0, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}>{t.no}</button>
              </div>
            )}
          </div>
          {/* 2026-10-17, Arturo: "clear form, cancel y save, primero
              remueve el icono, despues hazlos con relieve 3d silver
              metalico, save sigue en azul 3d metalico" — same
              brushed-metal plate style used throughout this redesign
              (headers, Preview/Print/kebab/Edit). */}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {!showClearConfirm ? (
              <button onClick={() => setShowClearConfirm(true)} title={t.confirmClear} style={{ padding: "7px 14px", borderRadius: 0, border: "1px solid #b9bcc0", background: "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.15)", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.clearForm}</button>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 11, color: "#ef4444" }}>{t.confirmClear}</span>
                <button onClick={doClearForm} style={{ padding: "4px 10px", borderRadius: 0, border: "none", background: "#ef4444", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.yes}</button>
                <button onClick={() => setShowClearConfirm(false)} style={{ padding: "4px 10px", borderRadius: 0, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}>{t.no}</button>
              </div>
            )}
            <button onClick={tryCancel} style={{ padding: "7px 16px", borderRadius: 0, border: "1px solid #b9bcc0", background: "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.15)", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.cancel}</button>
            <button onClick={handleSave} style={{ padding: "7px 20px", borderRadius: 0, border: "1px solid #1a3550", background: "linear-gradient(180deg, #6FA3C7 0%, #3E6E96 55%, #284d6b 100%)", boxShadow: "inset 0 1px 1px rgba(255,255,255,0.4), inset 0 -1px 2px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.25)", color: "#ffffff", textShadow: "0 1px 1px rgba(0,0,0,0.3)", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>{t.save}</button>
          </div>
        </div>

        {showExitConfirm && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10 }}>
            <div style={{ background: "var(--color-bg-card-solid)", padding: 24, borderRadius: 12, maxWidth: 300, textAlign: "center" }}>
              <p style={{ fontSize: 13, marginBottom: 14 }}>{t.confirmExit}</p>
              <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                <button onClick={onCancel} style={{ padding: "6px 14px", borderRadius: 0, border: "none", background: "#ef4444", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.yesExit}</button>
                <button onClick={() => setShowExitConfirm(false)} style={{ padding: "6px 14px", borderRadius: 0, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 12 }}>{t.noExit}</button>
              </div>
            </div>
          </div>
        )}

        {saveWarnText && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10 }}>
            <div style={{ background: "var(--color-bg-card-solid)", padding: 24, borderRadius: 12, maxWidth: 360, textAlign: "left" }}>
              <p style={{ fontSize: 12, marginBottom: 14, whiteSpace: "pre-wrap" }}>{saveWarnText}</p>
              <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                <button onClick={doSaveConfirmed} style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.yesContinueSaving}</button>
                <button onClick={() => setSaveWarnText(null)} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 12 }}>{t.noContinueSaving}</button>
              </div>
            </div>
          </div>
        )}
    </>
  );
  if (embedded) return body;
  /* 2026-10-17, Arturo: "shipment type a la barra de titulo despues de
     edit shipment" — standalone path (new shipment / duplicate) has
     direct access to isFob/setShipmentType, so no lifting needed here
     (unlike the embedded path, see onShipmentTypeApi above). */
  const shipmentTypeToggle = (
    <div style={{ display: "inline-flex", border: "1px solid #b9bcc0" }}>
      <button type="button" onClick={() => setShipmentType("import")} style={{ padding: "4px 12px", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", background: !isFob ? "var(--color-accent)" : "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", color: !isFob ? "#ffffff" : "#1a2636", boxShadow: !isFob ? "inset 0 1px 1px rgba(255,255,255,0.3), inset 0 -1px 2px rgba(0,0,0,0.2)" : "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08)", textShadow: !isFob ? "0 1px 1px rgba(0,0,0,0.25)" : "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)" }}>{lang === "es" ? "Importación" : "Import"}</button>
      <button type="button" onClick={() => setShipmentType("fob")} style={{ padding: "4px 12px", border: "none", borderLeft: "1px solid #b9bcc0", fontSize: 11, fontWeight: 700, cursor: "pointer", background: isFob ? "var(--color-accent)" : "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", color: isFob ? "#ffffff" : "#1a2636", boxShadow: isFob ? "inset 0 1px 1px rgba(255,255,255,0.3), inset 0 -1px 2px rgba(0,0,0,0.2)" : "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08)", textShadow: isFob ? "0 1px 1px rgba(0,0,0,0.25)" : "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)" }}>{"FOB (Texas)"}</button>
    </div>
  );
  return (
    <DraggableModal title={(isNew ? t.add : t.edit) + (f.lot ? "  ·  LOT " + f.lot : "")} onClose={tryCancel} initialWidth={820} initialHeight={Math.min(900, window.innerHeight - 60)} titleBarVariant="groundfresh" titleLeftActions={shipmentTypeToggle}>
      {body}
    </DraggableModal>
  );
}

/* ======================== MAIN REGISTRY ======================== */
/* `lockSupplierId` (optional) renders the registry inside the Shipper
   Portal. When set:
     * the list is pre-filtered to that supplier
     * the shipper / sale-status / cert filters in the toolbar are hidden
     * the supplier picker in ShipmentForm starts pre-set + locked
     * `shipperMode` ON → DetailPanel surfaces a "Submit to GFI" banner
       when the record's shipperStatus is draft / rejected / empty
     * `hideFinancials` (paired with lockSupplierId) hides the price
       column in the list and the price input in the form's line table
       so the shipper never sees financial data.
   Receives the LEGACY supplier id. When falsy the registry is the
   original admin view. */
export default function ShipmentRegistry({ lang: globalLang, lockSupplierId, hideFinancials, openShipmentId, onShipmentOpened }) {
  const lang = globalLang || "es"; const t = T[lang];
  const [shipments, setShipments] = useState([]);
  const [search, setSearch] = useState(""); const [filterShipper, setFilterShipper] = useState(""); const [filterWarehouse, setFilterWarehouse] = useState(""); const [filterStatus, setFilterStatus] = useState(""); const [filterSaleStatus, setFilterSaleStatus] = useState(""); const [filterCert, setFilterCert] = useState(""); const [filterWeek, setFilterWeek] = useState(""); const [filterProduct, setFilterProduct] = useState("");
  /* Tabs filter (status) — mirrors BillRegistry / InvoiceRegistry. */
  const [cardFilter, setCardFilter] = useState(null);
  /* For the Pending Pick Up / Pending Delivery SALE-column display
     default (see saleStatusCalc.js resolvePendingStage). */
  const [slPassingIndex, setSlPassingIndex] = useState({});
  useEffect(function () {
    var cancelled = false;
    loadFromSupabase("sweetleaf_invoices").then(function (sl) {
      if (!cancelled) setSlPassingIndex(buildSlPassingIndex(sl));
    }).catch(function () {});
    return function () { cancelled = true; };
  }, []);
  /* 2026-09-14 — Arturo: updated Terey Avocados' proforma prefix in
     Shipper Settings on his PC; a device that never separately opened
     the Settings screen (his iPad, in this case) kept whatever stale
     gfi-shipper-settings localStorage it already had, so the new
     prefix never showed when creating a shipment there — same
     mount-time priming pattern ShipperSettings.js itself already uses.
     Merge, not overwrite: Supabase wins per-field only when it has a
     real value, so an in-progress local edit elsewhere isn't clobbered. */
  useEffect(function () {
    var alive = true;
    loadFromSupabase("shipper_settings").then(function (rows) {
      if (!alive || !rows || rows.length === 0) return;
      try {
        var all = loadLS("gfi-shipper-settings", {}) || {};
        var changed = false;
        rows.forEach(function (r) {
          if (!r || !r.id) return;
          var existing = all[r.id] || {};
          var merged = { ...existing };
          Object.keys(r).forEach(function (k) {
            if (k === "id" || k === "_uuid" || k.indexOf("_") === 0) return;
            if (r[k] !== undefined && r[k] !== null) merged[k] = r[k];
          });
          if (JSON.stringify(merged) !== JSON.stringify(existing)) { all[r.id] = merged; changed = true; }
        });
        if (changed) localStorage.setItem("gfi-shipper-settings", JSON.stringify(all));
      } catch (e) { /* swallow — falls back to whatever localStorage already had */ }
    }).catch(function () {});
    return function () { alive = false; };
  }, []);
  /* Filter popover (⋮ Filters button) — collapses the full filter
     set into an anchored panel; only search stays always-visible. */
  const [showFilters, setShowFilters] = useState(false);
  const filtersRef = useRef(null);
  /* 2026-10-20, Arturo: "necesito un menu de 3 puntos... donde metas
     receiving sheet, bill of lading, send tracking link y export
     excel, y ponlo antes de new shipment" — collapses those 4
     toolbar actions (previously their own separate buttons) into one
     kebab menu, same anchored-popover pattern as showFilters right
     above. */
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef(null);
  /* 2026-10-26 — same staleness bug as ShipmentForm's own certsVersion
     fix above (different component instance, so it needs its own
     fetch): the cert FILTER dropdown in this list's toolbar reads
     getExportCerts() too, which is only ever as fresh as whichever
     module last opened ExportCertificateRegistry.js on this device. */
  const [listCertsVersion, setListCertsVersion] = useState(0);
  useEffect(function () {
    var alive = true;
    loadFromSupabase("export_certs", { force: true }).then(function (rows) {
      if (!alive) return;
      try { localStorage.setItem("gfi-export-certs", JSON.stringify(rows)); } catch (e) {}
      setListCertsVersion(function (v) { return v + 1; });
    }).catch(function () {});
    return function () { alive = false; };
  }, []);
  /* Cert filter options — read once from gfi-export-certs and sort by
     number. Each option's value is the cert id (matches line.exportCertId
     when set by the importer / shipment editor); the label shows the
     human cert number plus the shipper for disambiguation. */
  const certFilterOptions = useMemo(function () {
    var raw = getExportCerts();
    var seen = {};
    var arr = [];
    raw.forEach(function (c) {
      if (!c || seen[c.id]) return;
      seen[c.id] = true;
      arr.push({ value: c.id, label: (c.certNumber || c.id) + (c.shipperName ? " · " + c.shipperName : "") });
    });
    arr.sort(function (a, b) { return String(a.label).localeCompare(String(b.label)); });
    return arr;
  }, [listCertsVersion]);
  /* Reverse lookup id → normalized cert number for filter matching against
     lines that only carry exportCertNumber / exportCertManual (e.g.
     pre-import data where the id wasn't resolved). */
  const certIdToNum = useMemo(function () {
    var m = {};
    getExportCerts().forEach(function (c) { if (c && c.id) m[c.id] = nrmCertNum(c.certNumber); });
    return m;
  }, [listCertsVersion]);
  /* Season filter — multi-select grouped (Open / Closed) per CLAUDE.md
     "REGLA DE FILTRO DE SEASON". Pre-selects all Open season names so the
     first render only shows current data. */
  const [filterSeasons, setFilterSeasons] = useState(() => defaultOpenSeasonIds());
  const [dateFrom, setDateFrom] = useState(""); const [dateTo, setDateTo] = useState("");
  /* Seasons: seed from localStorage so the first paint isn't blank,
     then refresh from Supabase. The seed handles the warm case where
     a prior session populated the cache; the Supabase fetch handles
     the cold case (App.js prime hasn't landed yet, or localStorage
     was wiped). Re-renders when seasons changes because seasonNameFor
     depends on it. */
  const [seasons, setSeasons] = useState(() => getSeasons());
  useEffect(() => {
    let alive = true;
    loadFromSupabase("seasons").then(rows => {
      if (!alive) return;
      setSeasons(rows);
      try { localStorage.setItem("gfi-seasons", JSON.stringify(rows)); } catch (e) {}
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  /* Maps a record date to its season name. The lookup is by date range
     (startDate..endEstimate) so historical imports get classified even
     when seasonId wasn't stored. Falls back to the record's seasonId
     for older shipments. */
  const seasonNameFor = useCallback((dateStr, sid) => {
    if (sid) { var byId = seasons.find(s => String(s.id) === String(sid)); if (byId) return byId.seasonName || byId.startDate.substring(0, 4) + "-" + byId.endEstimate.substring(2, 4); }
    if (!dateStr) return "";
    var d = new Date(dateStr + "T00:00:00");
    var s = seasons.find(s => new Date(s.startDate + "T00:00:00") <= d && d <= new Date(s.endEstimate + "T00:00:00"));
    return s ? (s.seasonName || s.startDate.substring(0, 4) + "-" + s.endEstimate.substring(2, 4)) : "";
  }, [seasons]);
  /* Persist sort across sessions per CLAUDE.md feedback. */
  const [sortCol, setSortCol] = useState(() => { try { const s = JSON.parse(localStorage.getItem("gfi-shipments-sort") || "null"); return (s && s.column) || "lot"; } catch { return "lot"; } });
  const [sortDir, setSortDir] = useState(() => { try { const s = JSON.parse(localStorage.getItem("gfi-shipments-sort") || "null"); return (s && s.direction) || "desc"; } catch { return "desc"; } });
  useEffect(() => { try { localStorage.setItem("gfi-shipments-sort", JSON.stringify({ column: sortCol, direction: sortDir })); } catch {} }, [sortCol, sortDir]);
  /* Close the filter popover on outside click + Escape. */
  useEffect(() => {
    if (!showFilters) return;
    const onDown = (e) => { if (filtersRef.current && !filtersRef.current.contains(e.target) && !(e.target.closest && e.target.closest('[data-searchselect]'))) setShowFilters(false); };
    const onKey  = (e) => { if (e.key === "Escape") setShowFilters(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showFilters]);
  /* Close the "⋮" more-actions menu on outside click + Escape — same
     pattern as the filter popover right above. */
  useEffect(() => {
    if (!showMoreMenu) return;
    const onDown = (e) => { if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) setShowMoreMenu(false); };
    const onKey  = (e) => { if (e.key === "Escape") setShowMoreMenu(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showMoreMenu]);
  const [viewing, setViewing] = useState(null); const [editing, setEditing] = useState(null); const [showForm, setShowForm] = useState(false);
  /* 2026-10-26, Arturo — voice-dictation capture (Fase 1 of the
     approved voice-assistant plan). Never saves anything itself: on
     completion it just pre-fills a draft and opens the SAME
     ShipmentForm/handleSave flow as "+ New shipment", for Arturo to
     review and save manually. */
  const [showVoiceAssistant, setShowVoiceAssistant] = useState(false);
  const [toast, setToast] = useState(null); const [loading, setLoading] = useState(true);
  /* Expose a global hook so child components (DetailPanel) can fire
     toasts without prop-drilling. Cleaned up on unmount. */
  useEffect(function () {
    window.__gfiToast = function (msg) { setToast(msg); };
    return function () { try { delete window.__gfiToast; } catch (e) {} };
  }, []);
  const [selected, setSelected] = useState({});
  /* List-level Send-Tracking-Link state. Holds the resolved shipment
     payload when the toolbar button is clicked with exactly one row
     selected; null = modal closed. Separate from the DetailPanel's
     own trackingOpen so the two paths never collide. */
  const [listTrackingOpen, setListTrackingOpen] = useState(null);
  /* Pending submissions from the Shipper Portal. Each row in
     gfi-shipper-shipments is an EMBARQUE (shipper invoice) that
     carries multiple proformas; each proforma is what Arturo
     accepts/rejects individually (one accepted proforma = one GFI
     lot). We flatten the embarques into a list of { embarque, pro }
     pairs filtered to proformas with status="submitted". */
  const [portalPending, setPortalPending] = useState([]);
  const [reviewingPortal, setReviewingPortal] = useState(null); /* { embarque, pro } */
  const [portalBump, setPortalBump] = useState(0);
  useEffect(() => {
    var cancelled = false;
    /* CRITICAL: source of truth is Supabase `shipper_shipments` — the
       shipper user submits from THEIR browser, so the admin's
       localStorage never sees it. Falling back to LS would silently
       hide submissions on first load (the original bug: admin saw
       "0 pending" while the shipper saw "Enviado"). LS is kept as a
       last-resort fallback for offline mode only. */
    var loadPortal = function () {
      loadFromSupabase("shipper_shipments", { force: true })
        .then(function (embs) {
          if (cancelled) return;
          if (typeof console !== 'undefined' && console.log) {
            console.log('[ShipmentRegistry][loadPortal] loaded ' + (embs || []).length + ' embarque(s) from Supabase');
            var fileSummary = function (f) {
              if (!f) return '(none)';
              var bits = [f.name || '?'];
              if (typeof f.url === 'string' && f.url) bits.push('url=' + f.url.length + 'ch');
              if (typeof f.path === 'string' && f.path) bits.push('path=' + f.path);
              if (typeof f.data === 'string') bits.push('data=' + f.data.length + 'ch');
              return bits.join(' · ');
            };
            (embs || []).forEach(function (e, i) {
              console.log('  emb[' + i + ']', {
                id: e.id,
                consec: e.consecEmbarque,
                invoiceFile: fileSummary(e.invoiceFile),
                exportCertFile: fileSummary(e.exportCertFile),
                otherDocs: (e.otherDocs || []).length + ' file(s)',
                docsCol: e.docs ? Object.keys(e.docs).join(',') : '(none)',
                proformaStatuses: (e.proformas || []).map(function (p) { return p.status; }),
              });
            });
          }
          var pairs = [];
          (embs || []).forEach(function (e) {
            (e.proformas || []).forEach(function (p) {
              if ((p.status || "draft") === "submitted") pairs.push({ embarque: e, pro: p });
            });
          });
          setPortalPending(pairs);
          /* Dual-write LS so the accept/reject sync at line ~3982
             (still LS-aware) and any other legacy reader stays
             consistent across this tab. */
          try { localStorage.setItem("gfi-shipper-shipments", JSON.stringify(embs || [])); } catch (e) {}
        })
        .catch(function (err) {
          console.error("[ShipmentRegistry] portal pending load failed, falling back to LS", err);
          if (cancelled) return;
          var embs = loadLS("gfi-shipper-shipments", []);
          var pairs = [];
          embs.forEach(function (e) {
            (e.proformas || []).forEach(function (p) {
              if ((p.status || "draft") === "submitted") pairs.push({ embarque: e, pro: p });
            });
          });
          setPortalPending(pairs);
        });
    };
    loadPortal();
    var onFocus = function () { loadPortal(); };
    window.addEventListener("focus", onFocus);
    return function () { cancelled = true; window.removeEventListener("focus", onFocus); };
  }, [portalBump]);

  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const aliveRef = useRef(true);
  const shipmentsRef = useRef([]);
  useEffect(() => { shipmentsRef.current = shipments; }, [shipments]);
  /* Tracks whether the currently-open shipment viewer came from the
     openShipmentId deep link, so onClose knows to fire the
     "gfi-shipment-closed" return-navigation event. */
  const openedFromExternalRef = useRef(false);
  /* 2026-09-14 — Arturo: "cualquier cosa que abra desde el dashboard, al
     cerrar que me regrese al dashboard". Separate, self-contained
     mechanism from the openShipmentId deep link above: window.__gfiReturnModule
     is set by Dashboard.js's navigateAndOpen/navigateAndCall right
     before it calls __gfiShipmentOpen/__gfiShipmentNew — captured here
     and consumed on the matching Close/Cancel/Save/Delete so a shipment
     opened via a normal in-module click behaves exactly as before. */
  const returnModuleRef = useRef(null);
  const closeToOrigin = useCallback(() => {
    if (returnModuleRef.current && window.__gfiNavigate) window.__gfiNavigate(returnModuleRef.current);
    returnModuleRef.current = null;
  }, []);

  /* Source of truth = Supabase `shipments`. Dual-write to localStorage
     so legacy callers that still read gfi-shipments sync (BillRegistry
     line-sync, computeCertUsage, ShipperPortal, etc. — all in
     subsequent paso 3c batches) keep working. Drop the dual write
     once those callers are all migrated. */
  /* partialProtection is canonical again as of 2026-09-12 — computed
     from pallet-level status on the invoice (saleStatusCalc.js), never
     manually chosen. The collapse-to-"protection" step that used to run
     here unconditionally on every load was removed: it would have
     silently undone every freshly-computed Partial Protection value the
     moment shipments reloaded. */
  const SS_CANON_SHP = useMemo(() => ({ goodDelivery: 1, protection: 1, pas: 1, partialProtection: 1, samples: 1, dumped: 1, donated: 1, transfer: 1 }), []);
  const SS_MAP_SHP = useMemo(() => ({ gooddelivery: "goodDelivery", protection: "protection", pas: "pas", sample: "samples", samples: "samples", dumped: "dumped", donated: "donated", transfer: "transfer" }), []);
  /* In-memory transforms applied after every Supabase load so the UI
     shows canonical sale statuses. The persisted store (Supabase) may
     still have unnormalized labels if the localStorage migration didn't
     run before the Supabase migration. The Tools → Migrate to Supabase
     flow re-runs idempotently and resolves any drift. */
  const normalizeShipmentLines = useCallback((arr) => arr.map(s => {
    if (!s.lines || s.lines.length === 0) return s;
    let touched = false;
    const nl = s.lines.map(l => {
      if (!l) return l;
      let next = l;
      if (l.saleStatus && !SS_CANON_SHP[l.saleStatus]) {
        const k = String(l.saleStatus).toLowerCase().replace(/[^a-z0-9]/g, "");
        const mapped = SS_MAP_SHP[k];
        if (mapped && mapped !== l.saleStatus) { next = { ...next, saleStatus: mapped }; touched = true; }
      }
      return next;
    });
    return touched ? { ...s, lines: nl } : s;
  }), [SS_CANON_SHP, SS_MAP_SHP]);

  const writeShipmentsCache = (next) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (e) {}
  };

  const reload = async () => {
    setError(null); setLoading(true);
    try {
      const rows = await loadFromSupabase("shipments", { force: true });
      const normalized = normalizeShipmentLines(rows);
      if (aliveRef.current) { setShipments(normalized); writeShipmentsCache(normalized); }
    } catch (e) {
      if (aliveRef.current) setError("Could not load shipments: " + (e.message || String(e)));
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  };
  useEffect(() => {
    aliveRef.current = true;
    setError(null); setLoading(true);
    loadFromSupabase("shipments")
      .then(rows => {
        const normalized = normalizeShipmentLines(rows);
        if (aliveRef.current) { setShipments(normalized); writeShipmentsCache(normalized); }
      })
      .catch(e => { if (aliveRef.current) setError("Could not load shipments: " + (e.message || String(e))); })
      .finally(() => { if (aliveRef.current) setLoading(false); });
    return () => { aliveRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ONE-TIME DATA RESTORATION
     Lots 57903–57920 had their supplierId / shipperName overwritten when
     the Shipper/Client toggle's old click handlers wiped those fields on
     toggle. Re-stamp them here:
       57903–57917, 57919–57920 → Gomez Berries
       57918                    → Healthy Harvest
       all of the above         → shipperSource: "shipper"
     Guarded by a localStorage flag so the body short-circuits after the
     first successful run. Safe to leave in the codebase indefinitely. */
  useEffect(() => {
    var FLAG = "gfi-mig-shipper-57903-57920-v1";
    try { if (localStorage.getItem(FLAG)) return; } catch (e) { return; }
    var ships = loadLS(STORAGE_KEY, []);
    if (!Array.isArray(ships) || ships.length === 0) return;
    var sups = loadLS("gfi-suppliers", []);
    function findSup(needle) {
      var n = needle.toLowerCase();
      return sups.find(function(s) {
        var name = (s.shortName || s.fullName || s.name || s.companyName || "").toLowerCase();
        return name.indexOf(n) >= 0;
      });
    }
    var gomez = findSup("gomez");
    var healthy = findSup("healthy harvest");
    if (!gomez && !healthy) return; /* suppliers missing — skip silently, retry next mount */
    var changed = false;
    var next = ships.map(function(s) {
      var lotNum = parseInt(s.lot, 10);
      if (!(lotNum >= 57903 && lotNum <= 57920)) return s;
      var target = lotNum === 57918 ? healthy : gomez;
      if (!target) return s;
      var nm = target.shortName || target.fullName || target.name || target.companyName || "";
      var initials = target.initials || (nm || "XX").substring(0, 2).toUpperCase();
      /* Skip records already correct so we don't gratuitously rewrite */
      if (String(s.supplierId) === String(target.id) && s.shipperSource === "shipper") return s;
      changed = true;
      return { ...s, supplierId: target.id, shipperName: nm, shipperInitials: initials, shipperSource: "shipper" };
    });
    if (changed) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); setShipments(next); } catch (e) { return; }
    }
    try { localStorage.setItem(FLAG, "1"); } catch (e) {}
  }, []);

  /* Open-by-id hook for global search */
  useEffect(() => {
    window.__gfiShipmentOpen = (id) => {
      const ret = window.__gfiReturnModule; window.__gfiReturnModule = null;
      const s = (shipmentsRef.current || []).find(x => x.id === id);
      if (s) { returnModuleRef.current = ret; setViewing(s); }
    };
    return () => { delete window.__gfiShipmentOpen; };
  }, []);

  /* PalletBreakdown Lot-cell deep link — same open path as
     window.__gfiShipmentOpen / the row onClick (setViewing(s)). Waits
     on `loading` because shipments load async from Supabase; firing
     on the initial (empty) render would miss the shipment and clear
     the pending id before data arrives. */
  useEffect(() => {
    if (!openShipmentId || loading) return;
    const s = (shipmentsRef.current || []).find(x => x.id === openShipmentId);
    if (s) { setViewing(s); openedFromExternalRef.current = true; }
    if (onShipmentOpened) onShipmentOpened();
  }, [openShipmentId, loading]);

  /* "me voy a dormir" — reopen-after-cross-module-roundtrip, mirroring
     Bill/Invoice's window.__gfiBillReopenId/__gfiInvoiceReopenId. Lets
     a Supplier link opened FROM a shipment return to that exact
     shipment (not just the Shipments list) once the Supplier record
     is closed. */
  useEffect(() => {
    if (!window.__gfiShipmentReopenId) return;
    if (loading) return;
    const id = window.__gfiShipmentReopenId; window.__gfiShipmentReopenId = null;
    const s = (shipmentsRef.current || []).find(x => x.id === id);
    if (s) setViewing(s);
  }, [loading, shipments]);

  /* Blank-form create hook for Create New menu */
  useEffect(() => {
    window.__gfiShipmentNew = () => {
      returnModuleRef.current = window.__gfiReturnModule; window.__gfiReturnModule = null;
      setEditing(null); setShowForm(true);
    };
    return () => { delete window.__gfiShipmentNew; };
  }, []);

  /* persistSingle: save one record to Supabase, update local state, and
     mirror to localStorage for legacy sync consumers. Used by every
     handler below (single save, status change, accept-from-portal,
     onCreateBill flip). Throws on failure so callers can surface error. */
  const persistSingle = async (record) => {
    const saved = await saveToSupabase("shipments", record);
    const current = shipmentsRef.current || [];
    const ex = current.find(x => x.id === record.id);
    const next = ex ? current.map(x => x.id === record.id ? saved : x) : current.concat([saved]);
    setShipments(next); writeShipmentsCache(next);
    return saved;
  };
  /* persistMany: bulk saves for status changes and the like. */
  const persistMany = async (records) => {
    if (records.length === 0) return [];
    const saved = await saveManyToSupabase("shipments", records);
    const savedMap = {};
    saved.forEach(s => { savedMap[s.id] = s; });
    const next = (shipmentsRef.current || []).map(s => savedMap[s.id] || s);
    setShipments(next); writeShipmentsCache(next);
    return saved;
  };
  /* persistDelete: single delete from Supabase + local + cache. */
  const persistDelete = async (id) => {
    await deleteFromSupabase("shipments", id);
    const next = (shipmentsRef.current || []).filter(x => x.id !== id);
    setShipments(next); writeShipmentsCache(next);
  };

  /* Resolve names from registries — ALWAYS lookup from source of truth */
  const resolvedShipments = useMemo(() => {
    const sups = getSuppliers(); const whs = getWarehouses();
    return shipments.map(s => {
      var sn = s.shipperName || "";
      var wn = s.warehouseName || "";
      if (s.supplierId) {
        var sup = sups.find(x => String(x.id) === String(s.supplierId));
        if (sup) {
          var supName = sup.shortName || sup.fullName || sup.name || sup.companyName || "";
          if (!supName && (sup.ownerNombre || sup.nombre)) supName = [sup.ownerNombre || sup.nombre, sup.ownerApPaterno || sup.apPaterno, sup.ownerApMaterno || sup.apMaterno].filter(Boolean).join(" ");
          if (supName) sn = supName;
        }
      }
      if (s.warehouseId) {
        var wh = whs.find(x => String(x.id) === String(s.warehouseId));
        if (wh) {
          var whName = wh.shortName || wh.fullName || wh.name || wh.companyName || "";
          if (whName) wn = whName;
        }
      }
      return { ...s, shipperName: sn, warehouseName: wn };
    });
  }, [shipments]);

  /* Sync export certificate usage when shipment status changes */
  const syncExportCerts = useCallback((savedShipment, previousShipment) => {
    const newStatus = savedShipment.status;
    const oldStatus = previousShipment ? previousShipment.status : null;
    const becameReceived = (newStatus === "received" || newStatus === "billed") && oldStatus !== "received" && oldStatus !== "billed";
    const revertedFromReceived = (newStatus === "draft") && (oldStatus === "received" || oldStatus === "billed");

    if (!becameReceived && !revertedFromReceived) return;

    try {
      var certs = loadLS("gfi-export-certs", []);
      var changed = false;
      (savedShipment.lines || []).forEach(line => {
        if (!line.exportCertId || line.exportCertId === "pending") return;

        /* Find the cert — by ID if from dropdown, or by cert number if manual */
        var certIdx = -1;
        if (line.exportCertId === "manual") {
          var certNum = line.exportCertManual || line.exportCertNumber || "";
          if (!certNum) return;
          certIdx = certs.findIndex(c => c.certNumber === certNum);
        } else {
          certIdx = certs.findIndex(c => String(c.id) === String(line.exportCertId));
        }
        if (certIdx === -1) return;
        var cert = { ...certs[certIdx] };
        var used = Array.isArray(cert.usedInvoices) ? [...cert.usedInvoices] : [];

        if (becameReceived) {
          /* Add usage if not already tracked */
          if (!used.some(u => u.shipmentId === savedShipment.id && u.productLine === line.productId)) {
            used.push({ shipmentId: savedShipment.id, lot: savedShipment.lot, shipperInvoice: savedShipment.shipperInvoice || "", productLine: line.productId, date: savedShipment.dateArrival || savedShipment.dateDeparture || new Date().toISOString().substring(0, 10) });
            changed = true;
          }
        } else if (revertedFromReceived) {
          /* Remove usage */
          var before = used.length;
          used = used.filter(u => !(u.shipmentId === savedShipment.id && u.productLine === line.productId));
          if (used.length !== before) changed = true;
        }
        cert.usedInvoices = used;
        certs[certIdx] = cert;
      });
      if (changed) {
        localStorage.setItem("gfi-export-certs", JSON.stringify(certs));
      }
    } catch (e) { console.error("Error syncing export certs:", e); }
  }, []);

  const handleSave = async (s) => {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      const ex = shipments.find(x => x.id === s.id);
      /* 2026-10-13, Arturo: a shipment he captured directly in Ground-
         Fresh (proforma GF-001) and one Gomez Berries submitted
         independently through the Shipper Portal both landed on
         GF-001 for the same shipper — "deben estar en vivo los
         consecutivos en ambos lados". Root cause: reserveProformaFromSettings
         (above) and ShipperPortal.js's own reserveProformaConsec only
         ever read the LOCAL gfi-shipper-settings cache, never a fresh
         Supabase read — if either side's browser cache is stale when
         it reserves a number, both sides can independently compute
         the same "next" value (same staleness class as the PO-picker/
         fixed_pos bug fixed earlier this session). The one place that
         actually has to be right is here, immediately before the real
         write: re-check the chosen proforma against a FRESH read of
         both GFI's own shipments and every proforma already submitted
         through the Shipper Portal for this supplier.
         2026-10-16, Arturo (LOT 58073, trying to manually fix it to
         GF-029 after a collision on another lot): "cuando la cambio yo
         no me deja... necesito que yo pueda corregir aca" — this used
         to silently rewrite whatever he typed to the next free number
         (only a console.warn, nothing shown on screen) the instant it
         detected ANY collision, which is exactly what made a genuine
         manual correction look "stuck" — he'd type the right number,
         it would silently flip back to something else with zero
         explanation. Now it refuses the save with a real, visible
         error naming the colliding LOT, so Arturo can either fix that
         OTHER shipment (same real case that led here) or pick a
         different number himself — never a silent auto-swap again. */
      if (s.supplierId && s.proforma) {
        let freshShipmentsForProforma = [];
        let freshPortalRowsForProforma = [];
        try {
          [freshShipmentsForProforma, freshPortalRowsForProforma] = await Promise.all([
            loadFromSupabase("shipments", { force: true }),
            loadFromSupabase("shipper_shipments", { force: true }),
          ]);
        } catch (e) { console.warn("[ShipmentRegistry] proforma collision check failed (network):", e && e.message); }
        /* 2026-10-30, Arturo: proforma numbers can repeat across
           different seasons (e.g. A002 in 2025-2026 and A002 again
           in 2026-2027) — same convention as nextProforma() above,
           which already scopes its own scan by seasonId. This
           collision check didn't, so it blocked a legitimate reuse
           in a new season just because the number existed once in
           an old one. Only a same-season match is a real collision. */
        let collidingLot = null;
        freshShipmentsForProforma.forEach(function (x) {
          if (x.id !== s.id && String(x.supplierId) === String(s.supplierId) && String(x.seasonId || "") === String(s.seasonId || "") && x.proforma && String(x.proforma).toUpperCase() === String(s.proforma).toUpperCase()) {
            collidingLot = x.lot;
          }
        });
        if (!collidingLot) {
          freshPortalRowsForProforma.forEach(function (p) {
            if (!p || String(p.supplierId) !== String(s.supplierId)) return;
            (p.proformas || []).forEach(function (pr) {
              if (pr && pr.consec && pr.gfiShipmentId !== s.id && String(pr.seasonId || "") === String(s.seasonId || "") && String(pr.consec).toUpperCase() === String(s.proforma).toUpperCase()) collidingLot = pr.gfiLot || "—";
            });
          });
        }
        if (collidingLot) {
          throw new Error(lang === "es"
            ? "El proforma " + s.proforma + " ya está asignado al LOT " + collidingLot + ". Corrige ese embarque primero, o elige un número distinto aquí."
            : "Proforma " + s.proforma + " is already assigned to LOT " + collidingLot + ". Fix that shipment first, or pick a different number here.");
        }
      }
      syncExportCerts(s, ex || null);
      await persistSingle(s);
      /* 2026-09-28 (Arturo, Export Certificates: "el envio urgente se
         hace inmediatamente cuando se haya utilizado el ultimo uso y
         ya no tenga mas certificados usos para el siguiente
         embarque") — real-time trigger for the "0 uses remaining"
         urgent alert (export-cert-reminder-send.js). Fires right here,
         the moment a shipment referencing a real export cert is
         saved, instead of waiting for the daily sweep. Fire-and-forget
         per distinct cert id on the shipment's lines (skips manual/
         pending/blank entries, which aren't real cert records) — never
         blocks or fails the shipment save; the daily sweep re-checks
         exhausted certs anyway as a safety net if this ever misses. */
      try {
        const certIds = Array.from(new Set((s.lines || [])
          .map(l => l && l.exportCertId)
          .filter(id => id && id !== "manual" && id !== "pending" && id !== "__manual__" && id !== "tmp-other")));
        certIds.forEach(certId => {
          fetch("/.netlify/functions/export-cert-reminder-send", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ certId }),
          }).catch(() => {});
        });
      } catch (e) { /* best-effort, never blocks the save */ }
      /* Propagate Entry # to any Bill linked to this shipment's lot.
         Loads bills FRESH from Supabase (not localStorage, which may
         be stale if BillRegistry hasn't mounted this session). Lot
         comparison is string-trimmed on both sides so leading
         zeros / whitespace don't drop a match. Always re-stamps
         (not just on change) so the Bill stays authoritative even
         if it was edited separately. Best-effort — a failure here
         doesn't roll back the shipment save. */
      try {
        const newEntry = (s.entryNumber || "").trim();
        const newInvoice = (s.shipperInvoice || "").trim();
        const lotStr = String(s.lot == null ? "" : s.lot).trim();
        if (lotStr) {
          const bills = await loadFromSupabase("bills", { force: true });
          const matches = bills.filter(b => {
            if (!b) return false;
            const bLot = String(b.lot == null ? "" : b.lot).trim();
            if (bLot !== lotStr) return false;
            const bEntry = (b.entryNumber || "").trim();
            const bInv = (b.shipperInvoice || "").trim();
            return bEntry !== newEntry || bInv !== newInvoice;
          });
          if (matches.length > 0) {
            const updated = matches.map(b => ({ ...b, entryNumber: newEntry, shipperInvoice: newInvoice }));
            await saveManyToSupabase("bills", updated);
            try {
              const merged = bills.map(b => {
                const u = updated.find(x => x.id === b.id);
                return u ? u : b;
              });
              localStorage.setItem("gfi-bills", JSON.stringify(merged));
            } catch (e) {}
          }
        }
      } catch (e) { console.warn("[ShipmentRegistry] entry#/invoice propagate failed:", e && e.message); }

      try {
        /* Full propagate: admin edits are authoritative and flow to
           shipper's embarque/proforma regardless of status. Locates the
           proforma inside the embarque by gfiShipmentId === s.id.
           Updates embarque-level shipperInvoice AND proforma-level
           fields the admin can change. */
        const portalRows = await loadFromSupabase("shipper_shipments", { force: true });
        const pmatch = portalRows.find(function (p) {
          if (!p) return false;
          if (s.portalEmbarqueId && (p.legacy_id === s.portalEmbarqueId || p.id === s.portalEmbarqueId)) return true;
          var pros = Array.isArray(p.proformas) ? p.proformas : [];
          return pros.some(function (pr) { return pr && String(pr.gfiShipmentId || "") === String(s.id); });
        });
        if (pmatch) {
          var updatedRow = { ...pmatch };
          /* 2026-10-05 — Arturo: "el lote 58062 originalmente lo
             capture a nombre de gomez berries pero tuve que modificar
             y ahora pertenece a terey avocados, sin embargo en el
             shipper portal aparece en gomez berries y no en terey, en
             mi modulo si esta correcto". pmatch is found by gfiShipmentId
             alone, regardless of which supplier's row it currently
             lives under — so changing the shipment's supplier here
             kept silently re-saving the SAME row (still owned by the
             old supplier) with the new data, instead of moving it.
             Per the one-row-per-physical-embarque rule (2026-09-14
             fix above, "every prior Terey embarque is its own
             separate shipper_shipments row"), a row's `supplierId` IS
             what the Shipper Portal uses to decide whose embarques
             list it belongs in — reassigning it here is the "move". */
          if (s.supplierId) updatedRow.supplierId = s.supplierId;
          updatedRow.shipperInvoice = (s.shipperInvoice || "").trim();
          /* 2026-09-14 — Arturo: "Enviar Broker" from this module must
             leave a digital footprint the shipper can see — same
             embarque.bolSentAt/bolSentTo the portal already renders
             its own "BOL enviado al aduanal" block from. Never move
             backward: only overwrite when this shipment save actually
             carries a real timestamp (a save that never touched broker
             email has s.bolSentAt undefined and must leave whatever
             the row already had alone). */
          if (s.bolSentAt) { updatedRow.bolSentAt = s.bolSentAt; updatedRow.bolSentTo = s.bolSentTo || updatedRow.bolSentTo || []; }
          var pros = Array.isArray(updatedRow.proformas) ? updatedRow.proformas.slice() : [];
          var idx = pros.findIndex(function (pr) { return pr && String(pr.gfiShipmentId || "") === String(s.id); });
          if (idx >= 0) {
            var oldPro = pros[idx] || {};
            /* Map admin shipment lines to portal line shape.
               Portal lines carry: productId, productBase, productName,
               productDescription, pallets, cases, category,
               exportCertId, exportCertNumber, exportCertManual,
               declaredValue. Preserve fields the portal added that
               admin doesn't touch. */
            var oldLines = Array.isArray(oldPro.lines) ? oldPro.lines : [];
            var newLines = (s.lines || []).map(function (ln, i) {
              var base = oldLines[i] || {};
              return {
                ...base,
                productId: ln.productId || base.productId || "",
                productBase: ln.productBase || base.productBase || "",
                productName: ln.productName || base.productName || ln.productBase || "",
                productDescription: ln.productDescription || base.productDescription || "",
                pallets: ln.pallets != null ? String(ln.pallets) : base.pallets || "",
                cases: ln.cases != null ? String(ln.cases) : base.cases || "",
                category: ln.category || base.category || "",
                exportCertId: ln.exportCertId || base.exportCertId || "",
                exportCertNumber: ln.exportCertNumber || ln.exportCertManual || base.exportCertNumber || "",
                exportCertManual: ln.exportCertManual || base.exportCertManual || "",
                declaredValue: ln.price != null ? String(ln.price) : (ln.declaredValue != null ? String(ln.declaredValue) : (base.declaredValue || "")),
              };
            });
            pros[idx] = {
              ...oldPro,
              consec: s.proforma || oldPro.consec || "",
              dateArrival: s.dateArrival || oldPro.dateArrival || "",
              dateDeparture: s.dateDeparture || oldPro.dateDeparture || "",
              warehouseId: s.warehouseId || oldPro.warehouseId || "",
              warehouseName: s.warehouseName || oldPro.warehouseName || "",
              brokerMXId: s.brokerMXId || oldPro.brokerMXId || "",
              brokerMXName: s.brokerMXName || oldPro.brokerMXName || "",
              brokerUSId: s.brokerUSId || oldPro.brokerUSId || "",
              brokerUSName: s.brokerUSName || oldPro.brokerUSName || "",
              transport: { ...(oldPro.transport || {}), ...(s.transport || {}) },
              palletManifest: Array.isArray(s.palletManifest) ? s.palletManifest.slice() : (oldPro.palletManifest || []),
              lines: newLines,
              gfiLot: s.lot || oldPro.gfiLot || "",
              gfiShipmentId: s.id || oldPro.gfiShipmentId || "",
              /* Arturo, 2026-09-16: "hay que agregar... los qc reports
                 de texas al shipper portal para que sean visibles para
                 ellos". Same pattern as bolSentAt above — only stamp
                 forward when THIS save actually set it, never clear an
                 existing value on an unrelated save. Lives on the
                 proforma (not updatedRow) since a report is per lot/
                 shipment, matching where palletManifest itself lives. */
              qualityReportSentAt: s.qualityReportSentAt || oldPro.qualityReportSentAt || "",
            };
            updatedRow.proformas = pros;
          }
          await saveToSupabase("shipper_shipments", updatedRow);
        } else if (s.supplierId) {
          /* 2026-09-14 — Arturo: "si hacen otro embarque ellos, va a
             haber problemas con los consecutivos... si es generado por
             Ground Fresh se supone debe ser aceptado y lote asignado
             es obvio". A shipment created directly here (never
             originating from a shipper-portal submission, so no
             gfiShipmentId match above) never had anywhere to go in
             shipper_shipments — the shipper never saw it, AND their
             portal's own proforma counter (gfi-shipper-settings) never
             advanced past it, risking a real consecutive collision the
             next time they submit one themselves. Mirrors
             acceptPortalSubmission's shape in reverse: a new
             already-"accepted" proforma (gfiLot + gfiShipmentId
             stamped, no review step needed — GFI already owns it),
             appended to this supplier's existing embarque row, or a
             brand-new row if they've never used the portal at all.
             The proforma-counter side of this (gfi-shipper-settings
             nextProformaNumber) is already advanced separately by
             commitProformaToSettings, called from ShipmentForm's own
             handleSave before onSave reaches here. */
          var newProforma = {
            id: "SHP-PRO-" + Date.now() + "-" + Math.floor(Math.random() * 1000),
            lines: (s.lines || []).map(function (ln) {
              return {
                productId: ln.productId || "",
                productBase: ln.productBase || "",
                productName: ln.productName || ln.productBase || "",
                productDescription: ln.productDescription || "",
                pallets: ln.pallets != null ? String(ln.pallets) : "",
                cases: ln.cases != null ? String(ln.cases) : "",
                category: ln.category || "",
                exportCertId: ln.exportCertId || "",
                exportCertNumber: ln.exportCertNumber || ln.exportCertManual || "",
                exportCertManual: ln.exportCertManual || "",
                declaredValue: ln.price != null ? String(ln.price) : (ln.declaredValue != null ? String(ln.declaredValue) : ""),
              };
            }),
            notes: s.notes || "",
            consec: s.proforma || "",
            gfiLot: s.lot || "",
            status: "accepted",
            seasonId: s.seasonId || "",
            otherDocs: [],
            transport: { ...(s.transport || {}) },
            acceptedAt: new Date().toISOString(),
            brokerMXId: s.brokerMXId || "",
            brokerUSId: s.brokerUSId || "",
            rejectedAt: "",
            dateArrival: s.dateArrival || "",
            submittedAt: new Date().toISOString(),
            warehouseId: s.warehouseId || "",
            brokerMXName: s.brokerMXName || "",
            brokerUSName: s.brokerUSName || "",
            palletPhotos: [],
            rejectReason: "",
            dateDeparture: s.dateDeparture || "",
            gfiShipmentId: s.id || "",
            warehouseName: s.warehouseName || "",
            palletManifest: Array.isArray(s.palletManifest) ? s.palletManifest.slice() : [],
            proformaNumber: "",
            qualityReportSentAt: s.qualityReportSentAt || "",
          };
          /* 2026-09-14 — Arturo caught this immediately: lot 58049's
             own real invoice (TA-A-20050) showed as the PREVIOUS
             shipment's invoice (TA-A-20047) in the shipper's portal.
             Root cause: shipperInvoice lives at the EMBARQUE (row)
             level, not per-proforma — appending a new proforma into an
             arbitrary existing row (picked by supplierId alone) meant
             it inherited that row's OLD invoice number. The real
             established pattern (confirmed against production data:
             every prior Terey embarque is its own separate
             shipper_shipments row, each with its own shipperInvoice,
             even though several happen to share the same
             consec_embarque label) is one row per physical embarque/
             invoice. Always create a fresh row instead of appending —
             never share an embarque row across different real
             invoices. */
          var newConsecEmb = (function () {
            var all = loadLS("gfi-shipper-settings", {}) || {};
            var rec = all[s.supplierId] || {};
            var prefix = rec.embarquePrefix;
            var nextNum = parseInt(rec.nextEmbarqueNumber, 10);
            if (!isNaN(nextNum) && nextNum > 0) {
              var eff = (prefix != null && prefix !== "") ? prefix : (s.shipperInitials ? String(s.shipperInitials).toUpperCase() + "-E" : "XX-E");
              return eff + String(nextNum).padStart(3, "0");
            }
            return (s.shipperInitials || "XX") + "-E001";
          })();
          await saveToSupabase("shipper_shipments", {
            id: "SHP-EMB-" + Date.now(),
            supplierId: s.supplierId,
            consecEmbarque: newConsecEmb,
            shipperInvoice: s.shipperInvoice || "",
            proformas: [newProforma],
            docs: {},
            notes: "",
            bolSentAt: s.bolSentAt || "",
            bolSentTo: s.bolSentTo || [],
          });
        }
      } catch (e) { console.warn("[ShipmentRegistry] portal full propagate failed:", e && e.message); }
      setShowForm(false); setEditing(null); setToast(t.saved);
      /* Only the standalone new/edit form (showForm) is truly closing
         here — the DetailPanel's own onSave re-opens viewing right
         after calling handleSave, so a save made there must never
         redirect away. */
      if (showForm) closeToOrigin();
    } catch (e) {
      setError("Could not save shipment: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };

  /* Accept a Shipper Portal proforma. Assigns the next GFI lot,
     creates a real shipment record from embarque + proforma data,
     and stamps the proforma in place with status="accepted" +
     gfiLot + gfiShipmentId so the shipper sees the assigned lot.
     Embarque-level fields (shipperInvoice, invoiceFile) come from
     the parent embarque; proforma-level fields (dates, brokers,
     warehouse, transport, lines, pallets) come from the proforma. */
  const acceptPortalSubmission = async ({ embarque, pro }) => {
    var assignedLot = nextLot(shipments);
    var newId = nextId(shipments);
    var seasonObj = detectSeason(pro.dateDeparture || pro.dateArrival || new Date().toISOString().substring(0, 10));
    /* Build a product-catalog lookup so we can enrich both lines and
       pallets with size / unitsPerCase / labelInitials / packType
       that the portal didn't capture explicitly. The catalog is the
       single source of truth for these fields; the shipper only
       picks the presentation by id. */
    var catalog = loadLS("gfi-products", []);
    var productById = {};
    catalog.forEach(function (p) { if (p.id != null) productById[String(p.id)] = p; });
    /* Resolved lines: spread the portal line (preserves declaredValue
       and any other fields), normalize price ← declaredValue so
       downstream value math works, ensure presentation fields are
       populated from the catalog when missing. */
    /* Filter phantom rows: only lines with (productId or productBase)
       AND a positive case count carry over. Drops the empty trailing
       row the portal form keeps for ergonomics. */
    var resolvedLines = (pro.lines || []).filter(function (l) { return (l.productId || l.productBase) && (parseInt(l.cases, 10) || 0) > 0; }).map(function (l) {
      var prod = l.productId ? productById[String(l.productId)] : null;
      var cat = l.category || (prod ? (String(prod.category || "").toLowerCase().indexOf("org") >= 0 ? "OG" : "CV") : "");
      return {
        ...l,
        productBase: l.productBase || l.productName || (prod ? prod.product : "") || "",
        productName: l.productName || l.productBase || (prod ? prod.product : "") || "",
        productDescription: l.productDescription || (prod ? (prod.description || "") : ""),
        category: cat,
        size: l.size || (prod ? prod.size : "") || "",
        unitsPerCase: l.unitsPerCase || (prod ? (prod.unitsPerCase || prod.unitsCase) : "") || "",
        unitsCase: l.unitsCase || (prod ? (prod.unitsCase || prod.unitsPerCase) : "") || "",
        labelInitials: l.labelInitials || (prod ? prod.labelInitials : "") || "",
        packType: l.packType || (prod ? prod.packType : "") || "",
        /* Map shipper-supplied declaredValue → price so Carta Porte and
           every "value" aggregation has something real to multiply. */
        price: l.declaredValue !== undefined && l.declaredValue !== "" ? l.declaredValue : (l.price || ""),
        declaredValue: l.declaredValue || "",
        total: 0,
        exportCertId: l.exportCertId === "__manual__" ? "" : (l.exportCertId || ""),
        exportCertNumber: l.exportCertNumber || l.exportCertManual || "",
        exportCertManual: l.exportCertManual || "",
      };
    });
    /* Pallet enrichment: portal pallets only capture palletNum,
       productBase, casesPallet, julianCode, growerCode. Look up the
       matching resolved line by productBase and copy its presentation
       fields onto the pallet so the templates (Receiving, Carta
       Porte, Carta Instrucciones) can render size / weight / label. */
    /* Pallets: require productBase + positive casesPallet so a
       phantom row with only a palletNum (typed but never assigned
       a product) doesn't land in the persisted manifest. */
    var resolvedPallets = (pro.palletManifest || []).filter(function (p) { return p && p.productBase && (parseInt(p.casesPallet, 10) || 0) > 0; }).map(function (p) {
      var match = resolvedLines.find(function (l) { return String(l.productBase || "").trim().toLowerCase() === String(p.productBase || "").trim().toLowerCase(); });
      var prod = match && match.productId ? productById[String(match.productId)] : null;
      return {
        ...p,
        category: p.category || (match ? match.category : "") || "",
        size: p.size || (match ? match.size : "") || (prod ? prod.size : "") || "",
        unitsCase: p.unitsCase || (match ? (match.unitsCase || match.unitsPerCase) : "") || (prod ? (prod.unitsCase || prod.unitsPerCase) : "") || "",
        labelInitials: p.labelInitials || (match ? match.labelInitials : "") || (prod ? prod.labelInitials : "") || "",
        packType: p.packType || (match ? match.packType : "") || (prod ? prod.packType : "") || "",
        condition: p.condition || (prod ? prod.condition : "") || "",
        /* Map the portal's julianCode field to the standard `traceability`
           used by Arturo's templates so the Receiving Manifest renders
           the trace code without rename gymnastics. */
        traceability: p.traceability || p.julianCode || "",
        julianCode: p.julianCode || p.traceability || "",
      };
    });
    /* Inherit every document the portal captured, tagging each with
       its origin level so Arturo's Documents section can show which
       came from the embarque (shared across proformas) vs which
       belong to this specific proforma. Five buckets total:
         - embarque.invoiceFile      → level: shipment, kind: invoice
         - embarque.exportCertFile   → level: shipment, kind: exportCert
         - embarque.otherDocs        → level: shipment, kind: other (legacy)
         - pro.palletPhotos          → level: proforma, kind: palletPhoto
         - pro.otherDocs             → level: proforma, kind: other          */
    var inheritedDocs = [];
    var stamp = function (extra) { return { id: Date.now() + Math.random(), uploadedAt: new Date().toISOString(), source: "portal", ...extra }; };
    /* A file is "real" if it has either a Storage URL (new path) or
       inline base64 `data` (legacy path). The Storage URL is preferred
       since it points at a durable object instead of bloating the
       row, but we accept either to keep historical embarques
       working. The full file object is forwarded into the new
       shipment's `documents` column intact so the admin's existing
       documents UI sees the same shape it always did. */
    var hasFile = function (f) { return !!(f && (f.url || f.path || f.data)); };
    if (hasFile(embarque.invoiceFile)) {
      inheritedDocs.push(stamp({ ...embarque.invoiceFile, name: embarque.invoiceFile.name || "shipper-invoice", level: "shipment", kind: "invoice" }));
    }
    if (hasFile(embarque.exportCertFile)) {
      inheritedDocs.push(stamp({ ...embarque.exportCertFile, name: embarque.exportCertFile.name || "export-certificate", level: "shipment", kind: "exportCert" }));
    }
    (embarque.otherDocs || []).forEach(function (d) {
      if (hasFile(d)) inheritedDocs.push(stamp({ ...d, level: "shipment", kind: "other" }));
    });
    (pro.palletPhotos || []).forEach(function (d) {
      if (hasFile(d)) inheritedDocs.push(stamp({ ...d, level: "proforma", kind: "palletPhoto" }));
    });
    (pro.otherDocs || []).forEach(function (d) {
      if (hasFile(d)) inheritedDocs.push(stamp({ ...d, level: "proforma", kind: "other" }));
    });
    /* Fallback defaults that mirror the manual EMPTY_SHIPMENT seed
       (line ~2419): a shipper-portal proforma sometimes lands without
       a warehouse or US broker because those fields are optional on
       the shipper side, but downstream pieces (Master Inbound, Packing
       List, settlements) treat them as required and render blanks
       otherwise. Defaulting to American Cold Storage + Godinez here
       gives the admin the same starting point a manually-created
       shipment would have, and they can still change either field
       once the row is in the registry. */
    var _whList = getWarehouses();
    var _brList = getBrokers();
    var defaultWarehouse = _whList.find(function (x) { return (x.shortName || x.fullName || x.name || "").toLowerCase().includes("american"); });
    var defaultBrokerUS  = _brList.find(function (x) { return (x.shortName || x.fullName || x.name || "").toLowerCase().includes("godinez"); });
    var resolvedWhId   = pro.warehouseId  || (defaultWarehouse ? defaultWarehouse.id : "");
    var resolvedWhName = pro.warehouseName || (defaultWarehouse ? (defaultWarehouse.shortName || defaultWarehouse.fullName || defaultWarehouse.name || "") : "");
    var resolvedBrUSId   = pro.brokerUSId  || (defaultBrokerUS ? defaultBrokerUS.id : "");
    var resolvedBrUSName = pro.brokerUSName || (defaultBrokerUS ? (defaultBrokerUS.shortName || defaultBrokerUS.fullName || defaultBrokerUS.name || "") : "");

    var newShipment = {
      id: newId,
      lot: assignedLot,
      dateDeparture: pro.dateDeparture || "",
      dateArrival: pro.dateArrival || "",
      week: getISOWeek(pro.dateArrival || pro.dateDeparture || ""),
      supplierId: embarque.supplierId || "",
      shipperName: embarque.shipperName || "",
      shipperInitials: embarque.shipperInitials || "",
      shipperSource: "portal",
      /* Shipper Portal is Mexican-shipper-only by definition — an FOB
         domestic purchase is never submitted through it. */
      shipmentType: "import",
      portalEmbarqueId: embarque.id,
      portalProformaId: pro.id,
      proforma: pro.consec || "",
      shipperInvoice: embarque.shipperInvoice || "",
      entryNumber: "",
      brokerMXId: pro.brokerMXId || "", brokerMXName: pro.brokerMXName || "", brokerMXManual: "",
      brokerUSId: resolvedBrUSId, brokerUSName: resolvedBrUSName, brokerUSManual: "",
      warehouseId: resolvedWhId, warehouseName: resolvedWhName,
      seasonId: seasonObj ? String(seasonObj.id) : (pro.seasonId || embarque.seasonId || ""),
      /* status="received" (NOT "draft"): the shipper has submitted, the
         admin has reviewed and clicked Accept, and a real GFI lot
         number was assigned — by every downstream definition this is
         a confirmed received shipment. Leaving it as "draft" hid the
         row from the invoice-lot picker (status filter, since
         relaxed) AND from the Create Bill button (still status-
         gated) AND from any caller that only iterates received/
         billed rows. Promoting straight to "received" matches what a
         manual flow does after the admin types in the data. */
      status: "received",
      transport: { ...(pro.transport || {}) },
      lines: resolvedLines,
      palletManifest: resolvedPallets,
      documents: inheritedDocs,
      notes: (pro.notes ? pro.notes + "\n\n" : "") + "[" + (lang === "es" ? "Importado del Portal del Exportador" : "Imported from Shipper Portal") + " · " + (embarque.consecEmbarque || "—") + " / " + (pro.consec || "—") + "]",
    };
    syncExportCerts(newShipment, null);
    /* Save the new shipment + stamp the source portal proforma. The
       proforma stamp goes to Supabase `shipper_shipments` (source of
       truth read by ShipperPortal) AND to localStorage
       `gfi-shipper-shipments` for legacy sync. */
    /* AWAIT the shipment save before we touch the portal-side
       record — previously this was fire-and-forget which raced the
       portalBump reload below and could resurrect the first accepted
       proforma as "submitted". */
    try {
      await persistSingle(newShipment);
    } catch (e) {
      setError("Could not save shipment: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    }
    var updatedEmb = null;
    try {
      var all = loadLS("gfi-shipper-shipments", []);
      var updated = all.map(function (e) {
        if (e.id !== embarque.id) return e;
        var ue = { ...e, proformas: (e.proformas || []).map(function (p) {
          if (p.id !== pro.id) return p;
          return { ...p, status: "accepted", gfiLot: assignedLot, gfiShipmentId: newId, acceptedAt: new Date().toISOString(), rejectReason: "" };
        }) };
        updatedEmb = ue;
        return ue;
      });
      localStorage.setItem("gfi-shipper-shipments", JSON.stringify(updated));
    } catch (e) {}
    /* If the embarque wasn't in localStorage (different browser /
       device) build the updated record from the in-memory `embarque`
       reference so Supabase still gets stamped. */
    if (!updatedEmb) {
      updatedEmb = { ...embarque, proformas: (embarque.proformas || []).map(function (p) {
        if (p.id !== pro.id) return p;
        return { ...p, status: "accepted", gfiLot: assignedLot, gfiShipmentId: newId, acceptedAt: new Date().toISOString(), rejectReason: "" };
      }) };
    }
    /* AWAIT the shipper_shipments sync too — the portalBump below
       triggers a reload from Supabase; if the save hasn't landed
       yet the reload pulls the old "submitted" status back into
       localStorage. */
    try {
      await saveToSupabase("shipper_shipments", updatedEmb);
    } catch (e) {
      console.error("[ShipmentRegistry] shipper_shipments accept sync failed", e);
      setToast("⚠ Could not sync portal status to Supabase: " + (e.message || String(e)));
    }
    setReviewingPortal(null);
    setPortalBump(function (x) { return x + 1; });
    setToast(lang === "es" ? "Proforma aceptada — LOT " + assignedLot : "Proforma accepted — LOT " + assignedLot);
  };
  /* Reject a portal proforma with a reason. */
  const rejectPortalSubmission = async ({ embarque, pro }) => {
    var reason = window.prompt(lang === "es" ? "Razón del rechazo (visible al shipper):" : "Reject reason (visible to shipper):", "");
    if (reason === null) return;
    if (!reason.trim()) { alert(lang === "es" ? "Captura una razón." : "Enter a reason."); return; }
    var updatedEmb = null;
    try {
      var all = loadLS("gfi-shipper-shipments", []);
      var updated = all.map(function (e) {
        if (e.id !== embarque.id) return e;
        var ue = { ...e, proformas: (e.proformas || []).map(function (p) {
          if (p.id !== pro.id) return p;
          return { ...p, status: "rejected", rejectReason: reason.trim(), rejectedAt: new Date().toISOString() };
        }) };
        updatedEmb = ue;
        return ue;
      });
      localStorage.setItem("gfi-shipper-shipments", JSON.stringify(updated));
    } catch (e) {}
    if (!updatedEmb) {
      updatedEmb = { ...embarque, proformas: (embarque.proformas || []).map(function (p) {
        if (p.id !== pro.id) return p;
        return { ...p, status: "rejected", rejectReason: reason.trim(), rejectedAt: new Date().toISOString() };
      }) };
    }
    /* AWAIT — same race the accept path had: portalBump triggers a
       Supabase reload that would clobber localStorage with the old
       "submitted" status if the save was still in flight. */
    try {
      await saveToSupabase("shipper_shipments", updatedEmb);
    } catch (e) {
      console.error("[ShipmentRegistry] shipper_shipments reject sync failed", e);
      setToast("⚠ Could not sync portal status to Supabase: " + (e.message || String(e)));
    }
    setReviewingPortal(null);
    setPortalBump(function (x) { return x + 1; });
    setToast(lang === "es" ? "Proforma rechazada" : "Proforma rejected");
  };
  const handleDelete = async (id) => {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      /* Revert any cert usage before deleting */
      var toDelete = shipments.find(x => x.id === id);
      if (toDelete && (toDelete.status === "received" || toDelete.status === "billed")) {
        syncExportCerts({ ...toDelete, status: "draft" }, toDelete);
      }
      await persistDelete(id);
      setShowForm(false); setEditing(null); setViewing(null); setToast(t.deleted);
      closeToOrigin();
    } catch (e) {
      setError("Could not delete shipment: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };
  /* Reject an already-accepted portal-imported shipment back to the
     shipper. Prompts for a required reason, marks the source portal
     proforma as "rejected" with that reason, then DELETES the GFI
     shipment so it leaves no trace in our data. Cert usage is
     released through syncExportCerts before deletion. Only callable
     on shipments that carry portalEmbarqueId/portalProformaId
     (i.e., were imported from the shipper portal). */
  const rejectAcceptedShipment = (sh) => {
    if (!sh || !sh.portalEmbarqueId || !sh.portalProformaId) return;
    var reason = window.prompt(lang === "es"
      ? "Motivo del rechazo (visible al exportador):"
      : "Rejection reason (visible to the exporter):", "");
    if (reason === null) return;
    if (!reason.trim()) {
      alert(lang === "es" ? "Captura un motivo." : "Enter a reason.");
      return;
    }
    var confirmMsg = lang === "es"
      ? "Esto eliminará el embarque de GFI y notificará al exportador. ¿Continuar?"
      : "This will delete the shipment from GFI and notify the exporter. Continue?";
    if (!window.confirm(confirmMsg)) return;
    /* Flip the source portal proforma to "rejected" with reason. */
    try {
      var all = loadLS("gfi-shipper-shipments", []);
      var updated = all.map(function (e) {
        if (e.id !== sh.portalEmbarqueId) return e;
        return { ...e, proformas: (e.proformas || []).map(function (p) {
          if (p.id !== sh.portalProformaId) return p;
          return {
            ...p,
            status: "rejected",
            rejectReason: reason.trim(),
            rejectedAt: new Date().toISOString(),
            /* Clear the lot/shipment refs so the portal stops claiming
               it was accepted. The shipper sees "rejected" cleanly. */
            gfiLot: "",
            gfiShipmentId: "",
            acceptedAt: "",
          };
        }) };
      });
      localStorage.setItem("gfi-shipper-shipments", JSON.stringify(updated));
    } catch (e) {}
    /* Release any cert usage claimed by this shipment, then delete. */
    if (sh.status === "received" || sh.status === "billed") {
      syncExportCerts({ ...sh, status: "draft" }, sh);
    }
    persistDelete(sh.id).catch(e => {
      setError("Could not delete shipment: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    });
    setShowForm(false); setEditing(null); setViewing(null);
    closeToOrigin();
    setPortalBump(function (x) { return x + 1; });
    setToast(lang === "es" ? "Embarque rechazado y devuelto al exportador" : "Shipment rejected and returned to the exporter");
  };
  /* Build the context bundle once and reuse across the 3 print
     handlers — single localStorage read, then dispatch. */
  const buildPrintCtx = useCallback(() => ({
    suppliers: getSuppliers(),
    warehouses: getWarehouses(),
    brokers: getBrokers(),
    commodities: loadLS("gfi-commodities", []),
    seasons: getSeasons(),
    lang,
  }), [lang]);
  const handlePrintDocSingle = async (sh, kind) => {
    var k = kind === "shipping-instr" ? "shipping-instr" : kind;
    var ctx = buildPrintCtx();
    var driveKind = (k === "carta-porte" || k === "shipping-instr" || k === "receiving");
    /* Digital-first: save straight to the shipment's Drive folder. If
       there's no live Drive session (Nexus drops it on reload), sign in
       on the spot — same pattern as the "upload to Drive" button — so
       the save works without ever opening the print window (which can't
       be dismissed on tablets/PWA). Only fall back to the print window
       when Drive isn't configured at all or the sign-in fails. */
    if (driveKind && driveConfigured()) {
      setToast(lang === "es" ? "Guardando en Drive…" : "Saving to Drive…");
      try {
        if (!driveSignedIn()) { await driveSignIn(); }
        var file = await uploadShipmentDocToDrive(sh, k, ctx);
        setToast((lang === "es" ? "✓ Guardado en Drive: " : "✓ Saved to Drive: ") + file.name);
      } catch (e) {
        console.error("[Drive upload] failed for", k, e);
        setToast((lang === "es" ? "⚠ Falló guardado en Drive: " : "⚠ Drive save failed: ") + (e.message || String(e)));
      }
    } else {
      printShipmentDocs([sh], k, ctx, function (err) {
        if (err === "popup-blocked") setToast(t.docPopupBlocked);
      });
    }
  };
  const handlePrintDocBulk = (kind) => {
    if (selectedShipments.length === 0) return;
    /* Bulk gate — if ANY selected shipment fails print validation,
       refuse and list the offenders with their specific cause. */
    var blockers = [];
    selectedShipments.forEach(function (sh) {
      var errs = validateShipmentForPrint(sh, shipments, lang);
      if (errs.length > 0) blockers.push("LOT " + (sh.lot || "?") + ": " + errs.join(", "));
    });
    if (blockers.length > 0) {
      var head = lang === "es" ? "No se puede imprimir — los siguientes embarques tienen errores:" : "Cannot print — the following shipments have errors:";
      alert(head + "\n\n" + blockers.map(function (b) { return "• " + b; }).join("\n"));
      return;
    }
    var ctx = buildPrintCtx();
    var driveKind = (kind === "carta-porte" || kind === "shipping-instr" || kind === "receiving");
    /* Digital-first: save each doc straight to its embarque's Drive
       folder. Sign in on the spot if there's no live session (Nexus
       drops it on reload), same as the upload button. Only fall back to
       the print window when Drive isn't configured or sign-in fails. */
    if (driveKind && driveConfigured()) {
      (async function () {
        let ok = 0, fail = 0;
        setToast(lang === "es" ? "Guardando en Drive…" : "Saving to Drive…");
        try {
          if (!driveSignedIn()) { await driveSignIn(); }
        } catch (e) {
          console.error("[Drive sign-in] failed", e);
          setToast((lang === "es" ? "⚠ No se pudo conectar a Drive: " : "⚠ Could not connect to Drive: ") + (e.message || String(e)));
          printShipmentDocs(selectedShipments, kind, ctx, function (err) {
            if (err === "popup-blocked") setToast(t.docPopupBlocked);
          });
          return;
        }
        for (const sh of selectedShipments) {
          try { await uploadShipmentDocToDrive(sh, kind, ctx); ok++; }
          catch (e) { fail++; console.error("[Drive upload bulk] failed for LOT " + sh.lot, e); }
        }
        setToast((lang === "es" ? "✓ Guardado en Drive: " : "✓ Saved to Drive: ") + ok + (fail ? " · " + fail + (lang === "es" ? " con error" : " failed") : ""));
      })();
    } else {
      printShipmentDocs(selectedShipments, kind, ctx, function (err) {
        if (err === "popup-blocked") setToast(t.docPopupBlocked);
      });
    }
  };

  const handleDuplicate = (s) => {
    var newLot = nextLot(shipments);
    /* 2026-10-01 — Arturo: same splitting logic applies to FOB, but
       there "solo se compartiria el numero de factura del proveedor"
       — no Proforma concept for a domestic purchase at all (the field
       stays hidden/blank in the form for shipmentType "fob"), so skip
       generating one here too, instead of silently saving an unused
       number onto a record that never shows it. */
    var isFobSrc = s.shipmentType === "fob";
    /* 2026-10-05 — Arturo: the 2nd/3rd proforma generated off the same
       invoice/truck kept the shipper's OLD initials-based prefix
       (e.g. "TA-001") even after the Settings-configured prefix
       (e.g. "GF-") was already working correctly for the first
       proforma. Root cause: this handler still called the old
       nextProforma() directly instead of reserveProformaFromSettings()
       — the same gap already fixed everywhere else on 2026-09-14, just
       never applied here since this feature was added later
       (2026-10-01). commitProformaToSettings (called from the form's
       own save path) advances the shared counter once the duplicate is
       actually saved, same as any other new shipment. */
    var newProforma = (s.supplierId && !isFobSrc) ? reserveProformaFromSettings(s.supplierId, s.shipperInitials, shipments, s.seasonId) : "";
    var dup = {
      ...s, id: "", lot: newLot, proforma: newProforma, status: "draft",
      /* Keep: dates, shipper, brokers, warehouse, transport, entry#, shipperInvoice, export cert —
         this is GFI's equivalent of the Shipper Portal's "Add Proforma"
         (2026-10-01, Arturo: one truck/invoice can split across 2+
         warehouses — same transport/customs/invoice, different lot per
         destination). Warehouse is kept pre-filled (often the same
         destination) but editable since it CAN differ. For FOB, every
         one of those fields is already blank on the source record
         (no transport/brokers/entry#/Proforma for a domestic
         purchase) — only shipperInvoice/vendorReference carry over
         for real, exactly matching Arturo's "ahi solo se compartiria
         el numero de factura del proveedor, el producto puede
         asignarse a distintos lotes incluso bodegas". Warehouse stays
         just as freely editable for FOB as for Importación. */
      /* Clear: documents, product lines, pallet manifest — a new lot
         always means different fruit/pallets. palletManifest was a
         real bug here: lines reset to blank but the OLD pallet rows
         stayed, so the duplicate opened with pallets that no longer
         matched any product line (triggering the "pallets don't match
         lines" validation and making the feature look broken). */
      documents: [],
      lines: [{ productId: "", productName: "", productBase: "", productDescription: "", category: "", cases: "", pallets: "", price: "", size: "", unitsCase: "", exportCertId: "", exportCertNumber: "", exportCertManual: "" }],
      palletManifest: [{ ...EMPTY_PALLET }],
      transport: { ...(s.transport || {}) },
    };
    setViewing(null); setEditing(dup); setShowForm(true);
  };

  const uniqueShippers = useMemo(() => [...new Set(resolvedShipments.map(s => s.shipperName).filter(Boolean))].sort(), [resolvedShipments]);
  const uniqueWarehouses = useMemo(() => [...new Set(resolvedShipments.map(s => s.warehouseName).filter(Boolean))].sort(), [resolvedShipments]);
  const uniqueProducts = useMemo(() => [...new Set(resolvedShipments.flatMap(s => (s.lines || []).map(l => l.productBase || l.productName).filter(Boolean)))].sort(), [resolvedShipments]);

  /* Distinct sale statuses per lot — sourced from bills, indexed by
     shipment lot so the filter predicate is O(1) per row. */
  const allSaleStatusesByLot = useMemo(() => {
    const bills = loadLS("gfi-bills", []);
    const buckets = {};
    bills.forEach(b => {
      if (!b.lot) return;
      const key = String(b.lot);
      if (!buckets[key]) buckets[key] = {};
      (b.lines || []).forEach(l => { if (l.saleStatus) buckets[key][l.saleStatus] = true; });
    });
    const out = {};
    Object.keys(buckets).forEach(k => { out[k] = Object.keys(buckets[k]); });
    return out;
  }, [shipments]);

  const filtered = useMemo(() => {
    var list = resolvedShipments;
    /* Shipper-portal scope: every row must match the locked supplier
       before any other filter runs. */
    if (lockSupplierId) list = list.filter(x => String(x.supplierId) === String(lockSupplierId));
    if (search) { var s = search.toLowerCase(); list = list.filter(x => String(x.lot || "").includes(s) || (x.shipperName || "").toLowerCase().includes(s) || (x.shipperInvoice || "").toLowerCase().includes(s) || (x.proforma || "").toLowerCase().includes(s)); }
    if (filterShipper) list = list.filter(x => x.shipperName === filterShipper);
    if (filterWarehouse) list = list.filter(x => x.warehouseName === filterWarehouse);
    if (filterStatus) list = list.filter(x => x.status === filterStatus);
    if (filterProduct) list = list.filter(x => (x.lines || []).some(l => (l.productBase || l.productName) === filterProduct));
    /* Sale status — matches if ANY bill line on this lot carries the
       selected status (worst-only column display does not gate this). */
    if (filterSaleStatus) list = list.filter(x => { var arr = allSaleStatusesByLot[String(x.lot)] || []; return arr.indexOf(filterSaleStatus) >= 0; });
    /* Cert filter — matches if ANY line on the shipment references the
       selected cert (by id when resolved, else by normalized number
       against either exportCertNumber or exportCertManual). */
    if (filterCert) {
      var wantNum = certIdToNum[filterCert] || "";
      list = list.filter(x => (x.lines || []).some(l => {
        if (l.exportCertId && String(l.exportCertId) === String(filterCert)) return true;
        if (wantNum && nrmCertNum(l.exportCertNumber) === wantNum) return true;
        if (wantNum && nrmCertNum(l.exportCertManual) === wantNum) return true;
        return false;
      }));
    }
    if (filterSeasons.length > 0) list = list.filter(x => filterSeasons.indexOf(resolveSeasonId(seasons, x.dateArrival || x.dateDeparture || "", x.seasonId)) >= 0);
    if (dateFrom || dateTo) list = list.filter(x => { var d = x.dateArrival || x.dateDeparture || ""; if (!d) return false; if (dateFrom && d < dateFrom) return false; if (dateTo && d > dateTo) return false; return true; });
    /* Week filter — matches the W## column shown in the table
       (built off shipment.dateArrival with dateDeparture fallback). */
    if (filterWeek) {
      list = list.filter(x => {
        const d = x.dateArrival || x.dateDeparture || "";
        if (!d) return false;
        return ("W" + getISOWeek(d)) === filterWeek;
      });
    }
    /* Tab filter — when a tab other than All is active, restrict to
       that status. cardFilter being null is the All case. */
    if (cardFilter) list = list.filter(x => (x.status || "draft") === cardFilter);
    return list;
  }, [resolvedShipments, search, filterShipper, filterWarehouse, filterStatus, filterSaleStatus, filterCert, certIdToNum, filterSeasons, dateFrom, dateTo, filterWeek, filterProduct, cardFilter, seasons, allSaleStatusesByLot, lockSupplierId]);
  const uniqueSeasons = useMemo(() => [...new Set(seasons.map(s => s.seasonName || s.startDate.substring(0, 4) + "-" + s.endEstimate.substring(2, 4)).filter(Boolean))].sort(), [seasons]);
  /* Sorted ISO weeks present in the current shipment set. Drives the
     new Week filter dropdown in the popover. */
  const uniqueWeeks = useMemo(() => {
    const weeks = new Set();
    resolvedShipments.forEach(x => {
      const d = x.dateArrival || x.dateDeparture || "";
      if (d) weeks.add("W" + getISOWeek(d));
    });
    return Array.from(weeks).sort((a, b) => (parseInt(b.slice(1), 10) || 0) - (parseInt(a.slice(1), 10) || 0));
  }, [resolvedShipments]);

  /* Sale status by lot — computed LIVE from every invoice line tied to
     that lot via deriveDeliveryStatus (same mixing rule the Invoice and
     Bill screens use), instead of reading the Bill's persisted
     line.saleStatus. Arturo, 2026-09-16: lot 58046 had PU 17440 (Good
     Delivery) and PU 17441 (Protection) — the bill's own saved line
     had drifted to a bare "protection" (stale from before both PUs'
     real statuses were in, or from a direct data fix bypassing the
     Invoice→Bill push) instead of "partialProtection", and this
     column's own aggregation compounded it further: it ranked a bare
     "protection" bill line as MORE severe than "partialProtection",
     so even a correctly-recomputed bill could still show the wrong
     word here. Reading straight from invoice lines sidesteps both
     problems — this column self-heals the moment it renders, the same
     way Bill/Invoice/Shipment pallet-level status already does,
     without waiting on the bill to be re-saved. */
  const saleStatusByLot = useMemo(() => {
    /* Prefer the fresh in-memory Supabase cache over the localStorage
       mirror (same fix as ShipperPortal.js's consignee/broker bug,
       2026-09-16) — gfi-invoices only gets written once InvoiceRegistry.js
       has been opened in this tab, so a Shipments screen opened first
       in a fresh session would otherwise compute this column against
       an empty/stale invoice list. */
    const cachedInvoices = getCached("invoices");
    const invoicesForPending = (cachedInvoices && cachedInvoices.length > 0) ? cachedInvoices : loadLS("gfi-invoices", []);
    const linesByLot = {};
    invoicesForPending.forEach(inv => {
      (inv.lines || []).forEach(l => {
        const lot = l.lot || inv.lot;
        if (!lot) return;
        (linesByLot[String(lot)] || (linesByLot[String(lot)] = [])).push(l);
      });
    });
    const map = {};
    Object.keys(linesByLot).forEach(lot => {
      const lotInvoices = invoicesForPending.filter(inv => String(inv.lot) === lot);
      const derived = deriveDeliveryStatus(linesByLot[lot]);
      const worst = derived || resolvePendingStage(lotInvoices, slPassingIndex);
      if (worst) map[lot] = worst;
    });
    /* Twin River (2026-10-15) — ClariFresh-mode shipments report
       Protection through their own quality report, captured at the
       pallet level ON THE SHIPMENT (twinRiverStatus), never through
       an Invoice's Pallet Allocations. twinRiverLotStatus is the sole
       source for these lots, overriding whatever the invoice-lines
       loop above computed (usually nothing, since Arturo never
       touches saleStatus on a Twin River invoice) — same "shipment is
       the source for TR, invoice stays the source for FS" split
       Arturo described. */
    (shipments || []).forEach(function (s) {
      if (!s || s.texasQcMode !== "clarifresh" || !s.lot) return;
      const trStatus = twinRiverLotStatus(s);
      map[String(s.lot)] = trStatus || "pendingPickUp";
    });
    return map;
  }, [shipments, slPassingIndex]);

  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    var va, vb;
    switch (sortCol) {
      case "lot": va = parseInt(a.lot, 10) || 0; vb = parseInt(b.lot, 10) || 0; return sortDir === "asc" ? va - vb : vb - va;
      case "season": va = seasonNameFor(a.dateArrival || a.dateDeparture || "", a.seasonId); vb = seasonNameFor(b.dateArrival || b.dateDeparture || "", b.seasonId); break;
      case "dateDeparture": va = a.dateDeparture || ""; vb = b.dateDeparture || ""; break;
      case "dateArrival": va = a.dateArrival || ""; vb = b.dateArrival || ""; break;
      /* Week sorts by arrival ISO date — chronological order, the
         derived week number alone is ambiguous across years. */
      case "week": va = a.dateArrival || ""; vb = b.dateArrival || ""; break;
      case "saleStatus": { const rank = { protection: 4, pas: 4, partialProtection: 2, goodDelivery: 1 }; va = rank[saleStatusByLot[String(a.lot)]] || 0; vb = rank[saleStatusByLot[String(b.lot)]] || 0; return sortDir === "asc" ? va - vb : vb - va; }
      case "shipperName": va = a.shipperName || ""; vb = b.shipperName || ""; break;
      case "warehouseName": va = a.warehouseName || ""; vb = b.warehouseName || ""; break;
      case "cases": va = calcTotals(a.lines).cases; vb = calcTotals(b.lines).cases; return sortDir === "asc" ? va - vb : vb - va;
      default: va = a[sortCol] || ""; vb = b[sortCol] || "";
    }
    if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    return sortDir === "asc" ? va - vb : vb - va;
  }), [filtered, sortCol, sortDir, seasonNameFor]);

  var toggleSort = function(col) { if (sortCol === col) setSortDir(function(d) { return d === "asc" ? "desc" : "asc"; }); else { setSortCol(col); setSortDir("asc"); } };
  var arrow = function(col) { return sortCol === col ? (sortDir === "asc" ? " \u25b2" : " \u25bc") : ""; };

  const allSelected = sorted.length > 0 && sorted.every(s => selected[s.id]);
  const toggleAll = () => { const ns = {}; if (!allSelected) sorted.forEach(s => { ns[s.id] = true; }); setSelected(ns); };
  const toggleOne = (id, e) => { if (e) e.stopPropagation(); setSelected(p => { const ns = { ...p }; if (ns[id]) delete ns[id]; else ns[id] = true; return ns; }); };
  const selectedCount = Object.keys(selected).length;
  const clearSelection = () => setSelected({});
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [showBulkStatus, setShowBulkStatus] = useState(false);
  /* Shipments have no payments array, so the lock list is empty —
     status changes are always permitted. The dialog still honors the
     payment rule for consistency across modules. */
  const bulkStatusLocked = [];

  /* Excel export. Shipments do not have fees/credits/transfers —
     Summary is one row per shipment with line-totals, Detail is one
     row per product line. No Type column needed since the only
     concept exported is the product line itself. */
  const shipmentsToExport = () => {
    const sel = sorted.filter(sp => selected[sp.id]);
    return sel.length > 0 ? sel : sorted;
  };
  const buildShipmentSummaryRows = () => {
    return shipmentsToExport().map(sp => {
      const tot = calcTotals(sp.lines);
      return {
        Lot: xs(sp.lot),
        "Date Departure": xs(sp.dateDeparture),
        "Date Arrival": xs(sp.dateArrival),
        Shipper: xs(resolveShipperName(sp.supplierId, sp.shipperName)),
        Warehouse: xs(sp.warehouseName),
        Proforma: xs(sp.proforma),
        "Shipper Invoice": xs(sp.shipperInvoice),
        "Customs Broker": xs(sp.customsBrokerName),
        Cases: intOr(tot.cases),
        Pallets: intOr(tot.pallets),
        "Total Value": num2(tot.value),
        Status: xs(sp.status),
      };
    });
  };
  const buildShipmentDetailRows = () => {
    const out = [];
    shipmentsToExport().forEach(sp => {
      const meta = { Lot: xs(sp.lot), "Date Departure": xs(sp.dateDeparture), "Date Arrival": xs(sp.dateArrival), Shipper: xs(resolveShipperName(sp.supplierId, sp.shipperName)), Warehouse: xs(sp.warehouseName), Proforma: xs(sp.proforma), "Shipper Invoice": xs(sp.shipperInvoice) };
      (sp.lines || []).forEach(l => {
        out.push({
          ...meta,
          Type: "Product",
          /* Prefer productBase as the commodity column to match the
             pattern used in Bills/Invoices exports. Description uses
             the resolver so empty productDescription falls back to a
             catalog lookup, never collapses to commodity. */
          Product: xs(l.productBase || l.productName),
          Description: xs(resolveShipLineDesc(l)),
          Category: xs(l.category),
          Cases: intOr(l.cases),
          Pallets: intOr(l.pallets),
          Price: num2(l.price),
          Total: num2(l.total),
          "Export Cert": xs(l.exportCertNumber || l.exportCertManual),
        });
      });
    });
    return out;
  };
  const handleBulkStatusChange = async (newStatus) => {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      const toUpdate = shipments.filter(s => selected[s.id]).map(s => ({ ...s, status: newStatus }));
      await persistMany(toUpdate);
      setShowBulkStatus(false); setSelected({}); setToast(t.saved);
    } catch (e) {
      setError("Could not bulk update: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };
  const handleBulkDelete = async () => {
    if (saving) return;
    setSaving(true); setError(null);
    const ids = Object.keys(selected);
    try {
      await Promise.all(ids.map(id => deleteFromSupabase("shipments", id)));
      const keep = shipments.filter(s => !selected[s.id]);
      setShipments(keep); writeShipmentsCache(keep);
      setShowBulkDelete(false); setSelected({}); setToast(t.deleted);
    } catch (e) {
      setError("Could not bulk delete: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };

  const SS_COLORS = { goodDelivery: { bg: "rgba(16,185,129,0.12)", text: "#059669", border: "rgba(16,185,129,0.3)" }, protection: { bg: "rgba(239,68,68,0.12)", text: "#DC2626", border: "rgba(239,68,68,0.3)" }, pas: { bg: "rgba(245,158,11,0.12)", text: "#D97706", border: "rgba(245,158,11,0.3)" }, partialProtection: { bg: "rgba(217,119,6,0.10)", text: "#B45309", border: "rgba(217,119,6,0.25)" }, pendingPickUp: { bg: "rgba(100,116,139,0.10)", text: "#64748B", border: "rgba(100,116,139,0.25)" }, pendingDelivery: { bg: "rgba(37,99,235,0.10)", text: "#2563EB", border: "rgba(37,99,235,0.25)" } };
  const SS_LABELS_EN = { goodDelivery: "Good Delivery", protection: "Protection", pas: "PAS", partialProtection: "Partial Protection", pendingPickUp: "Pending Pick Up", pendingDelivery: "Pending Delivery" };
  const SS_LABELS_ES = { goodDelivery: "Buena Entrega", protection: "Protección", pas: "PAS", partialProtection: "Protección Parcial", pendingPickUp: "Pendiente de Recolección", pendingDelivery: "Pendiente de Entrega" };
  const ssLabel = (k) => (lang === "es" ? SS_LABELS_ES : SS_LABELS_EN)[k] || k;

  /* Totals: cases, pallets, $ value across selected / all shipments.
     The aggregate function is reused by the selection bar AND the
     module-total chip so both stay in lock-step. */
  const calcShipmentCases = (s) => (s.lines || []).reduce((sum, l) => sum + (parseInt(l.cases, 10) || 0), 0);
  const calcShipmentPallets = (s) => (s.lines || []).reduce((sum, l) => sum + (parseInt(l.pallets, 10) || 0), 0);
  const aggregateShipments = (list) => {
    let cases = 0, pallets = 0, total = 0;
    (list || []).forEach(sh => {
      (sh.lines || []).forEach(l => {
        cases += parseInt(l.cases, 10) || 0;
        pallets += parseInt(l.pallets, 10) || 0;
        total += parseFloat(l.total) || 0;
      });
    });
    return { cases, pallets, total };
  };
  const selectedShipments = useMemo(() => resolvedShipments.filter(s => selected[s.id]), [resolvedShipments, selected]);
  const selectedTotals = useMemo(() => aggregateShipments(selectedShipments), [selectedShipments]);
  /* 2026-09-30 (Arturo: "me esta mostrando lo de la temporada anterior
     mas lo de esta a pesar de que tengo el filtro activo, debe mostrar
     solamente lo que esté filtrado") — this ledger used to aggregate
     `resolvedShipments`, the full unfiltered list, so Season (and every
     other) filter had zero effect on the totals shown here even though
     the table below was already correctly filtered. Aggregate `sorted`
     instead — same rows the table renders. */
  const grandTotals = useMemo(() => aggregateShipments(sorted), [sorted]);
  const selectedCasesTotal = selectedTotals.cases;
  const grandCasesTotal = grandTotals.cases;

  if (loading) return null;
  /* 2026-10-20, Arturo: "vamos ahora con el diseño, habiamos hecho los
     embarques desde el interior de cada uno en modo edicion y modo no
     edicion, ahora la tabla para que coincida con el diseño metalico"
     — same Brushed Steel metal-plate spec documented in App.css
     (search "BRUSHED STEEL" / "QUICK REFERENCE"), applied to the main
     list's column headers: brushed-metal gradient + hairline texture,
     1px #b9bcc0 border, embossed text (dark #1a2636 fill, light-below/
     dark-above text-shadow), square corners. Column headers specifically
     call for 9px per that spec (was 10px plain gray before). */
  /* 2026-10-20, Arturo: "la barra de titulos... dale otro color mismo
     que la barra de shipment header dentro del embarque" — per-cell
     background was fragmenting the hairline texture at each column
     seam; moved the plate to the <tr> itself (see thead render below)
     for one continuous bar, same gradient as EditSectionHeader's
     "Shipment Header"/"Datos del Embarque" bar. `th` keeps only text
     + the shared border. */
  var th = { padding: "7px 5px", textAlign: "left", fontWeight: 700, fontSize: 9, textTransform: "uppercase", letterSpacing: "0.05em", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", background: "transparent", borderTop: "1px solid #b9bcc0", borderBottom: "1px solid #b9bcc0" };

  /* Xero-style header layout — mirrors BillRegistry / InvoiceRegistry.
     Tabs (status), search always-visible, filter popover, ledger
     totals. Print PDF buttons stay at the top alongside Export +
     New, not in the action bar. */
  const lockScopedShipments = lockSupplierId
    ? resolvedShipments.filter(x => String(x.supplierId) === String(lockSupplierId))
    : resolvedShipments;
  const statusCounts = (function () {
    let draft = 0, received = 0, billed = 0;
    lockScopedShipments.forEach(s => {
      const st = s.status || "draft";
      if (st === "draft") draft++;
      else if (st === "received") received++;
      else if (st === "billed") billed++;
    });
    return { draft, received, billed };
  })();
  const tabsCfg = [
    { key: "all",      label: lang === "es" ? "Todas" : "All",     count: lockScopedShipments.length, filterValue: null },
    { key: "draft",    label: t.draft || (lang === "es" ? "Borrador" : "Draft"),   count: statusCounts.draft,    filterValue: "draft" },
    { key: "received", label: t.received || (lang === "es" ? "Recibido" : "Received"), count: statusCounts.received, filterValue: "received" },
    { key: "billed",   label: t.billed || (lang === "es" ? "Facturado" : "Billed"),  count: statusCounts.billed,   filterValue: "billed" },
  ];
  const activeTabKey = cardFilter || "all";
  /* Active filter badge — excludes search + tabs + lockSupplierId. */
  const activeFilterCount =
      (filterShipper ? 1 : 0)
    + (filterWarehouse ? 1 : 0)
    + (filterStatus ? 1 : 0)
    + (filterSaleStatus ? 1 : 0)
    + (filterCert ? 1 : 0)
    + (filterSeasons.length > 0 ? 1 : 0)
    + ((dateFrom || dateTo) ? 1 : 0)
    + (filterWeek ? 1 : 0)
    + (filterProduct ? 1 : 0);
  const clearAllFilters = () => {
    setFilterShipper(""); setFilterWarehouse(""); setFilterStatus("");
    setFilterSaleStatus(""); setFilterCert(""); setFilterSeasons([]);
    setDateFrom(""); setDateTo(""); setFilterWeek("");
  };
  /* Toolbar Send-Tracking-Link handler. Requires exactly one row
     selected — zero or many is ambiguous since the tracking link
     binds to a single shipment + driver phone. */
  const openTrackingFromSelection = () => {
    const sel = sorted.filter(sp => selected[sp.id]);
    if (sel.length !== 1) {
      alert(lang === "es" ? "Selecciona un embarque" : "Select one shipment");
      return;
    }
    setListTrackingOpen(sel[0]);
  };
  /* Outline-style action button — Xero-look. */
  const actBtn = { padding: "5px 12px", borderRadius: 0, border: "1px solid #b9bcc0", background: "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.15)", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", cursor: "pointer", fontSize: 12, fontWeight: 600 };
  const actBtnDanger = Object.assign({}, actBtn, { borderColor: "rgba(220,38,38,0.4)", color: "#B91C1C", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.1)" });
  /* 2026-10-20, Arturo: "para uniformizar, ambos botones, filtro y 3
     puntos, cambia su color como la placa de shipment registry" —
     same dark-silver gradient/border/black-embossed-text as the
     title plate and the popovers these 2 buttons open, so trigger and
     panel read as one continuous plate. */
  const titlePlateBtn = { padding: "5px 12px", borderRadius: 0, border: "1px solid #7d838c", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)", cursor: "pointer", fontSize: 12, fontWeight: 600 };
  const totalsLabel = { textAlign: "right", color: "var(--color-text-muted)", fontSize: 11, whiteSpace: "nowrap" };
  const totalsValue = { textAlign: "right", color: "var(--color-text)", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" };
  /* Bulk-print readiness — used by both Receiving and Carta Porte
     buttons now living in the TOP header. Disabled when no rows
     are selected or any selected shipment fails validation. */
  const bulkPrintBlockers = (function () {
    if (selectedCount === 0) return { ok: false, msg: lang === "es" ? "Selecciona embarques primero" : "Select shipments first" };
    const lines = [];
    selectedShipments.forEach(sh => {
      const errs = validateShipmentForPrint(sh, shipments, lang);
      if (errs.length > 0) lines.push("LOT " + (sh.lot || "?") + ": " + errs.join(", "));
    });
    return {
      ok: lines.length === 0,
      msg: lines.length === 0 ? "" : (lang === "es" ? "No se puede imprimir:" : "Cannot print:") + "\n" + lines.map(l => "• " + l).join("\n"),
    };
  })();

  return (
    <div>
      {/* HEADER ROW — title (left); print docs + filters + export
          + new (right); module totals ledger right below. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6, gap: 24, flexWrap: "wrap" }}>
        {/* 2026-10-20, Arturo: "el titulo Shipment Registry y el
            siguiente renglon, envuelvelo en una placa metalica del
            mismo color que la barra de headers" — same dark silver
            title-bar gradient as the table's header bar / window
            title bar (#c7cbd1→#9aa0a8), black embossed text. */}
        <div style={{ lineHeight: 1.1, padding: "8px 14px", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)" }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" }}>{t.title}</h2>
          <span style={{ fontSize: 10, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{t.showing} {sorted.length} {t.of} {resolvedShipments.length} {t.total}</span>
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 24, flexWrap: "wrap" }}>
          {/* Action group: bulk-print + filter popover + export +
              add. Print buttons disabled when nothing's selected
              OR the selection fails validateShipmentForPrint. */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }} ref={filtersRef}>
            {/* 2026-10-20, Arturo: "la barra search muevela antes de
                filters" — was its own full row below the totals
                ledger; now inline in the same action group, right
                before the Filters button. */}
            {/* 2026-10-20, Arturo: neumorphic white pill, embossed icon + rim. */}
            <span style={{ position: "relative", display: "inline-block" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", filter: "drop-shadow(0 1px 0 rgba(255,255,255,0.9)) drop-shadow(0 -1px 1px rgba(0,0,0,0.3))" }}>
                <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t.search}
                style={{ width: 200, padding: "6px 10px 6px 28px", fontSize: 12, borderRadius: 999, border: "none", background: "#ffffff", boxShadow: "inset 1px 1px 3px rgba(0,0,0,0.15), inset -1px -1px 2px rgba(255,255,255,0.8), 0 1px 1px rgba(255,255,255,0.9), 0 -1px 1px rgba(0,0,0,0.1)", outline: "none", fontFamily: "inherit", color: "var(--color-text-secondary)" }}
              />
            </span>
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                style={{ padding: "5px 10px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}
                title={lang === "es" ? "Limpiar búsqueda" : "Clear search"}
              >×</button>
            )}
            <button
              type="button"
              onClick={() => setShowFilters(v => !v)}
              style={Object.assign({}, titlePlateBtn, { display: "flex", alignItems: "center", gap: 6, padding: "5px 10px" })}
              title={lang === "es" ? "Filtros" : "Filters"}
            >
              <span style={{ fontSize: 16, lineHeight: 1, letterSpacing: 0 }}>⋮</span>
              <span>{lang === "es" ? "Filtros" : "Filters"}</span>
              {activeFilterCount > 0 && (
                <span style={{ minWidth: 18, height: 18, padding: "0 5px", borderRadius: 9, background: "var(--color-accent)", color: "var(--color-accent-text)", fontSize: 10, fontWeight: 700, lineHeight: "18px", textAlign: "center", marginLeft: 2 }}>{activeFilterCount}</span>
              )}
            </button>
            {/* 2026-10-20, Arturo: "necesito un menu de 3 puntos...
                donde metas receiving sheet, bill of lading, send
                tracking link y export excel, y ponlo antes de new
                shipment" — these 4 were separate always-visible
                buttons before; collapsed into one kebab menu, right
                before "+ New Shipment". Export Excel embeds the real
                ExportButton component as its own row (reused as-is,
                never re-implemented) rather than duplicating its
                Summary/Detail logic here. */}
            <span style={{ position: "relative", display: "inline-block" }} ref={moreMenuRef}>
              <button
                type="button"
                onClick={() => setShowMoreMenu(v => !v)}
                style={Object.assign({}, titlePlateBtn, { display: "flex", alignItems: "center", padding: "5px 10px" })}
                title={lang === "es" ? "Más acciones" : "More actions"}
              >
                <span style={{ fontSize: 16, lineHeight: 1, letterSpacing: 0 }}>⋮</span>
              </button>
              {showMoreMenu && (
                /* 2026-10-20, Arturo: "repliques ese formato [la placa
                   de Shipment Registry] en los menus internos como
                   filters y los 3 puntos" — same dark-silver plate
                   gradient/border as the title plate, text switched to
                   the same black-embossed treatment, hover lightens
                   the plate instead of swapping to a flat card color. */
                <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderRadius: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.18)", zIndex: 50, minWidth: 200, overflow: "visible", padding: 4 }}>
                  <button
                    type="button"
                    disabled={!bulkPrintBlockers.ok}
                    title={bulkPrintBlockers.ok ? "" : bulkPrintBlockers.msg}
                    onClick={() => { if (!bulkPrintBlockers.ok) return; setShowMoreMenu(false); handlePrintDocBulk("receiving"); }}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", border: "none", background: "transparent", textAlign: "left", cursor: bulkPrintBlockers.ok ? "pointer" : "not-allowed", opacity: bulkPrintBlockers.ok ? 1 : 0.5, fontSize: 12, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", borderRadius: 0 }}
                    onMouseEnter={(e) => { if (bulkPrintBlockers.ok) e.currentTarget.style.background = "rgba(255,255,255,0.3)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  ><span>🖨</span><span>{t.printReceiving}</span></button>
                  <button
                    type="button"
                    disabled={!bulkPrintBlockers.ok}
                    title={bulkPrintBlockers.ok ? "" : bulkPrintBlockers.msg}
                    onClick={() => { if (!bulkPrintBlockers.ok) return; setShowMoreMenu(false); handlePrintDocBulk("carta-porte"); }}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", border: "none", background: "transparent", textAlign: "left", cursor: bulkPrintBlockers.ok ? "pointer" : "not-allowed", opacity: bulkPrintBlockers.ok ? 1 : 0.5, fontSize: 12, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", borderRadius: 0 }}
                    onMouseEnter={(e) => { if (bulkPrintBlockers.ok) e.currentTarget.style.background = "rgba(255,255,255,0.3)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  ><span>🖨</span><span>{t.printCartaPorte}</span></button>
                  <button
                    type="button"
                    onClick={() => { setShowMoreMenu(false); openTrackingFromSelection(); }}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", border: "none", background: "transparent", textAlign: "left", cursor: "pointer", fontSize: 12, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", borderRadius: 0 }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.3)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  ><span>📡</span><span>{t.sendTrackingLink}</span></button>
                  <div style={{ padding: "4px 10px" }}>
                    {/* 2026-10-20, Arturo: "export excel quedó en
                        color verde con marco verde, dale el mismo
                        formato que los demás elementos de esa misma
                        ventana" — btnStyle matches this menu's other
                        items exactly, instead of ExportButton's own
                        default green pill. */}
                    <ExportButton lang={lang} filenameBase="Shipments" selectedCount={selectedCount} totalCount={sorted.length} buildSummary={buildShipmentSummaryRows} buildDetail={buildShipmentDetailRows} style={{ width: "100%" }} btnStyle={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", border: "none", borderRadius: 0, background: "transparent", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", fontSize: 12, fontWeight: 400 }} />
                  </div>
                </div>
              )}
            </span>
            {/* 2026-10-20, Arturo: "new shipment cambia a la placa
                metalica con relieves, nada debe ser con esquinas
                redondeadas" — Brushed Steel's "colored/active plate"
                variant (same blue gradient already used for Save/
                active toggles elsewhere in this design, see App.css's
                BRUSHED STEEL comment), square corners. */}
            <button onClick={() => { returnModuleRef.current = null; setEditing(null); setShowForm(true); }} style={{ padding: "6px 14px", borderRadius: 0, border: "1px solid #1a3550", background: "linear-gradient(180deg, #6FA3C7 0%, #3E6E96 55%, #284d6b 100%)", boxShadow: "inset 0 1px 1px rgba(255,255,255,0.3), inset 0 -1px 2px rgba(0,0,0,0.2), 0 1px 2px rgba(0,0,0,0.15)", color: "#ffffff", textShadow: "0 1px 1px rgba(0,0,0,0.25)", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>+ {t.add}</button>
            {/* 2026-10-26, Arturo: "capturar un embarque a través de
                voz... mientras voy manejando" — opens VoiceAssistant;
                never saves by itself, see its onComplete below. */}
            <button type="button" onClick={() => setShowVoiceAssistant(true)} title={lang === "es" ? "Dictar embarque por voz" : "Dictate shipment by voice"} style={{ padding: "6px 14px", borderRadius: 0, border: "1px solid #1a3550", background: "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.15)", color: "#1a2636", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>🎙️ {lang === "es" ? "Dictar" : "Dictate"}</button>
            {/* FILTER POPOVER */}
            {showFilters && (
              /* 2026-10-20, Arturo: "repliques ese formato [la placa de
                 Shipment Registry] en los menus internos como filters y
                 los 3 puntos" — same dark-silver plate gradient/border
                 as the title plate; the fields inside (SearchSelect,
                 date inputs) stay their own white boxes sitting on the
                 plate, only the panel surface + header text change. */
              <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 8, zIndex: 50, width: 440, maxWidth: "94vw", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderRadius: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.18)", padding: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{lang === "es" ? "Filtros" : "Filters"}{activeFilterCount > 0 ? " (" + activeFilterCount + ")" : ""}</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {activeFilterCount > 0 && (
                      <button type="button" onClick={clearAllFilters} style={Object.assign({}, actBtn, { padding: "4px 8px", fontSize: 10 })}>{lang === "es" ? "Limpiar todo" : "Clear all"}</button>
                    )}
                    <button type="button" onClick={() => setShowFilters(false)} style={Object.assign({}, actBtn, { padding: "4px 8px", fontSize: 10 })}>{lang === "es" ? "Cerrar" : "Close"}</button>
                  </div>
                </div>
                {/* 2026-10-20, Arturo: "el panel de filters esta gigante,
                    reduce su tamaño" — was 760px/2-col/16px padding;
                    now 440px/2-col (3 for the few single-width fields
                    that fit), tighter padding/gaps, smaller labels. */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                  {!lockSupplierId && (
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Exportador" : "Shipper"}</div>
                      <SearchSelect value={filterShipper} onChange={setFilterShipper} placeholder={t.allShippers} options={uniqueShippers.map(s => ({ value: s, label: s }))} style={filterFieldPlate} />
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Bodega" : "Warehouse"}</div>
                    <SearchSelect value={filterWarehouse} onChange={setFilterWarehouse} placeholder={t.allWarehouses} options={uniqueWarehouses.map(w => ({ value: w, label: w }))} style={filterFieldPlate} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Producto" : "Product"}</div>
                    <SearchSelect value={filterProduct} onChange={setFilterProduct} placeholder={lang === "es" ? "Todos los productos" : "All products"} options={uniqueProducts.map(p => ({ value: p, label: p }))} style={filterFieldPlate} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Temporada" : "Season"}</div>
                    <SearchSelect multi value={filterSeasons} onChange={setFilterSeasons} placeholder={seasonFilterPlaceholder(lang)} groups={buildSeasonFilterOptions(lang)} style={filterFieldPlate} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Desde" : "From"}</div>
                    <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={Object.assign({}, inp, filterFieldPlate)} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Hasta" : "To"}</div>
                    <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={Object.assign({}, inp, filterFieldPlate)} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Semana" : "Week"}</div>
                    <SearchSelect value={filterWeek} onChange={setFilterWeek} placeholder={lang === "es" ? "Todas las semanas" : "All weeks"} options={uniqueWeeks.map(w => ({ value: w, label: w }))} style={filterFieldPlate} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Status venta" : "Sale status"}</div>
                    <SearchSelect value={filterSaleStatus} onChange={setFilterSaleStatus} placeholder={t.allSaleStatuses} options={SALE_STATUSES.map(s => ({ value: s, label: t[s] || s }))} style={filterFieldPlate} />
                  </div>
                  <div style={{ gridColumn: "span 2" }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Certificado" : "Certificate"}</div>
                    <SearchSelect value={filterCert} onChange={setFilterCert} placeholder={t.allCerts} limit={20} options={certFilterOptions} style={filterFieldPlate} />
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* MODULE TOTALS — accounting ledger, right-aligned. Counts
              the currently FILTERED rows (`sorted`), not
              `lockScopedShipments` (which only ever applies the
              lockSupplierId scope, ignoring Season/Shipper/Date/etc.
              — intentional for the tab badges above, wrong here).
              2026-10-20, Arturo: "subelo mas, reduce el tamaño, usa el
              font de pallets, y aplica formato 3d y mismo color no
              negritas" — was plain text (no plate) with Cases singled
              out in bold accent color + its own divider line; now a
              compact Brushed Steel metal plate (self-aligns to the top
              of the action row instead of wrapping to its own line
              below it), all 3 values sharing the exact same style as
              Pallets (totalsValue, unmodified) — no bold, no accent
              color anywhere in this block. */}
          {/* 2026-10-20, Arturo: "ese mismo color aplicalo a la placa
              de la derecha" — same dark title-bar silver as the
              header bar / title plate above, not the lighter
              EditSectionHeader shade it had. */}
          <div style={{ display: "grid", gridTemplateColumns: "auto auto", columnGap: 10, rowGap: 1, alignItems: "center", alignSelf: "flex-start", padding: "4px 10px", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)" }}>
            <span style={{ ...totalsLabel, fontSize: 9, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{lang === "es" ? "Embarques" : "Shipments"}:</span>
            <span style={{ ...totalsValue, fontSize: 10, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" }}>{sorted.length.toLocaleString("en-US")}</span>
            <span style={{ ...totalsLabel, fontSize: 9, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{lang === "es" ? "Pallets" : "Pallets"}:</span>
            <span style={{ ...totalsValue, fontSize: 10, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" }}>{grandTotals.pallets.toLocaleString("en-US")}</span>
            <span style={{ ...totalsLabel, fontSize: 9, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{lang === "es" ? "Cajas" : "Cases"}:</span>
            <span style={{ ...totalsValue, fontSize: 10, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" }}>{grandTotals.cases.toLocaleString("en-US")}</span>
          </div>
        </div>
      </div>
      {error && <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 10px", borderRadius: 8, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.3)", marginBottom: 6, fontSize: 12, color: "#B91C1C", fontWeight: 500 }}><span style={{ fontSize: 14 }}>⚠</span><span style={{ flex: 1, fontFamily: "'JetBrains Mono', monospace", wordBreak: "break-word" }}>{error}</span><button onClick={reload} style={{ padding: "4px 10px", borderRadius: 5, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#B91C1C", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Reintentar" : "Retry"}</button></div>}
      {/* TABS — All / Draft / Received / Billed. 2026-10-20, Arturo:
          "lo mismo para change status, delete selected y clear,
          ademas reubicalos despues de billed" — those 3 buttons used
          to be a separate ACTION BAR row below; moved here, right
          after the Billed tab, same compact Brushed Steel metal plate
          as the module totals ledger above (actBtn/actBtnDanger,
          already that style — just shrunk further to fit this
          tighter row). */}
      {/* 2026-10-20, Arturo: "la ventana que pusiste abajo de los
          headers para envolver all, draft etc, quitala" — dropped the
          sunken wrapper box; tabs stay the uniform color/size from
          the earlier "formato hundido" request, each active one still
          reads as pressed via its own inset shadow. */}
      <div style={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 6, flexWrap: "wrap" }}>
        {tabsCfg.map(tab => {
          const isActive = activeTabKey === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setCardFilter(tab.filterValue)}
              style={{
                padding: "5px 12px",
                background: isActive ? "#ffffff" : "transparent",
                boxShadow: isActive ? "inset 1px 1px 2px rgba(0,0,0,0.15)" : "none",
                border: "none",
                color: "#1a2636",
                fontWeight: 500,
                fontSize: 11,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {tab.label} ({tab.count})
            </button>
          );
        })}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 10, marginBottom: 2 }}>
          <button type="button" onClick={() => setShowBulkStatus(true)} disabled={selectedCount === 0} style={Object.assign({}, actBtn, { padding: "4px 9px", fontSize: 11, opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{lang === "es" ? "Cambiar Estado" : "Change Status"}</button>
          <button type="button" onClick={() => setShowBulkDelete(true)} disabled={selectedCount === 0} style={Object.assign({}, actBtnDanger, { padding: "4px 9px", fontSize: 11, opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{lang === "es" ? "Eliminar" : "Delete Selected"}</button>
          <button type="button" onClick={clearSelection} disabled={selectedCount === 0} style={Object.assign({}, actBtn, { padding: "4px 9px", fontSize: 11, opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{lang === "es" ? "Limpiar" : "Clear"}</button>
        </div>
        {selectedCount > 0 && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11, alignItems: "center", marginBottom: 2 }}>
            <span style={{ fontWeight: 700, color: "var(--color-accent)" }}>{selectedCount} {lang === "es" ? "seleccionados" : "selected"}</span>
            <span style={{ color: "var(--color-text-muted)" }}>|</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Pallets" : "Pallets"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-text)" }}>{selectedTotals.pallets.toLocaleString("en-US")}</span></span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Cajas" : "Cases"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-text)" }}>{selectedTotals.cases.toLocaleString("en-US")}</span></span>
          </div>
        )}
      </div>

      {/* Pending Review panel — proformas submitted via the Shipper
          Portal. Each card represents one proforma; Arturo reviews
          and accepts (one proforma = one new GFI lot) or rejects. */}
      {portalPending.length > 0 && (
        <div style={{ marginBottom: 6, padding: "8px 12px", borderRadius: 8, background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.25)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#1D4ED8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {lang === "es" ? "Proformas Pendientes de Revisión" : "Proformas Pending Review"} · {portalPending.length}
            </div>
            <span style={{ fontSize: 10, color: "var(--color-text-muted)", fontStyle: "italic" }}>{lang === "es" ? "Capturadas por el shipper desde el Portal" : "Captured by shipper via Portal"}</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 8 }}>
            {portalPending.map(function (pair) {
              var emb = pair.embarque, p = pair.pro;
              var prodSummary = (p.lines || []).filter(function (l) { return l.productBase || l.productName; }).map(function (l) { return (l.productName || l.productBase) + (l.cases ? " (" + l.cases + ")" : ""); }).slice(0, 2).join(" · ") || "—";
              var pltTotal = (p.lines || []).reduce(function (s, l) { return s + (parseInt(l.pallets, 10) || 0); }, 0);
              return (
                <div key={p.id} style={{ padding: 10, borderRadius: 6, background: "var(--color-bg-card)", border: "1px solid var(--color-border)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 12, color: "var(--color-accent)" }}>{(emb.consecEmbarque || "—") + " / " + (p.consec || "—")}</span>
                    <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{p.submittedAt ? new Date(p.submittedAt).toLocaleDateString("en-US") : ""}</span>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>{resolveShipperName(emb.supplierId, emb.shipperName) || "—"}</div>
                  <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 4 }}>{prodSummary} · {pltTotal} plt</div>
                  <div style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-secondary)", marginBottom: 6 }}>
                    {(lang === "es" ? "Fact" : "Inv") + ": " + (emb.shipperInvoice || "—")}
                  </div>
                  <button onClick={function () { setReviewingPortal(pair); }} style={{ width: "100%", padding: "6px 10px", borderRadius: 5, border: "1px solid #1D4ED8", background: "#1D4ED8", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{lang === "es" ? "Revisar →" : "Review →"}</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Review modal — read-only summary + Accept/Reject. */}
      {reviewingPortal && (function () {
        var emb = reviewingPortal.embarque, pro = reviewingPortal.pro;
        var trp = pro.transport || {};
        var realPallets = (pro.palletManifest || []).filter(function (p) { return p.palletNum || p.productBase; });
        var sectionHdr = { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)", marginBottom: 6, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" };
        var th = { padding: "5px 6px", textAlign: "left", fontSize: 9, color: "var(--color-text-secondary)" };
        var thR = { ...th, textAlign: "right" };
        var thC = { ...th, textAlign: "center" };
        return (
        <DraggableModal title={(lang === "es" ? "Revisión de Proforma " : "Proforma Review ") + (emb.consecEmbarque || "—") + " / " + (pro.consec || "—")} onClose={function () { setReviewingPortal(null); }} initialWidth={Math.min(1100, Math.floor(window.innerWidth * 0.88))} initialHeight={Math.min(820, window.innerHeight - 60)}>
          <div style={{ padding: "16px 22px", flex: 1, overflowY: "auto", fontSize: 12 }}>

            {/* Header summary — bigger grid since "Proforma No." was
                dropped. Shows everything Arturo needs to identify the
                proforma at a glance. */}
            <div style={{ marginBottom: 16, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px 18px" }}>
              {[
                [lang === "es" ? "Embarque" : "Shipment", emb.consecEmbarque, true],
                [lang === "es" ? "Proforma" : "Proforma", pro.consec, true],
                [lang === "es" ? "Exportador" : "Shipper", emb.shipperName],
                [lang === "es" ? "Factura del Exportador" : "Shipper Invoice", emb.shipperInvoice, true],
                [lang === "es" ? "Enviada" : "Submitted", pro.submittedAt ? new Date(pro.submittedAt).toLocaleString("en-US") : "—"],
                [lang === "es" ? "Bodega" : "Warehouse", pro.warehouseName],
                [lang === "es" ? "Salida" : "Departure", pro.dateDeparture],
                [lang === "es" ? "ETA" : "ETA", pro.dateArrival],
                [lang === "es" ? "Pallets" : "Pallets", realPallets.length || (pro.lines || []).reduce(function (s, l) { return s + (parseInt(l.pallets, 10) || 0); }, 0), true],
                [lang === "es" ? "Agente MX" : "Broker MX", pro.brokerMXName],
                [lang === "es" ? "Agente US" : "Broker US", pro.brokerUSName],
              ].map(function (arr, i) {
                return (
                  <div key={i}>
                    <div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>{arr[0]}</div>
                    <div style={{ fontWeight: 600, fontFamily: arr[2] ? "'JetBrains Mono', monospace" : "inherit", marginTop: 2 }}>{arr[1] || "—"}</div>
                  </div>
                );
              })}
            </div>

            {/* Products — full presentation columns + declared value */}
            <div style={sectionHdr}>{lang === "es" ? "Productos" : "Products"}</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginBottom: 16 }}>
              <thead><tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                <th style={th}>{lang === "es" ? "Producto" : "Product"}</th>
                <th style={th}>{lang === "es" ? "Descripción del Producto" : "Product Description"}</th>
                <th style={thC}>Cat</th>
                <th style={thR}>{lang === "es" ? "Pallets" : "Pallets"}</th>
                <th style={thR}>{lang === "es" ? "Cajas" : "Cases"}</th>
                <th style={thR}>{lang === "es" ? "Valor Decl." : "Declared $"}</th>
                <th style={th}>Cert</th>
              </tr></thead>
              <tbody>{(pro.lines || []).filter(function (l) { return l.productBase || l.productName; }).map(function (l, i) {
                var dv = parseFloat(l.declaredValue || 0);
                return (
                  <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <td style={{ padding: "5px 6px", fontWeight: 600 }}>{l.productName || l.productBase || "—"}</td>
                    <td style={{ padding: "5px 6px", color: "var(--color-text-secondary)" }}>{l.productDescription || "—"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace" }}>{l.category || "—"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{l.pallets || "—"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{l.cases || "—"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: dv > 0 ? "var(--color-accent)" : "var(--color-text-muted)" }}>{dv > 0 ? fmtMoney(dv) : "—"}</td>
                    <td style={{ padding: "5px 6px", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "var(--color-text-muted)" }}>{l.exportCertNumber || l.exportCertManual || "—"}</td>
                  </tr>
                );
              })}</tbody>
            </table>

            {/* Transport — full set of fields the shipper captured. */}
            <div style={sectionHdr}>{lang === "es" ? "Datos del Transportista" : "Carrier Info"}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px 14px", marginBottom: 16 }}>
              {[
                [lang === "es" ? "Línea Transportista" : "Carrier", trp.carrier],
                [lang === "es" ? "Chofer" : "Driver", trp.driver, true],
                [lang === "es" ? "Celular" : "Phone", trp.phone, true],
                [lang === "es" ? "No. Candado" : "Seal #", trp.sealNumber, true],
                [lang === "es" ? "Placas Tracto" : "Truck Plates", trp.platesTruck, true],
                [lang === "es" ? "Econ. Tracto" : "Truck Econ", trp.econTruck, true],
                [lang === "es" ? "Placas Caja" : "Trailer Plates", trp.platesTrailer, true],
                [lang === "es" ? "Econ. Caja" : "Trailer Econ", trp.econTrailer, true],
                ["CAAT", trp.caat, true],
                [lang === "es" ? "Código Alfa" : "Alpha Code", trp.codigoAlpha, true],
                [lang === "es" ? "Termógrafo" : "Thermograph", trp.thermograph, true],
              ].map(function (arr, i) {
                return (
                  <div key={i}>
                    <div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>{arr[0]}</div>
                    <div style={{ fontWeight: 500, fontSize: 11, fontFamily: arr[2] ? "'JetBrains Mono', monospace" : "inherit", marginTop: 2 }}>{arr[1] || "—"}</div>
                  </div>
                );
              })}
            </div>

            {/* Full Pallet Manifest — every captured field per pallet
                so Arturo can verify case totals + traceability before
                accepting. */}
            <div style={sectionHdr}>{lang === "es" ? "Manifiesto de Pallets" : "Pallet Manifest"} · {realPallets.length}</div>
            {realPallets.length === 0 ? (
              <div style={{ marginBottom: 16, fontSize: 11, color: "#B91C1C", fontStyle: "italic" }}>{lang === "es" ? "Sin pallets capturados." : "No pallets captured."}</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginBottom: 16, minWidth: 980 }}>
                <thead><tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                  <th style={thC}>#</th>
                  <th style={th}>{lang === "es" ? "Producto" : "Product"}</th>
                  <th style={thC}>Cat</th>
                  <th style={th}>{lang === "es" ? "Condición" : "Condition"}</th>
                  <th style={th}>{lang === "es" ? "Empaque" : "Pack"}</th>
                  <th style={th}>{lang === "es" ? "Descripción del Producto" : "Size"}</th>
                  <th style={thR}>U/C</th>
                  <th style={thR}>{lang === "es" ? "Cajas/Plt" : "Cases/Plt"}</th>
                  <th style={th}>{lang === "es" ? "Etiqueta" : "Label"}</th>
                  <th style={th}>{lang === "es" ? "Variedad" : "Variety"}</th>
                  <th style={th}>{lang === "es" ? "Trazabilidad" : "Traceability"}</th>
                  <th style={th}>{lang === "es" ? "Productor" : "Grower"}</th>
                </tr></thead>
                <tbody>{realPallets.map(function (p, i) {
                  return (
                    <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                      <td style={{ padding: "5px 6px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }}>{p.palletNum || "—"}</td>
                      <td style={{ padding: "5px 6px", fontWeight: 500 }}>{p.productBase || "—"}</td>
                      <td style={{ padding: "5px 6px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace" }}>{p.category || "—"}</td>
                      <td style={{ padding: "5px 6px" }}>{p.condition || "—"}</td>
                      <td style={{ padding: "5px 6px" }}>{p.packType || "—"}</td>
                      <td style={{ padding: "5px 6px", fontFamily: "'JetBrains Mono', monospace" }}>{p.size || "—"}</td>
                      <td style={{ padding: "5px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{p.unitsCase || "—"}</td>
                      <td style={{ padding: "5px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{p.casesPallet || "—"}</td>
                      <td style={{ padding: "5px 6px", fontFamily: "'JetBrains Mono', monospace" }}>{p.labelInitials || "—"}</td>
                      <td style={{ padding: "5px 6px" }}>{p.variety || "—"}</td>
                      <td style={{ padding: "5px 6px", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "var(--color-text-muted)" }}>{p.traceability || p.julianCode || "—"}</td>
                      <td style={{ padding: "5px 6px", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "var(--color-text-muted)" }}>{p.growerCode || "—"}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
              </div>
            )}

            {/* Documents — grouped by level so Arturo immediately sees
                what's shared across the embarque vs what's specific
                to this proforma. Shipment-level: Invoice (required) +
                Export Certificate + legacy embarque otherDocs.
                Proforma-level: Pallet Photos + Other Documents. */}
            <div style={sectionHdr}>{lang === "es" ? "Documentos" : "Documents"}</div>
            <div style={{ fontSize: 11, marginBottom: 16 }}>
              {/* Shipment-level bucket. Each file now carries a Storage
                  `url` (preferred) and may also carry legacy `data`
                  (base64) for pre-Storage records. We render against
                  whichever exists. */}
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", marginBottom: 5 }}>{lang === "es" ? "Documentos del Embarque" : "Shipment Documents"}</div>
              {(function () {
                var f = emb.invoiceFile;
                var src = f && (f.url || f.data);
                return src ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.25)", borderRadius: 5, marginBottom: 6 }}>
                    <span style={{ color: "#047857", flex: 1 }}>✓ <strong>{lang === "es" ? "Factura" : "Invoice"}:</strong> {f.name} <span style={{ color: "var(--color-text-muted)" }}>({f.size})</span></span>
                    <a href={src} target="_blank" rel="noreferrer" style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-accent)", textDecoration: "none", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Ver" : "View"}</a>
                    <a href={src} download={f.name} style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-accent)", textDecoration: "none", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Descargar" : "Download"}</a>
                  </div>
                ) : (
                  <div style={{ color: "#DC2626", marginBottom: 6 }}>✗ {lang === "es" ? "Sin factura del shipper" : "No shipper invoice file"}</div>
                );
              })()}
              {(function () {
                var f = emb.exportCertFile;
                var src = f && (f.url || f.data);
                if (!src) return null;
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.25)", borderRadius: 5, marginBottom: 6 }}>
                    <span style={{ color: "#047857", flex: 1 }}>✓ <strong>{lang === "es" ? "Cert. Exportación" : "Export Cert"}:</strong> {f.name} <span style={{ color: "var(--color-text-muted)" }}>({f.size})</span></span>
                    <a href={src} target="_blank" rel="noreferrer" style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-accent)", textDecoration: "none", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Ver" : "View"}</a>
                    <a href={src} download={f.name} style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-accent)", textDecoration: "none", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Descargar" : "Download"}</a>
                  </div>
                );
              })()}
              {(emb.otherDocs || []).length > 0 && (
                <div style={{ fontSize: 10, color: "var(--color-text-secondary)", marginTop: 4 }}>
                  {(emb.otherDocs || []).length} {lang === "es" ? "documento(s) extra del embarque:" : "extra shipment document(s):"}{" "}
                  {(emb.otherDocs || []).map(function (d, idx) {
                    var src = d.url || d.data;
                    return <span key={idx} style={{ marginLeft: 6 }}><a href={src} target="_blank" rel="noreferrer" style={{ color: "var(--color-accent)", textDecoration: "none" }}>{d.name}</a></span>;
                  })}
                </div>
              )}

              {/* Proforma-level bucket */}
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", marginTop: 12, marginBottom: 5 }}>{lang === "es" ? "Documentos de la Proforma" : "Proforma Documents"}</div>
              {(pro.palletPhotos || []).length > 0 ? (
                <div style={{ fontSize: 10, color: "var(--color-text-secondary)" }}>
                  {(pro.palletPhotos || []).length} {lang === "es" ? "foto(s) de pallets:" : "pallet photo(s):"}{" "}
                  {(pro.palletPhotos || []).map(function (ph, idx) {
                    var src = ph.url || ph.data;
                    return <span key={idx} style={{ marginLeft: 6 }}><a href={src} target="_blank" rel="noreferrer" style={{ color: "var(--color-accent)", textDecoration: "none" }}>{ph.name}</a></span>;
                  })}
                </div>
              ) : (
                <div style={{ fontSize: 10, color: "var(--color-text-muted)", fontStyle: "italic" }}>{lang === "es" ? "Sin fotos de pallets" : "No pallet photos"}</div>
              )}
              {(pro.otherDocs || []).length > 0 && (
                <div style={{ fontSize: 10, color: "var(--color-text-secondary)", marginTop: 4 }}>
                  {(pro.otherDocs || []).length} {lang === "es" ? "otro(s) documento(s):" : "other document(s):"}{" "}
                  {(pro.otherDocs || []).map(function (d, idx) {
                    var src = d.url || d.data;
                    return <span key={idx} style={{ marginLeft: 6 }}><a href={src} target="_blank" rel="noreferrer" style={{ color: "var(--color-accent)", textDecoration: "none" }}>{d.name}</a></span>;
                  })}
                </div>
              )}
            </div>

            {pro.notes && (
              <>
                <div style={sectionHdr}>{lang === "es" ? "Notas" : "Notes"}</div>
                <div style={{ fontSize: 11, color: "var(--color-text-secondary)", whiteSpace: "pre-wrap", padding: "8px 10px", background: "var(--color-bg)", borderRadius: 5 }}>{pro.notes}</div>
              </>
            )}
          </div>

          <div style={{ padding: "10px 20px", borderTop: "1px solid var(--color-border)", background: "var(--color-bg)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button onClick={function () { setReviewingPortal(null); }} style={{ padding: "7px 14px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{lang === "es" ? "Cerrar" : "Close"}</button>
            <button onClick={function () { rejectPortalSubmission(reviewingPortal); }} style={{ padding: "7px 16px", borderRadius: 5, border: "1px solid rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#DC2626", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>✗ {lang === "es" ? "Rechazar" : "Reject"}</button>
            <button onClick={function () { acceptPortalSubmission(reviewingPortal); }} style={{ padding: "7px 18px", borderRadius: 5, border: "none", background: "#047857", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>✓ {lang === "es" ? "Aceptar (asigna LOT)" : "Accept (assigns LOT)"}</button>
          </div>
        </DraggableModal>
        );
      })()}
      {sorted.length === 0 ? <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--color-text-muted)", fontSize: 14 }}>{t.noResults}</div> : (
        <div style={{ overflowX: "auto", borderRadius: 0, border: "1px solid #b9bcc0", background: "#ffffff" }}>
          <table className="gfi-shipments-list-table" style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }}>
            {/* Compact list header — headers shortened to abbreviations
                (PLT / T.PLT / CERT / SALE / WH / etc.) so the full row
                fits in one line at typical screen widths. Form + detail
                views still use the full translated labels via t.*. */}
            {/* 2026-10-20, Arturo sent the real Shipment Header bar
                screenshot: smooth flat gradient, no hairline texture. */}
            <thead><tr style={{ background: "linear-gradient(180deg, #eef0f2 0%, #d2d5d9 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08)" }}>
              <th style={{ ...th, width: 30, cursor: "default" }}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
              <th onClick={() => toggleSort("lot")} style={{ ...th, textAlign: "center", width: 60 }}>{t.lot}{arrow("lot")}</th>
              <th onClick={() => toggleSort("season")} style={{ ...th, width: 90 }}>{lang === "es" ? "Temporada" : "Season"}{arrow("season")}</th>
              <th onClick={() => toggleSort("dateDeparture")} style={{ ...th, width: 72 }}>{lang === "es" ? "SALIDA" : "DEPART"}{arrow("dateDeparture")}</th>
              <th onClick={() => toggleSort("dateArrival")} style={{ ...th, width: 72 }}>{t.arrival}{arrow("dateArrival")}</th>
              <th onClick={() => toggleSort("week")} style={{ ...th, textAlign: "center", width: 40 }}>{t.week}{arrow("week")}</th>
              <th onClick={() => toggleSort("shipperName")} style={th}>{t.shipper}{arrow("shipperName")}</th>
              <th style={{ ...th, width: 60 }} title={lang === "es" ? "Proforma" : "Proforma"}>PROF</th>
              <th style={th} title={t.products}>PROD</th>
              <th style={th} title={t.itemDesc}>ITEM/PACK</th>
              <th style={{ ...th, textAlign: "center", width: 44 }}>{lang === "es" ? "CAT" : "CAT"}</th>
              <th onClick={() => toggleSort("cases")} style={{ ...th, textAlign: "right", width: 60 }}>{t.cases}{arrow("cases")}</th>
              <th style={{ ...th, textAlign: "right", width: 50 }} title={lang === "es" ? "Pallets" : "Pallets"}>PLT</th>
              <th style={{ ...th, textAlign: "right", width: 56 }} title={lang === "es" ? "Total de pallets del embarque" : "Total pallets across the shipment"}>T.PLT</th>
              {!hideFinancials && <th style={{ ...th, textAlign: "right", width: 70 }} title={t.linePrice}>PRICE</th>}
              <th style={{ ...th, width: 80 }} title={t.shipperInvoice}>INV</th>
              <th style={{ ...th, width: 80 }} title={t.entryNumber}>ENTRY</th>
              <th onClick={() => toggleSort("warehouseName")} style={{ ...th, width: 50, textAlign: "center" }} title={t.warehouse}>WH{arrow("warehouseName")}</th>
              <th style={{ ...th, width: 80 }} title={t.exportCert}>CERT</th>
              <th onClick={() => toggleSort("saleStatus")} style={{ ...th, textAlign: "center", width: 70 }} title={lang === "es" ? "Status Venta" : "Sale Status"}>SALE{arrow("saleStatus")}</th>
              <th style={{ ...th, textAlign: "center", width: 80 }}>{t.status}</th>
              {/* Trailing icon columns — docs count + email-sent badge +
                  Quality Report sent badge. No header text per the
                  project design rule. */}
              <th style={{ ...th, textAlign: "center", width: 40 }}></th>
              <th style={{ ...th, textAlign: "center", width: 30 }}></th>
              <th style={{ ...th, textAlign: "center", width: 30 }}></th>
            </tr></thead>
            <tbody>{sorted.map(s => {
              var lines = (s.lines || []).filter(l => l.productBase || l.productName || l.productId);
              if (lines.length === 0) lines = [{}];
              var st = s.status || "draft"; var ss = STATUS_STYLES[st] || STATUS_STYLES.draft;
              return lines.map((line, li) => {
                var isFirst = li === 0;
                var rowKey = s.id + "-" + li;
                var desc = line.productDescription || "";
                if (!desc && line.productId) {
                  var prod = getProducts().find(function(p) { return String(p.id) === String(line.productId); });
                  if (prod) desc = prod.description || ((prod.size || "") + " " + (prod.unitsCase || "") + "u " + ((prod.category === "Organic" || prod.category === "OG") ? "OG" : "CV") + " " + (prod.labelInitials || ""));
                }
                /* 2026-10-20, Arturo: "al hacer hover... se ilumina
                   el renglon de color verde, cambia ese color por un
                   gris" — then: "plata mejor" — same #b9bcc0 silver
                   family as the rest of this Brushed Steel redesign,
                   not the app's default teal hover. */
                return (
                  <tr key={rowKey} onClick={() => { returnModuleRef.current = null; setViewing(s); }} style={{ cursor: "pointer", transition: "background 0.15s", borderBottom: isFirst || li === lines.length - 1 ? "1px solid var(--color-border)" : "1px solid var(--color-border-light, rgba(0,0,0,0.04))", background: selected[s.id] ? "rgba(185,188,192,0.3)" : "transparent" }} onMouseEnter={e => { e.currentTarget.style.background = selected[s.id] ? "rgba(185,188,192,0.4)" : "rgba(185,188,192,0.2)"; }} onMouseLeave={e => { e.currentTarget.style.background = selected[s.id] ? "rgba(185,188,192,0.3)" : "transparent"; }}>
                    <td style={{ padding: "6px 4px", textAlign: "center", whiteSpace: "nowrap" }} onClick={e => e.stopPropagation()}>{isFirst ? <input type="checkbox" checked={!!selected[s.id]} onChange={e => toggleOne(s.id, e)} /> : null}</td>
                    {/* LOT # — number stays clean. Email-sent + docs
                        indicators live in the two trailing icon columns
                        at the end of the row per the project design rule. */}
                    <td style={{ padding: "6px 4px", fontSize: 9.5, color: "#1a2636", textAlign: "center", whiteSpace: "nowrap" }}>
                      {s.lot}{isFirst ? <RejectionBadge cases={rejectedCasesForLot(s.lot, freshInvoicesShp())} /> : null}
                    </td>
                    <td style={{ padding: "6px 4px", fontSize: 9.5, color: "var(--color-text-muted)", whiteSpace: "nowrap", maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis" }}>{seasonNameFor(s.dateArrival || s.dateDeparture || "", s.seasonId) || "—"}</td>
                    <td style={{ padding: "6px 4px", fontSize: 9.5, color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}>{fmtDate(s.dateDeparture)}</td>
                    <td style={{ padding: "6px 4px", fontSize: 9.5, color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}>{fmtDate(s.dateArrival)}</td>
                    <td style={{ padding: "6px 4px", fontSize: 9.5, textAlign: "center", color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}>{s.dateArrival ? "W" + getISOWeek(s.dateArrival) : ""}</td>
                    <td style={{ padding: "6px 4px", fontSize: 9.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 140 }}>{resolveShipperName(s.supplierId, s.shipperName) || "\u2014"}</td>
                    <td style={{ padding: "6px 4px", fontSize: 9.5, color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}>{s.proforma || "\u2014"}</td>
                    <td style={{ padding: "6px 4px", fontSize: 9.5, whiteSpace: "nowrap" }}>{line.productName || line.productBase || "\u2014"}</td>
                    <td style={{ padding: "6px 4px", fontSize: 9.5, color: "var(--color-text-secondary)", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{desc || "\u2014"}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center", whiteSpace: "nowrap" }}>{line.category ? <span style={{ padding: "2px 6px", borderRadius: 0, fontSize: 9.5, fontWeight: 400, background: line.category === "OG" ? "var(--color-badge-og-bg)" : "var(--color-badge-cv-bg)", color: line.category === "OG" ? "var(--color-badge-og-text)" : "var(--color-badge-cv-text)" }}>{line.category}</span> : ""}</td>
                    <td style={{ padding: "6px 4px", textAlign: "right", fontSize: 9.5, whiteSpace: "nowrap" }}>{parseInt(line.cases, 10) || ""}</td>
                    <td style={{ padding: "6px 4px", textAlign: "right", fontSize: 9.5, whiteSpace: "nowrap" }}>{parseInt(line.pallets, 10) || ""}</td>
                    <td style={{ padding: "6px 4px", textAlign: "right", fontSize: 9.5, color: "var(--color-accent)", whiteSpace: "nowrap" }}>{(s.lines || []).reduce(function (acc, l) { return acc + (parseInt(l.pallets, 10) || 0); }, 0) || ""}</td>
                    {!hideFinancials && <td style={{ padding: "6px 4px", textAlign: "right", fontSize: 9.5, color: "#1a2636", whiteSpace: "nowrap" }}>{parseFloat(line.price) > 0 ? fmtMoney(line.price) : ""}</td>}
                    <td style={{ padding: "6px 4px", fontSize: 9.5, color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}>{s.shipperInvoice || "\u2014"}</td>
                    <td style={{ padding: "6px 4px", fontSize: 9.5, color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}>{s.entryNumber || "\u2014"}</td>
                    {/* Warehouse \u2014 show initials only (e.g. ACS, FB)
                        with the full name in a tooltip so the column
                        stays narrow but still discoverable. */}
                    <td style={{ padding: "6px 4px", fontSize: 9.5, textAlign: "center", color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}>
                      {(function () {
                        var name = s.warehouseName || "";
                        if (!name) return "\u2014";
                        var init = name.split(/\s+/).map(function (w) { return w ? w[0] : ""; }).join("").toUpperCase();
                        return <span title={name}>{init || "\u2014"}</span>;
                      })()}
                    </td>
                    <td style={{ padding: "6px 4px", fontSize: 9.5, color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}>{line.exportCertNumber || line.exportCertManual || (line.exportCertId === "pending" ? "\u23f3" : "\u2014")}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center", whiteSpace: "nowrap" }}>{(function() { var sk = saleStatusByLot[String(s.lot)]; if (!sk) return "\u2014"; var sc = SS_COLORS[sk]; return <span style={{ padding: "2px 7px", borderRadius: 0, fontSize: 9.5, fontWeight: 400, background: sc.bg, color: sc.text, border: "1px solid " + sc.border }}>{ssLabel(sk)}</span>; })()}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center", whiteSpace: "nowrap" }}><span style={{ padding: "3px 8px", borderRadius: 0, fontSize: 9.5, fontWeight: 400, background: ss.bg, color: ss.text, border: "1px solid " + ss.border, whiteSpace: "nowrap" }}>{t[st]}</span></td>
                    {/* Docs indicator — counts shipment.documents plus
                        any legacy portal-side file slots so embarques
                        from before the inheritance refactor still show
                        an icon. Hidden when count = 0. */}
                    <td style={{ padding: "7px 4px", textAlign: "center", width: 40 }}>
                      {isFirst ? (function () {
                        var n = (s.documents || []).length;
                        if (s.invoiceFile && (s.invoiceFile.url || s.invoiceFile.data || s.invoiceFile.name)) n += 1;
                        if (s.exportCertFile && (s.exportCertFile.url || s.exportCertFile.data || s.exportCertFile.name)) n += 1;
                        if (Array.isArray(s.otherDocs)) n += s.otherDocs.length;
                        if (s.bolFile && (s.bolFile.url || s.bolFile.data || s.bolFile.name)) n += 1;
                        if (n === 0) return null;
                        return (
                          <span title={(lang === "es" ? "Archivos adjuntos: " : "Attached files: ") + n} style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                            <svg width="14" height="16" viewBox="0 0 14 16" fill="none" stroke="#6B7280" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1.5 1h7l4 4v10h-11V1z"/>
                              <path d="M8.5 1v4h4" fill="#D1D5DB"/>
                              <line x1="4" y1="8" x2="10" y2="8" strokeWidth="1"/>
                              <line x1="4" y1="10.5" x2="10" y2="10.5" strokeWidth="1"/>
                              <line x1="4" y1="13" x2="7" y2="13" strokeWidth="1"/>
                            </svg>
                            <span style={{ fontSize: 10, fontWeight: 500, color: "#6B7280" }}>{n}</span>
                          </span>
                        );
                      })() : null}
                    </td>
                    {/* Email-sent indicator — envelope with green check. */}
                    <td style={{ padding: "7px 4px", textAlign: "center", width: 30 }}>
                      {isFirst && s.warehouseEmailSentAt && (
                        <span title={(lang === "es" ? "Enviado a bodega " : "Sent to warehouse ") + new Date(s.warehouseEmailSentAt).toLocaleString()} style={{ display: "inline-flex", alignItems: "center" }}>
                          <svg width="18" height="14" viewBox="0 0 18 14" fill="none" stroke="#059669" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="0.5" y="0.5" width="14" height="11" rx="2"/>
                            <path d="M0.5 0.5L7.5 6.5L14.5 0.5"/>
                            <circle cx="14" cy="10" r="3.5" fill="#059669" stroke="none"/>
                            <path d="M12.5 10L13.5 11L15.5 9" stroke="white" strokeWidth="1.5"/>
                          </svg>
                        </span>
                      )}
                    </td>
                    {/* Quality Report (Texas QC) sent indicator (2026-09-14,
                       Arturo: "debo poder visualizar cuando un reporte de
                       calidad de texas ya fue enviado en la tabla de
                       embarques") — same envelope+check glyph as the
                       warehouse-email indicator, just colored to match
                       the Texas QC feature's own accent (var(--color-accent),
                       same teal already used for its "Vista previa" button
                       and the Pallet Manifest's bracket header) so the two
                       distinct "sent" badges in this row don't read as the
                       same thing at a glance. */}
                    <td style={{ padding: "7px 4px", textAlign: "center", width: 30 }}>
                      {isFirst && s.qualityReportSentAt && (
                        <span title={(lang === "es" ? "Reporte de Calidad enviado " : "Quality Report sent ") + new Date(s.qualityReportSentAt).toLocaleString()} style={{ display: "inline-flex", alignItems: "center" }}>
                          <svg width="18" height="14" viewBox="0 0 18 14" fill="none" stroke="#2A7A6F" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="0.5" y="0.5" width="14" height="11" rx="2"/>
                            <path d="M0.5 0.5L7.5 6.5L14.5 0.5"/>
                            <circle cx="14" cy="10" r="3.5" fill="#2A7A6F" stroke="none"/>
                            <path d="M12.5 10L13.5 11L15.5 9" stroke="white" strokeWidth="1.5"/>
                          </svg>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              });
            })}</tbody>
          </table>
        </div>
      )}
      {viewing && <DetailPanel
        shipment={viewing}
        shipments={shipments}
        t={t} lang={lang}
        lockSupplierId={lockSupplierId}
        hideFinancials={hideFinancials}
        onSave={(updated) => { handleSave(updated); setViewing(updated); }}
        onDelete={handleDelete}
        onDuplicate={handleDuplicate}
        onPrintDoc={handlePrintDocSingle}
        onReject={rejectAcceptedShipment}
        onClose={() => { setViewing(null); if (openedFromExternalRef.current) { openedFromExternalRef.current = false; window.dispatchEvent(new CustomEvent("gfi-shipment-closed")); } closeToOrigin(); }}
        onCreateBill={async (shipment) => {
          /* 2026-10-14 — same race as the edit form's own Create Bill
             button (see that comment): navigation/bill-creation used
             to fire on a fixed timer without waiting for the
             status:"billed" write to actually land, so a subsequent
             read could still see "received". Await persistSingle
             before doing anything else. */
          var updated = { ...shipment, status: "billed" };
          var ex = shipments.find(x => x.id === updated.id);
          if (ex) {
            try {
              await persistSingle(updated);
            } catch (e) {
              setError("Could not flip shipment to billed: " + (e.message || String(e)));
            }
          }
          setViewing(null);
          returnModuleRef.current = null;
          if (window.__gfiNavigate) window.__gfiNavigate("bills");
          setTimeout(function() { if (window.__gfiBillCreate) window.__gfiBillCreate(updated); }, 200);
        }} />}
      {showForm && !viewing && <ShipmentForm shipment={editing} shipments={shipments} t={t} lang={lang} lockSupplierId={lockSupplierId} hideFinancials={hideFinancials} onSave={handleSave} onDelete={handleDelete} onCancel={() => { setShowForm(false); setEditing(null); closeToOrigin(); }} />}
      {showVoiceAssistant && (
        <VoiceAssistant
          contract={buildShipmentVoiceContract({ suppliers: getSuppliers(), warehouses: getActiveWarehouses() })}
          lang={lang}
          onClose={() => setShowVoiceAssistant(false)}
          onComplete={(voiceState) => {
            /* Hands off to the EXACT same manual flow as "+ New
               shipment" — the draft just arrives pre-filled. Arturo
               still reviews every field in the real form and clicks
               the real Save button; nothing here calls handleSave. */
            var draft = draftShipmentFromVoiceState(voiceState);
            setShowVoiceAssistant(false);
            returnModuleRef.current = null;
            setEditing(draft);
            setShowForm(true);
          }}
        />
      )}
      {showBulkDelete && <BulkDeleteDialog count={selectedCount} lang={lang} onCancel={() => setShowBulkDelete(false)} onConfirm={handleBulkDelete} />}
      {showBulkStatus && <BulkStatusDialog count={selectedCount} lockedRecords={bulkStatusLocked} statuses={STATUSES.map(s => ({ value: s, label: t[s] || s }))} lang={lang} onCancel={() => setShowBulkStatus(false)} onConfirm={handleBulkStatusChange} />}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
      {listTrackingOpen && (
        <TrackingLinkModal
          lang={lang}
          payload={listTrackingOpen}
          onClose={() => setListTrackingOpen(null)}
        />
      )}
    </div>
  );
}